const cardData = {
  "the-fool-tarot-card-reading-meaning": {
    slug: "the-fool-tarot-card-reading-meaning",
    name: "The Fool",
    subtitle: "New Beginnings, Innocence, Leap of Faith",
    image: "/card/the-fool-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The Fool represents the spirit of adventure, innocence and infinite potential. Unburdened by past experiences or future fears, this card signifies a moment where you are free to pursue something new, even if you don't know where the path leads. The Fool does not fear the unknown; rather, it trusts in life's timing. Whether it's starting a new relationship, job or life chapter, the Fool invites you to leap with joy, curiosity and openness. It encourages living in the present, letting go of hesitation and listening to your inner voice as you chart new territory. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "New beginnings: a fresh chapter or clean slate",
      "Spontaneity: embracing adventure with childlike wonder",
      "Innocence: approaching life with purity and an open heart",
      "Faith: trusting life and your inner guidance",
      "Potential: unlimited possibilities ahead"
    ],
    reversed: [
      "Recklessness: acting without thinking of consequences",
      "Naivety: ignoring important warnings or red flags",
      "Fear of the unknown: resisting growth due to insecurity",
      "Disconnection: lack of direction or purpose",
      "Missed opportunities: failure to seize the moment due to hesitation"
    ],
    reading: `When The Fool appears in your reading, it is doing more than suggesting change; it is summoning you to awaken.

You are being asked to stand barefoot at the edge of your life as you know it, to feel the earth humming beneath you and the unknown beckoning beyond. The Fool doesn't offer a map; it offers a moment: meaningful, raw and filled with possibility.

This card appears when logic no longer leads, when the heart speaks louder than reason and when the soul is ready to walk paths the mind cannot comprehend. If you've been feeling stuck, uncertain or afraid to make a move. The Fool reminds you that freedom lives on the other side of fear.

Yet this is not recklessness. This is spiritual trust. A surrendering to the timing of life, to instinct and to the call of something greater than comfort. You may not know the full picture, but you know enough to begin. And that is enough.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What have I been hesitating to begin, not because it's wrong, but because it's unfamiliar?
2. Where have I allowed fear or practicality to override the truth of my spirit?
3. Can I trust that life meets me halfway when I walk in alignment with my calling?

When The Fool appears, the old story is finished. The next one begins not with certainty, but with courage and your willingness to say yes to the mystery.`,
    mirroring: [
      "the-magician-tarot-card-reading-meaning",
      "the-hermit-tarot-card-reading-meaning",
      "the-world-tarot-card-reading-meaning"
    ]
  },

  "the-magician-tarot-card-reading-meaning": {
    slug: "the-magician-tarot-card-reading-meaning",
    name: "The Magician",
    subtitle: "Manifestation, Willpower, Creation",
    image: "/card/the-magician-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The Magician stands at the threshold between the spiritual and material worlds, channeling focused energy into tangible form. This card speaks of your ability to create your reality through focused intent, inspired action and confidence. Everything you need is already at your fingertips; tools, skills and insight. The Magician reminds you that with alignment and clarity, manifestation is not a miracle, but a discipline. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Manifestation: bringing desires into reality with clarity",
      "Willpower: focused intention backed by inspired action",
      "Skill: using your natural talents and tools wisely",
      "Alignment: balancing spiritual guidance with grounded steps",
      "Initiative: taking charge of your path with confidence"
    ],
    reversed: [
      "Manipulation: using skills for selfish or deceptive ends",
      "Scattered focus: lacking direction or concentration",
      "Missed potential: not using your resources effectively",
      "Illusions: believing in false power or superficial charm",
      "Insecurity: doubting your abilities despite having the tools"
    ],
    reading: `When The Magician appears, it is a clear signal of purpose echoing through your life.

This card is a mirror reflecting your own real potential, not just to dream, but to do. Life has placed tools before you: your intellect, your intuition, your passion, your practical know how. Now, the question is not if you are ready, but whether you are willing to begin.

The Magician doesn't wait for perfect timing; it creates it. It calls you to align your inner truth with outer action, to speak what you mean and move in the direction of what you seek. Be mindful, though: power used without ethics turns to trickery. Channel your magic through clarity, humility and heart.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Am I using all the tools available to me right now?
2. Where am I holding back from taking inspired action?
3. What would happen if I acted like life is working with me?

The Magician is your permission slip; now shape the unseen into the seen.`,
    mirroring: [
      "the-fool-tarot-card-reading-meaning",
      "the-empress-tarot-card-reading-meaning",
      "the-hierophant-tarot-card-reading-meaning"
    ]
  },

  "the-high-priestess-tarot-card-reading-meaning": {
    slug: "the-high-priestess-tarot-card-reading-meaning",
    name: "The High Priestess",
    subtitle: "Mystery, Intuition, Inner Knowledge",
    image: "/card/the-high-priestess-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The High Priestess sits silently between the pillars of light and shadow, guarding the threshold of hidden truths. She does not speak; she invites. Her presence is a reminder that not all answers lie in logic. Some truths are felt within, in dreams, symbols and subtle knowing. She represents your inner voice, the mysteries of the subconscious and the deep well of wisdom accessible through stillness. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Intuition: trusting your inner voice over external noise",
      "Mystery: accepting that some truths unfold in the timing of life",
      "Silence: stepping back to observe before acting",
      "Wisdom: accessing deep knowing from within",
      "Spiritual insight: attuning to dreams and meaningful symbols"
    ],
    reversed: [
      "Secrets: things hidden or withheld, within or without",
      "Disconnection: ignoring your inner guidance",
      "Confusion: unclear signs or clouded perception",
      "Repression: holding back truths or feelings",
      "Mistrust: doubting your spiritual or intuitive senses"
    ],
    reading: `When The High Priestess appears in your reading, she brings no noise; only presence.

She calls you into the temple of your own inner world, asking you to sit with the silence, to listen for what is not said aloud. This card does not urge you to act, but to feel, to sense, to know without proof. In a world of constant noise, she is the needed pause that restores clarity.

You may be on the edge of a truth, but it is not yet time to force it. Trust the rhythm of revelation. Honor your gut feelings and your nighttime visions. What seems invisible may already be forming in your soul's awareness.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What have I been sensing, but haven't fully acknowledged?
2. Where might I need to pause before seeking external answers?
3. What wisdom is ready to rise from within if I simply listen?

The High Priestess holds the veil, but she will lift it when you're ready to see. Until then, remain still and trust the knowing that lives in silence.`,
    mirroring: [
      "the-hermit-tarot-card-reading-meaning",
      "the-moon-tarot-card-reading-meaning",
      "the-hanged-man-tarot-card-reading-meaning"
    ]
  },

  "the-empress-tarot-card-reading-meaning": {
    slug: "the-empress-tarot-card-reading-meaning",
    name: "The Empress",
    subtitle: "Fertility, Nurturing, Abundance",
    image: "/card/the-empress-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The Empress embodies the essence of creation and the rhythm of nature's cycles. She is the fertile womb of ideas, art, love and life itself. Rooted in sensuality, comfort and care, this card speaks to nurturing yourself and others from a place of deep love. She reminds you that beauty grows best when it is nourished consistently and gently, not rushed or forced. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Creativity: birthing new ideas or projects",
      "Abundance: attracting wealth, love and comfort",
      "Nurturing: caring for yourself or others with gentleness",
      "Fertility: potential for growth in any form",
      "Sensuality: connecting to the body and physical pleasure"
    ],
    reversed: [
      "Neglect: giving too much to others while ignoring your needs",
      "Creative block: stagnation or burnout",
      "Dependence: overreliance on comfort or others' validation",
      "Emotional smothering: overwhelming others with care",
      "Disconnected: losing touch with nature or your body"
    ],
    reading: `When The Empress appears, she does not knock; she opens the door wide and invites you to come home to yourself.

This card is a sanctuary: a reminder that your worth is not in productivity, but in presence. The Empress doesn't rush; she receives, she nurtures, she allows. Whether you're planting seeds of a dream or caring for someone dear, she asks you to do so with tenderness and trust in the timing of life.

Your senses are useful tools. Indulge in beauty. Rest without guilt. Love without reserve. This card is an invitation to return to softness, to reclaim your creativity and to honor the cycles of birth, bloom and rest.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Am I nurturing myself as deeply as I nurture others?
2. What creative or emotional seed wants more attention?
3. How can I embody abundance in a grounded, graceful way?

The Empress reminds you that to grow anything worthwhile, you must first create a fertile space within yourself.`,
    mirroring: [
      "the-magician-tarot-card-reading-meaning",
      "the-moon-tarot-card-reading-meaning",
      "the-star-tarot-card-reading-meaning"
    ]
  },

  "the-emperor-tarot-card-reading-meaning": {
    slug: "the-emperor-tarot-card-reading-meaning",
    name: "The Emperor",
    subtitle: "Structure, Authority, Stability",
    image: "/card/the-emperor-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The Emperor sits upon a throne of stone, embodying leadership, logic and grounded power. He is the architect of order, the protector of legacy and the container of potential. When this card appears, it invites you to take responsibility, not through force, but through clarity, discipline and wisdom. True authority is not control; it's presence, consistency and earned respect. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Structure: creating systems that support your vision",
      "Authority: stepping into responsible leadership",
      "Discipline: establishing boundaries and focus",
      "Security: building stability and long term foundations",
      "Legacy: taking actions with future consequences in mind"
    ],
    reversed: [
      "Rigidity: being too controlling or inflexible",
      "Abuse of power: dominating rather than guiding",
      "Instability: weak foundations or lack of structure",
      "Resistance: rebelling against necessary discipline",
      "Ego: leading from pride rather than purpose"
    ],
    reading: `When The Emperor arrives, he is not here to charm you; he's here to challenge you.

This card calls forth the part of you that must take command of your circumstances, make hard decisions and build something that lasts. It's time to root your energy in action, not just dreams. The Emperor shows up when stability must be chosen over chaos, when duty must outweigh impulse.

But beware: power is not license. Real strength lies in self mastery. This card invites you to lead with integrity, to protect without possessiveness and to create structures that uplift, not restrict.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Where do I need to build stronger foundations in my life?
2. How can I lead with integrity and clarity?
3. Am I using power wisely or is it using me?

The Emperor asks you to rise, not as a ruler over others, but as the sovereign of your own path.`,
    mirroring: [
      "the-empress-tarot-card-reading-meaning",
      "justice-tarot-card-reading-meaning",
      "the-hierophant-tarot-card-reading-meaning"
    ]
  },

  "the-hierophant-tarot-card-reading-meaning": {
    slug: "the-hierophant-tarot-card-reading-meaning",
    name: "The Hierophant",
    subtitle: "Tradition, Wisdom, Spiritual Authority",
    image: "/card/the-hierophant-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The Hierophant is the guardian of inner knowledge passed down through time. He represents tradition, belief systems, mentors and rituals that connect us to meaning beyond the material. This card asks you to examine the frameworks that guide your life; are they aligned with your truth or inherited without question? The Hierophant invites you to learn, reflect and discern. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Spiritual learning: seeking guidance or deeper meaning",
      "Tradition: honoring rituals, teachings or structure",
      "Mentorship: finding or becoming a teacher or guide",
      "Community: connecting to shared beliefs or values",
      "Integrity: aligning personal actions with ethical principles"
    ],
    reversed: [
      "Dogma: blindly following outdated or rigid beliefs",
      "Rebellion: resisting guidance that could be beneficial",
      "Disconnection: feeling spiritually isolated",
      "Inauthenticity: living by others' rules instead of your own",
      "Confusion: unsure of your beliefs or moral compass"
    ],
    reading: `When The Hierophant appears, he does not instruct; he reflects.

This card is not about blind obedience, but about conscious alignment. Are you walking a path that resonates with your soul or simply following a map drawn by others? The Hierophant honors wisdom traditions, but he also invites questioning. He teaches, but he also listens.

This moment may call for mentorship or a return to spiritual practice. Or perhaps it's time to become the teacher, to pass on what you've learned. Examine your beliefs. Engage with meaningful texts, meditations or elders. Let ancient wisdom illuminate your modern path.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What values or traditions truly resonate with me?
2. Who or what can guide me toward deeper truth?
3. Where might I be conforming rather than aligning?

The Hierophant reminds you: knowledge is meaningful, but only when it awakens the heart as well as the mind.`,
    mirroring: [
      "the-magician-tarot-card-reading-meaning",
      "the-emperor-tarot-card-reading-meaning",
      "temperance-tarot-card-reading-meaning"
    ]
  },

  "the-lovers-tarot-card-reading-meaning": {
    slug: "the-lovers-tarot-card-reading-meaning",
    name: "The Lovers",
    subtitle: "Union, Choice, Deep Alignment",
    image: "/card/the-lovers-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The Lovers represents a deep union, not just between two people, but between your soul and your truth. This card embodies harmony, mutual attraction and the subtle pull of love and purpose. At its heart, it's not merely about romance, but about alignment. It asks: Are your choices guided by love or fear? The Lovers card calls you to integrate the fragmented parts of yourself and make decisions from a place of honesty and soulful connection. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Deep union: a meaningful connection or relationship",
      "Alignment: choices made in harmony with your values",
      "Attraction: magnetic energy pulling you toward a person or purpose",
      "Commitment: deepening bonds through trust and honesty",
      "Moral crossroads: a decision that defines your integrity"
    ],
    reversed: [
      "Disharmony: imbalance in relationships or within the self",
      "Conflict: values misaligned or difficult decisions avoided",
      "Temptation: being swayed by surface level desires",
      "Indecision: fear of making the wrong choice",
      "Separation: a loss of connection or mutual understanding"
    ],
    reading: `When The Lovers appear in a reading, you are being asked to look beyond the surface of a relationship or choice. This is not a card of fleeting emotions or shallow attraction; it is a mirror reflecting your deepest values and the agreements you've made with your own heart. Whether romantic, platonic or spiritual, this card signals a crossroads that requires truth. Not just emotional truth, but soul truth. What you choose now will shape who you become.

The Lovers do not simply represent passion; they represent alignment. That feeling when something or someone fits into your life with deep recognition. It may feel like destiny, but it still demands effort, courage and self awareness. This card calls you to choose with integrity, to ask not only what you want, but what is worthy of your heart. Love, in its highest form, is not just a feeling. It's a choice made over and over again, even when it's hard.

There may be temptation pulling you in multiple directions; a battle between the familiar and the transformative, the easy and the right. The Lovers remind you that true harmony doesn't ask you to lose yourself; it invites you to become more fully who you are. Choose not just who you love, but how you love and how you show up for love in all areas of your life.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Am I choosing from love or fear?
2. Where in my life am I being asked to realign with my deepest values?
3. What parts of me need to be integrated before I can truly connect with another?

The Lovers invite you into union, not just with another, but with the truth of your soul.`,
    mirroring: [
      "the-hierophant-tarot-card-reading-meaning",
      "temperance-tarot-card-reading-meaning",
      "two-of-cups-tarot-card-reading-meaning"
    ]
  },

  "the-chariot-tarot-card-reading-meaning": {
    slug: "the-chariot-tarot-card-reading-meaning",
    name: "The Chariot",
    subtitle: "Willpower, Triumph, Direction",
    image: "/card/the-chariot-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The Chariot symbolizes momentum born of discipline and determination. It is the force that arises when your will aligns with your purpose. Where there was once hesitation, there is now forward motion, not reckless, but deliberate. This card signals that victory is within reach, but only if you stay committed to your path and steer with both control and inner conviction. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Determination: a strong will pushing past obstacles",
      "Victory: triumph through discipline and focus",
      "Control: steering your life with conscious direction",
      "Courage: facing challenges with boldness and strength",
      "Progress: rapid forward momentum aligned with purpose"
    ],
    reversed: [
      "Lack of direction: feeling pulled in opposite directions",
      "Forcefulness: trying to control everything without flow",
      "Inner conflict: will and heart misaligned",
      "Delay: progress hindered by self doubt or resistance",
      "Defeat: losing focus or giving up before the breakthrough"
    ],
    reading: `When The Chariot appears in your reading, it brings a thunderous message: you are not lost, you are becoming. This is the card of the warrior spirit, not the kind who fights with fists, but the one who battles chaos with clarity and rides storms with unwavering resolve. The path before you is not smooth, but it is yours to claim. Your power now lies in forward motion and in trusting the direction your soul is pulling you toward.

The two sphinxes or horses often shown on the card, represent opposing forces. They may be ambition and fear, desire and duty, logic and emotion. The Chariot teaches that success does not come from choosing one over the other, but from holding the reins of both. It is your inner alignment that brings outer triumph. This moment calls for a decision, a commitment and the resolve to drive through distraction without losing your center.

This card does not promise ease. It demands focus. It reminds you that mastery isn't about domination; it's about devotion. Devotion to the journey, to your growth, to the version of yourself that refuses to abandon the mission even when it's hard. Let this card be your war cry: I will not be ruled by circumstance. I steer my fate with both hands.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Where am I being asked to take the reins of my own destiny?
2. What conflicting forces must I reconcile to move forward with strength?
3. Am I chasing external success or aligned progress?

The Chariot arrives to declare that momentum is meaningful and when guided by purpose, it becomes unstoppable.`,
    mirroring: [
      "strength-tarot-card-reading-meaning",
      "six-of-wands-tarot-card-reading-meaning",
      "two-of-wands-tarot-card-reading-meaning"
    ]
  },

  "strength-tarot-card-reading-meaning": {
    slug: "strength-tarot-card-reading-meaning",
    name: "Strength",
    subtitle: "Inner Power, Compassion, Resilience",
    image: "/card/strength-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `Strength is not brute force; it is calm resolve, quiet courage and the ability to tame the wild within. This card represents emotional mastery, patience and the graceful balance between gentleness and power. It teaches that true courage lies in meeting fear, anger or pain with compassion rather than control. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Inner strength: tapping into courage during adversity",
      "Compassion: responding to challenges with grace",
      "Resilience: quiet power that endures difficulty",
      "Self control: managing emotions with patience and presence",
      "Healing: turning wounds into wisdom"
    ],
    reversed: [
      "Self doubt: feeling inadequate or emotionally fragile",
      "Impatience: reacting instead of responding",
      "Inner turmoil: losing grip over intense emotions",
      "Abuse of power: forcing outcomes without empathy",
      "Fear: avoiding confrontation with one's shadow"
    ],
    reading: `When Strength appears, it does not roar. It suggests; softly, firmly; reminding you that tenderness can be more powerful than fury. This card invites you to look not at how loudly you resist life's storms, but how bravely you sit with them. True strength is not about how much you can carry, but how much peace you can keep while carrying it. You are being called to stand rooted in love, not just for others, but for your own wounded parts.

The lion in this card does not need to be caged; it is tamed through presence. So too, the parts of you that feel wild, angry, fearful or ashamed are not enemies to be conquered. They are energies to be met with compassion. Strength asks: Can you hold your pain with the same hands you use to hold your joy? Can you stay soft, even when life hardens around you?

This is the moment to gather yourself inward. To stop pushing, stop forcing and instead cultivate quiet resilience. If you've been tested, triggered or thrown off center; come back to your breath. You are stronger than you know, not because you win every battle, but because you have the heart to keep walking and the grace to do it kindly.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What part of me is asking to be tamed through love, not fear?
2. How can I show myself compassion in this challenge?
3. Am I measuring strength by appearances or inner peace?

Strength appears when your softness becomes your superpower and your endurance, a meaningful act of self respect.`,
    mirroring: [
      "the-chariot-tarot-card-reading-meaning",
      "the-empress-tarot-card-reading-meaning",
      "nine-of-wands-tarot-card-reading-meaning"
    ]
  },

  "the-hermit-tarot-card-reading-meaning": {
    slug: "the-hermit-tarot-card-reading-meaning",
    name: "The Hermit",
    subtitle: "Introspection, Solitude, Inner Guidance",
    image: "/card/the-hermit-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The Hermit is the seeker who steps away from the world not out of fear, but to find deeper truth. This card signifies a period of introspection, retreat or spiritual searching. It calls you to look within, to listen for your own wisdom rather than the noise of others. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Solitude: choosing time alone for reflection",
      "Inner guidance: trusting your own insight",
      "Spiritual quest: searching for deeper meaning",
      "Withdrawal: stepping back to recalibrate",
      "Truth: uncovering authentic answers within"
    ],
    reversed: [
      "Isolation: feeling lost or emotionally withdrawn",
      "Loneliness: resisting connection with others",
      "Avoidance: using solitude to escape problems",
      "Disconnection: losing touch with your inner light",
      "Stagnation: spiritual or emotional inertia"
    ],
    reading: `When The Hermit appears, it's an invitation to pause, not out of weakness, but out of wisdom. You are being called to step away from external validation and return to your meaningful cave. There, in the stillness, lives your truth. The answers you seek may not be loud or immediate, but they are waiting; beneath the noise, behind the distractions, within you.

This card honors those moments in life when the path becomes unclear and no one's voice but your own can guide you. It reminds you that solitude is not loneliness; it is sanctuary. And from this space, your light is rekindled. The Hermit walks with a lantern not just to find the way, but to become the light for others once again.

If you've been overwhelmed, disconnected or uncertain, The Hermit asks you to turn inward, not to hide, but to rediscover. Let go of others' expectations and remember your inner compass. Silence isn't absence; it's presence in its purest form.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Where in my life am I craving silence or space?
2. What inner wisdom have I been ignoring?
3. Am I seeking answers outward when I already carry them within?

When The Hermit appears, it's not the world that needs changing; it's the way you listen to your own light.`,
    mirroring: [
      "the-high-priestess-tarot-card-reading-meaning",
      "four-of-swords-tarot-card-reading-meaning",
      "the-hanged-man-tarot-card-reading-meaning"
    ]
  },

  "wheel-of-fortune-tarot-card-reading-meaning": {
    slug: "wheel-of-fortune-tarot-card-reading-meaning",
    name: "Wheel of Fortune",
    subtitle: "Cycles, Destiny, Turning Points",
    image: "/card/wheel-of-fortune-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The Wheel of Fortune reminds you that nothing stays the same. It is the meaningful turning of life's wheel, where fate, choice and unseen forces collide. This card represents change, karmic lessons and the cyclical nature of fortune and challenge. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Destiny: events aligning beyond your control",
      "Cycles: beginnings and endings in motion",
      "Opportunity: a window to shift your path",
      "Karma: reaping what has been sown",
      "Turning point: a significant life transition"
    ],
    reversed: [
      "Resistance: struggling against change",
      "Stagnation: feeling stuck in repeating patterns",
      "Bad timing: delays or unexpected setbacks",
      "Lack of control: feeling at the mercy of fate",
      "Missed chances: ignoring signs of transition"
    ],
    reading: `The Wheel of Fortune turns whether you're ready or not. Its message is simple but profound: life is always in motion. You may feel on top of the world or buried beneath its weight, but either way, this too shall pass. Your task is not to control the wheel, but to center yourself within it. Breathe into the uncertainty. Trust the cycle.

This card appears at moments of great shift; some welcome, some disruptive. But all meaningful. The Wheel teaches surrender, not as a loss of power, but as alignment with a greater rhythm. When you resist its turn, you create friction. When you embrace it, even the hardest changes reveal their purpose.

Ask yourself not what you can prevent, but what you are being prepared for. The Wheel spins you into your next chapter, whether you feel ready or not. The question is; will you ride the wave or cling to the shore?

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Where in my life is change trying to happen?
2. Am I trusting the flow of events or fighting them?
3. What pattern keeps repeating and what is it trying to teach me?

When the Wheel turns, it is not punishment or reward; it is invitation. Let go. Lean in. Trust the spin.`,
    mirroring: [
      "death-tarot-card-reading-meaning",
      "the-fool-tarot-card-reading-meaning",
      "judgement-tarot-card-reading-meaning"
    ]
  },

  "justice-tarot-card-reading-meaning": {
    slug: "justice-tarot-card-reading-meaning",
    name: "Justice",
    subtitle: "Truth, Fairness, Accountability",
    image: "/card/justice-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `Justice is the guardian of truth and balance. She carries both the sword and the scales; discernment and harmony. This card asks for integrity, responsibility and clarity. It signifies karmic reckoning, legal matters or a moment of decision that must be made with honesty. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Truth: clarity emerging from facts",
      "Accountability: taking ownership of actions",
      "Fairness: balance between logic and compassion",
      "Decisions: weighing outcomes carefully",
      "Karma: consequences of past choices"
    ],
    reversed: [
      "Dishonesty: withholding truth or self deceit",
      "Injustice: unfair treatment or imbalance",
      "Avoidance: refusing to take responsibility",
      "Corruption: misuse of power or bias",
      "Unresolved: issues lacking closure or clarity"
    ],
    reading: `Justice does not ask for perfection; she asks for truth. When she appears in a reading, you are being summoned to examine where clarity, fairness or integrity must rise. Perhaps you are being called to make a difficult decision or to reckon with one already made. Either way, this is a time for honesty, both with others and with yourself.

This card does not reward emotion over logic, nor logic over heart; it seeks alignment. If you have been wronged, Justice brings a promise of balance. If you have erred, it brings a chance to make things right. But one thing is certain: the scales will not be rushed. Life is asking you to slow down and weigh things with care.

You hold the sword now, but it is not for harm. It is for clarity. Cut through illusion. Name your truth. Take ownership, even if it stings. When you stand in truth, you are never powerless.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Am I avoiding accountability in any part of my life?
2. What truth must be spoken, even if uncomfortable?
3. How can I restore balance in this situation?

Justice stands firm, not with judgment, but with purpose. Let truth be your guide and the scales will find their center.`,
    mirroring: [
      "temperance-tarot-card-reading-meaning",
      "the-hierophant-tarot-card-reading-meaning",
      "two-of-swords-tarot-card-reading-meaning"
    ]
  },

  "the-hanged-man-tarot-card-reading-meaning": {
    slug: "the-hanged-man-tarot-card-reading-meaning",
    name: "The Hanged Man",
    subtitle: "Surrender, Perspective, Pause",
    image: "/card/the-hanged-man-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The Hanged Man hangs not in punishment, but in purpose. This card symbolizes a needed pause; a moment where progress halts so that deeper wisdom can surface. It asks for surrender, new perspectives and trust in the timing of life. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Surrender: releasing control over outcomes",
      "Pause: intentional delay for reflection",
      "Perspective: seeing things from a new angle",
      "Letting go: sacrificing ego for wisdom",
      "Enlightenment: stillness leading to awakening"
    ],
    reversed: [
      "Resistance: fighting stillness or change",
      "Stagnation: delay without purpose",
      "Martyrdom: sacrificing too much or too often",
      "Impatience: frustration with the timing of life",
      "Avoidance: not facing what must be seen"
    ],
    reading: `The Hanged Man comes to stop time. To turn your world upside down, not to confuse, but to clarify. If life feels like it's at a standstill, this card says: Good. Now you can see what you were too busy to notice. Now you can feel what rushing kept you from feeling. This pause is not punishment; it's preparation.

In this suspended space, you are being invited to unlearn. To drop old stories. To yield, not because you've given up, but because you are giving over, to insight, to spirit, to truth. Something must be seen differently before you can move forward. This is the space between stories. Honor it.

If you've been pushing, striving or demanding resolution; stop. Breathe. Listen. Surrender does not mean you've lost. It means you are wise enough to wait for real clarity.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Where am I being asked to release control?
2. What truth can I only see by pausing?
3. Am I willing to trust this delay as meaningful?

The Hanged Man is the stillness before transformation. Let it shape you gently. Then rise with a vision that was once upside down and is now profoundly clear.`,
    mirroring: [
      "the-hermit-tarot-card-reading-meaning",
      "death-tarot-card-reading-meaning",
      "four-of-swords-tarot-card-reading-meaning"
    ]
  },

  "death-tarot-card-reading-meaning": {
    slug: "death-tarot-card-reading-meaning",
    name: "Death",
    subtitle: "Endings, Transformation, Renewal",
    image: "/card/death-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `Despite its name, the Death card is not a symbol of literal demise, but of deep transformation and necessary endings. It signals a powerful clearing; a letting go of what has run its course so something new can be born. Death asks you to surrender what no longer serves you, even if it once defined you. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Transformation: major shifts and inner rebirth",
      "Endings: releasing people, patterns or chapters",
      "Letting go: surrendering attachments gracefully",
      "Renewal: space for new growth to emerge",
      "Liberation: freedom from stagnant energy"
    ],
    reversed: [
      "Resistance: fear of change or clinging to the past",
      "Stagnation: staying in dead end situations",
      "Emotional block: refusing to grieve or move on",
      "Avoidance: not facing necessary endings",
      "Delayed transformation: growth held back by fear"
    ],
    reading: `When Death arrives, it comes as a quiet message, not a threat. It nudges gently, asking what you're still carrying that has already expired in your soul. This card appears when something old; an identity, relationship, belief or cycle; must be laid to rest. And though it may bring discomfort or grief, it also brings truth: that you are evolving and life is asking you to unclench your grip.

The energy of Death is not cruel, but compassionate. It removes what no longer belongs so your essence can breathe again. You may feel loss, but beneath it, a quiet liberation grows. Something new is forming, not in haste, but with reverence. The pain of death is the pain of shedding, of stepping naked into your next chapter without knowing what it holds. And still, it calls you forward.

This is your invitation to grieve, but also to release. There is no rebirth without the death of what came before. If you've been afraid to end something; a toxic habit, a lifeless job, a fading attachment; Death is the mirror showing that your spirit already knows. Trust that in letting go, you will find yourself again; unburdened, renewed and closer to your truth.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What chapter in my life has reached its natural end?
2. Where am I clinging to what no longer serves me?
3. Can I trust that letting go will lead to deeper alignment?

Death appears when you're ready to bury the old story and rise from the roots of what remains.`,
    mirroring: [
      "the-tower-tarot-card-reading-meaning",
      "the-hanged-man-tarot-card-reading-meaning",
      "judgement-tarot-card-reading-meaning"
    ]
  },

  "temperance-tarot-card-reading-meaning": {
    slug: "temperance-tarot-card-reading-meaning",
    name: "Temperance",
    subtitle: "Balance, Harmony, the Timing of Life",
    image: "/card/temperance-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `Temperance is the meaningful art of moderation, the alchemy of blending opposites to create wholeness. It invites you to walk the middle path, integrating extremes with grace and patience. This card speaks of spiritual maturity; the ability to hold tension without rushing, forcing or breaking. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Harmony: finding inner peace and equilibrium",
      "Balance: managing emotions and priorities well",
      "Moderation: avoiding extremes and excess",
      "Healing: integrating body, mind and spirit",
      "The timing of life: trusting the process to unfold"
    ],
    reversed: [
      "Imbalance: feeling off center or scattered",
      "Excess: overindulging or overextending",
      "Conflict: resisting inner or outer integration",
      "Impatience: trying to force outcomes prematurely",
      "Disconnection: neglecting spiritual alignment"
    ],
    reading: `Temperance arrives like a gentle breath, reminding you that not everything must be rushed or controlled. It's a card of alchemy, where your inner opposites, your chaos and calm, your grief and growth, can be blended with mindful grace. When life pulls you toward extremes, Temperance brings you back to center, to softness, to the rhythm that is uniquely yours.

This card encourages you to become the meaningful container; holding both fire and water without letting either overflow. It's a time to let life unfold, to trust the unseen forces at play and to surrender urgency for presence. Temperance is the medicine for burnout, the balm for conflict and the teacher of the timing of life. Let it slow your breath and guide you back to your highest balance.

You are in a space where healing comes not from doing more, but from being still, from choosing measured responses over reactive ones. Whether you're rebuilding after a storm or seeking clarity, let Temperance guide you with its quiet wisdom. The middle path isn't a compromise; it's a meaningful choice.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Where am I being pulled to extremes in thought, emotion or behavior?
2. What would balance feel like in my current situation?
3. Can I trust that the timing of life is wiser than my own urgency?

Temperance appears when the soul needs peace, not progress and when your next step is to blend, not break.`,
    mirroring: [
      "the-star-tarot-card-reading-meaning",
      "justice-tarot-card-reading-meaning",
      "the-high-priestess-tarot-card-reading-meaning"
    ]
  },

  "the-devil-tarot-card-reading-meaning": {
    slug: "the-devil-tarot-card-reading-meaning",
    name: "The Devil",
    subtitle: "Temptation, Attachment, Shadow Work",
    image: "/card/the-devil-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The Devil reveals where you are bound, not by chains, but by choices. It speaks of temptation, illusion and the unconscious agreements that keep you small. This card asks you to confront your shadow, your vices and your fear driven patterns with radical honesty. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Temptation: indulgence or obsession clouding clarity",
      "Attachment: unhealthy relationships or habits",
      "Illusion: mistaking bondage for freedom",
      "Materialism: overfocus on pleasure, wealth or control",
      "Shadow work: facing inner fears or denied parts of self"
    ],
    reversed: [
      "Liberation: breaking free from toxic cycles",
      "Awareness: recognizing illusion and reclaiming power",
      "Detachment: releasing what controls you",
      "Inner healing: integrating the shadow self",
      "Empowerment: choosing freedom over fear"
    ],
    reading: `When The Devil emerges, it does not shame; it illuminates. It brings to light the chains you didn't know you were wearing. These are not always external forces. More often, they are inner patterns: addictions, fears, false beliefs or unhealthy attachments that keep you looping in cycles of self sabotage.

This card dares you to confront what you've denied. It offers no judgment, only reflection; a mirror to the places where desire has turned to dependence and comfort has become a cage. The Devil isn't evil; it's unconscious power misused. And through awareness, those chains can be broken, not with violence, but with clarity.

You are being called to choose yourself, not the easy way, not the escape, but the truth. When you reclaim your power, the Devil loses its grip. This card doesn't ask for fear, but for fierce self honesty. You already hold the key.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What habits or beliefs are silently controlling me?
2. Where have I confused pleasure with freedom?
3. What truth am I avoiding in order to stay comfortable?

The Devil appears when you are ready to reclaim your light; by meeting the darkness with eyes wide open.`,
    mirroring: [
      "the-tower-tarot-card-reading-meaning",
      "the-moon-tarot-card-reading-meaning",
      "death-tarot-card-reading-meaning"
    ]
  },

  "the-tower-tarot-card-reading-meaning": {
    slug: "the-tower-tarot-card-reading-meaning",
    name: "The Tower",
    subtitle: "Revelation, Collapse, Awakening",
    image: "/card/the-tower-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The Tower is the sudden lightning strike that shatters illusion. It represents chaos, upheaval and unexpected revelation, not to punish, but to liberate. When false foundations fall, what remains is what's true. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Sudden change: events that shake your sense of security",
      "Revelation: truth breaking through denial or illusion",
      "Collapse: the fall of false structures, beliefs or roles",
      "Breakthrough: profound awakening after disruption",
      "Liberation: freedom from what was never real"
    ],
    reversed: [
      "Resistance: avoiding inevitable change",
      "Delayed collapse: holding up what's already broken",
      "Fear of loss: clinging to comfort over truth",
      "Emotional chaos: inner storms needing release",
      "Stunted growth: failing to grow because of fear"
    ],
    reading: `The Tower does not come gently; it comes to free you. When you've built your life upon shaky foundations; illusions, roles, comforts, ego; the Tower appears as needed disruption. Though terrifying, it clears away the inauthentic so the real can rise. It is not destruction for cruelty's sake, but for necessary revelation.

This is the storm that shows you what matters. What falls away was never meant to last. The Tower strips you of illusion, forcing clarity in places you've long avoided. And though the collapse may be painful, it is also permission, to rebuild with integrity, with truth, with depth. This card is the deep reset.

Let go. Let it fall. Let the truth stand bare. This is where true power begins, not in resisting chaos, but in rising from it.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What truths have I been avoiding to maintain comfort?
2. Where have I built my identity on false foundations?
3. What would it feel like to trust the collapse?

The Tower appears not to destroy you, but to set your soul free.`,
    mirroring: [
      "the-devil-tarot-card-reading-meaning",
      "death-tarot-card-reading-meaning",
      "judgement-tarot-card-reading-meaning"
    ]
  },

  "the-star-tarot-card-reading-meaning": {
    slug: "the-star-tarot-card-reading-meaning",
    name: "The Star",
    subtitle: "Hope, Renewal, Deep Light",
    image: "/card/the-star-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The Star is a celestial balm; the quiet after the storm. It brings healing, inspiration and the return of faith. This card reflects a time when your soul is being nourished by grace and a renewed sense of hope begins to shimmer on the horizon. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Hope: renewed belief in your path and future",
      "Healing: emotional or spiritual restoration",
      "Inspiration: feeling guided by inner or deep light",
      "Clarity: seeing truth through intuition and peace",
      "Faith: trusting the journey ahead"
    ],
    reversed: [
      "Despair: losing sight of purpose or direction",
      "Disconnection: feeling numb or spiritually blocked",
      "Self doubt: struggling to trust yourself or the future",
      "Hopelessness: temporary loss of clarity or motivation",
      "Emotional fatigue: in need of rest and renewal"
    ],
    reading: `The Star appears when your spirit is thirsty for hope. It comes after hardship, often after the Tower, to soothe, to illuminate and to remind you that healing is not only possible, but already unfolding. Even in darkness, your inner light has never gone out. This card asks you to reconnect with that soft flicker of faith.

Let yourself rest. Let your heart breathe again. The Star doesn't ask you to leap or push, but to receive; beauty, grace, inspiration. Whether through nature, art, prayer or silence, find what renews your soul and let it wash over you. This is a needed pause where your dreams can be reimagined.

You are not alone. You are being guided, not by force, but by stars. Let them remind you of your deep essence and trust that healing will lead you home.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What is softly guiding me forward, even when I feel lost?
2. Where am I being invited to rest and heal?
3. Can I trust that something beautiful is unfolding beneath the surface?

The Star appears when your soul is ready to remember its own light.`,
    mirroring: [
      "temperance-tarot-card-reading-meaning",
      "the-moon-tarot-card-reading-meaning",
      "the-fool-tarot-card-reading-meaning"
    ]
  },

  "the-moon-tarot-card-reading-meaning": {
    slug: "the-moon-tarot-card-reading-meaning",
    name: "The Moon",
    subtitle: "Illusion, Intuition, Shadow Work",
    image: "/card/the-moon-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The Moon casts a mysterious light that blurs the boundaries between truth and illusion. This card speaks to the unconscious mind, dreams and fears that live beneath the surface. It invites you into shadow work, to explore the hidden parts of yourself, trust your intuition and walk through uncertainty with awareness. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Intuition: listening to inner wisdom over logic",
      "Dreams: subconscious symbols and messages",
      "Mystery: navigating unknown paths",
      "Shadow work: confronting inner fears and illusions",
      "Sensitivity: emotional depth and psychic openness"
    ],
    reversed: [
      "Confusion: feeling lost or mentally foggy",
      "Deception: being misled or self deluded",
      "Fear: anxiety amplified by uncertainty",
      "Blocked intuition: ignoring inner truth",
      "Illusion: seeing what you want instead of what is"
    ],
    reading: `When The Moon rises in your reading, it does not offer answers; it offers mirrors. You are being asked to peer through the veil of your life and see what is real, what is projection and what is fear disguised as fact. This card reveals the murkiness of your path, not to frighten you, but to awaken your trust in deeper sight. Not all truths live in daylight.

The Moon represents the emotional tides that pull you inward. It asks you to listen not to noise, but to the still voice within; the one that suggests in dreams and gut feelings. You may feel uncertain, but that's the nature of inner work. Like moonlight on water, your reflections may shift, but look closer and patterns begin to reveal themselves.

This is your invitation to embrace not knowing. To move forward even when clarity hasn't yet arrived. You're being called to trust your instincts, examine the emotional shadows you avoid and find wisdom in discomfort. The Moon doesn't lie; it teaches you how to read between the lines of your own soul.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What fears or assumptions might be distorting my perception?
2. Am I trusting my intuition or silencing it with doubt?
3. What truths are buried in my dreams, emotions or discomfort?

The Moon guides not by light, but by inner sight. Follow it inward.`,
    mirroring: [
      "the-high-priestess-tarot-card-reading-meaning",
      "the-hanged-man-tarot-card-reading-meaning",
      "the-devil-tarot-card-reading-meaning"
    ]
  },

  "the-sun-tarot-card-reading-meaning": {
    slug: "the-sun-tarot-card-reading-meaning",
    name: "The Sun",
    subtitle: "Joy, Clarity, Radiance",
    image: "/card/the-sun-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The Sun brings warmth, celebration and a deep sense of confidence. It is the card of optimism, clarity and vitality. When it shines on your reading, it dissolves shadows and reveals truth, illuminating the path forward with joy and certainty. This card reminds you that your inner light is your greatest strength. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Joy: a period of happiness and celebration",
      "Clarity: truth is illuminated and confusion fades",
      "Confidence: self assurance and inner radiance",
      "Vitality: physical wellness and high energy",
      "Success: breakthroughs and visible progress"
    ],
    reversed: [
      "Temporary sadness: joy clouded by doubt or delay",
      "Overconfidence: ego overshadowing clarity",
      "Blocked optimism: struggle to see the light",
      "Impatience: feeling stuck despite progress",
      "False joy: masking wounds with superficial positivity"
    ],
    reading: `The Sun appears in your reading like a full breath after long struggle. Its presence is a radiant affirmation: you are seen, supported and standing in your power. This card calls you back to joy, not as escapism, but as clear understanding. The Sun doesn't merely warm; it reveals. It shows you what's real, what's thriving and what you've grown beyond.

It invites you to release doubt and let your inner child lead. This is a time for simple pleasures, honest expression and fearless authenticity. You no longer need to dim yourself to fit into spaces that asked for less than your full truth. The Sun urges you to embrace your wholeness and bask in the result of your growth.

Even if you've been moving through darkness, this card promises light is returning. Lean into it. You're not naive for being hopeful; you're wise for choosing joy when it's hardest to reach. The Sun reminds you: your life is allowed to feel good.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What truth am I finally ready to embrace, even if it's uncomfortable?
2. Where have I denied myself joy or withheld celebration?
3. How can I let my inner light guide me, even through uncertainty?

Let the Sun remind you: you are your own warmth, your own radiance, your own miracle.`,
    mirroring: [
      "the-empress-tarot-card-reading-meaning",
      "strength-tarot-card-reading-meaning",
      "the-fool-tarot-card-reading-meaning"
    ]
  },

  "judgement-tarot-card-reading-meaning": {
    slug: "judgement-tarot-card-reading-meaning",
    name: "Judgement",
    subtitle: "Awakening, Redemption, Higher Calling",
    image: "/card/judgement-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `Judgement marks a moment of reckoning, where your past, choices and inner truth converge into revelation. It represents an awakening, a call to rise into your higher self. This card invites forgiveness, clarity and courage as you step into a more conscious version of yourself. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Awakening: spiritual rebirth and deep insight",
      "Redemption: letting go of guilt or self judgment",
      "Higher calling: being pulled toward your soul's mission",
      "Reflection: reviewing your past with compassion",
      "Renewal: emerging with new purpose and clarity"
    ],
    reversed: [
      "Self doubt: fear of rising to your potential",
      "Denial: resisting inner truth or ignoring signs",
      "Regret: fixation on past mistakes",
      "Avoidance: refusing to take accountability",
      "Delayed transformation: clinging to outdated stories"
    ],
    reading: `Judgement is not about punishment; it's about truth. When this card appears, you are being invited to examine your life not with shame, but with clarity. It's a clear mirror, showing you not only who you've been, but who you're capable of becoming. This is your wake up call; your soul asking if you're ready to rise.

The call may feel loud. Uncomfortable. Even terrifying. It may come as a dream, a confrontation, a longing to start over. Whatever form it takes, it's asking: Are you willing to shed what no longer aligns with your highest self? This card holds meaningful space for your rebirth, but only if you choose it.

Now is not the time to look away. It is the time to forgive, release and answer the calling that's been echoing in your bones. You are more than your past. You are your potential in motion. The gates are open; walk through.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What am I being called to face that I've been avoiding?
2. Where in my life am I being asked to rise above my past?
3. Can I trust that transformation requires truth; even when it hurts?

Judgement doesn't end a story; it resurrects the soul that's ready to live it fully.`,
    mirroring: [
      "death-tarot-card-reading-meaning",
      "the-lovers-tarot-card-reading-meaning",
      "the-world-tarot-card-reading-meaning"
    ]
  },

  "the-world-tarot-card-reading-meaning": {
    slug: "the-world-tarot-card-reading-meaning",
    name: "The World",
    subtitle: "Completion, Wholeness, Integration",
    image: "/card/the-world-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The World signifies fulfillment, wholeness and the completion of a powerful cycle. You have grown, transformed and now arrive at a place of harmony. This card represents the integration of all lessons; a spiritual graduation that prepares you for the next evolution of your journey. In daily life, it asks where this larger lesson is showing up in your choices, habits and relationships right now.`,
    upright: [
      "Completion: the end of a significant chapter",
      "Wholeness: feeling aligned and integrated",
      "Achievement: celebrating hard earned success",
      "Expansion: stepping into your fullest potential",
      "Unity: harmony between body, mind and spirit"
    ],
    reversed: [
      "Lack of closure: lingering unfinished energy",
      "Stagnation: delaying your next phase",
      "Fragmentation: feeling disconnected or incomplete",
      "Resistance: fear of moving beyond the known",
      "Unrealized potential: hesitating at the threshold"
    ],
    reading: `The World arrives as a meaningful breath; the kind that comes after long effort, deep healing or a soul journey that has fundamentally changed you. This card doesn't just close a chapter; it honors it. Every challenge, breakthrough and quiet resilience has led you here. You are not just ending something. You are completing yourself.

This is a moment to reflect and to celebrate. You may feel a quiet satisfaction or a full body exhale; both are valid. The World invites you to notice how far you've come, to see how all the pieces fit and to embrace the person you've become because of what you've endured. Integration is the gift. You no longer carry your past; you embody its wisdom.

Now, the circle closes. But remember: all endings are beginnings in disguise. With this cycle complete, a new one is waiting. Walk forward not as who you were, but as the whole version of who you've become.

Look for the ordinary signs too: the choice you keep circling, the pattern that keeps repeating and the part of you that already knows what needs to change. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What have I finally come to understand or release?
2. How can I honor my growth before rushing into the next chapter?
3. Am I ready to step into the world with full presence and truth?

The World doesn't just mark an end; it announces your readiness to begin again, whole and wise.`,
    mirroring: [
      "the-fool-tarot-card-reading-meaning",
      "judgement-tarot-card-reading-meaning",
      "wheel-of-fortune-tarot-card-reading-meaning"
    ]
  },

  "ace-of-wands-tarot-card-reading-meaning": {
    slug: "ace-of-wands-tarot-card-reading-meaning",
    name: "Ace of Wands",
    subtitle: "Inspiration, Passion, Spark of Action",
    image: "/card/ace-of-wands-tarot-card-reading-meaning.webp",
    suit: "Wands",
    description: `The Ace of Wands is a burst of fire, the first flash of desire, the start of an idea, the seed of action. It symbolizes new beginnings that come from passion and potential. This card urges you to follow what excites you and begin where your spirit feels most alive. In daily life, it asks what your energy is trying to start, protect or finish before the spark fades.`,
    upright: [
      "Inspiration: a powerful idea or calling",
      "Initiation: taking bold action",
      "Creativity: expressing yourself fearlessly",
      "Passion: feeling alive and driven",
      "Opportunity: the spark to start something big"
    ],
    reversed: [
      "Blocked energy: lack of motivation or clarity",
      "Delayed start: fear or hesitation holding you back",
      "Creative block: struggling to express inspiration",
      "Misguided passion: scattered focus or burnout",
      "False starts: enthusiasm without follow through"
    ],
    reading: `The Ace of Wands doesn't ask, it ignites. This is the moment before the leap, when you feel the stir of something powerful rising inside you. It is raw, untamed energy, asking to be channeled into form. You may not know the outcome, but you feel the flame and that is enough to begin.

This card signals the start of something meaningful: a new project, path or relationship rooted in genuine desire. It urges you to trust the force that pulls you forward, even if it doesn't make perfect sense yet. Passion is its own kind of wisdom. Listen. Move. Create. Let your fire lead.

Sometimes we wait for the world to give us permission. The Ace of Wands is your permission. Your idea matters. Your desire is meaningful. Begin now, not when it's safe, but when it's real.

Look for the ordinary signs too: where your energy rises, where it drains and where action would bring relief. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
• What excites me enough to risk uncertainty?
• Where have I been waiting instead of creating?
• What would I start if I trusted my inner fire completely?

The Ace of Wands is the match in your hand, all it asks is that you strike it.`,
    mirroring: [
      "the-magician-tarot-card-reading-meaning",
      "the-sun-tarot-card-reading-meaning",
      "two-of-wands-tarot-card-reading-meaning"
    ]
  },

  "two-of-wands-tarot-card-reading-meaning": {
    slug: "two-of-wands-tarot-card-reading-meaning",
    name: "Two of Wands",
    subtitle: "Vision, Decisions, Exploration",
    image: "/card/two-of-wands-tarot-card-reading-meaning.webp",
    suit: "Wands",
    description: `The Two of Wands stands at the edge of the known world, gazing outward into vast possibility. This card is about vision, about standing in your power and asking what more could be. It speaks to the stage between comfort and action, where choice and courage are your companions. In daily life, it asks what your energy is trying to start, protect or finish before the spark fades.`,
    upright: [
      "Planning: thinking ahead and making long term decisions",
      "Vision: seeing broader horizons and new paths",
      "Boldness: stepping beyond your comfort zone",
      "Discovery: a hunger for more experience or purpose",
      "Initiation: the beginning of outward expansion"
    ],
    reversed: [
      "Fear of change: hesitating despite the call to grow",
      "Missed opportunities: indecision leads to stagnation",
      "Doubt: lack of faith in your vision or path",
      "Comfort zone: clinging to the familiar over fulfillment",
      "Disconnection: lost between desires and action"
    ],
    reading: `The Two of Wands arrives when your spirit begins to outgrow its current container. It honors what you've built, but calls your gaze beyond it. Something within you is stirring, a sense that there is more waiting if only you dare to reach for it. This card isn't about reckless leaps, it is about conscious expansion, calculated vision and the courage to begin shaping what is next.

In the quiet space between dreaming and doing, the Two of Wands holds a needed pause. It asks you to reflect deeply: What do you truly want? What lights your soul with a sense of possibility? You may not yet have all the answers, but you have the clarity to start forming them and that is enough to begin.

Do not wait until everything is certain. Do not shrink your vision to fit your current circumstances. You are being asked to step outside the walls of habit and look toward the horizon of your potential. The world is bigger than your fears. Walk toward it.

Look for the ordinary signs too: where your energy rises, where it drains and where action would bring relief. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
• Where am I being called to explore beyond the familiar?
• What vision have I suppressed out of fear or practicality?
• Can I begin even if I don't yet know how the story ends?

Every great journey begins with one bold look outward.`,
    mirroring: [
      "three-of-wands-tarot-card-reading-meaning",
      "the-world-tarot-card-reading-meaning",
      "the-emperor-tarot-card-reading-meaning"
    ]
  },

  "three-of-wands-tarot-card-reading-meaning": {
    slug: "three-of-wands-tarot-card-reading-meaning",
    name: "Three of Wands",
    subtitle: "Progress, Expansion, Foresight",
    image: "/card/three-of-wands-tarot-card-reading-meaning.webp",
    suit: "Wands",
    description: `The Three of Wands represents momentum. The plans have been made, the first steps taken and now you stand in the rising glow of potential. This card signifies expansion of dreams, vision and personal power. It affirms that something is unfolding and that the winds are shifting in your favor. In daily life, it asks what your energy is trying to start, protect or finish before the spark fades.`,
    upright: [
      "Momentum: early progress on long term goals",
      "Expansion: widening your scope or territory",
      "Opportunity: new pathways and open doors",
      "Preparation: waiting for results of past efforts",
      "Optimism: expecting growth and movement"
    ],
    reversed: [
      "Delays: stalled progress or unforeseen obstacles",
      "Frustration: impatience with slow movement",
      "Doubt: second guessing your original vision",
      "Setbacks: logistical issues hindering results",
      "Overextension: reaching too far too fast"
    ],
    reading: `The Three of Wands is the pause before the payoff, that meaningful space where you have launched your intentions and now await the tide to return. It asks you to stay steady in faith and preparation. Your vision was not in vain, your actions have already set forces into motion. Now is the time to watch the horizon with trust.

You may feel both excitement and anxiety. What will come back to me? Will it be what I hoped for? This card encourages you to hold fast to belief and keep expanding your field of awareness. What you have imagined is making its way toward you, not all at once, but steadily.

It is also a reminder that success is not a finish line, it is a continuum. Keep strategizing, keep preparing for what is next and remain open to surprising detours that lead to even richer destinations.

Look for the ordinary signs too: where your energy rises, where it drains and where action would bring relief. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
• Am I prepared to receive what I've asked for?
• Where can I make space for more growth and alignment?
• What fear still keeps me anchored to the shore?

The horizon is not the end, it is the beginning of a grander world unfolding.`,
    mirroring: [
      "two-of-wands-tarot-card-reading-meaning",
      "wheel-of-fortune-tarot-card-reading-meaning",
      "six-of-wands-tarot-card-reading-meaning"
    ]
  },

  "four-of-wands-tarot-card-reading-meaning": {
    slug: "four-of-wands-tarot-card-reading-meaning",
    name: "Four of Wands",
    subtitle: "Celebration, Stability, Milestones",
    image: "/card/four-of-wands-tarot-card-reading-meaning.webp",
    suit: "Wands",
    description: `The Four of Wands is a joyful card of celebration and homecoming. It speaks of reaching a meaningful milestone, one that deserves to be honored. This card embodies community, gratitude and the meaningful act of pausing to appreciate how far you have come. In daily life, it asks what your energy is trying to start, protect or finish before the spark fades.`,
    upright: [
      "Celebration: marking a victory or life event",
      "Harmony: balance in relationships or home",
      "Homecoming: returning to a safe, loved space",
      "Achievement: reaching a major milestone",
      "Unity: connection and shared joy"
    ],
    reversed: [
      "Tension: discord within home or family",
      "Instability: lack of grounding or peace",
      "Unmet expectations: celebrations that feel hollow",
      "Loneliness: feeling disconnected or unacknowledged",
      "Delayed success: postponed joy or milestones"
    ],
    reading: `The Four of Wands reminds you that joy is not just a reward, it is a spiritual necessity. Too often, we rush from one goal to the next, forgetting that the soul needs celebration as much as effort. This card invites you to stop, breathe and fully receive what you have created.

There may be a sense of reunion, with loved ones, with your values or with your own sense of belonging. The energy is warm, communal and grounding. If you have been feeling disconnected, this is a sign to return to your roots and find stability in connection.

Celebrate not because everything is perfect, but because it is meaningful. Let joy be meaningful. Let peace be a worthy goal in itself.

Look for the ordinary signs too: where your energy rises, where it drains and where action would bring relief. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
• What recent victory have I not yet honored?
• Who or what feels like "home" to me and why?
• How can I create more meaningful space for joy?

Gratitude turns every moment into a foundation of light.`,
    mirroring: [
      "the-sun-tarot-card-reading-meaning",
      "ten-of-cups-tarot-card-reading-meaning",
      "six-of-wands-tarot-card-reading-meaning"
    ]
  },

  "five-of-wands-tarot-card-reading-meaning": {
    slug: "five-of-wands-tarot-card-reading-meaning",
    name: "Five of Wands",
    subtitle: "Conflict, Competition, Tension",
    image: "/card/five-of-wands-tarot-card-reading-meaning.webp",
    suit: "Wands",
    description: `The Five of Wands depicts dynamic tension, ideas clashing, egos colliding, voices rising in chaos. This card suggests inner or outer conflict that stirs discomfort but also sharpens clarity. It is not always a battle to fear; sometimes friction leads to refinement. In daily life, it asks what your energy is trying to start, protect or finish before the spark fades.`,
    upright: [
      "Conflict: interpersonal or internal disagreements",
      "Competition: vying for attention, success or control",
      "Tension: energy that feels chaotic or scattered",
      "Resistance: struggling to align or cooperate",
      "Testing ground: challenges that sharpen skills"
    ],
    reversed: [
      "Avoidance: suppressing necessary confrontation",
      "Resentment: buried tension erupting unexpectedly",
      "Exhaustion: worn out from conflict or comparison",
      "Submission: giving up your voice to keep peace",
      "Miscommunication: talking over, not to, each other"
    ],
    reading: `The Five of Wands shows up when something inside or around you feels unruly. It could be external, a competitive environment, arguments, misunderstandings or internal, as your own desires and doubts clash for dominance. Either way, it asks for your presence, not your panic.

This card does not always warn of destruction; it invites engagement. Sometimes we need to wrestle with our limitations or challenge outdated ideas. The goal is not to win, but to discover what truly matters and rise above the noise with clarity.

You cannot always control others' energy, but you can center your own. Choose your battles wisely, speak your truth without attachment and seek not dominance, but understanding.

Look for the ordinary signs too: where your energy rises, where it drains and where action would bring relief. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
• Am I fighting for truth or feeding my ego?
• Where am I afraid of conflict and why?
• What tension is asking to be expressed honestly?

Chaos can be the fire that forges sharper focus.`,
    mirroring: [
      "seven-of-wands-tarot-card-reading-meaning",
      "the-tower-tarot-card-reading-meaning",
      "five-of-swords-tarot-card-reading-meaning"
    ]
  },

  "six-of-wands-tarot-card-reading-meaning": {
    slug: "six-of-wands-tarot-card-reading-meaning",
    name: "Six of Wands",
    subtitle: "Victory, Recognition, Achievement",
    image: "/card/six-of-wands-tarot-card-reading-meaning.webp",
    suit: "Wands",
    description: `The Six of Wands heralds a moment of triumph. After effort and endurance, your hard work is seen, respected and celebrated. This card signals external validation and inner pride, a reminder that it is okay to shine when you have earned it. In daily life, it asks what your energy is trying to start, protect or finish before the spark fades.`,
    upright: [
      "Victory: success after struggle or adversity",
      "Recognition: being praised or acknowledged",
      "Confidence: feeling empowered and capable",
      "Leadership: guiding others through example",
      "Momentum: continued progress and motivation"
    ],
    reversed: [
      "Pride: letting praise inflate ego or entitlement",
      "Imposter syndrome: doubting your own success",
      "Insecurity: fearing loss of approval",
      "Overexposure: discomfort with being in the spotlight",
      "False praise: applause that feels unearned"
    ],
    reading: `The Six of Wands is your standing ovation. It celebrates not just what you have achieved, but how far you have come to get here. You have likely weathered challenges, made sacrifices and stayed committed when the path felt unclear. Now, others see what you have built and so should you.

But recognition is not just about applause. It is about reflection. Who did I become in the pursuit of this goal? Did I stay true to my values? Your achievement is not an endpoint, but a mirror, one that reflects both your effort and your evolution.

Receive praise with humility. Accept support without deflecting. Stand tall not from arrogance, but from grounded pride in your journey.

Look for the ordinary signs too: where your energy rises, where it drains and where action would bring relief. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
• Do I allow myself to feel proud or do I downplay my wins?
• What does true recognition feel like to me?
• How can I stay humble while still embracing the spotlight?

Victory is not just the finish line, it is the story you tell with your head held high.`,
    mirroring: [
      "the-chariot-tarot-card-reading-meaning",
      "four-of-wands-tarot-card-reading-meaning",
      "ten-of-pentacles-tarot-card-reading-meaning"
    ]
  },

  "seven-of-wands-tarot-card-reading-meaning": {
    slug: "seven-of-wands-tarot-card-reading-meaning",
    name: "Seven of Wands",
    subtitle: "Defense, Standing Ground, Perseverance",
    image: "/card/seven-of-wands-tarot-card-reading-meaning.webp",
    suit: "Wands",
    description: `The Seven of Wands depicts the moment after you have reached a high point and now must protect what you have earned. This card represents resilience, asserting your beliefs and the courage to defend your truth in the face of opposition. It speaks to the battle after success, where strength must be renewed. In daily life, it asks what your energy is trying to start, protect or finish before the spark fades.`,
    upright: [
      "Defending your position: holding your ground with integrity",
      "Courage: standing up against opposition or criticism",
      "Resilience: enduring pressure without giving up",
      "Persistence: refusing to be pushed down or aside",
      "Conviction: believing deeply in your values or path"
    ],
    reversed: [
      "Overwhelm: feeling outnumbered or unsupported",
      "Exhaustion: losing strength in an uphill battle",
      "Avoidance: not wanting to deal with confrontation",
      "Doubt: questioning your right to stand your ground",
      "Withdrawal: giving in or stepping back under pressure"
    ],
    reading: `When the Seven of Wands appears, it reminds you that achievement invites challenge. You have come far, but now the test is not just reaching the summit, it is staying there. Others may question your place, your right, your vision. Do not let the noise shake the roots of your knowing.

This is the card of the warrior heart, the one who stands for what matters, even when it is hard. It speaks to moments where you feel alone in your defense, where you are tired but still upright. Your stance need not be loud or aggressive, it need only be firm. Quiet conviction is power too.

In spiritual terms, this is not just about fending off others, it is about reaffirming your path to yourself. When everything external shakes, return inward. Re anchor. Your soul chose this journey for a reason. Stand tall in the storm and let your spirit speak louder than resistance.

Look for the ordinary signs too: where your energy rises, where it drains and where action would bring relief. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
• What am I being called to defend in my life right now?
• Where am I feeling challenged and what does that reveal?
• How can I honor my truth without falling into fear or aggression?

You did not climb this far to be pushed off the mountain.`,
    mirroring: [
      "five-of-wands-tarot-card-reading-meaning",
      "strength-tarot-card-reading-meaning",
      "nine-of-wands-tarot-card-reading-meaning"
    ]
  },

  "eight-of-wands-tarot-card-reading-meaning": {
    slug: "eight-of-wands-tarot-card-reading-meaning",
    name: "Eight of Wands",
    subtitle: "Momentum, Swift Change, Forward Motion",
    image: "/card/eight-of-wands-tarot-card-reading-meaning.webp",
    suit: "Wands",
    description: `The Eight of Wands is a card of rapid movement, communication and breakthroughs. It signals a phase where energies align and things unfold with speed. What was once stagnant now flows, opening up possibilities and swift resolution. In daily life, it asks what your energy is trying to start, protect or finish before the spark fades.`,
    upright: [
      "Rapid progress: things accelerating quickly",
      "Clear communication: messages and travel",
      "Decisive action: clarity that moves things forward",
      "Alignment: all energies pointing in one direction",
      "Breakthroughs: sudden realizations or green lights"
    ],
    reversed: [
      "Delays: frustration from slowdowns or miscommunication",
      "Scattered energy: too much happening at once",
      "Missed messages: important signals getting lost",
      "Resistance to change: clinging to slow pace",
      "Overwhelm: struggling to keep up with speed"
    ],
    reading: `The Eight of Wands signals the moment after inertia breaks, when things that once crawled now soar. This card does not knock gently. It arrives like a gust of wind and tells you that now is the time. Delays have lifted. Life is in motion. So must you be.

It is a cosmic green light, a burst of flow that carries projects, conversations or dreams forward. Trust that what is coming is swift, aligned and purposeful. When synchronicities begin to unfold, pay attention. Life is showing you where to aim your energy and what to let fly.

In spirit, this card is an invitation to ride the current rather than control it. When you let go of needing to plan every step, you make room for magic. Let the arrows fly and trust they will land exactly where they should.

Look for the ordinary signs too: where your energy rises, where it drains and where action would bring relief. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
• Where is momentum naturally building in my life right now?
• What needs to move quickly and what am I still hesitating on?
• How can I align my intentions with the flow already unfolding?

The wind is at your back. Aim true and leap.`,
    mirroring: [
      "the-chariot-tarot-card-reading-meaning",
      "knight-of-wands-tarot-card-reading-meaning",
      "two-of-wands-tarot-card-reading-meaning"
    ]
  },

  "nine-of-wands-tarot-card-reading-meaning": {
    slug: "nine-of-wands-tarot-card-reading-meaning",
    name: "Nine of Wands",
    subtitle: "Resilience, Wounded Warrior, Last Stand",
    image: "/card/nine-of-wands-tarot-card-reading-meaning.webp",
    suit: "Wands",
    description: `The Nine of Wands appears when you have endured much, but your spirit still rises. It is the card of perseverance in the face of hardship, a wounded warrior holding the line. You are near the end, but not finished yet. In daily life, it asks what your energy is trying to start, protect or finish before the spark fades.`,
    upright: [
      "Resilience: standing strong despite wounds",
      "Perseverance: continuing even when exhausted",
      "Courage: facing challenges with grit",
      "Boundaries: knowing what is worth protecting",
      "Near completion: almost at the end of a cycle"
    ],
    reversed: [
      "Burnout: pushing yourself past your limit",
      "Defensiveness: building walls too high",
      "Paranoia: fearing the next blow before it comes",
      "Reluctance: not wanting to try again",
      "Wounds unhealed: carrying past pain into the present"
    ],
    reading: `The Nine of Wands does not promise ease, it promises strength. You have fought battles that few have seen and now you stand, bruised but breathing. This is not weakness, it is spiritual endurance. You have come this far for a reason. You are closer than you think.

Sometimes the soul chooses paths that test every layer of our resilience. This card asks whether you can hold the line for one more breath, one more sunrise. Can you tend your wounds without surrendering your will? The reward lies just beyond this final climb.

Energetically, this card is your reminder that survival is not the endgame, thriving is. You are not meant to live in defense forever. Let this be your last stand and the first step toward softer ground.

Look for the ordinary signs too: where your energy rises, where it drains and where action would bring relief. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
• What in me has been tested the most and how have I endured?
• What am I defending and is it still worth it?
• How can I move forward with strength without losing softness?

This is the final test before the dawn. Do not give up, rise once more.`,
    mirroring: [
      "seven-of-wands-tarot-card-reading-meaning",
      "ten-of-wands-tarot-card-reading-meaning",
      "strength-tarot-card-reading-meaning"
    ]
  },

  "ten-of-wands-tarot-card-reading-meaning": {
    slug: "ten-of-wands-tarot-card-reading-meaning",
    name: "Ten of Wands",
    subtitle: "Burden, Overload, Responsibility",
    image: "/card/ten-of-wands-tarot-card-reading-meaning.webp",
    suit: "Wands",
    description: `The Ten of Wands represents carrying too much. It speaks of burdens, some chosen, some inherited and the weight that builds when you try to hold it all alone. It marks the end of a journey, where pressure peaks before release. In daily life, it asks what your energy is trying to start, protect or finish before the spark fades.`,
    upright: [
      "Heavy burdens: taking on too much responsibility",
      "Completion: final stretch before resolution",
      "Stress: feeling overwhelmed by duties",
      "Sacrifice: carrying the load for others",
      "Hard earned success: reaching goals through struggle"
    ],
    reversed: [
      "Delegation: asking for support or release",
      "Collapse: buckling under pressure",
      "Obligation: doing things out of guilt or duty",
      "Resentment: frustration at unshared labor",
      "Avoidance: not recognizing the toll it's taking"
    ],
    reading: `The Ten of Wands is a weighty card, not because you are weak, but because you have taken on too much. Sometimes in our pursuit of success, purpose or duty, we forget to ask whether we must carry all of this. This card asks you to pause and reevaluate.

You are nearing the finish line, but the weight is unsustainable. Let this card be permission to set some burdens down. Not everything must be carried alone. Ask where your strength has become self sacrifice and where you have confused martyrdom with meaning.

In soul terms, this is the end of a spiritual cycle. You have grown through labor, but now it is time to lighten the load. Lay down what is not yours. Unburden your soul. Completion is sweeter when you can breathe.

Look for the ordinary signs too: where your energy rises, where it drains and where action would bring relief. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
• What am I carrying that no longer serves me?
• Who am I trying to save and at what cost?
• What would it feel like to move through life unburdened?

Let go. What is meant for you will remain, even when your hands are empty.`,
    mirroring: [
      "the-hermit-tarot-card-reading-meaning",
      "nine-of-wands-tarot-card-reading-meaning",
      "ten-of-swords-tarot-card-reading-meaning"
    ]
  },

  "page-of-wands-tarot-card-reading-meaning": {
    slug: "page-of-wands-tarot-card-reading-meaning",
    name: "Page of Wands",
    subtitle: "Inspiration, Discovery, Enthusiasm",
    image: "/card/page-of-wands-tarot-card-reading-meaning.webp",
    suit: "Wands",
    description: `The Page of Wands bursts with creative energy and unformed potential. This is the card of youthful curiosity, artistic sparks and a thirst for new adventures. It marks the moment before action, when ideas take root and imagination fuels direction. In daily life, it asks what your energy is trying to start, protect or finish before the spark fades.`,
    upright: [
      "Creative spark: inspiration bubbling to the surface",
      "Curiosity: eagerness to explore or experiment",
      "Excitement: feeling energized by new ideas",
      "Exploration: stepping into new territory",
      "Playfulness: reconnecting with joyful expression"
    ],
    reversed: [
      "Restlessness: starting too many things at once",
      "Immaturity: acting without thinking things through",
      "Blocked creativity: not knowing how to channel inspiration",
      "Fear of failure: hesitation to act on ideas",
      "Scattered focus: too much energy with no grounding"
    ],
    reading: `The Page of Wands is the messenger of creative fire. When it appears, something within you is ready to be born, a project, a calling, a version of yourself you have only just begun to imagine. The spark is real. Now, you must choose to fan it.

This card invites you to play, to dream, to explore without attachment to outcome. It does not need a full plan, it needs your enthusiasm. Often, the Page of Wands shows up when your soul is craving joy and movement. Let yourself follow the pull of excitement.

Spiritually, this is about igniting the inner artist, the dreamer, the wanderer. You are allowed to begin, even if it is messy. What matters is that you listen to what is trying to rise. Give your fire permission to burn.

Look for the ordinary signs too: where your energy rises, where it drains and where action would bring relief. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
• What new desire or idea has been nudging me lately?
• What's stopping me from taking the first step?
• How can I let curiosity lead, without pressure to be perfect?

Your fire is rising. Let it guide you toward discovery.`,
    mirroring: [
      "the-fool-tarot-card-reading-meaning",
      "knight-of-wands-tarot-card-reading-meaning",
      "ace-of-wands-tarot-card-reading-meaning"
    ]
  },

  "knight-of-wands-tarot-card-reading-meaning": {
    slug: "knight-of-wands-tarot-card-reading-meaning",
    name: "Knight of Wands",
    subtitle: "Drive, Passion, Bold Pursuit",
    image: "/card/knight-of-wands-tarot-card-reading-meaning.webp",
    suit: "Wands",
    description: `The Knight of Wands bursts forth with energy, charisma and ambition. He represents swift movement, bold ideas and the relentless pursuit of a goal. This card reflects action without delay, a call to chase inspiration, break free of hesitation and lead with fire. In daily life, it asks what your energy is trying to start, protect or finish before the spark fades.`,
    upright: [
      "Ambition: charging toward your goal with confidence",
      "Boldness: taking initiative and breaking new ground",
      "Adventure: embracing risk and excitement",
      "Momentum: fast moving developments or travel",
      "Charisma: inspiring others with your presence and ideas"
    ],
    reversed: [
      "Impulsiveness: acting before thinking things through",
      "Arrogance: ignoring guidance or warning signs",
      "Burnout: rushing in without rest or reflection",
      "Recklessness: making hasty or risky decisions",
      "Disruption: plans derailed due to impatience"
    ],
    reading: `The Knight of Wands arrives like a gust of wildfire, hot, intense and impossible to ignore. He does not knock; he kicks the door open. His presence in a reading signals a moment of intense energy, a push forward, a rising call to act before the spark fades.

Yet passion must be wielded with purpose. The Knight invites you to harness your momentum wisely. Not every path needs to be charged into, but the one that is calling your soul, that is where your fire belongs. This is the season of motion, of not waiting for permission, of letting your heartbeat set the rhythm.

Spiritually, this card dares you to trust your drive and channel it. Ride your passion like a meaningful steed, but do not forget to guide the reins. You are not here to burn out. You are here to blaze trails and light up the path for others who follow.

Look for the ordinary signs too: where your energy rises, where it drains and where action would bring relief. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
• What pursuit sets my heart ablaze right now?
• Am I charging forward with direction or just with desire?
• How can I keep my inner fire burning without losing control?

You are the flame in motion, direct it with purpose and the world moves with you.`,
    mirroring: [
      "the-chariot-tarot-card-reading-meaning",
      "ace-of-wands-tarot-card-reading-meaning",
      "page-of-wands-tarot-card-reading-meaning"
    ]
  },

  "queen-of-wands-tarot-card-reading-meaning": {
    slug: "queen-of-wands-tarot-card-reading-meaning",
    name: "Queen of Wands",
    subtitle: "Confidence, Radiance, Inner Flame",
    image: "/card/queen-of-wands-tarot-card-reading-meaning.webp",
    suit: "Wands",
    description: `The Queen of Wands radiates magnetic energy, self assurance and independence. She is the embodiment of fire in its most graceful and assertive form, a natural leader who knows her worth and walks with passion, creativity and warmth. In daily life, it asks what your energy is trying to start, protect or finish before the spark fades.`,
    upright: [
      "Confidence: embracing your power and magnetism",
      "Charisma: inspiring and drawing others in with ease",
      "Courage: standing boldly in your truth",
      "Leadership: guiding through intuition and integrity",
      "Creativity: channeling fire into meaningful expression"
    ],
    reversed: [
      "Insecurity: doubting your light or suppressing it",
      "Jealousy: comparing yourself to others unfairly",
      "Manipulation: using charm for control",
      "Overextension: giving too much and burning out",
      "Hiddenness: shrinking away from your potential"
    ],
    reading: `The Queen of Wands does not ask for attention, she naturally commands it. When she appears, it is a sign that your inner fire is meant to be seen. This is not arrogance; this is presence. You are being reminded that confidence is not about noise, it is about knowing who you are without apology.

She encourages you to own your radiance. Let your gifts be visible. Step into roles where your spirit can lead, heal or inspire. You do not have to dim your light for anyone, the right people will be warmed by it, not blinded. This is your moment to reclaim your magic and let your intuition guide bold, beautiful steps.

Spiritually, the Queen is a reminder that power can be tender. That fire can be healing. And that your authenticity is your greatest strength. She speaks to the meaningful feminine fire within you, the one that builds, nurtures, protects and blazes on even through adversity.

Look for the ordinary signs too: where your energy rises, where it drains and where action would bring relief. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
• Where am I hiding my gifts and why?
• How can I lead without losing my softness?
• What would it look like to live fully, fiercely and freely?

You are not too much, you are fire in balance, meant to be seen, felt and followed.`,
    mirroring: [
      "the-empress-tarot-card-reading-meaning",
      "the-sun-tarot-card-reading-meaning",
      "king-of-wands-tarot-card-reading-meaning"
    ]
  },

  "king-of-wands-tarot-card-reading-meaning": {
    slug: "king-of-wands-tarot-card-reading-meaning",
    name: "King of Wands",
    subtitle: "Vision, Authority, Mastery of Fire",
    image: "/card/king-of-wands-tarot-card-reading-meaning.webp",
    suit: "Wands",
    description: `The King of Wands is the visionary leader, bold, ambitious and deeply aligned with purpose. He represents someone who leads with heart and action, not just words. This card signals mastery over creative forces and the ability to inspire, delegate and actualize grand visions. In daily life, it asks what your energy is trying to start, protect or finish before the spark fades.`,
    upright: [
      "Leadership: taking charge with clarity and confidence",
      "Vision: seeing the bigger picture and committing to it",
      "Influence: motivating others with passion and presence",
      "Courage: embracing challenge with steadiness",
      "Purpose: living and acting from your highest calling"
    ],
    reversed: [
      "Domineering: overpowering others instead of inspiring",
      "Impatience: rushing plans without clarity",
      "Ego: putting image above integrity",
      "Stubbornness: refusing collaboration or advice",
      "Disconnection: losing touch with deeper purpose"
    ],
    reading: `The King of Wands appears when you are ready to step into full embodiment of your creative power. This is not the reckless energy of the Knight, nor the radiant presence of the Queen, this is direction, vision and conviction rolled into form. You are being asked to lead, but not just for yourself, for others who depend on your clarity.

He calls on you to stop hesitating and start acting. To build. To delegate. To commit. Your gifts are no longer in the raw, they are refined and now it is time to leave legacy instead of just spark. This is the moment where leadership becomes spiritual, not just strategy, but soul.

And yet, true power is humble. The King teaches that impact does not come from control, but from alignment. Lead from your why, not your need to prove. Inspire from purpose, not pressure. Your path is not about domination, it is about ignition.

Look for the ordinary signs too: where your energy rises, where it drains and where action would bring relief. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
• What am I meant to lead right now and why?
• Am I moving from ego or from essence?
• How can I build something that serves both myself and others?

You are the fire on the throne, strong, steady, meaningful. Lead with light.`,
    mirroring: [
      "the-emperor-tarot-card-reading-meaning",
      "the-magician-tarot-card-reading-meaning",
      "queen-of-wands-tarot-card-reading-meaning"
    ]
  },

  "ace-of-cups-tarot-card-reading-meaning": {
    slug: "ace-of-cups-tarot-card-reading-meaning",
    name: "Ace of Cups",
    subtitle: "Emotional Renewal, Love, Overflowing Spirit",
    image: "/card/ace-of-cups-tarot-card-reading-meaning.webp",
    suit: "Cups",
    description: `The Ace of Cups represents the opening of the heart, the overflowing of emotion and the open invitation to love and be loved. It symbolizes the birth of compassion, spiritual connection and deep emotional healing. This card marks a moment where joy wells up from within, often unbidden; a new relationship, a spark of inner peace or a profound spiritual insight. In daily life, it asks how honestly you are meeting your feelings and the people who matter to you.`,
    upright: [
      "New love: romantic, platonic or self love blossoming",
      "Emotional healing: releasing pain to make room for joy",
      "Spiritual awakening: connection to deep love and intuition",
      "Compassion: a heart open to give and receive freely",
      "Creative flow: inspiration born from emotional truth"
    ],
    reversed: [
      "Blocked emotions: difficulty expressing or receiving love",
      "Emptiness: feeling numb or emotionally drained",
      "Overwhelm: drowning in unchecked feelings",
      "Unrequited love: offering what is not returned",
      "Closed heart: past wounds inhibiting connection"
    ],
    reading: `When the Ace of Cups appears in your reading, it is a welcome blessing and an open invitation to open your heart and let it be filled. This is the wellspring of love, compassion and creative emotion rising to meet you at the surface of your life. It doesn't need to be chased. It arrives like rain on thirsty ground.

This card asks: what in your life is ready to receive love? Whether it's the start of a relationship, a healing within or the simple choice to be gentler with yourself, the Ace of Cups reminds you that every drop of compassion given is returned tenfold.

Let this be your emotional beginning. Don't be afraid to feel fully, to cry, to laugh, to create from your heart. This is not weakness. This is where your strength is reborn. A new chapter begins, one written not from the mind, but from the soul.

Look for the ordinary signs too: what you feel, what you need and what you are ready to say honestly. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Where am I being invited to open my heart without fear?
2. What is longing to be expressed emotionally, that I've kept bottled up?
3. How can I let deep, personal or creative love flow through me today?

When the cup overflows, do not resist it. Drink deeply. Let love be your guide.`,
    mirroring: [
      "two-of-cups-tarot-card-reading-meaning",
      "the-star-tarot-card-reading-meaning",
      "queen-of-cups-tarot-card-reading-meaning"
    ]
  },

  "two-of-cups-tarot-card-reading-meaning": {
    slug: "two-of-cups-tarot-card-reading-meaning",
    name: "Two of Cups",
    subtitle: "Union, Mutual Love, Soulful Connection",
    image: "/card/two-of-cups-tarot-card-reading-meaning.webp",
    suit: "Cups",
    description: `The Two of Cups is about the moment two people meet each other honestly. It can show romance, friendship, repair after distance or any relationship where both sides are willing to listen instead of perform. Trust is the center of this card: not perfect agreement, but mutual respect, emotional steadiness and the feeling that your care is being returned. It often points to a bond that grows because both people show up with presence, kindness and enough self knowledge to keep the exchange balanced. In daily life, it asks how honestly you are meeting your feelings and the people who matter to you.`,
    upright: [
      "Mutual love: a relationship built on shared emotion and respect",
      "Reconciliation: healing and reunion after distance or hurt",
      "Soul connection: feeling truly seen and met by another",
      "Emotional balance: harmony within and between hearts",
      "Beginning of a deep union: romantic or spiritual"
    ],
    reversed: [
      "Imbalance: one sided relationships or unmet needs",
      "Misunderstanding: emotional disconnect or lack of communication",
      "Disunity: growing apart or inner conflict within a bond",
      "Emotional codependency: losing self in another",
      "Unreciprocated feelings: love not returned equally"
    ],
    reading: `When the Two of Cups appears in your reading, look at the quality of the exchange between hearts. This card is not only about attraction. It is about whether two people can be honest, receptive and emotionally responsible with each other. A real bond is built in the small moments: listening without preparing a defense, speaking before resentment hardens and letting affection be steady rather than dramatic.

It may point to a new relationship, a reconciliation, a friendship becoming more tender or a partnership that is ready for deeper trust. The important question is not whether the connection feels intense. The better question is whether it feels mutual. Do both people have room to be known? Is care moving in both directions? Are you giving from fullness rather than fear?

The Two of Cups also turns you back toward yourself. The love you offer another is strongest when it is not an escape from your own needs. Let this card remind you that healthy closeness does not ask either person to disappear. It asks both people to become more honest, more generous and more fully present.

Look for the ordinary signs too: what you feel, what you need and what you are ready to say honestly. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Am I showing up fully and honestly in my relationships?
2. Is this bond a mirror of mutual love or am I pouring into an empty cup?
3. How do I honor the balance between love for others and love for myself?

When hearts meet in truth, trust has room to grow. Let love rise in a way that can be met in kind.`,
    mirroring: [
      "ace-of-cups-tarot-card-reading-meaning",
      "the-lovers-tarot-card-reading-meaning",
      "ten-of-cups-tarot-card-reading-meaning"
    ]
  },

  "three-of-cups-tarot-card-reading-meaning": {
    slug: "three-of-cups-tarot-card-reading-meaning",
    name: "Three of Cups",
    subtitle: "Celebration, Sisterhood, Shared Joy",
    image: "/card/three-of-cups-tarot-card-reading-meaning.webp",
    suit: "Cups",
    description: `The Three of Cups is a toast to togetherness. It represents joyous union, community and emotional uplift shared among kindred spirits. This card invites moments of laughter, shared stories, healing conversations and spiritual kinship. It celebrates the power of connection to lift our hearts. In daily life, it asks how honestly you are meeting your feelings and the people who matter to you.`,
    upright: [
      "Celebration: weddings, births, reunions or joyous milestones",
      "Friendship: meaningful emotional support from your circle",
      "Community: shared emotional healing through others",
      "Harmony: emotional alignment with those around you",
      "Spiritual sisterhood: feminine or meaningful group energy"
    ],
    reversed: [
      "Disconnection: feeling out of place within your community",
      "Overindulgence: escapism through pleasure or distraction",
      "Gossip: miscommunication or harmful speech among friends",
      "Isolation: longing for connection but feeling unseen",
      "Fake joy: smiling on the outside, aching within"
    ],
    reading: `The Three of Cups arrives as a warm embrace from your soul tribe; those who see you, uplift you and remind you of your joy. It signals a time to celebrate, not because life is perfect, but because connection itself is reason enough. In togetherness, healing accelerates. In joy, sorrow dissolves.

You are being asked to soften into support, to allow celebration without guilt. Life has rhythms; some for work, others for dance. When this card appears, lean into joy. Let music carry you, let laughter be your medicine. There is healing in the clink of cups, in the echo of stories, in being reminded that you're not alone.

And perhaps, most importantly, this card reminds you to make space for joy without needing justification. You are worthy of celebration, not just after success, but in the meaningful now.

Look for the ordinary signs too: what you feel, what you need and what you are ready to say honestly. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. When was the last time I celebrated simply being alive?
2. Who are the people that uplift me and how can I connect with them more deeply?
3. Can I receive joy as a healing practice, not just a reward?

Raise your cup. You are not alone. You are seen, loved and surrounded by light.`,
    mirroring: [
      "four-of-wands-tarot-card-reading-meaning",
      "ten-of-cups-tarot-card-reading-meaning",
      "the-sun-tarot-card-reading-meaning"
    ]
  },

  "four-of-cups-tarot-card-reading-meaning": {
    slug: "four-of-cups-tarot-card-reading-meaning",
    name: "Four of Cups",
    subtitle: "Discontent, Contemplation, Emotional Withdrawal",
    image: "/card/four-of-cups-tarot-card-reading-meaning.webp",
    suit: "Cups",
    description: `The Four of Cups captures a moment of emotional stagnation, when the heart turns inward, questioning everything it once desired. You may be surrounded by offerings, yet none feel right. This card signals a need to reflect, but warns against becoming so introspective that you miss what's being offered. In daily life, it asks how honestly you are meeting your feelings and the people who matter to you.`,
    upright: [
      "Reflection: taking emotional inventory before the next step",
      "Pause: retreat to examine desires and dissatisfaction",
      "Missed opportunities: ignoring gifts due to preoccupation",
      "Emotional fatigue: nothing feels satisfying or joyful",
      "Yearning: a quiet longing for something deeper"
    ],
    reversed: [
      "Renewal: reawakening emotional awareness and receptivity",
      "New perspective: seeing blessings that were once overlooked",
      "Emotional opening: readiness to connect again",
      "Release: letting go of cynicism and emotional numbness",
      "Seizing chances: embracing a new offer with courage"
    ],
    reading: `The Four of Cups appears when your heart feels tired, not necessarily broken, but uninspired. You're being called to pause and assess what truly fulfills you. Are your current desires even yours or have they been shaped by others' expectations? This is the soul's quiet rebellion against false pursuits.

But be mindful: what you long for may already be in reach, just unseen through your current lens. Sometimes the deep places gifts before us that don't look like what we expected. This card asks you to open not just your eyes, but your heart.

It's okay to retreat, but not forever. The world is still full of wonder. Look again. Something meaningful might be waiting to be received.

Look for the ordinary signs too: what you feel, what you need and what you are ready to say honestly. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Am I pushing away love or opportunity out of fear or disappointment?
2. What parts of me are asking for rest and renewal?
3. Can I soften my grip on what I thought I wanted, to receive what I truly need?

The heart doesn't always need more; sometimes it needs to feel again.`,
    mirroring: [
      "five-of-cups-tarot-card-reading-meaning",
      "the-hermit-tarot-card-reading-meaning",
      "the-star-tarot-card-reading-meaning"
    ]
  },

  "five-of-cups-tarot-card-reading-meaning": {
    slug: "five-of-cups-tarot-card-reading-meaning",
    name: "Five of Cups",
    subtitle: "Grief, Disappointment, Emotional Loss",
    image: "/card/five-of-cups-tarot-card-reading-meaning.webp",
    suit: "Cups",
    description: `The Five of Cups is the aching heart, the sting of loss, the heaviness of regret. This card honors sorrow; it doesn't bypass pain but calls you to sit with it. Yet in every depiction of the Five of Cups, not all is lost. Behind the mourning figure are cups still standing, a quiet symbol of hope. In daily life, it asks how honestly you are meeting your feelings and the people who matter to you.`,
    upright: [
      "Grief: loss of love, dreams or expectations",
      "Disappointment: feeling let down by people or outcomes",
      "Regret: replaying past mistakes or missed chances",
      "Emotional focus: dwelling on what went wrong",
      "Sorrow: needing to process emotional pain fully"
    ],
    reversed: [
      "Acceptance: coming to peace with what cannot be changed",
      "Healing: beginning to move forward after grief",
      "Forgiveness: releasing blame toward self or others",
      "Hope: recognizing what still remains and matters",
      "Emotional growth: integrating pain into wisdom"
    ],
    reading: `The Five of Cups does not ask you to cheer up; it asks you to honor what hurts. Sometimes things don't work out. Sometimes people leave. Sometimes we lose versions of ourselves we thought would stay forever. This card is an invitation to grieve consciously, not to stay stuck in sorrow, but to let it move through you.

You are allowed to feel the sting. But don't forget to turn around. Behind you stand the remaining cups; love that endures, lessons gained and the quiet resilience of your soul. Healing begins not when pain vanishes, but when you choose to face it without shame.

This card reminds you that your story doesn't end in loss. What has spilled is not all there is. New waters flow again, but only when you are ready to look up.

Look for the ordinary signs too: what you feel, what you need and what you are ready to say honestly. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What am I still grieving that I haven't fully allowed myself to feel?
2. Where am I focusing so much on loss that I miss what still supports me?
3. Can I forgive myself for what I didn't know back then?

Let your sorrow speak and when it's done, let your healing rise.`,
    mirroring: [
      "three-of-swords-tarot-card-reading-meaning",
      "ten-of-swords-tarot-card-reading-meaning",
      "the-star-tarot-card-reading-meaning"
    ]
  },

  "six-of-cups-tarot-card-reading-meaning": {
    slug: "six-of-cups-tarot-card-reading-meaning",
    name: "Six of Cups",
    subtitle: "Nostalgia, Innocence, Emotional Roots",
    image: "/card/six-of-cups-tarot-card-reading-meaning.webp",
    suit: "Cups",
    description: `The Six of Cups invites you to revisit the emotional landscapes of your past, not to dwell, but to reclaim joy, innocence and the truths of your inner child. It speaks of familiar comforts, kind gestures and healing through memory. This card is a soft return to what made your heart bloom before life became complicated. In daily life, it asks how honestly you are meeting your feelings and the people who matter to you.`,
    upright: [
      "Nostalgia: reflecting on joyful past experiences",
      "Innocence: childlike wonder and trust",
      "Kindness: emotional generosity and affection",
      "Reconnection: healing through old relationships or memories",
      "Harmony: emotional peace through familiarity"
    ],
    reversed: [
      "Stuck in the past: idealizing what was and avoiding the present",
      "Emotional immaturity: reacting through childlike patterns",
      "Disillusionment: old memories no longer bring comfort",
      "Unresolved childhood wounds: surfacing to be healed",
      "Clinging: holding onto something that has already changed"
    ],
    reading: `When the Six of Cups appears, it brings with it a scent of home, a childhood laugh, a photograph tucked in a drawer. It is a gentle pull from your past; a reminder that not everything old must be left behind. Some parts of who you were are still meaningful and worthy of bringing forward.

This card may be asking you to return emotionally to a place of softness, where your trust wasn't broken yet, where joy came easily, where your heart knew what it wanted without fear. It might signal reconnection with an old friend, a family member or even a version of yourself you thought you'd outgrown but now realize you still need.

Yet while this card reveres memory, it also warns against being lost in it. Let nostalgia be a visitor, not a home. Use what was to heal what is, but don't forget to keep walking forward.

Look for the ordinary signs too: what you feel, what you need and what you are ready to say honestly. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What aspect of my past is calling me with love, not pain?
2. Am I idealizing something that no longer serves me?
3. How can I re integrate the wonder and joy of my childhood into my present?

Not all returns are regressions. Some are steady homecomings.`,
    mirroring: [
      "the-sun-tarot-card-reading-meaning",
      "four-of-wands-tarot-card-reading-meaning",
      "page-of-cups-tarot-card-reading-meaning"
    ]
  },

  "seven-of-cups-tarot-card-reading-meaning": {
    slug: "seven-of-cups-tarot-card-reading-meaning",
    name: "Seven of Cups",
    subtitle: "Choices, Illusions, Dreams",
    image: "/card/seven-of-cups-tarot-card-reading-meaning.webp",
    suit: "Cups",
    description: `The Seven of Cups represents the realm of dreams, possibilities and distractions. It reveals a time when your imagination is overflowing, but your focus may be scattered. With so many options laid before you, it's easy to get lost in fantasy or seduced by the allure of what could be, rather than anchoring yourself in reality. This card reminds you that while dreaming is beautiful, discernment is essential. In daily life, it asks how honestly you are meeting your feelings and the people who matter to you.`,
    upright: [
      "Many opportunities: a time of abundant choices",
      "Imagination: rich inner visions and fantasies",
      "Temptation: being drawn to illusions or false promises",
      "Indecision: feeling overwhelmed by options",
      "Desire: longing for ideal outcomes or escape"
    ],
    reversed: [
      "Clarity: seeing through illusion and narrowing focus",
      "Avoidance: refusal to make necessary decisions",
      "Disillusionment: realizing some dreams were unrealistic",
      "Distraction: losing focus on what truly matters",
      "Emotional overwhelm: struggling to ground oneself"
    ],
    reading: `When the Seven of Cups appears, you are standing in the garden of possibility, surrounded by glittering visions that call your name. Each cup holds a different path, a different version of who you could become; yet not all are what they seem. Some are filled with truth, others with illusion. And the challenge lies not in choosing, but in choosing wisely.

This card arrives when your heart is yearning, when your mind is flooded with dreams or when life offers more doors than you know how to open. It's a magical moment, but also one that calls for presence. Let your intuition be the lens through which you see clearly. Do not let fantasy seduce you into paralysis or false hope.

Ground yourself. Breathe. Then ask: which of these paths aligns not only with your desires, but with your deepest truth? Sometimes the most enchanting choice is not the most enriching one. Discern with your soul, not your fear.

Look for the ordinary signs too: what you feel, what you need and what you are ready to say honestly. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What am I chasing that might be rooted in illusion rather than purpose?
2. Which option reflects my true values, not just my current desires?
3. Am I afraid to commit because I fear choosing wrongly?

When the Seven of Cups appears, you are being reminded that your dreams are meaningful, but they must be chosen, not chased blindly.`,
    mirroring: [
      "the-moon-tarot-card-reading-meaning",
      "the-devil-tarot-card-reading-meaning",
      "two-of-swords-tarot-card-reading-meaning"
    ]
  },

  "eight-of-cups-tarot-card-reading-meaning": {
    slug: "eight-of-cups-tarot-card-reading-meaning",
    name: "Eight of Cups",
    subtitle: "Departure, Transition, Emotional Growth",
    image: "/card/eight-of-cups-tarot-card-reading-meaning.webp",
    suit: "Cups",
    description: `The Eight of Cups symbolizes a conscious turning away, not out of failure, but from inner wisdom. You are walking away from something that once mattered, yet no longer nourishes your spirit. This card signals the bravery it takes to leave behind comfort, relationships or situations that no longer align with your soul's evolution. In daily life, it asks how honestly you are meeting your feelings and the people who matter to you.`,
    upright: [
      "Letting go: choosing growth over comfort",
      "Spiritual quest: seeking deeper fulfillment",
      "Turning inward: prioritizing emotional truth",
      "Moving on: leaving behind what's familiar",
      "Re evaluation: recognizing when it's time to walk away"
    ],
    reversed: [
      "Avoidance: staying in a situation out of fear",
      "Unfinished business: emotional loose ends",
      "Stagnation: resisting needed change",
      "Fear of solitude: clinging to connections for security",
      "False hope: hoping something will change that won't"
    ],
    reading: `When the Eight of Cups appears, it is a quiet revolution. A soul decision. You may not have all the answers, but you do have a deep knowing that something must shift. This is not an escape; it is a passage toward truth, even if the path is lonely or unclear. Sometimes growth demands we leave behind what once felt like home.

You are being called to release what is no longer resonant, to honor the wisdom that suggests, "This is not enough for me anymore." Whether it's a job, a relationship or a version of yourself, this departure is not abandonment; it is alignment. Trust that what you are walking toward holds greater depth.

There is courage in walking away, even when others don't understand. Let your journey be meaningful. The unknown may feel daunting, but your soul knows where it's going, even if your mind does not.

Look for the ordinary signs too: what you feel, what you need and what you are ready to say honestly. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What am I holding onto that no longer nourishes my spirit?
2. Am I brave enough to choose truth over familiarity?
3. What version of myself am I outgrowing?

When the Eight of Cups appears, you are not lost; you are on pilgrimage to something more whole.`,
    mirroring: [
      "the-hermit-tarot-card-reading-meaning",
      "death-tarot-card-reading-meaning",
      "six-of-swords-tarot-card-reading-meaning"
    ]
  },

  "nine-of-cups-tarot-card-reading-meaning": {
    slug: "nine-of-cups-tarot-card-reading-meaning",
    name: "Nine of Cups",
    subtitle: "Contentment, Gratitude, Fulfillment",
    image: "/card/nine-of-cups-tarot-card-reading-meaning.webp",
    suit: "Cups",
    description: `The Nine of Cups is often called the 'wish card'; a sign of emotional satisfaction and dreams manifesting into reality. It reflects a time when your heart feels full, your desires are being met and joy flows effortlessly. This card invites you to celebrate your blessings and relish the sweetness of the present moment. In daily life, it asks how honestly you are meeting your feelings and the people who matter to you.`,
    upright: [
      "Emotional satisfaction: deep contentment and joy",
      "Gratitude: appreciating what you have",
      "Fulfillment: wishes coming true",
      "Pride: enjoying personal accomplishments",
      "Self love: feeling whole within oneself"
    ],
    reversed: [
      "Hollow success: getting what you want but still feeling empty",
      "Overindulgence: pleasure without balance",
      "Disconnection: not appreciating your blessings",
      "Self centeredness: focusing only on personal gain",
      "Unrealistic expectations: wanting more than is sustainable"
    ],
    reading: `When the Nine of Cups appears, it is a gentle exhale; a reminder that you have come far and deserve to feel the joy that blooms from within. This is not about ego or excess, but about honoring the emotional milestones you've reached. Let yourself feel proud. Let yourself savor the beauty.

Sometimes we are so focused on what's next that we forget to sit in the radiance of now. This card asks you to look around and truly see what is good. Let contentment be a ritual. Let gratitude be your heartbeat. Fulfillment is not a destination; it is a practice.

You are being reminded that joy is meaningful. Allow yourself to feel it without guilt, to receive it without question. This moment is a gift and you are allowed to unwrap it fully.

Look for the ordinary signs too: what you feel, what you need and what you are ready to say honestly. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Am I allowing myself to feel the joy I've earned?
2. What do I take for granted that is actually a blessing?
3. Can I be at peace with what I have, even as I dream of more?

When the Nine of Cups appears, happiness is not a future goal; it's the cup you're already holding.`,
    mirroring: [
      "the-empress-tarot-card-reading-meaning",
      "four-of-wands-tarot-card-reading-meaning",
      "ten-of-pentacles-tarot-card-reading-meaning"
    ]
  },

  "ten-of-cups-tarot-card-reading-meaning": {
    slug: "ten-of-cups-tarot-card-reading-meaning",
    name: "Ten of Cups",
    subtitle: "Emotional Harmony, Unity, Love Fulfilled",
    image: "/card/ten-of-cups-tarot-card-reading-meaning.webp",
    suit: "Cups",
    description: `The Ten of Cups is the card of emotional wholeness, spiritual connection and shared joy. It represents not only the fulfillment of individual dreams, but the harmonious union of hearts; family, partnership or community. This card radiates love, peace and a sense of belonging. In daily life, it asks how honestly you are meeting your feelings and the people who matter to you.`,
    upright: [
      "Harmony: deep emotional peace with loved ones",
      "Family: joy in connection and togetherness",
      "Fulfillment: spiritual and emotional contentment",
      "Alignment: living in accordance with your values",
      "Lasting love: stable and supportive relationships"
    ],
    reversed: [
      "Disharmony: conflict within close relationships",
      "False ideals: chasing perfection in love or family",
      "Disconnection: feeling emotionally isolated",
      "Broken expectations: reality not meeting dreams",
      "Longing: yearning for deeper bonds"
    ],
    reading: `When the Ten of Cups appears, it is the soul's exhale after a long journey. You have found or are moving toward a place of emotional resonance, where love flows freely and peace is no longer a fleeting feeling but a foundation. This card sings of shared joy, of homes built on heart and not just walls.

You are being invited to receive love more deeply, to let yourself be held by those who see you and to pour that same love back into your circles. This is not just personal happiness, but collective harmony. It is about finding or creating emotional sanctuary.

Whether this card reflects current reality or a vision just over the horizon, it reminds you that love is not a myth; it is a mirror. And when you offer love from a place of truth, it will find its way back to you.

Look for the ordinary signs too: what you feel, what you need and what you are ready to say honestly. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What relationships bring me the deepest peace?
2. Am I allowing myself to be loved as fully as I love others?
3. What does emotional fulfillment truly look like for me?

When the Ten of Cups appears, know that love is your birthright and joy, your steady home.`,
    mirroring: [
      "the-sun-tarot-card-reading-meaning",
      "two-of-cups-tarot-card-reading-meaning",
      "four-of-wands-tarot-card-reading-meaning"
    ]
  },

  "page-of-cups-tarot-card-reading-meaning": {
    slug: "page-of-cups-tarot-card-reading-meaning",
    name: "Page of Cups",
    subtitle: "Creative Innocence, Emotional Curiosity, Messages of the Heart",
    image: "/card/page-of-cups-tarot-card-reading-meaning.webp",
    suit: "Cups",
    description: `The Page of Cups embodies youthful wonder and the first stirrings of emotional or artistic exploration. This card signals the arrival of an unexpected message that opens your heart to something new. It carries innocence, creative insight and emotional vulnerability, encouraging you to listen to the quiet voice of your inner child. In daily life, it asks how honestly you are meeting your feelings and the people who matter to you.`,
    upright: [
      "New emotional experiences: openness to love or friendships",
      "Creative spark: beginning of artistic or intuitive expression",
      "Emotional messages: unexpected or sincere news",
      "Playfulness: viewing life with wonder and softness",
      "Dreaminess: connecting with fantasy, poetry and imagination"
    ],
    reversed: [
      "Emotional immaturity: avoiding responsibility or depth",
      "Creative blocks: ignoring your artistic voice",
      "Insecurity: fear of expressing true feelings",
      "Oversensitivity: taking things too personally",
      "Naivety: misinterpreting emotions or signs"
    ],
    reading: `When the Page of Cups appears, it arrives like a soft breeze that stirs the surface of still water. Something gentle is unfolding: a realization, a confession or a creative impulse, asking you not to analyze, but to feel. This card doesn't shout; it suggests. It opens the door to wonder and invites you to step through with a trusting heart.

You may be receiving an emotional message soon or discovering a side of yourself that longs to create, love or simply dream. The Page is the soul's muse, innocent, unfiltered and courageous in its vulnerability. You are being reminded that sensitivity is not weakness, but quiet wisdom.

To embrace this card fully, soften your defenses. Let curiosity guide you. Let yourself be surprised by the sweetness of life, even if just for a moment. Within your heart, there is still a child who believes in magic and this card tells you it's safe to listen.

Look for the ordinary signs too: what you feel, what you need and what you are ready to say honestly. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Where in my life am I being asked to lead with innocence and openness?
2. What creative or emotional truth have I been afraid to explore?
3. Can I receive love or inspiration without overthinking it?

When the Page of Cups appears, know that a tender message is being delivered, not to your mind, but to your heart.`,
    mirroring: [
      "the-fool-tarot-card-reading-meaning",
      "the-star-tarot-card-reading-meaning",
      "two-of-cups-tarot-card-reading-meaning"
    ]
  },

  "knight-of-cups-tarot-card-reading-meaning": {
    slug: "knight-of-cups-tarot-card-reading-meaning",
    name: "Knight of Cups",
    subtitle: "Romantic Pursuit, Artistic Vision, Emotional Action",
    image: "/card/knight-of-cups-tarot-card-reading-meaning.webp",
    suit: "Cups",
    description: `The Knight of Cups is the poet, the dreamer, the lover who follows his heart. This card signifies a journey or action inspired by emotion or intuition, whether it's the pursuit of love, the birth of a creative project or an idealistic vision you are ready to bring into motion. The Knight reminds you that passion is not passive; it moves, quests and believes. In daily life, it asks how honestly you are meeting your feelings and the people who matter to you.`,
    upright: [
      "Romantic pursuit: following your heart toward connection",
      "Artistic drive: taking action on creative dreams",
      "Emotional bravery: expressing your feelings with sincerity",
      "Charm: enchanting energy that draws people in",
      "Idealism: holding onto noble dreams and values"
    ],
    reversed: [
      "Over romanticizing: falling for fantasy over reality",
      "Moodiness: letting emotions dictate actions",
      "Avoidance: charming your way out of depth",
      "Emotional manipulation: using feelings to control or influence",
      "Creative inconsistency: starting without finishing"
    ],
    reading: `When the Knight of Cups appears, your heart is not content to sit still. There is a longing here for love, for beauty, for something meaningful to pursue. This card signals the time to act on your emotional desires. It could mean sending that message, starting that poem or saying yes to something that stirs your soul.

But the Knight also teaches discernment. Passion without grounding can lead you chasing illusions. You must balance your idealism with self awareness. Are you in love with someone or with the idea of being in love? Are you pursuing your art or the attention it brings? Ask gently, but ask honestly.

At its best, the Knight of Cups invites you into a meaningful dance with inspiration. You are the vessel of emotion and vision; now ride forward with integrity, tenderness and truth.

Look for the ordinary signs too: what you feel, what you need and what you are ready to say honestly. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Am I taking meaningful action on my emotional or creative desires?
2. Where am I being guided by beauty and where am I being seduced by illusion?
3. Can I express what I feel without fear of rejection or misunderstanding?

When the Knight of Cups appears, your heart is your compass; follow it bravely, but keep your eyes open to the road ahead.`,
    mirroring: [
      "the-lovers-tarot-card-reading-meaning",
      "page-of-cups-tarot-card-reading-meaning",
      "the-chariot-tarot-card-reading-meaning"
    ]
  },

  "queen-of-cups-tarot-card-reading-meaning": {
    slug: "queen-of-cups-tarot-card-reading-meaning",
    name: "Queen of Cups",
    subtitle: "Compassion, Intuition, Emotional Depth",
    image: "/card/queen-of-cups-tarot-card-reading-meaning.webp",
    suit: "Cups",
    description: `The Queen of Cups is the embodiment of emotional maturity, spiritual intuition and nurturing energy. She sits on her throne with a heart open wide, not because she is fragile, but because she is powerful in her ability to feel, hold and heal. This card calls you to embrace your empathic gifts and to trust the quiet wisdom that comes from deep feeling. In daily life, it asks how honestly you are meeting your feelings and the people who matter to you.`,
    upright: [
      "Compassion: offering support and understanding to others",
      "Emotional intelligence: wise and grounded sensitivity",
      "Intuition: strong spiritual and emotional insight",
      "Nurturing: caring for others with love and calm",
      "Healing: emotional comfort and restoration"
    ],
    reversed: [
      "Emotional overwhelm: taking on others' pain",
      "Codependency: losing yourself in another's needs",
      "Suppressed emotions: hiding your feelings to keep peace",
      "Moodiness: sensitivity turned inward or erratic",
      "Manipulation: using emotion as a subtle control"
    ],
    reading: `When the Queen of Cups appears, she comes to you as a mirror, not of your surface self, but of your emotional and spiritual depth. She reminds you that tenderness is not weakness, that your feelings are not flaws and that your ability to empathize is meaningful. This is a card of healing; within and for others.

You may be called to hold space for someone else or to hold space for yourself in a way you never have. Trust your intuition. Let your emotions guide your understanding rather than dismissing them as noise. The Queen teaches that deep feeling is a form of deep wisdom.

She sits quietly by the water, not because she fears the tide, but because she knows how to read its rhythm. So too must you listen deeply, feel fully and respond gently. There is magic in your stillness.

Look for the ordinary signs too: what you feel, what you need and what you are ready to say honestly. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Where am I being asked to respond with compassion rather than control?
2. How can I honor my sensitivity as a source of strength?
3. Am I listening to what my heart truly feels beneath the surface?

When the Queen of Cups appears, know that empathy is your superpower and love, your meaningful language.`,
    mirroring: [
      "the-high-priestess-tarot-card-reading-meaning",
      "the-empress-tarot-card-reading-meaning",
      "four-of-cups-tarot-card-reading-meaning"
    ]
  },

  "king-of-cups-tarot-card-reading-meaning": {
    slug: "king-of-cups-tarot-card-reading-meaning",
    name: "King of Cups",
    subtitle: "Emotional Mastery, Wisdom, Calm Authority",
    image: "/card/king-of-cups-tarot-card-reading-meaning.webp",
    suit: "Cups",
    description: `The King of Cups represents emotional balance, maturity and the power of calm leadership. He has mastered the stormy seas of the heart and now rules with a steady, compassionate hand. This card signals your ability to lead with wisdom, offer stability in emotional situations and stay centered even when others are in turmoil. In daily life, it asks how honestly you are meeting your feelings and the people who matter to you.`,
    upright: [
      "Emotional maturity: calm under pressure",
      "Compassionate leadership: guiding with empathy",
      "Balance: navigating logic and emotion with grace",
      "Diplomacy: resolving conflict with kindness",
      "Creative mentorship: nurturing others' growth"
    ],
    reversed: [
      "Emotional detachment: repressing feelings or shutting down",
      "Coldness: withholding empathy or affection",
      "Manipulation: using emotional insight for control",
      "Imbalance: over identifying with either mind or heart",
      "Instability: being overwhelmed by internal emotions"
    ],
    reading: `When the King of Cups appears, you are being asked to embody emotional maturity, to be the calm within the storm. Whether you're navigating your own inner waters or supporting someone else through theirs, this card calls for compassionate leadership. Not dominance, but deep emotional presence.

This King doesn't avoid emotion; he understands it deeply. He listens without judgment, speaks with kindness and acts from wisdom. You are being asked to rise into your strength, not through force, but through emotional mastery. Be the anchor others can hold and the guide they can trust.

Let your steadiness be a gift. Even if you are feeling waves inside, the ability to hold yourself with grace is what allows others to find peace in your presence. The King teaches: power is quiet and love is a legacy.

Look for the ordinary signs too: what you feel, what you need and what you are ready to say honestly. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Am I leading with wisdom or reacting from emotion?
2. How can I offer support without losing my own center?
3. Where in my life can I bring calm to chaos?

When the King of Cups appears, your greatest strength lies not in control, but in compassion, grounded in truth.`,
    mirroring: [
      "justice-tarot-card-reading-meaning",
      "temperance-tarot-card-reading-meaning",
      "queen-of-cups-tarot-card-reading-meaning"
    ]
  },

  "ace-of-swords-tarot-card-reading-meaning": {
    slug: "ace-of-swords-tarot-card-reading-meaning",
    name: "Ace of Swords",
    subtitle: "Clarity, Breakthroughs, Mental Power",
    image: "/card/ace-of-swords-tarot-card-reading-meaning.webp",
    suit: "Swords",
    description: `The Ace of Swords slices through illusion to reveal the truth beneath. This card is a symbol of sharp intellect, clarity of thought and the power to see things as they are. Like a bolt of lightning, it brings sudden insight or revelation that changes your perception forever. It represents a new beginning in the realm of the mind; a chance to make decisions based on truth and justice rather than emotion or confusion. In daily life, it asks which thought, conversation or decision needs clearer language now.`,
    upright: [
      "Mental clarity: seeing situations with precision and truth",
      "Breakthroughs: sudden realizations or epiphanies",
      "New ideas: a surge of intellectual or creative energy",
      "Honest communication: direct, clear and powerful expression",
      "Justice: cutting through bias to uphold truth"
    ],
    reversed: [
      "Confusion: mental fog or overwhelming thoughts",
      "Miscommunication: unclear or harmful words",
      "Mental blocks: difficulty making decisions or thinking clearly",
      "Delusion: refusing to see the truth",
      "Harsh judgment: overly critical or cold minded behavior"
    ],
    reading: `When the Ace of Swords appears in your reading, it arrives like the first breath of cold air after a storm; invigorating, sobering and undeniable. This card does not quiet message; it announces. It pierces through the noise, the lies and the chaos to bring a flash of clarity that demands action.

You are being handed a sword of truth, not for battle, but for discernment. It is time to cut away the illusions that cloud your mind and make decisions from a place of empowered understanding. There is power in your voice, integrity in your thoughts and a moment now where truth can reshape your path.

This card invites you to speak boldly, to think freely and to rise into mental sovereignty. Even if the truth is uncomfortable, it is meaningful. And it is yours to hold.

Look for the ordinary signs too: the thought you keep rehearsing, the conversation waiting for clarity and the decision that needs clean language. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What truth have I been avoiding because it is inconvenient or painful?
2. Where am I being called to speak up or stand up for what is right?
3. Can I trust my mind to lead when my heart feels clouded?

The Ace of Swords reminds you that truth is not always soft, but it is always liberating.`,
    mirroring: [
      "justice-tarot-card-reading-meaning",
      "the-tower-tarot-card-reading-meaning",
      "king-of-swords-tarot-card-reading-meaning"
    ]
  },

  "two-of-swords-tarot-card-reading-meaning": {
    slug: "two-of-swords-tarot-card-reading-meaning",
    name: "Two of Swords",
    subtitle: "Indecision, Inner Conflict, Impasse",
    image: "/card/two-of-swords-tarot-card-reading-meaning.webp",
    suit: "Swords",
    description: `The Two of Swords presents a moment of stasis; a quiet before action, a pause filled with tension. It depicts a state of inner conflict or avoidance, where two opposing truths or paths weigh equally and no decision feels quite right. This is the card of being stuck in mental limbo, seeking peace through inaction while the world awaits your move. In daily life, it asks which thought, conversation or decision needs clearer language now.`,
    upright: [
      "Difficult choices: stuck between two opposing options",
      "Avoidance: refusing to face a necessary truth",
      "Inner conflict: battling with self doubt or duality",
      "Detachment: emotional numbing to avoid pain",
      "Seeking balance: striving for objectivity amidst confusion"
    ],
    reversed: [
      "Mental overload: paralysis through overthinking",
      "Emotional bias: letting feelings cloud clear judgment",
      "Rushed decisions: reacting without full understanding",
      "Denial: ignoring the signs or truths before you",
      "Breakdown: pressure finally forcing action"
    ],
    reading: `The Two of Swords asks you to sit in the discomfort of not knowing, to acknowledge the space between what you feel and what you think. You may be tempted to avoid a decision, hoping the situation resolves itself, but silence does not equal resolution. This card reflects the moment you hold your breath before diving in.

There is wisdom in stillness, but stagnation is not the same as peace. If you've been denying your inner truth or trying to "wait it out," it's time to look within. What are you afraid to confront? What consequences are you shielding yourself from?

When this card appears, the answer is not always logical; it is intuitive. You must trust the quiet voice beneath the noise, the knowing behind the doubt.

Look for the ordinary signs too: the thought you keep rehearsing, the conversation waiting for clarity and the decision that needs clean language. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What choice have I been avoiding and why?
2. Have I been silencing my own voice to keep the peace?
3. What truth would I face if I removed the blindfold?

The Two of Swords reminds you that the pause is meaningful, but the choice is yours to make.`,
    mirroring: [
      "justice-tarot-card-reading-meaning",
      "the-hanged-man-tarot-card-reading-meaning",
      "eight-of-swords-tarot-card-reading-meaning"
    ]
  },

  "three-of-swords-tarot-card-reading-meaning": {
    slug: "three-of-swords-tarot-card-reading-meaning",
    name: "Three of Swords",
    subtitle: "Heartbreak, Grief, Emotional Pain",
    image: "/card/three-of-swords-tarot-card-reading-meaning.webp",
    suit: "Swords",
    description: `Few cards cut as deeply as the Three of Swords. It represents sorrow, betrayal or the kind of heartbreak that shifts the ground beneath you. This card does not hide from emotional pain; it faces it head on, acknowledging that grief is part of the human journey. Though it stings, it also clears the way for healing by making space for the truth. In daily life, it asks which thought, conversation or decision needs clearer language now.`,
    upright: [
      "Heartbreak: deep emotional pain or separation",
      "Grief: mourning a loss or shattered hope",
      "Truth revealed: painful honesty or betrayal",
      "Emotional release: crying, mourning or opening wounds",
      "Catharsis: clearing the way for new healing"
    ],
    reversed: [
      "Lingering pain: refusing to let go of heartbreak",
      "Suppression: bottling up grief or sorrow",
      "False recovery: pretending you're okay before you're ready",
      "Fear of vulnerability: avoiding connection to prevent pain",
      "Emotional numbness: stuck in despair without expression"
    ],
    reading: `The Three of Swords comes when something has pierced the heart; a sharp truth, a broken connection, a wound that refuses to be ignored. Its arrival may feel cruel, but its purpose is not to punish; it is to awaken. This pain is not meant to define you, but to remind you that you have loved, trusted and felt deeply.

You are being asked not to deny your suffering but to honor it. Let the tears come. Let the sorrow speak. This card asks you to hold your own heart gently, to sit with what has shattered and to offer yourself the grace of time.

Healing begins the moment you stop pretending you are fine. Allow this heartbreak to hollow you out, not to leave you empty, but to make space for something more true.

Look for the ordinary signs too: the thought you keep rehearsing, the conversation waiting for clarity and the decision that needs clean language. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What am I grieving that I haven't fully acknowledged?
2. Have I given myself permission to truly feel this pain?
3. What might grow from the space this sorrow has carved?

The Three of Swords assures you that brokenness is not failure; it is the beginning of becoming whole.`,
    mirroring: [
      "ten-of-swords-tarot-card-reading-meaning",
      "five-of-cups-tarot-card-reading-meaning",
      "the-tower-tarot-card-reading-meaning"
    ]
  },

  "four-of-swords-tarot-card-reading-meaning": {
    slug: "four-of-swords-tarot-card-reading-meaning",
    name: "Four of Swords",
    subtitle: "Rest, Reflection, Recuperation",
    image: "/card/four-of-swords-tarot-card-reading-meaning.webp",
    suit: "Swords",
    description: `The Four of Swords calls for a needed pause; a retreat into stillness where the mind can rest and the heart can breathe. After conflict or emotional upheaval, this card signals a period of recovery, a moment to gather strength before moving forward. It is not a card of avoidance, but of necessary restoration. In daily life, it asks which thought, conversation or decision needs clearer language now.`,
    upright: [
      "Rest: taking a break to recharge mentally and emotionally",
      "Meditation: finding inner peace through quiet reflection",
      "Recovery: healing from illness, heartbreak or burnout",
      "Solitude: retreating to reconnect with yourself",
      "Perspective: stepping back to gain clarity"
    ],
    reversed: [
      "Restlessness: difficulty slowing down or relaxing",
      "Burnout: pushing yourself beyond your limits",
      "Avoidance: using rest as a way to escape confrontation",
      "Mental chaos: thoughts racing without resolution",
      "Unprocessed pain: needing healing but resisting the pause"
    ],
    reading: `When the Four of Swords appears, it is not an invitation; it is a necessity. Your spirit has been running too long without pause. This is the meaningful silence after the storm, the deep exhale that the soul so often forgets to take.

You are being asked to withdraw, not as a sign of weakness but as an act of strength. In this stillness, wisdom surfaces. In this pause, your inner voice grows louder. True clarity often arrives not in action, but in rest.

Give yourself permission to stop, to listen, to breathe and to rebuild your energy. Your healing is not a luxury. It is a birthright.

Look for the ordinary signs too: the thought you keep rehearsing, the conversation waiting for clarity and the decision that needs clean language. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. When did I last allow myself to truly rest?
2. Am I avoiding healing by staying busy?
3. What message awaits me in the silence?

The Four of Swords reminds you that peace is not a destination; it is a state you must choose to return to.`,
    mirroring: [
      "the-hermit-tarot-card-reading-meaning",
      "four-of-cups-tarot-card-reading-meaning",
      "temperance-tarot-card-reading-meaning"
    ]
  },

  "five-of-swords-tarot-card-reading-meaning": {
    slug: "five-of-swords-tarot-card-reading-meaning",
    name: "Five of Swords",
    subtitle: "Conflict, Ego Battles, Hollow Victory",
    image: "/card/five-of-swords-tarot-card-reading-meaning.webp",
    suit: "Swords",
    description: `The Five of Swords speaks of conflict; the kind where winning feels empty and pride overshadows peace. It reveals arguments, power struggles or a moment where you or others may act out of self interest. This card invites you to look closely at what you're fighting for and whether the cost of victory is truly worth it. In daily life, it asks which thought, conversation or decision needs clearer language now.`,
    upright: [
      "Conflict: disagreements, arguments or tension",
      "Ego clashes: battles of will or control",
      "Hollow victory: winning at the cost of others' well being",
      "Self interest: prioritizing your gain over harmony",
      "Manipulation: playing mind games or coercion"
    ],
    reversed: [
      "Regret: remorse over past arguments or actions",
      "Resolution: seeking reconciliation or peace",
      "Letting go: walking away from toxic battles",
      "Self awareness: recognizing patterns of conflict",
      "Avoidance: fearing confrontation or standing your ground"
    ],
    reading: `The Five of Swords appears when the air is thick with words unspoken or blades sharpened by pride. You may have won the argument, but at what cost? This card does not shame you, but it does challenge you to consider your motives.

It asks you to reflect on the difference between being right and being wise. Have you sought to prove your worth by diminishing others? Or allowed someone else's ego to override your truth? It's time to examine the battlefield and ask what truly matters now.

Peace is not weakness. Humility is not defeat. Sometimes, the bravest thing is not to win, but to walk away.

Look for the ordinary signs too: the thought you keep rehearsing, the conversation waiting for clarity and the decision that needs clean language. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Am I clinging to a victory that left me feeling emptier?
2. Have I allowed pride or fear to dominate my relationships?
3. What would it look like to choose peace instead of power?

The Five of Swords teaches that real strength lives not in domination, but in self honesty.`,
    mirroring: [
      "seven-of-wands-tarot-card-reading-meaning",
      "the-devil-tarot-card-reading-meaning",
      "six-of-swords-tarot-card-reading-meaning"
    ]
  },

  "six-of-swords-tarot-card-reading-meaning": {
    slug: "six-of-swords-tarot-card-reading-meaning",
    name: "Six of Swords",
    subtitle: "Transition, Recovery, Letting Go",
    image: "/card/six-of-swords-tarot-card-reading-meaning.webp",
    suit: "Swords",
    description: `The Six of Swords speaks of transition; a passage from turbulence into calm. It represents the moment when one chooses to move forward, leaving behind emotional or mental burdens. Whether it's a literal journey or a metaphorical one, this card is a symbol of hope, healing and forward motion after hardship. The water may still ripple, but a quieter horizon awaits. In daily life, it asks which thought, conversation or decision needs clearer language now.`,
    upright: [
      "Moving on: choosing peace over conflict",
      "Healing journey: emotional recovery and restoration",
      "New direction: leaving behind what no longer serves",
      "Assisted passage: support during a difficult time",
      "Mental clarity: gaining perspective after turmoil"
    ],
    reversed: [
      "Resistance to change: clinging to the past",
      "Stagnation: stuck in emotional patterns",
      "Delayed transitions: interrupted progress",
      "Unresolved baggage: avoiding emotional processing",
      "False start: trying to move forward without healing"
    ],
    reading: `When the Six of Swords appears, it is the quiet voice urging you toward calmer waters. You may be emerging from a difficult chapter; perhaps one marked by conflict, grief or confusion and this card signals that a turning point is near. It does not promise a complete resolution, but rather a moment of grace, a lull that allows space to breathe again.

This card honors the struggle you've endured and validates your need for rest and reprieve. It suggests that healing does not require you to forget; only to move with your pain instead of against it. You are not being asked to run, but to drift forward gently with trust that the current carries you toward peace. The destination may not be clear, but the momentum is real.

You are not alone on this journey. Whether supported by loved ones, spirit or your inner strength, the crossing has already begun. Let this be a moment where your heart softens, your mind settles and your soul begins to mend.

Look for the ordinary signs too: the thought you keep rehearsing, the conversation waiting for clarity and the decision that needs clean language. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What am I still carrying that I'm ready to set down?
2. Who or what is helping me through this transition and am I allowing myself to receive it?
3. Am I willing to trust the direction my healing is taking me?

The Six of Swords is a quiet message that you're already on the way to somewhere lighter; don't turn back now.`,
    mirroring: [
      "the-moon-tarot-card-reading-meaning",
      "eight-of-cups-tarot-card-reading-meaning",
      "three-of-swords-tarot-card-reading-meaning"
    ]
  },

  "seven-of-swords-tarot-card-reading-meaning": {
    slug: "seven-of-swords-tarot-card-reading-meaning",
    name: "Seven of Swords",
    subtitle: "Strategy, Deception, Self Preservation",
    image: "/card/seven-of-swords-tarot-card-reading-meaning.webp",
    suit: "Swords",
    description: `The Seven of Swords is a card of cunning, secrecy and clever maneuvering. It highlights the tension between honesty and survival; the moments when one must act behind the scenes or rethink how truth is wielded. This card can suggest either deceit or a need for discretion. It reminds us that sometimes strategy is necessary, but it must be aligned with integrity. In daily life, it asks which thought, conversation or decision needs clearer language now.`,
    upright: [
      "Strategy: planning carefully before acting",
      "Tact: handling a situation discreetly",
      "Self preservation: protecting your energy or truth",
      "Creative problem solving: thinking outside the box",
      "Independence: going it alone for your own good"
    ],
    reversed: [
      "Deceit: being dishonest with yourself or others",
      "Guilt: struggling with hidden actions or motives",
      "Exposure: secrets coming to light",
      "Sabotage: undermining your own progress",
      "Avoidance: escaping responsibility"
    ],
    reading: `When the Seven of Swords enters your spread, it asks: what am I not saying or why? This card can point to moments when you've needed to protect yourself by staying quiet or times when you've bent the truth in ways that felt justified. It doesn't judge but it does invite scrutiny.

Sometimes we hide because we fear vulnerability or betrayal. Sometimes we strategize because the path ahead requires careful navigation. This card urges you to examine your motives: are you acting in alignment with your values or hiding from your truth? The answer determines whether your silence empowers or erodes you.

This is a chance to reclaim honesty as a meaningful weapon; one that serves you, not shames you. The true question is not whether you can get away with something, but whether you can live peacefully with how you've chosen to move.

Look for the ordinary signs too: the thought you keep rehearsing, the conversation waiting for clarity and the decision that needs clean language. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Am I withholding something important out of fear or wisdom?
2. What truth am I avoiding that would set me free if I faced it?
3. Have my silent decisions built safety or distance?

The Seven of Swords is a mirror, not of deception, but of intention. Hold it close and choose your next step with conscious clarity.`,
    mirroring: [
      "the-high-priestess-tarot-card-reading-meaning",
      "five-of-swords-tarot-card-reading-meaning",
      "justice-tarot-card-reading-meaning"
    ]
  },

  "eight-of-swords-tarot-card-reading-meaning": {
    slug: "eight-of-swords-tarot-card-reading-meaning",
    name: "Eight of Swords",
    subtitle: "Restriction, Limiting Beliefs, Mental Trap",
    image: "/card/eight-of-swords-tarot-card-reading-meaning.webp",
    suit: "Swords",
    description: `The Eight of Swords reveals a state of mental imprisonment, where fear, doubt or shame distort clarity. Though the binds appear tight, they are often self imposed. This card reflects times when you feel stuck or powerless, not because of real limitations, but because of the stories you've come to believe. In daily life, it asks which thought, conversation or decision needs clearer language now.`,
    upright: [
      "Feeling trapped: stuck in a mental loop",
      "Self imposed limits: internalizing false beliefs",
      "Fear: letting anxiety control decisions",
      "Helplessness: loss of personal agency",
      "Avoidance: unwillingness to confront truth"
    ],
    reversed: [
      "Breakthrough: seeing the truth beyond fear",
      "Liberation: unbinding yourself from lies",
      "Self empowerment: reclaiming choice",
      "Clarity: dissolving illusion",
      "New mindset: shifting the internal narrative"
    ],
    reading: `The Eight of Swords may arrive when your thoughts have become your cage. You might feel paralyzed by overthinking, caught in an emotional loop or trapped by a fear that keeps you from moving forward. But this card gently reminds you: the walls are made of paper. The key is yours.

You are not powerless. Even if external forces have contributed to your feeling stuck, the path to freedom begins within. This is a call to name the fear, to challenge the belief and to recognize that your perception is not the full truth. There is more waiting for you outside this narrow view.

Liberation doesn't require immediate answers. It begins with a quiet message: "What if I'm not actually stuck?" Let this be the first thread you pull to unravel the illusion. You have not lost your way; just your faith in it.

Look for the ordinary signs too: the thought you keep rehearsing, the conversation waiting for clarity and the decision that needs clean language. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What thought or belief has kept me frozen in place?
2. Am I willing to question the voice that tells me I can't?
3. What would freedom feel like and do I believe I deserve it?

The Eight of Swords is not the end; it is the threshold of your liberation. The blindfold is ready to fall.`,
    mirroring: [
      "the-devil-tarot-card-reading-meaning",
      "nine-of-swords-tarot-card-reading-meaning",
      "the-moon-tarot-card-reading-meaning"
    ]
  },

  "nine-of-swords-tarot-card-reading-meaning": {
    slug: "nine-of-swords-tarot-card-reading-meaning",
    name: "Nine of Swords",
    subtitle: "Anxiety, Nightmares, Inner Demons",
    image: "/card/nine-of-swords-tarot-card-reading-meaning.webp",
    suit: "Swords",
    description: `The Nine of Swords reflects the deep corners of the mind where worry multiplies. It speaks to sleepless nights, emotional spirals and fears that haunt in silence. This card doesn't predict suffering; it reveals where it already lives, often hidden. It is a call for compassion, not condemnation. In daily life, it asks which thought, conversation or decision needs clearer language now.`,
    upright: [
      "Anxiety: overwhelmed by negative thoughts",
      "Insomnia: restlessness and worry",
      "Guilt: haunted by past actions or regrets",
      "Inner demons: facing your deepest fears",
      "Mental overload: too much to process alone"
    ],
    reversed: [
      "Release: letting go of inner torment",
      "Hope: light after emotional darkness",
      "Healing: confronting fears with support",
      "Self forgiveness: releasing past burdens",
      "Perspective: realizing things aren't as bad as they seem"
    ],
    reading: `The Nine of Swords arrives when the mind becomes a storm. It reveals that you've been carrying heavy emotional burdens alone and they have begun to take a toll. This is not a sign of weakness, but a cry for gentleness. The fears that visit you in the night are asking to be heard, not obeyed.

This card encourages you to separate the real from the imagined. Pain is real, yes, but it does not have to be eternal. You may need rest, help or simply a breath of compassion. Allow yourself to be human. Let yourself be seen.

You are not broken. You are breaking open. From this vulnerable place, clarity will come and with it, healing. The morning is closer than you think.

Look for the ordinary signs too: the thought you keep rehearsing, the conversation waiting for clarity and the decision that needs clean language. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What am I carrying in silence that I need to speak aloud?
2. Is my mind telling stories that my soul doesn't believe?
3. Can I soften instead of harden when fear arises?

The Nine of Swords is not your sentence; it is your signal. You are stronger than the shadow.`,
    mirroring: [
      "eight-of-swords-tarot-card-reading-meaning",
      "three-of-swords-tarot-card-reading-meaning",
      "the-tower-tarot-card-reading-meaning"
    ]
  },

  "ten-of-swords-tarot-card-reading-meaning": {
    slug: "ten-of-swords-tarot-card-reading-meaning",
    name: "Ten of Swords",
    subtitle: "Endings, Betrayal, Collapse",
    image: "/card/ten-of-swords-tarot-card-reading-meaning.webp",
    suit: "Swords",
    description: `The Ten of Swords shows a moment of utter collapse, where something has definitively ended, often painfully. This card symbolizes betrayal, loss or burnout, yet also holds the seed of finality. While harsh, it brings a clear message: it is over. From this depth, new life can eventually rise. In daily life, it asks which thought, conversation or decision needs clearer language now.`,
    upright: [
      "End of a cycle: finality and closure",
      "Betrayal: feeling stabbed in the back",
      "Burnout: exhaustion from sustained struggle",
      "Rock bottom: nothing more to give",
      "Acceptance: realizing it's time to let go"
    ],
    reversed: [
      "Recovery: beginning to rise again",
      "Survival: learning from the collapse",
      "Release: letting go of the pain",
      "Perspective: seeing the lessons beneath the loss",
      "Hope: trusting the dawn after darkness"
    ],
    reading: `The Ten of Swords does not quiet message; it shouts. It arrives when something has broken beyond repair. But within that breaking is a honest truth: you have survived. You are here, breathing, reading these words and that is proof of your strength.

This card asks you to stop trying to resurrect what has already fallen. It's time to lay down the story, the guilt, the exhaustion. Yes, the pain was real. But so is your capacity to rise. The worst has happened. Now comes the stillness. And then; the rebirth.

Do not fear this ending. It has cleared a path. Where one cycle dies, another begins. Let yourself grieve, rest and then slowly, as if waking from a long night; begin again.

Look for the ordinary signs too: the thought you keep rehearsing, the conversation waiting for clarity and the decision that needs clean language. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What am I still clinging to that has already ended?
2. How can I honor my pain without becoming it?
3. What is quietly waiting to grow from this ending?

The Ten of Swords is your meaningful bottom, but it is not your forever. The dawn is already reaching for you.`,
    mirroring: [
      "death-tarot-card-reading-meaning",
      "three-of-swords-tarot-card-reading-meaning",
      "the-star-tarot-card-reading-meaning"
    ]
  },

  "page-of-swords-tarot-card-reading-meaning": {
    slug: "page-of-swords-tarot-card-reading-meaning",
    name: "Page of Swords",
    subtitle: "Curiosity, Truth Seeking, Mental Clarity",
    image: "/card/page-of-swords-tarot-card-reading-meaning.webp",
    suit: "Swords",
    description: `The Page of Swords is a messenger of mental awakening; sharp, curious and relentlessly in pursuit of truth. This card represents the energy of someone who questions, observes and speaks with conviction. It encourages you to stay alert, seek knowledge and be unafraid to challenge ideas; including your own. In daily life, it asks which thought, conversation or decision needs clearer language now.`,
    upright: [
      "Curiosity: eager to learn and ask questions",
      "Clarity: cutting through confusion",
      "Alertness: quick thinking and mentally sharp",
      "Truth seeking: honesty and mental integrity",
      "New ideas: fresh perspective or approach"
    ],
    reversed: [
      "Gossip: spreading or receiving misinformation",
      "Impatience: rushing into conclusions",
      "Mental restlessness: scattered thoughts",
      "Overanalyzing: stuck in overthinking",
      "Immaturity: speaking without understanding"
    ],
    reading: `When the Page of Swords appears, it is the spark of inquiry; the invitation to observe, ask and challenge. You may feel called to seek answers or share ideas that have long lived in your mind. This card honors intellectual courage: the willingness to explore without fear, to speak even when your voice shakes.

Yet it also reminds you that truth is more than facts; it is intention, nuance and timing. The Page of Swords encourages boldness, yes, but also discernment. Are your words building bridges or drawing lines? Are you seeking knowledge to grow or to win?

This card asks you to honor your mind as a meaningful tool; sharpen it, don't wield it carelessly. Speak when it matters. Listen when it counts. And remember: the greatest wisdom often begins with a question.

Look for the ordinary signs too: the thought you keep rehearsing, the conversation waiting for clarity and the decision that needs clean language. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What truth am I hungry to uncover or express?
2. Am I listening as fiercely as I'm speaking?
3. Is my curiosity rooted in growth or defensiveness?

The Page of Swords is your inner student; curious, courageous and ready to see with fresh eyes.`,
    mirroring: [
      "ace-of-swords-tarot-card-reading-meaning",
      "the-hermit-tarot-card-reading-meaning",
      "knight-of-swords-tarot-card-reading-meaning"
    ]
  },

  "knight-of-swords-tarot-card-reading-meaning": {
    slug: "knight-of-swords-tarot-card-reading-meaning",
    name: "Knight of Swords",
    subtitle: "Determination, Bold Action, Unstoppable Drive",
    image: "/card/knight-of-swords-tarot-card-reading-meaning.webp",
    suit: "Swords",
    description: `The Knight of Swords charges forward with fierce clarity and intention. This card represents a phase of bold decision making, swift movement and mental conviction. It may reflect your determination to pursue an idea or cause or warn against acting without pause for reflection. In daily life, it asks which thought, conversation or decision needs clearer language now.`,
    upright: [
      "Bold action: swift and decisive moves",
      "Confidence: strong belief in your mission",
      "Focused drive: mentally sharp and purposeful",
      "Justice: standing up for truth or ideals",
      "Leadership: taking charge of a situation"
    ],
    reversed: [
      "Impulsiveness: charging ahead without reflection",
      "Aggression: forceful or argumentative energy",
      "Disregard: ignoring consequences or others' input",
      "Burnout: pushing too hard, too fast",
      "Tunnel vision: losing sight of nuance"
    ],
    reading: `The Knight of Swords doesn't wait; it acts. When this card appears, something within you is ready to move forward with speed and certainty. There's fire in your intellect, conviction in your voice and urgency in your mission. This is not a time to second guess; it is a time to trust your momentum.

And yet, this knight also serves as a mirror. Is your clarity truly grounded or are you racing ahead out of fear of stillness? Sometimes, the fastest way forward is to slow down just enough to see the full path. Wisdom is not the absence of speed, but the presence of alignment.

Harness this energy with balance. Let your drive lead, but not trample. Speak with passion, but not at the expense of empathy. You are powerful now; make sure your power serves something meaningful.

Look for the ordinary signs too: the thought you keep rehearsing, the conversation waiting for clarity and the decision that needs clean language. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What mission am I being called to pursue with boldness?
2. Where do I need more clarity before taking action?
3. Am I driven by truth or by urgency to escape discomfort?

The Knight of Swords reminds you: speed without soul is chaos. Let your purpose lead the charge.`,
    mirroring: [
      "justice-tarot-card-reading-meaning",
      "the-chariot-tarot-card-reading-meaning",
      "page-of-swords-tarot-card-reading-meaning"
    ]
  },

  "queen-of-swords-tarot-card-reading-meaning": {
    slug: "queen-of-swords-tarot-card-reading-meaning",
    name: "Queen of Swords",
    subtitle: "Truth, Independence, Clear Boundaries",
    image: "/card/queen-of-swords-tarot-card-reading-meaning.webp",
    suit: "Swords",
    description: `The Queen of Swords is a master of discernment; intelligent, honest and emotionally perceptive without being clouded by sentiment. She represents a mature form of clarity, where compassion and boundaries co exist. This card invites you to speak truth, stand in integrity and cut through illusion with grace. In daily life, it asks which thought, conversation or decision needs clearer language now.`,
    upright: [
      "Clarity: seeing things for what they are",
      "Boundaries: emotional strength and protection",
      "Wisdom: intellect fused with life experience",
      "Authenticity: speaking truth without apology",
      "Independence: self reliance and emotional maturity"
    ],
    reversed: [
      "Coldness: detaching too much from emotion",
      "Bitterness: carrying pain into current choices",
      "Judgment: being overly critical or harsh",
      "Isolation: keeping others at a distance",
      "Suppression: avoiding vulnerability"
    ],
    reading: `The Queen of Swords arrives when you are being called to lead with truth. Not just honesty with others, but the deeper honesty with yourself; the kind that asks you to name your needs, own your pain and choose what is real even when it's hard. She carries both sword and sorrow; wisdom forged by experience.

This is not a cold card. It is a card of compassion with boundaries. You are being asked to see clearly and speak clearly, but to remember that clarity can still be kind. Let this queen guide you to clean communication, not sharp tongues; firm decisions, not closed hearts.

This is your invitation to hold your head high. To trust that your perspective matters. And to know that vulnerability and strength are not opposites; they are companions on the path to truth.

Look for the ordinary signs too: the thought you keep rehearsing, the conversation waiting for clarity and the decision that needs clean language. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Am I communicating my truth or withholding to keep peace?
2. Where do I need clearer boundaries in thought or emotion?
3. How can I honor both my heart and my intellect?

The Queen of Swords reminds you: your voice is a meaningful tool. Use it with clarity and care.`,
    mirroring: [
      "justice-tarot-card-reading-meaning",
      "king-of-swords-tarot-card-reading-meaning",
      "the-empress-tarot-card-reading-meaning"
    ]
  },

  "king-of-swords-tarot-card-reading-meaning": {
    slug: "king-of-swords-tarot-card-reading-meaning",
    name: "King of Swords",
    subtitle: "Mental Mastery, Truth, Authority",
    image: "/card/king-of-swords-tarot-card-reading-meaning.webp",
    suit: "Swords",
    description: `The King of Swords stands as the embodiment of intellect, truth and ethical authority. He brings sharp focus and wise discernment to complex issues. This card suggests a time to lead with logic, maintain clarity under pressure and stand firmly for what is just, even if it's not easy. In daily life, it asks which thought, conversation or decision needs clearer language now.`,
    upright: [
      "Mental strength: wisdom grounded in experience",
      "Truth: unwavering commitment to what's right",
      "Authority: respected leadership and clear decisions",
      "Clarity: ability to see through emotional fog",
      "Integrity: aligning thought, word and action"
    ],
    reversed: [
      "Tyranny: misuse of intellect or power",
      "Manipulation: twisting logic to suit ego",
      "Rigidity: unwillingness to see other perspectives",
      "Harsh judgment: cold or critical energy",
      "Emotional distance: neglecting the heart's input"
    ],
    reading: `The King of Swords asks: what would it look like to lead with wisdom, not just knowledge? When this card appears, it calls on your highest mind; the one not swayed by drama, fear or ego, but grounded in reason and ethical clarity. This is the seat of your inner sovereign.

You are being asked to rise above confusion, to cut through noise and to anchor your decisions in integrity. This king is not loud; he is precise. Not controlling, but commanding. He leads not just with answers, but with the courage to ask the right questions.

Let this card guide you toward just choices and conscious leadership. Speak the truth and be brave enough to live it, even when it's inconvenient. You are more capable than you know.

Look for the ordinary signs too: the thought you keep rehearsing, the conversation waiting for clarity and the decision that needs clean language. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Am I aligning my decisions with both truth and compassion?
2. Where must I hold steady, even when challenged?
3. Am I using my intellect to lead or to defend?

The King of Swords invites you to embody wisdom not just in thought, but in presence and purpose.`,
    mirroring: [
      "justice-tarot-card-reading-meaning",
      "the-emperor-tarot-card-reading-meaning",
      "queen-of-swords-tarot-card-reading-meaning"
    ]
  },

  "ace-of-pentacles-tarot-card-reading-meaning": {
    slug: "ace-of-pentacles-tarot-card-reading-meaning",
    name: "Ace of Pentacles",
    subtitle: "Prosperity, Foundations, Material Blessings",
    image: "/card/ace-of-pentacles-tarot-card-reading-meaning.webp",
    suit: "Pentacles",
    description: `The Ace of Pentacles symbolizes a deep offering of stability, security and material opportunity. It marks the fertile ground upon which dreams can be built; the seed of wealth, health or home. This card appears when a fresh opportunity in the material world is being granted, often tied to career, finances or physical well being. It invites you to step through a portal of abundance with clarity and grounded intention. In daily life, it asks how your money, time, work and body are asking to be cared for.`,
    upright: [
      "New financial opportunity: job offer, investment or career path",
      "Solid foundations: starting something stable and secure",
      "Prosperity: aligning with the energy of abundance",
      "Health and vitality: physical wellness and grounded strength",
      "Practical growth: long term potential being planted"
    ],
    reversed: [
      "Missed opportunities: overlooking blessings at your doorstep",
      "Instability: shaky foundations or false starts",
      "Greed: attachment to material outcomes",
      "Lack of planning: failing to build long term strategies",
      "Blocked abundance: self sabotage or fear of success"
    ],
    reading: `When the Ace of Pentacles shows up in your reading, life is handing you a meaningful key; one that opens the door to tangible prosperity. This is the seed of something real, something that grows with care, effort and consistency. Whether it's a new job, a financial breakthrough or a healthier lifestyle, the foundation is now being laid.

You are not being asked to leap, but to plant. To nurture your plans like a gardener would tend to fertile soil, with presence, patience and purpose. Trust that what you begin now has the potential to blossom into a lasting source of stability. Every small step matters and every choice builds the path toward fulfillment in the physical realm.

This is not just about money or possessions; it's about security, dignity and the life you are building brick by brick. Can you honor the sacredness of your effort? Can you commit to the long game? The Ace of Pentacles assures that you are being supported; now take that first grounded step.

Look for the ordinary signs too: how your body feels, how your resources are being used and what kind of stability you are actually building. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What am I ready to invest in physically, financially or energetically?
2. Do I believe I'm worthy of receiving material blessings?
3. What structures or habits can help me build long term stability?

When the Ace of Pentacles appears, prosperity is not a promise; it's a portal. Walk through it with faith and grounded determination.`,
    mirroring: [
      "the-empress-tarot-card-reading-meaning",
      "four-of-wands-tarot-card-reading-meaning",
      "ten-of-pentacles-tarot-card-reading-meaning"
    ]
  },

  "two-of-pentacles-tarot-card-reading-meaning": {
    slug: "two-of-pentacles-tarot-card-reading-meaning",
    name: "Two of Pentacles",
    subtitle: "Balance, Priorities, Juggling Responsibilities",
    image: "/card/two-of-pentacles-tarot-card-reading-meaning.webp",
    suit: "Pentacles",
    description: `The Two of Pentacles represents the art of balancing multiple aspects of life, whether finances, relationships or responsibilities. It reflects a period of adaptability, decision making and dynamic energy. While things may feel in motion, this card encourages you to stay agile, flexible and focused, knowing that equilibrium is not fixed but constantly evolving. In daily life, it asks how your money, time, work and body are asking to be cared for.`,
    upright: [
      "Time management: handling several duties with grace",
      "Financial decisions: weighing pros and cons before investing",
      "Adaptability: flowing with change and uncertainty",
      "Multitasking: managing personal and professional spheres",
      "Maintaining balance: staying grounded amidst movement"
    ],
    reversed: [
      "Overwhelm: too much on your plate",
      "Disorganization: poor time or resource management",
      "Instability: dropping the ball in critical areas",
      "Avoidance: refusing to make necessary choices",
      "Burnout: emotional or financial fatigue"
    ],
    reading: `When the Two of Pentacles arrives, life may feel like a dance; one foot in work, the other in personal life; one hand in the future, the other juggling the now. This is not chaos, but choreography. And the question is: are you leading the rhythm or are you being pulled by it?

You are being invited to cultivate conscious balance. To pause and assess where your energy is truly needed and where it's being drained. There may be hard choices to make or shifting priorities to acknowledge. But through presence and intentionality, you can transform overwhelm into alignment.

Balance is not perfection. It is fluid, forgiving and grounded in awareness. The Two of Pentacles reminds you that you have what it takes to manage this chapter, but only if you remain flexible, honest and willing to adjust your sails.

Look for the ordinary signs too: how your body feels, how your resources are being used and what kind of stability you are actually building. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What areas of my life are competing for my attention right now?
2. Where am I overcommitting and where can I simplify?
3. Am I making choices based on values or merely reacting to demands?

When the Two of Pentacles appears, remember: your power lies not in doing it all, but in choosing wisely what to hold and what to gently let go.`,
    mirroring: [
      "temperance-tarot-card-reading-meaning",
      "six-of-pentacles-tarot-card-reading-meaning",
      "seven-of-cups-tarot-card-reading-meaning"
    ]
  },

  "three-of-pentacles-tarot-card-reading-meaning": {
    slug: "three-of-pentacles-tarot-card-reading-meaning",
    name: "Three of Pentacles",
    subtitle: "Teamwork, Mastery, Shared Goals",
    image: "/card/three-of-pentacles-tarot-card-reading-meaning.webp",
    suit: "Pentacles",
    description: `The Three of Pentacles is a card of collaboration, skill building and unified effort. It reflects a moment when your talents are recognized and valued within a greater structure; be it a team, community or shared project. It speaks to the power of combining individual strengths toward a collective vision. In daily life, it asks how your money, time, work and body are asking to be cared for.`,
    upright: [
      "Teamwork: working together for mutual success",
      "Skill development: honing your craft with focus",
      "Recognition: being appreciated for your contributions",
      "Shared goals: collaborating on a larger mission",
      "Solid foundations: early success built on planning"
    ],
    reversed: [
      "Lack of cohesion: disharmony or misaligned goals",
      "Poor craftsmanship: rushing without planning",
      "Ego clashes: difficulty working with others",
      "Unrecognized effort: feeling undervalued",
      "Disorganization: lack of structure or clear roles"
    ],
    reading: `When the Three of Pentacles appears in your reading, it brings a meaningful reminder: you are not meant to build everything alone. There is strength in community, in collaboration, in shared purpose. This is the architecture of dreams; each person bringing their stone to the cathedral of your becoming.

You are being called to both offer and receive support. To honor your craft and expertise while acknowledging that others bring gifts too. This card suggests that your path forward will be shaped not just by what you know, but by who you journey with and how you build together.

This is a card of structure, mentorship and integrity. It invites you to bring your best, to communicate clearly and to remember: the masterpiece is not just the result, but the shared effort behind it.

Look for the ordinary signs too: how your body feels, how your resources are being used and what kind of stability you are actually building. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Where in my life am I being asked to collaborate or create together?
2. Am I recognizing the value of others and allowing them to see mine?
3. How can I build something sustainable with those around me?

When the Three of Pentacles appears, know this: great things are built not in solitude, but in symphony.`,
    mirroring: [
      "three-of-cups-tarot-card-reading-meaning",
      "the-emperor-tarot-card-reading-meaning",
      "eight-of-pentacles-tarot-card-reading-meaning"
    ]
  },

  "four-of-pentacles-tarot-card-reading-meaning": {
    slug: "four-of-pentacles-tarot-card-reading-meaning",
    name: "Four of Pentacles",
    subtitle: "Control, Possession, Holding On",
    image: "/card/four-of-pentacles-tarot-card-reading-meaning.webp",
    suit: "Pentacles",
    description: `The Four of Pentacles represents the energy of control, often born from a fear of loss. Whether it's clinging to money, status, beliefs or emotional walls, this card warns against the trap of excessive guarding. It speaks to the tension between safety and stagnation and invites reflection on what you're holding and why. In daily life, it asks how your money, time, work and body are asking to be cared for.`,
    upright: [
      "Financial stability: creating a secure foundation",
      "Self protection: setting healthy boundaries",
      "Conservation: being mindful with resources",
      "Discipline: control over desires and spending",
      "Possession: holding on tightly to what you value"
    ],
    reversed: [
      "Greed: hoarding out of fear",
      "Fear of loss: refusing to let go of what no longer serves",
      "Insecurity: overly dependent on material things",
      "Stagnation: resisting growth or vulnerability",
      "Isolation: emotional walls built too high"
    ],
    reading: `The Four of Pentacles asks: what are you gripping so tightly that you can no longer move? This card is a mirror for our relationship with control and whether that control is truly serving us or suffocating our expansion.

You may be protecting something valuable: your finances, your time, your heart. But in doing so, have you also closed the door to possibility? There is wisdom in boundaries, but there is also danger in walls too high. This card invites you to loosen your grip, to trust that security does not come from possession, but from presence.

Freedom begins where fear ends. And generosity, in all its forms, is a portal to both abundance and connection. Let the Four of Pentacles remind you that holding on too tightly can cost more than it protects.

Look for the ordinary signs too: how your body feels, how your resources are being used and what kind of stability you are actually building. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What am I afraid to lose and why?
2. Have I confused control with safety?
3. How can I invite more trust and flow into this area of my life?

When the Four of Pentacles appears, remember: what is truly yours cannot be taken and what you release often returns with grace.`,
    mirroring: [
      "five-of-pentacles-tarot-card-reading-meaning",
      "the-devil-tarot-card-reading-meaning",
      "seven-of-swords-tarot-card-reading-meaning"
    ]
  },

  "five-of-pentacles-tarot-card-reading-meaning": {
    slug: "five-of-pentacles-tarot-card-reading-meaning",
    name: "Five of Pentacles",
    subtitle: "Hardship, Loss, Spiritual Poverty",
    image: "/card/five-of-pentacles-tarot-card-reading-meaning.webp",
    suit: "Pentacles",
    description: `The Five of Pentacles reflects a season of struggle, often financial, emotional or spiritual. It reveals the feeling of being left out in the cold, of scarcity and abandonment. Yet it also points toward help that is near, if only we lift our heads to see it. This is a card of endurance, resilience and the quiet hope that flickers even in darkness. In daily life, it asks how your money, time, work and body are asking to be cared for.`,
    upright: [
      "Financial hardship: loss of income or security",
      "Emotional rejection: feeling isolated or abandoned",
      "Health challenges: physical or mental strain",
      "Fear of lack: scarcity mindset taking hold",
      "Asking for help: support is closer than it seems"
    ],
    reversed: [
      "Recovery: light returning after dark times",
      "Hope: rebuilding your sense of self worth",
      "Support: receiving assistance or guidance",
      "Shifting mindset: releasing fear and shame",
      "Endurance: emerging stronger from the storm"
    ],
    reading: `The Five of Pentacles does not come to condemn; it comes to comfort. It knows the terrain of rock bottom, of empty pockets and heavier hearts. This card walks with you through the cold, not to dwell there, but to remind you that even here, you are not alone.

Pain may be real, but so is the hand reaching for you. So is the light in the window nearby. This is not a card of solutions, but of survival or survival itself is meaningful. If you are here, still breathing, still hoping then you are already victorious in ways the world cannot measure.

Let this card remind you that support is closer than you think. That your worth is not measured by your wealth. That sometimes, the greatest transformation begins not with having more, but with believing you are enough; even now.

Look for the ordinary signs too: how your body feels, how your resources are being used and what kind of stability you are actually building. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Where have I felt abandoned or unsupported and is that story still true?
2. Am I willing to ask for help or do I carry pain in silence?
3. What beliefs about lack or worth am I ready to release?

When the Five of Pentacles appears, you are not forsaken. You are on the edge of rebirth, where brokenness becomes breakthrough.`,
    mirroring: [
      "four-of-pentacles-tarot-card-reading-meaning",
      "the-hermit-tarot-card-reading-meaning",
      "nine-of-swords-tarot-card-reading-meaning"
    ]
  },

  "six-of-pentacles-tarot-card-reading-meaning": {
    slug: "six-of-pentacles-tarot-card-reading-meaning",
    name: "Six of Pentacles",
    subtitle: "Generosity, Reciprocity, Financial Balance",
    image: "/card/six-of-pentacles-tarot-card-reading-meaning.webp",
    suit: "Pentacles",
    description: `The Six of Pentacles symbolizes the delicate dance of giving and receiving. It highlights the importance of balance in resources, support and compassion. This card speaks to the flow of abundance how what is shared from the heart returns in unseen ways. Whether you are in a position to offer help or seeking aid, the Six of Pentacles encourages fairness, humility and gratitude in all exchanges. In daily life, it asks how your money, time, work and body are asking to be cared for.`,
    upright: [
      "Generosity: freely giving resources, time or wisdom",
      "Balance: a healthy exchange between giving and receiving",
      "Support: help offered or received during a time of need",
      "Compassion: aiding others without judgment",
      "Karma: what goes around, comes around in kind"
    ],
    reversed: [
      "Power imbalance: giving with strings attached",
      "Dependency: relying too heavily on others' generosity",
      "Greed: unwillingness to share or support others",
      "Resentment: feeling unappreciated for your efforts",
      "Conditional help: generosity used for control or manipulation"
    ],
    reading: `The Six of Pentacles asks you to observe the current balance of exchange in your life. Are you the giver or the receiver? And in either case, is the energy rooted in love or ego? This card isn't just about charity, it's about the unseen value of support, acknowledgment and mutual respect.

If you're giving, do so without the need for praise or control. Your generosity should nourish the soul of another, not create silent debt. If you're receiving, allow yourself to accept with humility and openness, without shame. True giving does not diminish the giver and true receiving does not lessen the receiver.

This card reminds you that abundance is not only monetary it is also emotional, spiritual and relational. What you share returns in unexpected forms. To live in flow, you must let yourself both pour and be poured into.

Look for the ordinary signs too: how your body feels, how your resources are being used and what kind of stability you are actually building. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Am I giving from a place of love or seeking something in return?
2. Do I accept help easily or resist support out of pride?
3. Where in my life do I need to restore balance in giving and receiving?

In the hands of the generous, blessings multiply. When the Six of Pentacles appears, it's an invitation to restore harmony in the honest exchange of life.`,
    mirroring: [
      "justice-tarot-card-reading-meaning",
      "the-empress-tarot-card-reading-meaning",
      "six-of-cups-tarot-card-reading-meaning"
    ]
  },

  "seven-of-pentacles-tarot-card-reading-meaning": {
    slug: "seven-of-pentacles-tarot-card-reading-meaning",
    name: "Seven of Pentacles",
    subtitle: "Patience, Investment, Long Term Growth",
    image: "/card/seven-of-pentacles-tarot-card-reading-meaning.webp",
    suit: "Pentacles",
    description: `The Seven of Pentacles reflects the moment of pause in a long journey. You have planted seeds, tended the soil and waited now you assess what has grown. It is a card of quiet contemplation, urging you to consider the fruits of your labor, your long term goals and whether your current path aligns with your deeper values. In daily life, it asks how your money, time, work and body are asking to be cared for.`,
    upright: [
      "Assessment: evaluating progress and results",
      "Patience: allowing growth to unfold in its time",
      "Investment: committed efforts for long term return",
      "Reflection: measuring alignment with your vision",
      "Stewardship: nurturing what matters with care"
    ],
    reversed: [
      "Impatience: wanting results too quickly",
      "Wasted effort: energy spent on fruitless pursuits",
      "Doubt: questioning the value of your path",
      "Neglect: failing to tend to what you've started",
      "Burnout: overworking without joy or balance"
    ],
    reading: `This card suggests: pause. In the rush to build and achieve, you may overlook the importance of reflection. The Seven of Pentacles calls for a still moment to reassess. Not everything worth having comes quickly and not all growth is immediately visible. The roots often deepen before the blossom breaks through.

You are being asked to measure more than productivity you are being asked to weigh fulfillment. Is what you're building meaningful? Are you investing your energy into something that truly feeds your spirit? This is not a time for rash decisions but for quiet recalibration.

Trust that every drop of effort, even unseen, contributes to something becoming. Let patience be your companion and clarity your reward. This card is a needed pause before the next harvest.

Look for the ordinary signs too: how your body feels, how your resources are being used and what kind of stability you are actually building. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Am I aligned with the long term rewards I seek?
2. Where am I planting seeds that no longer inspire me?
3. Can I trust that growth is occurring, even if I can't yet see it?

When the Seven of Pentacles arrives, it honors your labor but also urges you to be just as intentional about what comes next.`,
    mirroring: [
      "the-hermit-tarot-card-reading-meaning",
      "three-of-wands-tarot-card-reading-meaning",
      "knight-of-pentacles-tarot-card-reading-meaning"
    ]
  },

  "eight-of-pentacles-tarot-card-reading-meaning": {
    slug: "eight-of-pentacles-tarot-card-reading-meaning",
    name: "Eight of Pentacles",
    subtitle: "Mastery, Craft, Dedication to Growth",
    image: "/card/eight-of-pentacles-tarot-card-reading-meaning.webp",
    suit: "Pentacles",
    description: `The Eight of Pentacles is the card of the apprentice, the artisan, the diligent soul who finds fulfillment through focus. It speaks of devotion to one's craft, of learning through repetition and of honoring the process over the outcome. This card encourages steady growth through commitment and care. In daily life, it asks how your money, time, work and body are asking to be cared for.`,
    upright: [
      "Skill building: developing mastery through practice",
      "Discipline: showing up daily with intention",
      "Commitment: dedicating yourself to a purpose",
      "Craftsmanship: taking pride in your work",
      "Progress: consistent steps toward your goal"
    ],
    reversed: [
      "Perfectionism: never feeling your work is good enough",
      "Monotony: losing passion through routine",
      "Lack of growth: stagnating despite effort",
      "Overwork: neglecting balance or rest",
      "Mediocrity: cutting corners or lacking passion"
    ],
    reading: `This card is a meditation on mastery. True excellence is not sudden; it is sculpted through time, repetition and wholehearted effort. The Eight of Pentacles reminds you that you are building more than a skill you are building a version of yourself who can hold the weight of your dreams.

In the meaningful rhythm of doing and redoing, of showing up with heart and humility, you become more than just productive you become transformed. Whether your labor is artistic, intellectual or spiritual, let your hands remember what your spirit longs to express.

Don't rush this phase. The journey of mastery is holy. You are crafting something that matters possibly even yourself.

Look for the ordinary signs too: how your body feels, how your resources are being used and what kind of stability you are actually building. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What am I devoted to building, even if it takes time?
2. Do I honor the process or only the outcome?
3. Where in my life am I being called to greater consistency and care?

The Eight of Pentacles is your sign to keep going, keep refining and keep loving the process that shapes your purpose.`,
    mirroring: [
      "three-of-pentacles-tarot-card-reading-meaning",
      "the-emperor-tarot-card-reading-meaning",
      "knight-of-swords-tarot-card-reading-meaning"
    ]
  },

  "nine-of-pentacles-tarot-card-reading-meaning": {
    slug: "nine-of-pentacles-tarot-card-reading-meaning",
    name: "Nine of Pentacles",
    subtitle: "Independence, Luxury, Self Worth",
    image: "/card/nine-of-pentacles-tarot-card-reading-meaning.webp",
    suit: "Pentacles",
    description: `The Nine of Pentacles is a symbol of earned luxury and inner wealth. It speaks of a life cultivated through intention, discipline and grace. This card honors the rewards of self sufficiency and reminds you that true abundance is not only material, but spiritual and emotional as well. In daily life, it asks how your money, time, work and body are asking to be cared for.`,
    upright: [
      "Self reliance: standing strong in your independence",
      "Achievement: enjoying the fruits of your labor",
      "Elegance: grace and sophistication through effort",
      "Comfort: a life aligned with your values",
      "Fulfillment: satisfaction in your accomplishments"
    ],
    reversed: [
      "Isolation: success without connection",
      "Overindulgence: living beyond your means",
      "Superficiality: mistaking wealth for self worth",
      "Imposter syndrome: doubting your deserved success",
      "Disconnection: having but not appreciating"
    ],
    reading: `This card is the garden you cultivated with your own hands. The Nine of Pentacles honors not just what you have, but who you've become in earning it. It asks you to breathe in the quiet elegance of your effort without guilt, without comparison.

Independence does not mean loneliness. This card invites you to celebrate your sovereignty, to remember that creating a life of beauty and peace is a meaningful art. You don't need permission to enjoy what you've built.

At its heart, this card is about worthiness. You are allowed to savor your life. You are allowed to bloom.

Look for the ordinary signs too: how your body feels, how your resources are being used and what kind of stability you are actually building. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Have I given myself permission to feel proud of what I've built?
2. Am I grounded in gratitude or distracted by what's next?
3. Do I recognize the emotional and spiritual richness in my life?

The Nine of Pentacles reminds you that luxury isn't always opulent sometimes it's the simple, sovereign joy of being content in your own skin.`,
    mirroring: [
      "queen-of-pentacles-tarot-card-reading-meaning",
      "the-empress-tarot-card-reading-meaning",
      "four-of-wands-tarot-card-reading-meaning"
    ]
  },

  "ten-of-pentacles-tarot-card-reading-meaning": {
    slug: "ten-of-pentacles-tarot-card-reading-meaning",
    name: "Ten of Pentacles",
    subtitle: "Legacy, Stability, Generational Wealth",
    image: "/card/ten-of-pentacles-tarot-card-reading-meaning.webp",
    suit: "Pentacles",
    description: `The Ten of Pentacles represents the culmination of long term effort material security, family bonds and a life designed to endure. This card speaks to legacy, tradition and the foundations you build for the future. It honors stability earned through devotion and the blessings passed between generations. In daily life, it asks how your money, time, work and body are asking to be cared for.`,
    upright: [
      "Legacy: contributing to something that outlasts you",
      "Security: financial and emotional stability",
      "Family: deep roots and shared values",
      "Wealth: material success and comfort",
      "Fulfillment: living with purpose and continuity"
    ],
    reversed: [
      "Fragility: instability in legacy or finances",
      "Conflict: family disputes over money or values",
      "Short sightedness: ignoring the long term impact",
      "Disconnection: wealth without emotional depth",
      "Loss: financial or relational disruption"
    ],
    reading: `This is the card of enduring legacy. The Ten of Pentacles reminds you that what you build today echoes through time. It may be wealth, yes, but it may also be wisdom, values and the love you pass forward. The truest inheritance is not just assets, but presence.

You are being called to think beyond your lifetime. What kind of stability are you creating? How do your decisions ripple into the lives of others now and in generations to come?

This card is not only about having, it is about holding, preserving and transmitting. Honor what has been handed to you. Cherish what you will one day hand to others.

Look for the ordinary signs too: how your body feels, how your resources are being used and what kind of stability you are actually building. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What am I building that will outlive me?
2. Do I define wealth by possessions or by legacy?
3. Where in my life can I create more meaningful stability?

The Ten of Pentacles reminds you that your life is part of a greater tapestry and your thread matters.`,
    mirroring: [
      "the-hierophant-tarot-card-reading-meaning",
      "king-of-pentacles-tarot-card-reading-meaning",
      "four-of-wands-tarot-card-reading-meaning"
    ]
  },

  "page-of-pentacles-tarot-card-reading-meaning": {
    slug: "page-of-pentacles-tarot-card-reading-meaning",
    name: "Page of Pentacles",
    subtitle: "Ambition, Study, Grounded Dreams",
    image: "/card/page-of-pentacles-tarot-card-reading-meaning.webp",
    suit: "Pentacles",
    description: `The Page of Pentacles is the seeker of grounded growth. It represents a time of learning, planning and planting seeds for something meaningful. This card speaks to ambition shaped by humility and dreams rooted in discipline. It is the slow, intentional beginning of a path meant to last. In daily life, it asks how your money, time, work and body are asking to be cared for.`,
    upright: [
      "New ventures: starting a financial, educational or personal project",
      "Discipline: focused effort and long term planning",
      "Growth: learning new skills and building solid foundations",
      "Potential: seeing the first signs of a bigger vision",
      "Responsibility: committing to your own future"
    ],
    reversed: [
      "Procrastination: delaying plans or avoiding effort",
      "Lack of focus: drifting without structure or clarity",
      "Unrealistic goals: wanting results without the work",
      "Neglect: ignoring the details or foundations needed",
      "Insecurity: doubting your own abilities or value"
    ],
    reading: `The Page of Pentacles appears as a call to honor the beginning of something real. Not the daydream, but the quiet moment you roll up your sleeves and begin. Whether it's your finances, studies or a passion project this card says the soil is ready.

It's a reminder that success isn't loud at first. It suggests. It grows in the background while you show up, day by day, with integrity and intention. Be curious. Be committed. Learn as you go. The Page asks you to take yourself seriously not with pressure, but with care.

Every structure begins with a foundation. The Page of Pentacles encourages you to become the architect of your next chapter sketching, shaping and believing in what's possible.

Look for the ordinary signs too: how your body feels, how your resources are being used and what kind of stability you are actually building. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What small step can I take toward something lasting?
2. Am I honoring my goals through action?
3. Where am I being asked to learn, not rush?

The Page of Pentacles reminds you: true wealth begins with the courage to begin.`,
    mirroring: [
      "ace-of-pentacles-tarot-card-reading-meaning",
      "three-of-pentacles-tarot-card-reading-meaning",
      "the-magician-tarot-card-reading-meaning"
    ]
  },

  "knight-of-pentacles-tarot-card-reading-meaning": {
    slug: "knight-of-pentacles-tarot-card-reading-meaning",
    name: "Knight of Pentacles",
    subtitle: "Diligence, Responsibility, Steady Progress",
    image: "/card/knight-of-pentacles-tarot-card-reading-meaning.webp",
    suit: "Pentacles",
    description: `The Knight of Pentacles represents persistence, loyalty and a grounded approach to long term success. This card values routine, hard work and reliability. It signals a time when consistency and dedication matter more than speed or flash. The Knight is committed to finishing what they start with care, patience and pride. In daily life, it asks how your money, time, work and body are asking to be cared for.`,
    upright: [
      "Commitment: showing up consistently for your goals",
      "Reliability: being dependable and trustworthy",
      "Diligence: sticking to routines that support progress",
      "Practicality: taking measured, thoughtful actions",
      "Persistence: refusing to give up, even when it's slow"
    ],
    reversed: [
      "Stagnation: being stuck in routine or monotony",
      "Overworking: focusing too much on productivity",
      "Resistance: struggling with change or flexibility",
      "Neglect: failing to follow through on obligations",
      "Inertia: feeling uninspired or directionless"
    ],
    reading: `The Knight of Pentacles reminds you that the most enduring progress often comes from steady, silent effort. This card isn't here to dazzle, it's here to finish the race. It's about honoring the process, even when it feels slow or unseen.

You may be in a season that demands repetition, discipline or showing up without applause. But this isn't wasted energy it's an investment. The Knight teaches you that there is power in patience and success in the details.

This is your invitation to move forward without rush. Trust the slow build. Keep your eyes on the path, your hands in the work and your heart anchored in purpose.

Look for the ordinary signs too: how your body feels, how your resources are being used and what kind of stability you are actually building. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Where in my life am I being asked to show more consistency?
2. Am I honoring my commitments with care?
3. What can I achieve if I stay steady and intentional?

The Knight of Pentacles reminds you: greatness is not always fast, but it is always faithful.`,
    mirroring: [
      "seven-of-pentacles-tarot-card-reading-meaning",
      "king-of-pentacles-tarot-card-reading-meaning",
      "eight-of-pentacles-tarot-card-reading-meaning"
    ]
  },

  "queen-of-pentacles-tarot-card-reading-meaning": {
    slug: "queen-of-pentacles-tarot-card-reading-meaning",
    name: "Queen of Pentacles",
    subtitle: "Nurturing, Prosperity, Grounded Balance",
    image: "/card/queen-of-pentacles-tarot-card-reading-meaning.webp",
    suit: "Pentacles",
    description: `The Queen of Pentacles embodies the harmony between abundance and care. She is grounded, nurturing and deeply attuned to the physical and emotional needs of herself and others. This card speaks to stability earned through wisdom, generosity and intentional living. It invites you to lead with both practicality and heart. In daily life, it asks how your money, time, work and body are asking to be cared for.`,
    upright: [
      "Nurturing: caring for others while honoring your own needs",
      "Abundance: creating financial and emotional security",
      "Balance: managing responsibilities with grace",
      "Practical wisdom: making grounded, thoughtful choices",
      "Comfort: building a warm and safe environment"
    ],
    reversed: [
      "Neglect: putting others' needs ahead of your own well being",
      "Imbalance: struggling to manage home, work or finances",
      "Overcontrol: micromanaging instead of trusting",
      "Insecurity: feeling unworthy of success or care",
      "Disconnection: losing touch with what truly matters"
    ],
    reading: `The Queen of Pentacles is the steady steward of stability and care. When she appears, she asks you to embrace both the provider and the nurturer within. You are being called to create a life that is not only successful but sustainable, beautiful and rooted in love.

This card honors the quiet power of tending to your home, your finances, your relationships, your self. It speaks to the magic of showing up fully: not just chasing goals, but creating an ecosystem where growth feels safe. It invites you to slow down, tune in and lead with gentle strength.

True abundance is not hoarded it's shared. The Queen of Pentacles reminds you that success is richest when it is warm, inclusive and aligned with your values.

Look for the ordinary signs too: how your body feels, how your resources are being used and what kind of stability you are actually building. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. Am I caring for my needs as deeply as I care for others?
2. What does true prosperity look like in my daily life?
3. Where can I create more harmony between doing and being?

The Queen of Pentacles reminds you: wealth is not just what you have it's how you hold it.`,
    mirroring: [
      "the-empress-tarot-card-reading-meaning",
      "ten-of-pentacles-tarot-card-reading-meaning",
      "nine-of-pentacles-tarot-card-reading-meaning"
    ]
  },

  "king-of-pentacles-tarot-card-reading-meaning": {
    slug: "king-of-pentacles-tarot-card-reading-meaning",
    name: "King of Pentacles",
    subtitle: "Mastery, Wealth, Leadership with Integrity",
    image: "/card/king-of-pentacles-tarot-card-reading-meaning.webp",
    suit: "Pentacles",
    description: `The King of Pentacles represents success built through patience, wisdom and disciplined effort. He is a provider, protector and master of material realms. This card embodies mature leadership one that values security, responsibility and the well being of others. It is a sign of abundance earned through integrity. In daily life, it asks how your money, time, work and body are asking to be cared for.`,
    upright: [
      "Leadership: guiding others through wisdom and example",
      "Wealth: enjoying material success and stability",
      "Responsibility: honoring commitments and long term vision",
      "Security: building strong foundations for self and others",
      "Support: offering guidance and tangible help to those in need"
    ],
    reversed: [
      "Greed: prioritizing wealth over values or relationships",
      "Control: using power in rigid or dominating ways",
      "Neglect: failing to support others emotionally or practically",
      "Materialism: defining worth by possessions alone",
      "Instability: fearing loss despite external success"
    ],
    reading: `The King of Pentacles calls you to step into a role of grounded authority. He does not chase, he builds. His success comes not from luck, but from loyalty to his values, his people and his purpose. When this card appears, you're being asked to lead with both confidence and care.

This is about more than wealth, it's about legacy. What are you creating that will last? How do you use your resources to uplift others, not just yourself? The King invites you to trust your maturity, your experience and your ability to create something meaningful.

Whether you're managing a business, a family or your own future the King of Pentacles reminds you to be generous, structured and rooted in what truly matters.

Look for the ordinary signs too: how your body feels, how your resources are being used and what kind of stability you are actually building. The card becomes more useful when it points to one honest conversation, one changed habit or one next step you can actually take.

Ask yourself:
1. What does responsible leadership look like in my life?
2. Am I using my success to nurture others or only myself?
3. Where can I bring more integrity and intention to my work?

The King of Pentacles reminds you: true wealth is not just having, it's holding it with honor.`,
    mirroring: [
      "the-emperor-tarot-card-reading-meaning",
      "knight-of-pentacles-tarot-card-reading-meaning",
      "ten-of-pentacles-tarot-card-reading-meaning"
    ]
  }

};

const suitPagePaths = {
  "Major Arcana": "/tarot/major-arcana",
  Wands: "/tarot/wands",
  Cups: "/tarot/cups",
  Swords: "/tarot/swords",
  Pentacles: "/tarot/pentacles",
};

const suitDetails = {
  "Major Arcana": {
    field: "a larger life lesson, turning point or soul pattern",
    love: "truth, maturity and the deeper pattern beneath attraction",
    career: "purpose, timing and the kind of decision that changes direction",
    spiritual: "the soul lesson that keeps returning until it is understood",
    element: "archetype",
    symbol: "archetypal image",
    plural: "archetypal images",
  },
  Wands: {
    field: "energy, desire, courage and creative movement",
    love: "chemistry, pursuit, confidence and the courage to be honest",
    career: "initiative, visibility, leadership and creative momentum",
    spiritual: "the inner fire that shows where your life wants motion",
    element: "fire",
    symbol: "wand",
    plural: "wands",
  },
  Cups: {
    field: "emotion, attachment, healing and the language of the heart",
    love: "emotional availability, tenderness, trust and mutual care",
    career: "creative cooperation, emotional intelligence and meaningful work",
    spiritual: "the feeling truth that rises before the mind can explain it",
    element: "water",
    symbol: "cup",
    plural: "cups",
  },
  Swords: {
    field: "thought, truth, conflict, language and mental clarity",
    love: "direct communication, honest boundaries and the courage to name what is real",
    career: "strategy, decision making, clean communication and focused thinking",
    spiritual: "the clarity that arrives when fear stops writing the story",
    element: "air",
    symbol: "sword",
    plural: "swords",
  },
  Pentacles: {
    field: "money, body, work, time and the physical shape of daily life",
    love: "loyalty, consistency, care through action and emotional reliability",
    career: "craft, resources, patience and results that can be sustained",
    spiritual: "the sacred lesson hidden inside ordinary routines and material choices",
    element: "earth",
    symbol: "pentacle",
    plural: "pentacles",
  },
};

const majorSymbols = {
  "The Fool": [
    { label: "Cliff edge", meaning: "The open edge shows a threshold where trust matters more than certainty." },
    { label: "Small pack", meaning: "The light bundle suggests lived wisdom carried simply, without letting the past become a weight." },
    { label: "White dog", meaning: "The companion at the heel points to instinct, protection and the body noticing what the mind may miss." },
    { label: "Rising sun", meaning: "The bright sky gives the card its feeling of beginning, freshness and a life not yet defined." },
  ],
  "The Magician": [
    { label: "Raised wand", meaning: "The upward hand connects intention with action and reminds you that desire must be directed." },
    { label: "Four tools", meaning: "The cup, wand, sword and pentacle show that the Magician works with every part of life." },
    { label: "Infinity sign", meaning: "The sign above the head points to focused will and the endless exchange between spirit and matter." },
    { label: "Flowering table", meaning: "The garden below shows what can grow when skill, timing and attention meet." },
  ],
  "The High Priestess": [
    { label: "Twin pillars", meaning: "The pillars frame the threshold between what is known and what has not yet been spoken." },
    { label: "Veil", meaning: "The curtain suggests hidden knowledge that opens through patience, not force." },
    { label: "Scroll", meaning: "The scroll points to wisdom carried quietly and understood when the inner life becomes still." },
    { label: "Moon crown", meaning: "The lunar crown ties the card to dreams, cycles and subtle intuitive timing." },
  ],
  "The Empress": [
    { label: "Wheat", meaning: "The grain shows nourishment, fertility and the results of patient tending." },
    { label: "Crown of stars", meaning: "The stars place creativity inside a larger rhythm of season, body and spirit." },
    { label: "Flowing water", meaning: "The water softens the card and points to emotional ease, sensuality and receptivity." },
    { label: "Venus shield", meaning: "The emblem of Venus names love, beauty and the sacred value of pleasure." },
  ],
  "The Emperor": [
    { label: "Stone throne", meaning: "The throne gives the card its weight and speaks of structure that can hold pressure." },
    { label: "Ram heads", meaning: "The rams bring force, courage and the instinct to protect what matters." },
    { label: "Armor", meaning: "The hidden armor suggests vulnerability guarded by discipline and earned authority." },
    { label: "Mountains", meaning: "The mountains show endurance, boundaries and the long view behind leadership." },
  ],
  "The Hierophant": [
    { label: "Crossed keys", meaning: "The keys point to teachings, rituals and doors opened through study." },
    { label: "Raised hand", meaning: "The blessing hand marks guidance, initiation and the responsibility of wisdom." },
    { label: "Twin pillars", meaning: "The pillars show tradition as a structure that can guide or confine." },
    { label: "Acolytes", meaning: "The figures below suggest learning, mentorship and shared belief." },
  ],
  "The Lovers": [
    { label: "Angel", meaning: "The angel gives the card a sacred tone and asks that desire answer to truth." },
    { label: "Two figures", meaning: "The pair shows choice, vulnerability and the courage to meet another person honestly." },
    { label: "Serpent and tree", meaning: "The serpent brings temptation, knowledge and the cost of becoming conscious." },
    { label: "Mountain", meaning: "The mountain between them suggests the growth required by real intimacy." },
  ],
  "The Chariot": [
    { label: "Sphinxes", meaning: "The opposing creatures show two forces that must be guided by one clear will." },
    { label: "Star canopy", meaning: "The canopy points to purpose above the noise of the road." },
    { label: "Armor", meaning: "The armor protects focus and reminds you that progress still needs boundaries." },
    { label: "City behind", meaning: "The city shows what must be left in order to move with conviction." },
  ],
  Strength: [
    { label: "Lion", meaning: "The lion is raw instinct, passion and fear met with steadiness." },
    { label: "Gentle hands", meaning: "The soft touch shows that strength can guide without domination." },
    { label: "Infinity sign", meaning: "The sign above the figure suggests patient courage that renews itself." },
    { label: "White robe", meaning: "The robe points to sincerity, compassion and power that is not performative." },
  ],
  "The Hermit": [
    { label: "Lantern", meaning: "The lantern carries hard won wisdom and lights only the next honest step." },
    { label: "Mountain path", meaning: "The high ground shows solitude, perspective and the discipline of inner work." },
    { label: "Staff", meaning: "The staff offers support from experience rather than outside approval." },
    { label: "Cloak", meaning: "The cloak protects attention from distraction and noise." },
  ],
  "Wheel of Fortune": [
    { label: "Turning wheel", meaning: "The wheel shows change, timing and the movement of cycles beyond personal control." },
    { label: "Sphinx", meaning: "The sphinx asks for steadiness and wisdom while circumstances shift." },
    { label: "Four creatures", meaning: "The guardians in the corners suggest stability inside motion." },
    { label: "Letters on the wheel", meaning: "The markings point to mystery, pattern and meaning hidden inside change." },
  ],
  Justice: [
    { label: "Scales", meaning: "The scales weigh truth, consequence and the balance between action and answer." },
    { label: "Sword", meaning: "The sword cuts through confusion and asks for clean accountability." },
    { label: "Pillars", meaning: "The pillars show law, order and the structure behind fair judgment." },
    { label: "Red robe", meaning: "The robe gives the card urgency and reminds you that truth has a living cost." },
  ],
  "The Hanged Man": [
    { label: "Suspended figure", meaning: "The hanging posture shows surrender, pause and a view that comes from reversal." },
    { label: "Halo", meaning: "The light around the head points to insight born from stillness." },
    { label: "Bound foot", meaning: "The tie suggests chosen limitation and the wisdom of not forcing movement." },
    { label: "Living tree", meaning: "The wood beneath him shows that waiting can still be fertile." },
  ],
  Death: [
    { label: "White horse", meaning: "The horse carries change forward with purity, force and inevitability." },
    { label: "Black banner", meaning: "The banner marks endings that clear the ground for renewal." },
    { label: "Fallen crown", meaning: "The crown shows that no role, status or old identity is beyond transformation." },
    { label: "Rising sun", meaning: "The distant sun keeps the card from despair and points toward rebirth." },
  ],
  Temperance: [
    { label: "Two cups", meaning: "The pouring water shows blending, moderation and the art of making opposites cooperate." },
    { label: "One foot in water", meaning: "The stance balances feeling with grounded presence." },
    { label: "Path to the mountains", meaning: "The path suggests healing as a gradual practice, not a sudden escape." },
    { label: "Radiant crown", meaning: "The light in the distance points to peace earned through integration." },
  ],
  "The Devil": [
    { label: "Loose chains", meaning: "The chains suggest bondage that may be stronger in belief than in fact." },
    { label: "Torch", meaning: "The lowered flame shows desire turned downward into compulsion or distraction." },
    { label: "Horned figure", meaning: "The central figure names instinct, appetite and the shadow that wants honesty." },
    { label: "Dark altar", meaning: "The altar shows what has been given power through repetition." },
  ],
  "The Tower": [
    { label: "Lightning", meaning: "The strike brings sudden truth where denial has held too long." },
    { label: "Falling crown", meaning: "The crown shows false certainty losing its authority." },
    { label: "Breaking tower", meaning: "The cracked structure reveals what could not support real life." },
    { label: "Falling figures", meaning: "The figures show the shock of release when old positions can no longer hold." },
  ],
  "The Star": [
    { label: "Eight pointed star", meaning: "The star offers guidance, renewal and the feeling of hope returning after difficulty." },
    { label: "Water jars", meaning: "The pouring water restores the inner and outer worlds at the same time." },
    { label: "Open figure", meaning: "The unguarded posture speaks of trust after survival." },
    { label: "Distant bird", meaning: "The bird points to perspective, spirit and the quiet return of inspiration." },
  ],
  "The Moon": [
    { label: "Two towers", meaning: "The towers mark a passage through uncertainty and the limits of ordinary sight." },
    { label: "Dog and wolf", meaning: "The animals show the tension between trained response and wild instinct." },
    { label: "Crayfish", meaning: "The creature rising from water points to old fear surfacing from the unconscious." },
    { label: "Moonlit path", meaning: "The path asks for trust when the whole road cannot be seen." },
  ],
  "The Sun": [
    { label: "Bright sun", meaning: "The sun names clarity, warmth and the simple power of being seen." },
    { label: "Child", meaning: "The child brings joy, openness and a return to unguarded life." },
    { label: "White horse", meaning: "The horse carries vitality without force or fear." },
    { label: "Sunflowers", meaning: "The flowers show growth that turns naturally toward light." },
  ],
  Judgement: [
    { label: "Trumpet", meaning: "The trumpet is a call to awaken, answer and stop living below your truth." },
    { label: "Rising figures", meaning: "The figures show renewal after a long inner sleep." },
    { label: "Banner", meaning: "The banner suggests spiritual summons and the courage to respond." },
    { label: "Open arms", meaning: "The posture points to acceptance, reckoning and readiness." },
  ],
  "The World": [
    { label: "Laurel wreath", meaning: "The wreath shows completion, wholeness and a cycle brought into form." },
    { label: "Dancer", meaning: "The central figure moves with freedom inside a finished pattern." },
    { label: "Four guardians", meaning: "The guardians connect the card to balance across body, mind, heart and spirit." },
    { label: "Crossed wands", meaning: "The wands suggest mastery of energy and the grace of integration." },
  ],
};

const minorSymbols = {
  "Ace of Wands": [
    { label: "Hand from a cloud", meaning: "The hand appears suddenly from the cloud, showing inspiration arriving before there is a plan." },
    { label: "Living wand", meaning: "The wand is not a dead stick. Its new leaves make the card about active growth, desire and creative force." },
    { label: "Castle in the distance", meaning: "The far castle gives the spark somewhere to go. It suggests that raw energy needs a real direction." },
  ],
  "Two of Wands": [
    { label: "Globe in hand", meaning: "The small globe shows a person thinking beyond the familiar and weighing a wider future." },
    { label: "Wand fixed to the wall", meaning: "One wand is secured to the parapet, showing that one part of life is stable while another part is ready to move." },
    { label: "View from a height", meaning: "The elevated setting makes the card about planning from a distance rather than acting on impulse." },
  ],
  "Three of Wands": [
    { label: "Ships below", meaning: "The ships connect the card to trade, travel and results that are already moving beyond the first idea." },
    { label: "Back turned to the viewer", meaning: "The figure looks outward instead of inward, showing attention placed on what is coming next." },
    { label: "Three planted wands", meaning: "The wands stand like markers of a chosen path. They show that the first steps have already been taken." },
  ],
  "Four of Wands": [
    { label: "Garlanded wands", meaning: "The wreath strung between the four wands makes the structure feel festive, public and shared." },
    { label: "Dancing figures", meaning: "The raised bouquets show celebration that is witnessed by others rather than kept private." },
    { label: "Castle behind them", meaning: "The building in the background gives the joy a sense of home, arrival and social belonging." },
  ],
  "Five of Wands": [
    { label: "Crossing staves", meaning: "The wands collide without landing a clear blow, showing disorder, competition and unfocused effort." },
    { label: "Five young figures", meaning: "The different bodies and gestures suggest many wills trying to be heard at once." },
    { label: "No central leader", meaning: "The scene lacks one guiding figure, which makes the conflict feel more chaotic than truly hostile." },
  ],
  "Six of Wands": [
    { label: "Laurel wreath", meaning: "The wreath on the wand is a clear sign of public recognition and a victory that others can see." },
    { label: "Rider above the crowd", meaning: "The mounted figure is raised into view, showing visibility, praise and the pressure of being watched." },
    { label: "Following crowd", meaning: "The people behind the rider show that success is social here. The card is about reputation as much as achievement." },
  ],
  "Seven of Wands": [
    { label: "High ground", meaning: "The figure stands above the opposing wands, showing advantage but also exposure." },
    { label: "Six wands below", meaning: "The unseen challengers press upward, making the card about defending a position already earned." },
    { label: "Uneven stance", meaning: "The figure appears hurried and off balance, which keeps the courage honest rather than heroic." },
  ],
  "Eight of Wands": [
    { label: "Wands in flight", meaning: "The eight wands move through open air with no figure holding them, showing momentum already released." },
    { label: "Clear sky", meaning: "The uncluttered sky makes the card feel direct, fast and free of negotiation." },
    { label: "River and hills", meaning: "The landscape below gives the motion a destination, so speed is not random." },
  ],
  "Nine of Wands": [
    { label: "Bandaged head", meaning: "The bandage shows that the figure has already been through difficulty and is still standing." },
    { label: "Wall of wands", meaning: "The row behind him works like a boundary, showing experience turned into protection." },
    { label: "Single wand held close", meaning: "The wand in his hands shows readiness. He is tired but not unprepared." },
  ],
  "Ten of Wands": [
    { label: "Bundled wands", meaning: "The ten wands are carried all at once, making the burden visible and self-contained." },
    { label: "Bent figure", meaning: "The posture shows strain, not failure. The person can still move but cannot see clearly." },
    { label: "Town ahead", meaning: "The nearby buildings suggest the load has a destination, which is why the pressure may still feel necessary." },
  ],
  "Page of Wands": [
    { label: "Wand held upright", meaning: "The Page studies the living wand closely, showing curiosity before action." },
    { label: "Salamander tunic", meaning: "The salamanders connect the figure to fire, transformation and raw creative appetite." },
    { label: "Desert landscape", meaning: "The dry open land gives the card a restless feeling. Potential is present but must be animated." },
  ],
  "Knight of Wands": [
    { label: "Rearing horse", meaning: "The horse rises with energy, showing movement that is bold, fast and hard to contain." },
    { label: "Salamander robe", meaning: "The salamanders link the knight to fire and transformation, but their open mouths suggest energy still seeking completion." },
    { label: "Pyramids behind", meaning: "The distant pyramids place the action in a hot landscape, adding heat, ambition and momentum." },
  ],
  "Queen of Wands": [
    { label: "Sunflower", meaning: "The sunflower faces light and makes the card about confidence, warmth and visible life force." },
    { label: "Black cat", meaning: "The cat at her feet adds independence, instinct and comfort with what others may not understand." },
    { label: "Lions on the throne", meaning: "The lions connect her authority to courage and steady creative power." },
  ],
  "King of Wands": [
    { label: "Living wand", meaning: "The sprouting wand shows that his authority is creative, active and still growing." },
    { label: "Salamanders and lions", meaning: "The throne imagery combines fire with command, making his leadership passionate but controlled." },
    { label: "Forward posture", meaning: "The king leans into the scene rather than resting back, which shows readiness to act." },
  ],
  "Ace of Cups": [
    { label: "Overflowing cup", meaning: "Water pours from the cup in many streams, showing emotion that is abundant enough to move outward." },
    { label: "Dove above the cup", meaning: "The dove gives the image a feeling of peace, blessing and emotional offering." },
    { label: "Lotus covered water", meaning: "The water below is alive with lotus forms, tying the card to receptivity and spiritual feeling." },
  ],
  "Two of Cups": [
    { label: "Exchanged cups", meaning: "The two figures each hold a cup, making the card about mutual offering rather than one sided longing." },
    { label: "Caduceus", meaning: "The winged staff between them suggests healing, agreement and balanced exchange." },
    { label: "Lion head", meaning: "The lion above the pair adds warmth, desire and the courage required for honest union." },
  ],
  "Three of Cups": [
    { label: "Raised cups", meaning: "The cups are lifted together, showing shared joy rather than private satisfaction." },
    { label: "Three figures in a circle", meaning: "The circle of bodies suggests friendship, community and emotional support." },
    { label: "Harvest at their feet", meaning: "The fruit and vines connect the celebration to something that has ripened." },
  ],
  "Four of Cups": [
    { label: "Cup from a cloud", meaning: "The offered cup echoes the aces, but the seated figure does not reach for it." },
    { label: "Crossed arms", meaning: "The closed posture shows emotional withdrawal, resistance or deep inward focus." },
    { label: "Three cups on the ground", meaning: "The cups already present suggest that dissatisfaction may exist even when options are available." },
  ],
  "Five of Cups": [
    { label: "Spilled cups", meaning: "The fallen cups make loss visible and immediate. The figure's attention is fixed on what cannot be restored." },
    { label: "Two cups standing", meaning: "The upright cups behind the figure show that not everything has been lost." },
    { label: "Bridge and castle", meaning: "The bridge points toward return, repair and a way back to shelter." },
  ],
  "Six of Cups": [
    { label: "Flower filled cups", meaning: "The cups hold flowers rather than drink, linking emotion with memory, tenderness and innocence." },
    { label: "Child offering a cup", meaning: "The gesture shows kindness given simply, without negotiation or performance." },
    { label: "Quiet courtyard", meaning: "The protected setting gives the card its feeling of safety and remembered belonging." },
  ],
  "Seven of Cups": [
    { label: "Cups in a cloud", meaning: "The cups appear as visions rather than objects on the ground, showing imagination and uncertainty." },
    { label: "Veiled figure", meaning: "The hidden figure in one cup suggests that not every desire is fully understood." },
    { label: "Mixed contents", meaning: "Jewels, a snake, a wreath and other images show that choices can include both reward and danger." },
  ],
  "Eight of Cups": [
    { label: "Abandoned cups", meaning: "The cups remain standing, showing that the departure is not always caused by total failure." },
    { label: "Walking figure", meaning: "The cloaked person moves away from what is known, making the card about a chosen emotional exit." },
    { label: "Moon overhead", meaning: "The moon brings uncertainty and inner prompting to the journey." },
  ],
  "Nine of Cups": [
    { label: "Cups arranged behind", meaning: "The nine cups form a display, showing satisfaction that can be seen and claimed." },
    { label: "Seated figure", meaning: "The person sits firmly with crossed arms, giving the card a mood of self possession and contentment." },
    { label: "Covered table", meaning: "The draped surface creates a staged feeling, reminding the reader that fulfillment also has a public face." },
  ],
  "Ten of Cups": [
    { label: "Rainbow of cups", meaning: "The cups appear in the rainbow, making emotional fulfillment feel blessed, visible and larger than one person." },
    { label: "Family below", meaning: "The figures on the ground connect happiness to relationship, home and shared life." },
    { label: "Open landscape", meaning: "The river, trees and house give the joy a grounded setting rather than a fantasy." },
  ],
  "Page of Cups": [
    { label: "Fish rising from cup", meaning: "The fish is surprising and alive, showing a message from feeling, imagination or the unconscious." },
    { label: "Blue tunic", meaning: "The clothing ties the Page to water and emotional openness." },
    { label: "Sea behind him", meaning: "The water in the background keeps the reading close to intuition, mood and creative response." },
  ],
  "Knight of Cups": [
    { label: "Cup held forward", meaning: "The knight carries the cup carefully, showing an offer made with intention rather than force." },
    { label: "White horse", meaning: "The calm horse keeps the movement gentle, romantic and controlled." },
    { label: "Winged helmet", meaning: "The wings connect the knight to messages, imagination and the movement of feeling." },
  ],
  "Queen of Cups": [
    { label: "Closed ornate cup", meaning: "Her cup is covered and elaborate, suggesting an inner emotional life that is deep but not easily exposed." },
    { label: "Throne at the water", meaning: "The throne sits beside the sea, placing her authority at the edge of feeling and intuition." },
    { label: "Cherub imagery", meaning: "The carved figures around the throne connect her care to tenderness, memory and emotional protection." },
  ],
  "King of Cups": [
    { label: "Throne on the sea", meaning: "The throne floats on moving water, showing emotional control without denying the waves." },
    { label: "Cup and scepter", meaning: "The cup and scepter balance feeling with authority." },
    { label: "Ship and fish", meaning: "The background figures show life moving through emotion while the king remains steady." },
  ],
  "Ace of Swords": [
    { label: "Sword from a cloud", meaning: "The sword appears suddenly, showing truth, thought or decision entering with force." },
    { label: "Crown and wreath", meaning: "The crown suggests victory, while the wreath shows that clarity carries responsibility." },
    { label: "Sharp mountains", meaning: "The hard landscape below points to challenge and the clean edge of truth." },
  ],
  "Two of Swords": [
    { label: "Blindfold", meaning: "The blindfold shows a refusal or inability to see the full situation directly." },
    { label: "Crossed swords", meaning: "The crossed blades hold two positions in tension, creating a guarded stalemate." },
    { label: "Moonlit water", meaning: "The water and moon behind the figure suggest emotion is present even when the pose is controlled." },
  ],
  "Three of Swords": [
    { label: "Pierced heart", meaning: "The heart makes pain explicit and personal rather than abstract." },
    { label: "Three swords", meaning: "The blades show that words, thoughts or truths have entered the emotional center." },
    { label: "Rain clouds", meaning: "The storm behind the heart grounds the card in grief, release and the weather of sorrow." },
  ],
  "Four of Swords": [
    { label: "Resting figure", meaning: "The figure lies still like a tomb effigy, making rest feel deliberate and protected." },
    { label: "Three swords above", meaning: "The swords on the wall suggest thoughts put aside rather than solved immediately." },
    { label: "Stained glass", meaning: "The window gives the scene a chapel like quiet, tying recovery to reflection and prayer." },
  ],
  "Five of Swords": [
    { label: "Collected swords", meaning: "The central figure gathers swords after conflict, showing advantage gained at a cost." },
    { label: "Figures walking away", meaning: "The defeated figures make the emotional fallout visible." },
    { label: "Jagged clouds", meaning: "The broken sky keeps the victory unsettled rather than clean." },
  ],
  "Six of Swords": [
    { label: "Boat crossing water", meaning: "The boat shows transition, distance and the act of being carried out of one state into another." },
    { label: "Six swords in the boat", meaning: "The swords travel with the passengers, showing that the mind's burdens are not simply left behind." },
    { label: "Far shore", meaning: "The destination is visible but not reached yet, which makes the card about passage rather than arrival." },
  ],
  "Seven of Swords": [
    { label: "Figure carrying swords", meaning: "The person moves away with five swords, making secrecy and strategy central to the image." },
    { label: "Two swords left behind", meaning: "The remaining swords show that the plan is incomplete or that something cannot be taken." },
    { label: "Camp in the background", meaning: "The tents place the action near other people, so the card involves social risk and consequence." },
  ],
  "Eight of Swords": [
    { label: "Bound figure", meaning: "The bindings show restriction, but they are not the same as a locked prison." },
    { label: "Blindfold", meaning: "The covered eyes make the card about perception and fear as much as circumstance." },
    { label: "Open gap among swords", meaning: "The swords do not fully enclose the figure, showing that movement may be possible once fear is faced." },
  ],
  "Nine of Swords": [
    { label: "Figure upright in bed", meaning: "The person sits awake, making the card about private mental distress and sleepless thought." },
    { label: "Nine swords on the wall", meaning: "The swords hang above the figure like repeated thoughts." },
    { label: "Patterned quilt", meaning: "The decorated bedding contrasts with the distress, showing that anxiety can arise even in a safe room." },
  ],
  "Ten of Swords": [
    { label: "Ten swords in the back", meaning: "The image makes an ending unmistakable. There is no need to pretend the situation is fine." },
    { label: "Dark sky", meaning: "The black sky gives the card its feeling of finality and exhaustion." },
    { label: "Dawn at the horizon", meaning: "The light in the distance keeps the card honest. The ending is severe but not endless." },
  ],
  "Page of Swords": [
    { label: "Sword held high", meaning: "The Page holds the sword ready, showing alertness and a mind eager to respond." },
    { label: "Windblown ground", meaning: "The moving trees and clouds show an unsettled mental atmosphere." },
    { label: "Birds overhead", meaning: "The birds reinforce watchfulness, messages and the restless movement of thought." },
  ],
  "Knight of Swords": [
    { label: "Charging horse", meaning: "The horse rushes forward, making speed and force impossible to miss." },
    { label: "Sword aimed ahead", meaning: "The raised sword turns thought into direct action." },
    { label: "Stormy sky", meaning: "The torn clouds show pressure, urgency and the risk of acting before the weather clears." },
  ],
  "Queen of Swords": [
    { label: "Raised sword", meaning: "The upright blade shows clear judgment and a willingness to name what is true." },
    { label: "Open hand", meaning: "Her extended hand softens the sword, suggesting honesty that can still invite dialogue." },
    { label: "Butterflies and clouds", meaning: "The throne imagery links her clarity to transformation, weather and thought in motion." },
  ],
  "King of Swords": [
    { label: "Upright sword", meaning: "The vertical sword shows authority rooted in principle and clear thought." },
    { label: "Stone throne", meaning: "The throne makes his judgment feel formal, structured and difficult to move." },
    { label: "Birds in the sky", meaning: "The birds connect the card to air, perspective and mental distance." },
  ],
  "Ace of Pentacles": [
    { label: "Pentacle in the hand", meaning: "The coin is offered from a cloud, making opportunity feel real but not yet possessed." },
    { label: "Garden path", meaning: "The cultivated garden shows that material growth needs tending." },
    { label: "Archway and mountains", meaning: "The arch opens toward a harder path beyond comfort, linking opportunity with effort." },
  ],
  "Two of Pentacles": [
    { label: "Infinity loop", meaning: "The loop around the coins shows ongoing exchange, adjustment and movement." },
    { label: "Juggling figure", meaning: "The figure's dance makes balance active rather than still." },
    { label: "Ships on waves", meaning: "The ships behind him show that practical balance happens while life keeps moving." },
  ],
  "Three of Pentacles": [
    { label: "Cathedral arch", meaning: "The building setting makes the work part of something larger and lasting." },
    { label: "Craftsperson on bench", meaning: "The elevated worker shows skill being recognized in real time." },
    { label: "Two observers", meaning: "The figures holding plans show collaboration, review and shared standards." },
  ],
  "Four of Pentacles": [
    { label: "Pentacle held to chest", meaning: "The coin is guarded at the heart, showing attachment to security." },
    { label: "Pentacles underfoot", meaning: "The feet on the coins suggest stability but also fear of losing ground." },
    { label: "City behind him", meaning: "The city shows the social world at a distance while the figure focuses on possession." },
  ],
  "Five of Pentacles": [
    { label: "Figures in snow", meaning: "The cold scene makes hardship physical and immediate." },
    { label: "Stained glass window", meaning: "The lit window suggests help or shelter nearby, even if the figures do not enter." },
    { label: "Bare feet and crutch", meaning: "The visible injuries keep the card grounded in material vulnerability." },
  ],
  "Six of Pentacles": [
    { label: "Scales", meaning: "The scales show judgment, fairness and the measured distribution of resources." },
    { label: "Coins being given", meaning: "The falling coins make generosity practical rather than symbolic." },
    { label: "Two kneeling figures", meaning: "The different positions show imbalance between giver and receiver." },
  ],
  "Seven of Pentacles": [
    { label: "Pentacle vine", meaning: "The coins grow on the plant, making effort and reward part of a living process." },
    { label: "Worker leaning on tool", meaning: "The posture shows assessment after labor rather than laziness." },
    { label: "Single pentacle near the feet", meaning: "The lower coin suggests one result is close while the larger harvest still needs time." },
  ],
  "Eight of Pentacles": [
    { label: "Workbench", meaning: "The bench makes the card about repeated craft, not abstract ambition." },
    { label: "Finished pentacles", meaning: "The coins displayed in a row show progress made through repetition." },
    { label: "Town in the distance", meaning: "The separated town suggests focus, apprenticeship and time spent away from distraction." },
  ],
  "Nine of Pentacles": [
    { label: "Falcon on the hand", meaning: "The trained bird shows discipline, refinement and mastery of instinct." },
    { label: "Grapevines and coins", meaning: "The garden connects wealth to cultivation and patient care." },
    { label: "Snail near the ground", meaning: "The small snail reminds the reader that luxury and independence were built slowly." },
  ],
  "Ten of Pentacles": [
    { label: "Tree of Life pattern", meaning: "The ten pentacles are arranged in a symbolic pattern, linking wealth with lineage and structure." },
    { label: "Elder and dogs", meaning: "The seated elder with animals brings ancestry, loyalty and household continuity into the card." },
    { label: "Family under the arch", meaning: "The figures inside the gateway show legacy lived through family, home and shared place." },
  ],
  "Page of Pentacles": [
    { label: "Pentacle held up", meaning: "The Page studies the coin carefully, showing attention to a new practical opportunity." },
    { label: "Green field", meaning: "The fertile land suggests growth that can become real with patience." },
    { label: "Mountains beyond", meaning: "The distant mountains remind the reader that ambition still requires effort and training." },
  ],
  "Knight of Pentacles": [
    { label: "Still horse", meaning: "The horse stands rather than charges, showing patience, steadiness and deliberate movement." },
    { label: "Pentacle in hand", meaning: "The knight holds the coin as a task or duty, not as decoration." },
    { label: "Plowed fields", meaning: "The worked land ties the card to routine, labor and results that come through repetition." },
  ],
  "Queen of Pentacles": [
    { label: "Pentacle in her lap", meaning: "The coin is held close and gently, showing care for resources rather than display." },
    { label: "Rabbit near the throne", meaning: "The rabbit connects the card to fertility, comfort and natural abundance." },
    { label: "Carved throne and garden", meaning: "The rich setting shows prosperity shaped by attention, protection and taste." },
  ],
  "King of Pentacles": [
    { label: "Grapevine robe", meaning: "The fruiting vines on his clothing show wealth that has ripened over time." },
    { label: "Bull imagery", meaning: "The bulls on the throne connect him to earth, patience and material strength." },
    { label: "Castle behind him", meaning: "The castle grounds his success in structure, property and lasting achievement." },
  ],
};

const groundedSections = {
  "The Fool": {
    loveMeaning: "In love, The Fool points to openness, early attraction and the risk of stepping in before trust has been tested. It is a real beginning, not proof that the path is safe.",
    careerMeaning: "At work, The Fool describes a new role, project or direction that has room to grow because it is still unformed. It also warns against ignoring practical details just because the idea feels exciting.",
    spiritualMeaning: "For inner growth, The Fool asks for beginner's mind and honest courage. The cliff in the artwork matters because the next step needs awareness as much as faith.",
    shadowMessage: "The shadow of The Fool is recklessness, avoidance of consequences or calling every impulse a sign. The card asks you to keep wonder without abandoning judgment.",
    journalPrompts: ["What new step is real enough to take and what still needs preparation?", "Where am I confusing freedom with avoidance?", "What would courage look like if I respected the risk too?"],
  },
  "The Magician": {
    loveMeaning: "In love, The Magician points to clear intention, strong attraction and the power of honest communication. It is strongest when words, behavior and motives match.",
    careerMeaning: "At work, The Magician is about using the tools already on the table. Skill, timing, preparation and confidence matter more here than waiting for perfect conditions.",
    spiritualMeaning: "For inner growth, The Magician asks you to align will with action. The card is practical because intention only becomes meaningful when it is used.",
    shadowMessage: "The shadow of The Magician is manipulation, performance or clever language that hides the truth. The card asks for clean motives and direct action.",
    journalPrompts: ["Which tool or skill am I underusing right now?", "Where do my words need to match my actions more closely?", "What am I trying to influence and why?"],
  },
  "The High Priestess": {
    loveMeaning: "In love, The High Priestess points to quiet feelings, privacy and what has not yet been spoken. It asks for patience rather than pressure.",
    careerMeaning: "At work, The High Priestess favors research, observation and listening before committing. Important information may be available but not obvious.",
    spiritualMeaning: "For inner growth, The High Priestess asks for stillness and trust in careful perception. The veil in the artwork makes the card about mystery that opens slowly.",
    shadowMessage: "The shadow of The High Priestess is secrecy, passivity or using silence to avoid a necessary conversation. The card asks you to respect what is hidden without hiding from reality.",
    journalPrompts: ["What do I know quietly but have not admitted?", "Where am I using silence wisely and where am I hiding?", "What fact should I observe before I decide?"],
  },
  "The Empress": {
    loveMeaning: "In love, The Empress points to warmth, pleasure, care and emotional generosity. It can show fertility in a broad sense, including affection, creativity and a relationship that wants tending.",
    careerMeaning: "At work, The Empress favors creative output, resource growth and environments where people can do their best work. It asks what needs nourishment, not just pressure.",
    spiritualMeaning: "For inner growth, The Empress brings attention to the body, nature and the value of receiving. Growth is treated as something cultivated over time.",
    shadowMessage: "The shadow of The Empress is overgiving, smothering or staying comfortable when growth needs movement. The card asks for care that does not erase your own needs.",
    journalPrompts: ["What am I nurturing and is it returning life to me?", "Where do I need more beauty, rest or physical care?", "How can I give without taking responsibility for everything?"],
  },
  "The Emperor": {
    loveMeaning: "In love, The Emperor points to commitment, boundaries and the need for stable behavior. It supports devotion when structure protects the relationship rather than controls it.",
    careerMeaning: "At work, The Emperor favors leadership, systems, clear roles and decisions that can hold pressure. It asks for order that makes action easier.",
    spiritualMeaning: "For inner growth, The Emperor teaches discipline, responsibility and the dignity of keeping promises. Structure becomes spiritual when it supports what matters.",
    shadowMessage: "The shadow of The Emperor is control, rigidity or confusing authority with being right. The card asks for strength that can still listen.",
    journalPrompts: ["Where would a clear boundary make life calmer?", "What responsibility is mine and what is not mine?", "How can I lead without tightening my grip?"],
  },
  "The Hierophant": {
    loveMeaning: "In love, The Hierophant points to shared values, commitment and the beliefs a relationship is built on. It asks whether the bond has a real ethical or spiritual foundation.",
    careerMeaning: "At work, The Hierophant favors training, mentorship, institutions and proven methods. It is useful when learning the rules matters before changing them.",
    spiritualMeaning: "For inner growth, The Hierophant asks what tradition, ritual or teaching can genuinely support you. The lesson is guidance, not blind obedience.",
    shadowMessage: "The shadow of The Hierophant is dogma, approval seeking or treating convention as truth. The card asks you to keep the wisdom and question the rigidity.",
    journalPrompts: ["Which belief is guiding this choice?", "What teacher, method or tradition has earned my trust?", "Where am I following rules only to be accepted?"],
  },
  "The Lovers": {
    loveMeaning: "In love, The Lovers points to attraction, mutual choice and values that must be named honestly. It is not only romance. It is the responsibility of choosing with awareness.",
    careerMeaning: "At work, The Lovers appears around partnerships, offers and decisions that need to match your values. The best choice is the one you can stand behind after the excitement fades.",
    spiritualMeaning: "For inner growth, The Lovers asks for integration between desire and conscience. The card treats choice as a spiritual act.",
    shadowMessage: "The shadow of The Lovers is avoiding a decision, splitting loyalty or letting chemistry overrule truth. The card asks for alignment rather than confusion.",
    journalPrompts: ["What choice reflects my values rather than my fear?", "Where do attraction and responsibility need to meet?", "What truth would make this decision cleaner?"],
  },
  "The Chariot": {
    loveMeaning: "In love, The Chariot points to direction, commitment and the will to move together. It warns against forcing the relationship to go somewhere before both people are aligned.",
    careerMeaning: "At work, The Chariot favors focus, discipline and progress through controlled effort. Victory comes from steering competing demands rather than being pulled by them.",
    spiritualMeaning: "For inner growth, The Chariot is self mastery in motion. It asks you to choose a direction and keep your emotional forces under conscious guidance.",
    shadowMessage: "The shadow of The Chariot is control, impatience or scattered drive. The card asks for determination without domination.",
    journalPrompts: ["What direction am I actually choosing?", "Which competing demands need firmer steering?", "Where am I pushing when I should be guiding?"],
  },
  Strength: {
    loveMeaning: "In love, Strength points to patience, tenderness and the ability to stay present with strong feelings. It favors trust built through gentleness rather than pressure.",
    careerMeaning: "At work, Strength favors calm influence, steady confidence and handling tension without escalating it. It is quiet competence under pressure.",
    spiritualMeaning: "For inner growth, Strength asks you to meet instinct with compassion. The lion is not destroyed. It is understood and guided.",
    shadowMessage: "The shadow of Strength is forcing confidence, suppressing anger or mistaking control for courage. The card asks for honesty with your own instincts.",
    journalPrompts: ["What feeling needs patience instead of force?", "Where can I be firm and kind at the same time?", "What part of me is asking to be understood rather than conquered?"],
  },
  "The Hermit": {
    loveMeaning: "In love, The Hermit points to space, reflection and the need to hear your own voice clearly. It can show solitude, distance or a relationship that needs quieter truth.",
    careerMeaning: "At work, The Hermit favors research, expertise, independent focus and mentorship. It asks you to value depth over noise.",
    spiritualMeaning: "For inner growth, The Hermit is the practice of solitude with purpose. The lantern lights only the next honest step.",
    shadowMessage: "The shadow of The Hermit is isolation, withdrawal or using wisdom as a wall. The card asks for solitude that clarifies rather than separates you from life.",
    journalPrompts: ["What do I know when I stop asking everyone else?", "Where is solitude helping and where is it becoming avoidance?", "What is the next honest step, even if it is small?"],
  },
  "Wheel of Fortune": {
    loveMeaning: "In love, Wheel of Fortune points to a turning point, repeated pattern or change in timing. It does not promise fate will fix the relationship. It asks how you respond to change.",
    careerMeaning: "At work, Wheel of Fortune can mark opportunity, shifting circumstances or a cycle reaching a visible turn. It favors readiness over trying to control every variable.",
    spiritualMeaning: "For inner growth, Wheel of Fortune teaches impermanence. The card asks you to notice cycles without giving away your agency.",
    shadowMessage: "The shadow of Wheel of Fortune is passivity, gambling on luck or blaming fate for choices. The card asks you to participate wisely in changing conditions.",
    journalPrompts: ["What pattern is repeating and what can I do differently?", "Where is timing changing around me?", "How can I stay responsible without pretending I control everything?"],
  },
  Justice: {
    loveMeaning: "In love, Justice points to honesty, accountability and the consequences of past choices. It asks for fairness in words, commitments and repair.",
    careerMeaning: "At work, Justice favors contracts, decisions, policy, documentation and balanced judgment. It asks you to check the facts before acting.",
    spiritualMeaning: "For inner growth, Justice is integrity made visible. The scales ask whether your choices match your stated values.",
    shadowMessage: "The shadow of Justice is harsh judgment, defensiveness or refusing accountability. The card asks for truth without cruelty.",
    journalPrompts: ["What fact am I avoiding because it changes the answer?", "Where do I need to make repair?", "What would be fair if I were not defending my ego?"],
  },
  "The Hanged Man": {
    loveMeaning: "In love, The Hanged Man points to pause, surrender and a new perspective. It can show waiting, but the wait should reveal something useful rather than become a habit.",
    careerMeaning: "At work, The Hanged Man can mark delay, review or the need to solve a problem from a different angle. It favors patience when forcing action would waste effort.",
    spiritualMeaning: "For inner growth, The Hanged Man asks you to release the old viewpoint long enough for another truth to appear. Stillness is the method.",
    shadowMessage: "The shadow of The Hanged Man is martyrdom, indecision or calling avoidance surrender. The card asks whether the pause is teaching you or trapping you.",
    journalPrompts: ["What changes if I look at this from the opposite side?", "Where am I waiting with purpose and where am I stalling?", "What am I ready to release control over?"],
  },
  Death: {
    loveMeaning: "In love, Death points to an ending, transformation or the need for a relationship pattern to change completely. It is not automatically breakup, but it does not support pretending nothing is changing.",
    careerMeaning: "At work, Death can mark closing a chapter, leaving an old role or ending a method that no longer works. It favors honest transition.",
    spiritualMeaning: "For inner growth, Death teaches release. The card is factual about impermanence and asks what must end so life can move.",
    shadowMessage: "The shadow of Death is clinging, fear of change or dragging out an ending. The card asks for grief and courage rather than denial.",
    journalPrompts: ["What is already over in practice?", "What am I keeping alive through fear?", "What would respectful closure look like?"],
  },
  Temperance: {
    loveMeaning: "In love, Temperance points to patience, healing and two people learning how to meet in the middle. It favors steady repair over dramatic proof.",
    careerMeaning: "At work, Temperance favors process, moderation, teamwork and pacing. It asks you to blend resources carefully instead of rushing to extremes.",
    spiritualMeaning: "For inner growth, Temperance is integration. The pouring cups show that balance is active practice, not passive calm.",
    shadowMessage: "The shadow of Temperance is avoidance of necessary conflict, people pleasing or swinging between extremes. The card asks for honest balance.",
    journalPrompts: ["What needs more patience and what needs more movement?", "Where am I trying to force harmony?", "What two parts of my life need better integration?"],
  },
  "The Devil": {
    loveMeaning: "In love, The Devil points to attachment, intense chemistry, dependency or patterns that feel hard to stop. It asks what is binding the relationship and whether that bond is healthy.",
    careerMeaning: "At work, The Devil can show material pressure, unhealthy ambition, controlling environments or choices made from fear. It asks you to name the tradeoff clearly.",
    spiritualMeaning: "For inner growth, The Devil is about seeing attachment without shame. The loose chains matter because awareness can begin to restore choice.",
    shadowMessage: "The shadow of The Devil is compulsion, denial, secrecy or letting desire become a cage. The card asks for honesty before liberation.",
    journalPrompts: ["What do I feel bound to and what keeps the bond in place?", "Where am I calling pressure ambition?", "What choice becomes possible when I stop hiding the pattern?"],
  },
  "The Tower": {
    loveMeaning: "In love, The Tower points to revelation, rupture or a truth that destabilizes the old structure. It does not create the crack. It exposes what was already weak.",
    careerMeaning: "At work, The Tower can mark sudden change, failure of a plan, exposure of a problem or a structure that cannot hold. It asks for fast honesty and practical rebuilding.",
    spiritualMeaning: "For inner growth, The Tower is the collapse of false certainty. The lightning is disruptive because denial has run out of room.",
    shadowMessage: "The shadow of The Tower is denial, drama or rebuilding the same unstable structure too quickly. The card asks you to learn from the break.",
    journalPrompts: ["What truth is already breaking through?", "What structure failed because it was not honest?", "What should be rebuilt differently, not just rebuilt fast?"],
  },
  "The Star": {
    loveMeaning: "In love, The Star points to healing, renewed trust and the possibility of tenderness after difficulty. It asks for openness that is supported by time and care.",
    careerMeaning: "At work, The Star favors recovery, creative direction and a longer view after stress. It is hope with a plan, not passive wishing.",
    spiritualMeaning: "For inner growth, The Star is restoration. The open figure and flowing water make the card about returning to faith without losing honesty.",
    shadowMessage: "The shadow of The Star is wishful thinking, emotional distance or waiting for rescue. The card asks you to let hope guide action.",
    journalPrompts: ["What would healing look like in real behavior?", "Where do I need hope with a plan?", "What part of me is ready to trust life again slowly?"],
  },
  "The Moon": {
    loveMeaning: "In love, The Moon points to uncertainty, projection, mixed signals or feelings that are hard to verify. It asks for patience until reality is clearer.",
    careerMeaning: "At work, The Moon warns that information may be incomplete, emotional or confusing. It favors checking facts, reading the room and delaying big assumptions.",
    spiritualMeaning: "For inner growth, The Moon brings attention to dreams, fear and the unconscious. The path is real but it cannot be seen all at once.",
    shadowMessage: "The shadow of The Moon is fear, fantasy or treating a feeling as evidence before it has been examined. The card asks for compassion and verification.",
    journalPrompts: ["What do I know and what am I imagining?", "Where is fear filling in missing information?", "What fact would help me move through uncertainty?"],
  },
  "The Sun": {
    loveMeaning: "In love, The Sun points to warmth, clarity, joy and a relationship that can be seen honestly. It favors openness rather than guessing.",
    careerMeaning: "At work, The Sun favors visibility, success, confidence and clear results. It asks you to step into the light without losing humility.",
    spiritualMeaning: "For inner growth, The Sun is vitality and truth. The card reminds you that joy can be simple and still be serious.",
    shadowMessage: "The shadow of The Sun is ego, overexposure or forcing positivity over real feelings. The card asks for joy that does not deny complexity.",
    journalPrompts: ["Where is life becoming clearer?", "What joy am I allowed to take seriously?", "How can I be visible without performing?"],
  },
  Judgement: {
    loveMeaning: "In love, Judgement points to an honest review, a possible second chance or a relationship reaching a moment of truth. It asks for accountability before renewal.",
    careerMeaning: "At work, Judgement can mark evaluation, a calling, a decision point or the need to answer for past work. It favors honest assessment and mature response.",
    spiritualMeaning: "For inner growth, Judgement is awakening through responsibility. The trumpet calls for a response, not vague inspiration.",
    shadowMessage: "The shadow of Judgement is self condemnation, harsh criticism or refusing the call because change would be inconvenient. The card asks for reckoning without punishment.",
    journalPrompts: ["What is asking for an honest review?", "Where do I need to answer clearly instead of delaying?", "What can be renewed after accountability?"],
  },
  "The World": {
    loveMeaning: "In love, The World points to completion, maturity and a bond that has reached an important stage. It can also show distance, travel or closure around a relationship cycle.",
    careerMeaning: "At work, The World favors finishing a project, reaching a milestone and integrating what has been learned. It is achievement that needs proper completion.",
    spiritualMeaning: "For inner growth, The World is wholeness through integration. The card asks what has come full circle and how you carry it forward.",
    shadowMessage: "The shadow of The World is refusing closure, perfectionism or chasing completion outside yourself. The card asks you to finish honestly and begin again from a fuller place.",
    journalPrompts: ["What cycle is complete enough to honor?", "What lesson have I integrated through experience?", "Where am I delaying closure because the next beginning feels unknown?"],
  },
  "Ace of Wands": {
    loveMeaning: "In love, Ace of Wands points to attraction, chemistry and a fresh spark. It is passion at the beginning stage, so consistency still has to be proven.",
    careerMeaning: "At work, Ace of Wands favors a new idea, pitch, launch or creative impulse. The opportunity is real when energy turns into first action.",
    spiritualMeaning: "For inner growth, Ace of Wands asks you to notice what wakes up your life force. Desire is treated as information that needs direction.",
    shadowMessage: "The shadow of Ace of Wands is a false start, scattered heat or mistaking excitement for commitment. The card asks you to protect the spark by giving it structure.",
    journalPrompts: ["What idea has real energy behind it?", "Where do I need first action rather than more excitement?", "How can I keep this spark from burning out too quickly?"],
  },
  "Two of Wands": {
    loveMeaning: "In love, Two of Wands points to planning, distance or deciding what future the relationship is moving toward. Attraction is not enough without direction.",
    careerMeaning: "At work, Two of Wands favors strategy, expansion and choosing between staying familiar or reaching wider. The globe in the card makes the question practical and future facing.",
    spiritualMeaning: "For inner growth, Two of Wands asks you to own your vision before acting. The card is about looking beyond the wall without abandoning discernment.",
    shadowMessage: "The shadow of Two of Wands is staying in the planning stage because action would create risk. The card asks for a decision that can become movement.",
    journalPrompts: ["What future am I actually planning for?", "Where am I choosing comfort over expansion?", "What decision would turn the vision into a path?"],
  },
  "Three of Wands": {
    loveMeaning: "In love, Three of Wands points to growth, distance, waiting for a response or seeing whether a connection can expand. It asks for patience with visible progress.",
    careerMeaning: "At work, Three of Wands favors expansion, trade, outreach and results that begin returning after earlier effort. It is not idle waiting. It is watching movement already set in motion.",
    spiritualMeaning: "For inner growth, Three of Wands asks you to trust the horizon while staying engaged. The ships show that effort can travel beyond your immediate reach.",
    shadowMessage: "The shadow of Three of Wands is waiting without participating or expecting results from plans never launched. The card asks for active patience.",
    journalPrompts: ["What effort is already moving beyond me?", "Where do I need patience and where do I need follow through?", "What horizon am I ready to grow toward?"],
  },
  "Four of Wands": {
    loveMeaning: "In love, Four of Wands points to celebration, homecoming, commitment milestones and shared joy. It is about stability that can be witnessed by others.",
    careerMeaning: "At work, Four of Wands favors team wins, successful launches and a stable foundation after effort. It asks you to mark progress before rushing to the next demand.",
    spiritualMeaning: "For inner growth, Four of Wands is belonging. The garlanded structure asks where joy, place and community make life feel held.",
    shadowMessage: "The shadow of Four of Wands is performing harmony or chasing celebration while ignoring what needs repair. The card asks for joy with a real foundation.",
    journalPrompts: ["What deserves to be celebrated honestly?", "Where do I feel at home and why?", "What foundation supports the joy I want?"],
  },
  "Five of Wands": {
    loveMeaning: "In love, Five of Wands points to disagreement, tension, mixed needs or competition for attention. It asks whether conflict is productive or just noisy.",
    careerMeaning: "At work, Five of Wands can show competition, brainstorming, clashing opinions or a team without clear coordination. The value is in channeling friction into better work.",
    spiritualMeaning: "For inner growth, Five of Wands teaches that conflict is information. It shows where energy needs direction instead of suppression.",
    shadowMessage: "The shadow of Five of Wands is petty conflict, defensiveness or enjoying the fight more than the solution. The card asks for honest coordination.",
    journalPrompts: ["What is the real issue beneath the noise?", "Where could healthy friction improve the result?", "How can I stop competing and start clarifying?"],
  },
  "Six of Wands": {
    loveMeaning: "In love, Six of Wands points to being seen, appreciated and publicly valued. It asks whether recognition is mutual or only one person is being celebrated.",
    careerMeaning: "At work, Six of Wands favors recognition, promotion, praise or visible success after effort. It also asks you to handle attention with maturity.",
    spiritualMeaning: "For inner growth, Six of Wands teaches healthy pride. The laurel is earned, but it still needs humility.",
    shadowMessage: "The shadow of Six of Wands is vanity, dependence on applause or success that ignores the people who helped. The card asks for recognition without ego inflation.",
    journalPrompts: ["What achievement can I acknowledge without apology?", "Where am I depending too much on approval?", "Who should share in this success?"],
  },
  "Seven of Wands": {
    loveMeaning: "In love, Seven of Wands points to boundaries, defending a relationship or standing up for your position. It asks whether the fight is necessary or habitual.",
    careerMeaning: "At work, Seven of Wands favors defending your work, holding a position and staying steady under challenge. The high ground matters, but it still requires effort.",
    spiritualMeaning: "For inner growth, Seven of Wands teaches courage under pressure. It asks you to know what is worth protecting.",
    shadowMessage: "The shadow of Seven of Wands is defensiveness, exhaustion or treating every question as an attack. The card asks for strength with discernment.",
    journalPrompts: ["What is truly worth defending?", "Where am I reacting as if every challenge is a threat?", "What boundary would protect my energy without closing my heart?"],
  },
  "Eight of Wands": {
    loveMeaning: "In love, Eight of Wands points to quick messages, fast movement and momentum in a connection. It asks that speed not replace clarity.",
    careerMeaning: "At work, Eight of Wands favors rapid progress, communication, travel, launches or a decision moving through quickly. It is useful when preparation has already been done.",
    spiritualMeaning: "For inner growth, Eight of Wands teaches timing and directness. The wands are already in flight, so the lesson is how to respond cleanly.",
    shadowMessage: "The shadow of Eight of Wands is rushing, impatience or communication that moves faster than understanding. The card asks for speed with accuracy.",
    journalPrompts: ["What is moving faster than expected?", "Where do I need to respond directly?", "What detail must not be lost in the rush?"],
  },
  "Nine of Wands": {
    loveMeaning: "In love, Nine of Wands points to guardedness after past hurt. It supports caution but asks whether the wall is protecting the relationship or preventing it.",
    careerMeaning: "At work, Nine of Wands favors persistence, stamina and protecting hard won progress. It also warns of burnout from staying on alert too long.",
    spiritualMeaning: "For inner growth, Nine of Wands teaches resilience with self awareness. The bandage shows strength that has a history.",
    shadowMessage: "The shadow of Nine of Wands is suspicion, fatigue or expecting every new situation to repeat the old wound. The card asks for protection without permanent closure.",
    journalPrompts: ["What have I survived that still affects my response?", "Where is caution wise and where is it automatic?", "What would rest make possible now?"],
  },
  "Ten of Wands": {
    loveMeaning: "In love, Ten of Wands points to carrying too much responsibility alone. It asks whether duty has replaced honest connection.",
    careerMeaning: "At work, Ten of Wands shows overload, deadlines, heavy responsibility and the final push before completion. It also asks what can be delegated or set down.",
    spiritualMeaning: "For inner growth, Ten of Wands teaches the cost of carrying what no longer needs to be carried. Responsibility should serve life, not bury it.",
    shadowMessage: "The shadow of Ten of Wands is martyrdom, overcommitment or pride in exhaustion. The card asks for a more honest relationship with capacity.",
    journalPrompts: ["What load is actually mine?", "Where am I using busyness to avoid a clearer choice?", "What can be finished, shared or released?"],
  },
  "Page of Wands": {
    loveMeaning: "In love, Page of Wands points to flirtation, curiosity and the first signs of attraction. It is playful and honest when no one pretends it is more mature than it is.",
    careerMeaning: "At work, Page of Wands favors a message, idea, experiment or learning stage. It asks you to explore without needing mastery immediately.",
    spiritualMeaning: "For inner growth, Page of Wands teaches creative curiosity. The card asks what makes you want to move, learn and try.",
    shadowMessage: "The shadow of Page of Wands is scattered enthusiasm, lack of follow through or mistaking novelty for purpose. The card asks for curiosity with a next step.",
    journalPrompts: ["What am I genuinely curious about?", "Where do I need permission to be a beginner?", "What small experiment would teach me more than thinking?"],
  },
  "Knight of Wands": {
    loveMeaning: "In love, Knight of Wands points to passion, pursuit and movement. It can be exciting, but it needs consistency if the connection is meant to last.",
    careerMeaning: "At work, Knight of Wands favors bold action, travel, entrepreneurship or pushing a plan forward. It warns against acting faster than the facts allow.",
    spiritualMeaning: "For inner growth, Knight of Wands teaches courage in motion. Fire is useful when it has direction.",
    shadowMessage: "The shadow of Knight of Wands is impulsiveness, inconsistency or chasing the next thrill when effort becomes boring. The card asks for passion with responsibility.",
    journalPrompts: ["Where does action need more direction?", "What am I rushing because excitement is high?", "How can I keep courage from becoming recklessness?"],
  },
  "Queen of Wands": {
    loveMeaning: "In love, Queen of Wands points to confidence, warmth, attraction and independence. It favors desire that does not require shrinking yourself.",
    careerMeaning: "At work, Queen of Wands favors visibility, leadership, charisma and creative confidence. It asks you to trust your presence as part of your skill.",
    spiritualMeaning: "For inner growth, Queen of Wands teaches embodied confidence. The sunflower and black cat make the card both radiant and instinctive.",
    shadowMessage: "The shadow of Queen of Wands is jealousy, insecurity disguised as control or needing attention to feel powerful. The card asks for self trust that does not compete.",
    journalPrompts: ["Where am I allowed to take up space?", "What makes my confidence feel natural rather than performed?", "Where does comparison steal my fire?"],
  },
  "King of Wands": {
    loveMeaning: "In love, King of Wands points to direct desire, confidence and mature leadership in the relationship. It is strongest when vision includes care for the other person.",
    careerMeaning: "At work, King of Wands favors entrepreneurship, leadership, long range vision and the ability to mobilize people. It asks you to lead from purpose rather than ego.",
    spiritualMeaning: "For inner growth, King of Wands teaches responsibility for your fire. Inspiration becomes meaningful when it can guide action over time.",
    shadowMessage: "The shadow of King of Wands is domination, impatience or making every room answer to your will. The card asks for leadership that creates space for others too.",
    journalPrompts: ["What vision am I responsible for leading?", "Where does my impatience weaken my influence?", "How can my fire serve more than my ego?"],
  },
  "Ace of Cups": {
    loveMeaning: "In love, Ace of Cups points to new feeling, emotional openness and the beginning of care. It is an opening of the heart, not a guarantee of long term compatibility.",
    careerMeaning: "At work, Ace of Cups favors creative inspiration, compassionate work or an opportunity that feels emotionally meaningful. It asks whether the work can hold real feeling.",
    spiritualMeaning: "For inner growth, Ace of Cups teaches receptivity and compassion. The overflowing cup shows emotion that can move outward without being forced.",
    shadowMessage: "The shadow of Ace of Cups is emotional blockage, idealizing a new feeling or giving from an empty cup. The card asks for openness with self care.",
    journalPrompts: ["What feeling is beginning honestly?", "Where am I ready to receive rather than perform?", "How can I keep an open heart without losing my center?"],
  },
  "Two of Cups": {
    loveMeaning: "In love, Two of Cups points to mutual attraction, respect and emotional exchange. It is strongest when both people are present and choosing each other freely.",
    careerMeaning: "At work, Two of Cups favors partnership, client trust, mediation and a shared agreement. The card asks for reciprocity rather than one sided effort.",
    spiritualMeaning: "For inner growth, Two of Cups teaches relationship as a mirror. It asks how connection can reveal your capacity for honesty and care.",
    shadowMessage: "The shadow of Two of Cups is imbalance, dependence or confusing chemistry with mutual respect. The card asks whether the exchange is truly shared.",
    journalPrompts: ["Where is reciprocity present and where is it missing?", "What does this connection mirror back to me?", "How can I offer care without abandoning equality?"],
  },
  "Three of Cups": {
    loveMeaning: "In love, Three of Cups points to friendship, celebration and support around the relationship. It can show joy shared with others, but it also asks whether the bond has depth beyond the social moment.",
    careerMeaning: "At work, Three of Cups favors collaboration, team morale, creative groups and celebrating progress. The card is practical because good work often depends on healthy relationships.",
    spiritualMeaning: "For inner growth, Three of Cups teaches the value of chosen community. The three figures show healing that can happen through honest support.",
    shadowMessage: "The shadow of Three of Cups is exclusion, gossip, overindulgence or relying on the group to avoid private truth. The card asks for celebration with sincerity.",
    journalPrompts: ["Who genuinely supports my growth?", "Where does social connection feel nourishing and where does it feel performative?", "What deserves celebration without losing depth?"],
  },
  "Four of Cups": {
    loveMeaning: "In love, Four of Cups points to emotional withdrawal, boredom or an offer that is hard to receive. It asks whether dissatisfaction is protecting you or closing you down.",
    careerMeaning: "At work, Four of Cups can show disengagement, a stale routine or missing an opportunity because motivation is low. It asks for reassessment before refusal.",
    spiritualMeaning: "For inner growth, Four of Cups teaches discernment between true disinterest and emotional numbness. The offered cup matters because help may not look exciting at first.",
    shadowMessage: "The shadow of Four of Cups is sulking, apathy or rejecting care because it does not arrive in the preferred form. The card asks you to notice what is actually being offered.",
    journalPrompts: ["What am I refusing to receive?", "Where is boredom hiding a deeper need?", "What offer deserves a second look?"],
  },
  "Five of Cups": {
    loveMeaning: "In love, Five of Cups points to grief, regret or disappointment after emotional loss. It honors pain while reminding you that not everything has been lost.",
    careerMeaning: "At work, Five of Cups can show disappointment, a failed plan or regret over a decision. It asks you to learn from the loss without making it your whole story.",
    spiritualMeaning: "For inner growth, Five of Cups teaches mourning and perspective. The upright cups behind the figure keep the card from becoming despair.",
    shadowMessage: "The shadow of Five of Cups is fixation on what went wrong, guilt that cannot repair anything or refusing the support still available. The card asks for grief that can move.",
    journalPrompts: ["What loss needs to be named honestly?", "What remains available even now?", "What lesson can I carry without carrying the whole pain?"],
  },
  "Six of Cups": {
    loveMeaning: "In love, Six of Cups points to tenderness, memory, past connections or the emotional tone of early attachment. It can show an old bond returning, but it asks for present day honesty.",
    careerMeaning: "At work, Six of Cups can show old skills, familiar support, former colleagues or work connected to care and memory. It asks what the past can teach without taking over.",
    spiritualMeaning: "For inner growth, Six of Cups teaches innocence, memory and emotional repair. The children in the image keep the card close to kindness.",
    shadowMessage: "The shadow of Six of Cups is nostalgia, romanticizing the past or treating memory as proof that something should return. The card asks you to remember clearly.",
    journalPrompts: ["What memory is influencing my choice now?", "Where does the past offer wisdom and where does it distort?", "What younger part of me needs kindness?"],
  },
  "Seven of Cups": {
    loveMeaning: "In love, Seven of Cups points to confusion, many options, fantasy or projection. It asks you to separate what is real from what is imagined.",
    careerMeaning: "At work, Seven of Cups can show too many possibilities, unclear priorities or attractive ideas that need testing. It favors criteria before commitment.",
    spiritualMeaning: "For inner growth, Seven of Cups teaches discernment inside imagination. The cups hold different images because not every desire deserves the same trust.",
    shadowMessage: "The shadow of Seven of Cups is escapism, wishful thinking or avoiding choice by keeping every option alive. The card asks for clarity.",
    journalPrompts: ["Which option is real and which is fantasy?", "What criteria would make this choice clearer?", "Where am I using possibility to avoid commitment?"],
  },
  "Eight of Cups": {
    loveMeaning: "In love, Eight of Cups points to leaving what no longer nourishes you or searching for a deeper emotional truth. It asks for honesty about what is missing.",
    careerMeaning: "At work, Eight of Cups can show walking away from a role, project or goal that no longer feels meaningful. It is not quitting on impulse. It is leaving after recognizing emptiness.",
    spiritualMeaning: "For inner growth, Eight of Cups teaches the search for meaning beyond visible success. The abandoned cups show that having something is not the same as being fulfilled.",
    shadowMessage: "The shadow of Eight of Cups is abandoning too soon, disappearing without honesty or returning to what you already know is empty. The card asks for a clean departure or a clear reason to stay.",
    journalPrompts: ["What no longer nourishes me?", "What am I seeking that comfort cannot provide?", "How can I leave or stay with more honesty?"],
  },
  "Nine of Cups": {
    loveMeaning: "In love, Nine of Cups points to satisfaction, pleasure and emotional self worth. It supports happiness but asks whether contentment is shared or mostly self focused.",
    careerMeaning: "At work, Nine of Cups can show reward, recognition, comfort or a goal being met. It asks you to enjoy progress without becoming complacent.",
    spiritualMeaning: "For inner growth, Nine of Cups teaches gratitude and enoughness. The seated figure asks what genuine satisfaction feels like in the body.",
    shadowMessage: "The shadow of Nine of Cups is smugness, overindulgence or confusing comfort with deep fulfillment. The card asks for pleasure with humility.",
    journalPrompts: ["What satisfaction is real and earned?", "Where am I mistaking comfort for fulfillment?", "How can I enjoy this without closing my heart?"],
  },
  "Ten of Cups": {
    loveMeaning: "In love, Ten of Cups points to shared happiness, family harmony, emotional safety and a vision of life together. It asks whether the ideal is lived in daily behavior.",
    careerMeaning: "At work, Ten of Cups favors supportive teams, meaningful culture and success that benefits more than one person. It asks what kind of environment people can thrive in.",
    spiritualMeaning: "For inner growth, Ten of Cups teaches gratitude, belonging and emotional wholeness within community. The rainbow shows joy that is shared.",
    shadowMessage: "The shadow of Ten of Cups is idealizing a perfect picture, hiding conflict for appearances or expecting others to complete your happiness. The card asks for real harmony, not a performance of it.",
    journalPrompts: ["What does emotional safety look like in daily life?", "Where am I chasing a picture instead of a reality?", "Who shares in the happiness I want to build?"],
  },
  "Page of Cups": {
    loveMeaning: "In love, Page of Cups points to a tender message, a crush, an apology or emotional curiosity. It is sincere when feelings are allowed to be young without being careless.",
    careerMeaning: "At work, Page of Cups favors creative ideas, intuitive hunches and emotionally intelligent communication. It asks you to take imagination seriously enough to test it.",
    spiritualMeaning: "For inner growth, Page of Cups teaches openness to feeling before it is fully understood. The fish from the cup makes the message surprising and alive.",
    shadowMessage: "The shadow of Page of Cups is immaturity, mixed signals or avoiding responsibility by staying innocent. The card asks for tenderness with honesty.",
    journalPrompts: ["What feeling is trying to speak?", "Where can I be sincere without being naive?", "What creative message should I not dismiss?"],
  },
  "Knight of Cups": {
    loveMeaning: "In love, Knight of Cups points to romance, pursuit, apology or an emotional offer. It asks whether the gesture is backed by consistent behavior.",
    careerMeaning: "At work, Knight of Cups favors creative proposals, mission driven work and acting on a vision. It warns against beautiful plans with no practical route.",
    spiritualMeaning: "For inner growth, Knight of Cups teaches movement guided by feeling. The cup is carried carefully, so emotion should be directed rather than spilled everywhere.",
    shadowMessage: "The shadow of Knight of Cups is fantasy, inconsistency, emotional persuasion or promising more than can be lived. The card asks for romance with responsibility.",
    journalPrompts: ["What offer is sincere enough to act on?", "Where does beauty need a practical plan?", "How can I follow my heart without abandoning discernment?"],
  },
  "Queen of Cups": {
    loveMeaning: "In love, Queen of Cups points to empathy, emotional depth and deep care. It asks for compassion with boundaries so the relationship does not become one person's emotional labor.",
    careerMeaning: "At work, Queen of Cups favors counseling, care, creative sensitivity and emotionally intelligent leadership. It asks you to read the room without absorbing the whole room.",
    spiritualMeaning: "For inner growth, Queen of Cups teaches intuition held with maturity. The covered cup shows that inner life can be deep and protected.",
    shadowMessage: "The shadow of Queen of Cups is codependency, emotional overwhelm or losing boundaries while trying to help. The card asks for care that includes yourself.",
    journalPrompts: ["Where do I feel deeply and need a boundary?", "What emotion belongs to me and what belongs to someone else?", "How can compassion stay clear?"],
  },
  "King of Cups": {
    loveMeaning: "In love, King of Cups points to emotional maturity, steadiness and care that can remain calm under pressure. It favors feelings expressed responsibly.",
    careerMeaning: "At work, King of Cups favors calm leadership, mediation, counseling and decisions made with both empathy and control. It asks you not to let stress rule the room.",
    spiritualMeaning: "For inner growth, King of Cups teaches emotional regulation. The throne on the water shows feeling present without being in charge of every action.",
    shadowMessage: "The shadow of King of Cups is emotional suppression, moodiness behind a calm mask or using composure to avoid vulnerability. The card asks for steady honesty.",
    journalPrompts: ["What feeling can I hold without being ruled by it?", "Where do I need to respond instead of react?", "How can maturity include vulnerability?"],
  },
  "Ace of Swords": {
    loveMeaning: "In love, Ace of Swords points to a clear conversation, truth coming forward or a decision that cuts through confusion. It asks for honesty without cruelty.",
    careerMeaning: "At work, Ace of Swords favors a new idea, strategy, contract, decision or breakthrough. It is strongest when clarity becomes action.",
    spiritualMeaning: "For inner growth, Ace of Swords teaches truth and mental clarity. The crown and blade make the card about insight that carries responsibility.",
    shadowMessage: "The shadow of Ace of Swords is harshness, overthinking or using truth as a weapon. The card asks for clean language and clean intent.",
    journalPrompts: ["What is the clearest truth I can name?", "Where do I need precision instead of intensity?", "How can honesty be direct and humane?"],
  },
  "Two of Swords": {
    loveMeaning: "In love, Two of Swords points to stalemate, avoidance or a decision neither person wants to make. It asks for the blindfold to come off gently but honestly.",
    careerMeaning: "At work, Two of Swords can show decision delay, competing options or a tense truce. It favors gathering enough information to choose rather than freezing.",
    spiritualMeaning: "For inner growth, Two of Swords teaches the cost of blocking emotion from thought. The sea behind the figure shows feelings are still present.",
    shadowMessage: "The shadow of Two of Swords is denial, emotional shutdown or calling indecision peace. The card asks for a choice that respects the facts and the heart.",
    journalPrompts: ["What decision am I delaying?", "What feeling am I keeping out of the conversation?", "What would become possible if I named both truths?"],
  },
  "Three of Swords": {
    loveMeaning: "In love, Three of Swords points to heartbreak, grief, betrayal or painful truth. It does not soften the pain, but it does make the wound visible enough to address.",
    careerMeaning: "At work, Three of Swords can show painful feedback, conflict, disappointment or a hard truth about a plan. It asks for honesty before repair.",
    spiritualMeaning: "For inner growth, Three of Swords teaches that grief is part of truth. The rain and heart make the card emotional and mental at once.",
    shadowMessage: "The shadow of Three of Swords is replaying the hurt, identifying with the wound or using pain to justify closing permanently. The card asks for grief that can become clarity.",
    journalPrompts: ["What pain needs to be acknowledged without exaggeration?", "What truth hurts because it matters?", "What would healing require after the truth is named?"],
  },
  "Four of Swords": {
    loveMeaning: "In love, Four of Swords points to rest, space and recovery before more discussion. It asks for a pause that heals rather than punishes.",
    careerMeaning: "At work, Four of Swords favors recovery, strategic pause, planning and stepping back before making a decision. Rest is part of the work here.",
    spiritualMeaning: "For inner growth, Four of Swords teaches stillness after conflict. The chapel like imagery makes reflection protective.",
    shadowMessage: "The shadow of Four of Swords is avoidance, emotional distance or resting so long that life stops moving. The card asks for restoration with an eventual return.",
    journalPrompts: ["What needs rest before it needs action?", "Where is silence healing and where is it avoidance?", "What would a useful pause make clearer?"],
  },
  "Five of Swords": {
    loveMeaning: "In love, Five of Swords points to arguments, pride or a win that damages trust. It asks whether being right is costing the relationship too much.",
    careerMeaning: "At work, Five of Swords can show office conflict, politics, competition or a decision that creates fallout. It asks you to consider the cost of victory.",
    spiritualMeaning: "For inner growth, Five of Swords teaches motive checking. The scene after conflict asks what kind of person you become when you win.",
    shadowMessage: "The shadow of Five of Swords is cruelty, humiliation, revenge or walking away with more swords than integrity. The card asks for repair where possible.",
    journalPrompts: ["What is the real cost of winning here?", "Where has pride replaced resolution?", "What would integrity ask after conflict?"],
  },
  "Six of Swords": {
    loveMeaning: "In love, Six of Swords points to transition, healing distance or moving away from a painful phase. It can be a quiet repair or a necessary departure.",
    careerMeaning: "At work, Six of Swords favors transition, relocation, gradual improvement or leaving a difficult environment. Progress may be quiet but real.",
    spiritualMeaning: "For inner growth, Six of Swords teaches passage. The boat carries the swords too, showing that healing still includes memory.",
    shadowMessage: "The shadow of Six of Swords is resisting help, dragging the old story into every new place or confusing distance with resolution. The card asks for movement with awareness.",
    journalPrompts: ["What am I moving away from?", "What burden is still traveling with me?", "Who or what can help me cross more safely?"],
  },
  "Seven of Swords": {
    loveMeaning: "In love, Seven of Swords points to secrecy, avoidance, strategy or someone not being fully direct. It asks what is being withheld and why.",
    careerMeaning: "At work, Seven of Swords can show strategy, discretion, shortcuts or questionable tactics. It favors careful planning but warns against dishonest methods.",
    spiritualMeaning: "For inner growth, Seven of Swords teaches integrity under pressure. The card asks whether cleverness is serving truth or hiding from it.",
    shadowMessage: "The shadow of Seven of Swords is deception, evasion or trying to escape consequences. The card asks for enough honesty to stop the pattern.",
    journalPrompts: ["What am I not saying directly?", "Where is strategy useful and where is it dishonest?", "What consequence am I trying to avoid?"],
  },
  "Eight of Swords": {
    loveMeaning: "In love, Eight of Swords points to feeling trapped, afraid to speak or convinced there are no options. It asks whether the restriction is external, internal or both.",
    careerMeaning: "At work, Eight of Swords can show self doubt, limited options, fear of making the wrong move or a role that feels confining. It asks for one practical opening.",
    spiritualMeaning: "For inner growth, Eight of Swords teaches that perception can become a prison. The gap between the swords matters because movement may still be possible.",
    shadowMessage: "The shadow of Eight of Swords is learned helplessness, overthinking or waiting for permission to move. The card asks for one small act of agency.",
    journalPrompts: ["What limitation is factual and what is assumed?", "Where is fear narrowing my view?", "What is one safe step I can take from here?"],
  },
  "Nine of Swords": {
    loveMeaning: "In love, Nine of Swords points to anxiety, guilt, regret or worry that grows in private. It asks for truth, support and perspective rather than silent spiraling.",
    careerMeaning: "At work, Nine of Swords can show stress, insomnia, fear of failure or a mind replaying mistakes. It asks you to separate the actual problem from the imagined catastrophe.",
    spiritualMeaning: "For inner growth, Nine of Swords teaches compassion for the anxious mind. The bedroom setting shows suffering that may be invisible to others.",
    shadowMessage: "The shadow of Nine of Swords is catastrophizing, shame or punishing yourself with thoughts that do not solve the issue. The card asks for care and reality testing.",
    journalPrompts: ["What is the actual fact and what is the fear?", "Who can help me bring this worry into daylight?", "What would compassion say to my mind tonight?"],
  },
  "Ten of Swords": {
    loveMeaning: "In love, Ten of Swords points to a painful ending, betrayal or the moment when denial can no longer continue. It asks for acceptance before rebuilding.",
    careerMeaning: "At work, Ten of Swords can show burnout, failure, a harsh ending or a plan reaching its limit. The dawn in the image matters because finality can also clear the ground.",
    spiritualMeaning: "For inner growth, Ten of Swords teaches that some endings are complete. The lesson is not to make the pain prettier, but to stop pretending it is not over.",
    shadowMessage: "The shadow of Ten of Swords is victim identity, dramatic finality or refusing to see the first light after the ending. The card asks for truth and recovery.",
    journalPrompts: ["What is truly over?", "Where am I adding extra swords through the story I tell?", "What recovery step becomes possible after acceptance?"],
  },
  "Page of Swords": {
    loveMeaning: "In love, Page of Swords points to questions, messages, curiosity and sometimes guarded communication. It asks for honesty without turning the relationship into an interrogation.",
    careerMeaning: "At work, Page of Swords favors research, study, writing, analysis and learning a new system. It is sharp beginner energy.",
    spiritualMeaning: "For inner growth, Page of Swords teaches alertness and mental curiosity. The wind in the card shows thought in motion.",
    shadowMessage: "The shadow of Page of Swords is gossip, suspicion or using questions to avoid vulnerability. The card asks for curiosity with respect.",
    journalPrompts: ["What question needs a direct answer?", "Where am I gathering facts and where am I feeding suspicion?", "How can I speak truth without hiding behind analysis?"],
  },
  "Knight of Swords": {
    loveMeaning: "In love, Knight of Swords points to fast conversations, confrontation or someone charging in with certainty. It asks that speed not crush listening.",
    careerMeaning: "At work, Knight of Swords favors urgent action, decisive communication and pushing through delay. It warns against skipping strategy because momentum feels powerful.",
    spiritualMeaning: "For inner growth, Knight of Swords teaches courage in thought and speech. The challenge is to keep truth connected to wisdom.",
    shadowMessage: "The shadow of Knight of Swords is recklessness, argument, certainty without context or words used like weapons. The card asks for speed with discipline.",
    journalPrompts: ["Where am I moving too fast to listen?", "What truth needs action and what needs timing?", "How can I be direct without becoming destructive?"],
  },
  "Queen of Swords": {
    loveMeaning: "In love, Queen of Swords points to boundaries, clarity and honest communication after experience. It favors truth spoken cleanly rather than emotionally blurred.",
    careerMeaning: "At work, Queen of Swords favors analysis, editing, strategy, leadership through discernment and cutting away confusion. It asks you to trust earned judgment.",
    spiritualMeaning: "For inner growth, Queen of Swords teaches clear seeing. Her open hand matters because truth can be firm without being closed.",
    shadowMessage: "The shadow of Queen of Swords is coldness, harsh criticism or using independence to avoid tenderness. The card asks for clarity that still has a heart.",
    journalPrompts: ["What boundary would make the truth easier to live?", "Where has experience sharpened my judgment?", "How can I stay clear without becoming closed?"],
  },
  "King of Swords": {
    loveMeaning: "In love, King of Swords points to mature communication, fairness and decisions made with a clear head. It asks that logic serve the relationship rather than replace feeling.",
    careerMeaning: "At work, King of Swords favors strategy, law, policy, leadership, contracts and high level decisions. It asks for principle over personal preference.",
    spiritualMeaning: "For inner growth, King of Swords teaches disciplined thought. The upright sword is authority held in service of truth.",
    shadowMessage: "The shadow of King of Swords is authoritarian judgment, emotional distance or using intellect to dominate. The card asks for wisdom with humility.",
    journalPrompts: ["What principle should guide this decision?", "Where do facts need to lead and where do feelings still matter?", "How can authority stay fair?"],
  },
  "Ace of Pentacles": {
    loveMeaning: "In love, Ace of Pentacles points to a practical new beginning, trust built through action and a connection that can become stable over time. It asks for patience with the seed stage.",
    careerMeaning: "At work, Ace of Pentacles favors a job offer, financial opportunity, new project, client or practical resource. It is potential that needs cultivation.",
    spiritualMeaning: "For inner growth, Ace of Pentacles teaches embodiment. The garden path shows that spiritual insight has to become lived practice.",
    shadowMessage: "The shadow of Ace of Pentacles is missed opportunity, poor planning or wanting results before tending the seed. The card asks for grounded follow through.",
    journalPrompts: ["What real opportunity is being offered?", "What first practical step would help it grow?", "Where does my body or daily routine need more care?"],
  },
  "Two of Pentacles": {
    loveMeaning: "In love, Two of Pentacles points to balancing priorities, schedules, responsibilities or emotional availability. It asks whether the relationship is being fit into life or genuinely supported by it.",
    careerMeaning: "At work, Two of Pentacles shows juggling tasks, money, deadlines or roles. It favors flexibility but warns against pretending overload is balance.",
    spiritualMeaning: "For inner growth, Two of Pentacles teaches adaptability. The infinity loop makes balance an ongoing movement, not a fixed pose.",
    shadowMessage: "The shadow of Two of Pentacles is overcommitment, avoidance through busyness or keeping too many things in motion to choose. The card asks for honest capacity.",
    journalPrompts: ["What am I juggling and what needs to be set down?", "Where am I calling chaos flexibility?", "What rhythm would make balance more real?"],
  },
  "Three of Pentacles": {
    loveMeaning: "In love, Three of Pentacles points to teamwork, building trust and learning how to contribute together. It asks whether both people are willing to do the work.",
    careerMeaning: "At work, Three of Pentacles strongly favors collaboration, skilled craft, feedback and shared standards. The cathedral setting makes the work part of something lasting.",
    spiritualMeaning: "For inner growth, Three of Pentacles teaches humility through practice. Growth happens through skill, cooperation and learning from others.",
    shadowMessage: "The shadow of Three of Pentacles is refusing feedback, poor teamwork or wanting recognition without improving the craft. The card asks for teachability.",
    journalPrompts: ["Who can help me build this better?", "Where do I need feedback instead of praise?", "What skill deserves more disciplined practice?"],
  },
  "Four of Pentacles": {
    loveMeaning: "In love, Four of Pentacles points to guardedness, possessiveness or needing security before opening more. It asks whether protection has become control.",
    careerMeaning: "At work, Four of Pentacles favors saving, budgeting and protecting resources. It warns against scarcity thinking that blocks growth or collaboration.",
    spiritualMeaning: "For inner growth, Four of Pentacles teaches your relationship with enough. The figure holds tightly because safety and fear have become tangled.",
    shadowMessage: "The shadow of Four of Pentacles is greed, control, fear of loss or measuring worth only through what can be held. The card asks for security without contraction.",
    journalPrompts: ["What am I holding too tightly?", "Where is fear pretending to be responsibility?", "What would enough feel like in practice?"],
  },
  "Five of Pentacles": {
    loveMeaning: "In love, Five of Pentacles points to feeling left out, unsupported or under strain together. It asks whether help is available but not being reached for.",
    careerMeaning: "At work, Five of Pentacles can show financial pressure, job insecurity, resource gaps or feeling excluded. It asks for practical support rather than silent endurance.",
    spiritualMeaning: "For inner growth, Five of Pentacles teaches humility and the courage to seek help. The lit window matters because hardship is not the only thing in the scene.",
    shadowMessage: "The shadow of Five of Pentacles is isolation, shame or refusing assistance because need feels humiliating. The card asks you to look for the door, not just the cold.",
    journalPrompts: ["Where do I need support and have not asked?", "What resource is closer than I am admitting?", "How can I reduce shame around need?"],
  },
  "Six of Pentacles": {
    loveMeaning: "In love, Six of Pentacles points to giving, receiving and whether care is balanced. It asks if generosity is mutual or tied to control.",
    careerMeaning: "At work, Six of Pentacles favors fair pay, assistance, mentorship, grants, generosity and resource sharing. It asks you to notice power dynamics in the exchange.",
    spiritualMeaning: "For inner growth, Six of Pentacles teaches ethical generosity. The scales show that giving should be conscious, not self flattering.",
    shadowMessage: "The shadow of Six of Pentacles is strings attached, imbalance, savior behavior or resentment around receiving. The card asks for dignity on both sides.",
    journalPrompts: ["Where is giving balanced and where is it unequal?", "What help can I receive without shame?", "What motive sits underneath my generosity?"],
  },
  "Seven of Pentacles": {
    loveMeaning: "In love, Seven of Pentacles points to patience, investment and evaluating whether the relationship is growing. It asks for honest assessment rather than instant results.",
    careerMeaning: "At work, Seven of Pentacles favors long term projects, review, farming your effort and waiting for results. It asks whether the investment still deserves care.",
    spiritualMeaning: "For inner growth, Seven of Pentacles teaches timing. The vine grows slowly and asks you to respect process.",
    shadowMessage: "The shadow of Seven of Pentacles is impatience, sunk cost thinking or abandoning effort right before it can teach you something. The card asks for clear evaluation.",
    journalPrompts: ["What have I invested and what is it producing?", "Where do I need patience and where do I need a new plan?", "What result am I measuring too soon?"],
  },
  "Eight of Pentacles": {
    loveMeaning: "In love, Eight of Pentacles points to effort, consistency and learning how to love better through practice. It asks whether both people are willing to improve the craft of the relationship.",
    careerMeaning: "At work, Eight of Pentacles favors apprenticeship, repetition, mastery, study and strong workmanship. The card is about getting better by doing the work.",
    spiritualMeaning: "For inner growth, Eight of Pentacles teaches devotion through practice. Repetition becomes meaningful when attention stays present.",
    shadowMessage: "The shadow of Eight of Pentacles is perfectionism, workaholism or focusing on technique while forgetting the larger purpose. The card asks for dedicated work that stays alive.",
    journalPrompts: ["What skill needs steady practice?", "Where am I improving and where am I just repeating?", "How can devotion stay connected to purpose?"],
  },
  "Nine of Pentacles": {
    loveMeaning: "In love, Nine of Pentacles points to independence, self respect and enjoying your own life inside or outside partnership. It asks for love that does not erase autonomy.",
    careerMeaning: "At work, Nine of Pentacles favors earned success, self sufficiency, refined skill and enjoying the results of discipline. It is abundance built over time.",
    spiritualMeaning: "For inner growth, Nine of Pentacles teaches self possession. The garden and falcon show comfort shaped by discipline.",
    shadowMessage: "The shadow of Nine of Pentacles is isolation, dependence on status or comfort that keeps you emotionally separate. The card asks for independence with openness.",
    journalPrompts: ["What have I earned through patience?", "Where does independence protect me and where does it isolate me?", "How can I enjoy comfort without becoming closed?"],
  },
  "Ten of Pentacles": {
    loveMeaning: "In love, Ten of Pentacles points to long term commitment, family systems, shared resources and the legacy a relationship builds. It asks how private love fits into public life.",
    careerMeaning: "At work, Ten of Pentacles favors long term security, business structure, inheritance, family work or a project that can outlast the current moment. It asks for legacy thinking.",
    spiritualMeaning: "For inner growth, Ten of Pentacles teaches continuity. The elder, family and archway make the card about belonging across generations.",
    shadowMessage: "The shadow of Ten of Pentacles is inherited pressure, materialism, family control or choosing security at the cost of truth. The card asks what legacy is actually worth keeping.",
    journalPrompts: ["What am I building that could last?", "Which family or cultural pattern is shaping this choice?", "What legacy do I want to continue and what needs to end?"],
  },
  "Page of Pentacles": {
    loveMeaning: "In love, Page of Pentacles points to practical interest, reliability beginning to form and care shown through small actions. It is slow but sincere when effort is consistent.",
    careerMeaning: "At work, Page of Pentacles favors study, training, apprenticeship, job applications and learning how to make an opportunity real. It asks for focus.",
    spiritualMeaning: "For inner growth, Page of Pentacles teaches beginner discipline in the material world. The pentacle is studied carefully because growth needs attention.",
    shadowMessage: "The shadow of Page of Pentacles is lack of focus, procrastination or treating potential as achievement. The card asks for study and follow through.",
    journalPrompts: ["What practical skill am I ready to learn?", "Where do small actions prove sincerity?", "What opportunity needs focus before it can grow?"],
  },
  "Knight of Pentacles": {
    loveMeaning: "In love, Knight of Pentacles points to loyalty, steadiness and care shown over time. It can be slow, but the slowness is useful when it builds trust rather than avoids feeling.",
    careerMeaning: "At work, Knight of Pentacles favors routine, reliability, planning, maintenance and work that succeeds through persistence. It asks for patient execution.",
    spiritualMeaning: "For inner growth, Knight of Pentacles teaches devotion to the path in front of you. The still horse makes progress deliberate.",
    shadowMessage: "The shadow of Knight of Pentacles is stagnation, stubbornness or hiding fear behind routine. The card asks for consistency that still allows growth.",
    journalPrompts: ["What needs steady effort rather than speed?", "Where has routine become a hiding place?", "How can I make loyalty active instead of passive?"],
  },
  "Queen of Pentacles": {
    loveMeaning: "In love, Queen of Pentacles points to care through action, warmth, sensuality and practical support. It asks that nurturing include the self as well as the other person.",
    careerMeaning: "At work, Queen of Pentacles favors resource management, hospitality, caregiving, design, finances and grounded leadership. It asks how comfort and competence can work together.",
    spiritualMeaning: "For inner growth, Queen of Pentacles teaches sacred ordinary life. The rabbit, garden and pentacle keep the lesson close to body, home and care.",
    shadowMessage: "The shadow of Queen of Pentacles is self neglect, over caretaking or controlling others through usefulness. The card asks for generosity with boundaries.",
    journalPrompts: ["Where am I caring well and where am I disappearing into care?", "What does my body need from me?", "How can practical support stay generous without becoming control?"],
  },
  "King of Pentacles": {
    loveMeaning: "In love, King of Pentacles points to stability, loyalty and a desire to provide something lasting. It is healthy when security is generous rather than controlling.",
    careerMeaning: "At work, King of Pentacles favors business maturity, financial stewardship, management, property, resources and results built through experience. It asks for responsible use of power.",
    spiritualMeaning: "For inner growth, King of Pentacles teaches stewardship. The wealth in the card is meaningful when it protects life, not just status.",
    shadowMessage: "The shadow of King of Pentacles is greed, status obsession, rigidity or measuring people by usefulness. The card asks for success with conscience.",
    journalPrompts: ["What resources am I responsible for stewarding?", "Where does security become control?", "How can success serve life beyond myself?"],
  },
};

const meaningOverrides = {
  "The Fool": {
    upright: [
      "A real beginning: The Fool marks the first step into something untested, where experience is still forming and the path is not fully known.",
      "Trust with awareness: This card supports openness, curiosity and the willingness to begin before every detail is settled.",
      "Freedom from old limits: It can show a break from habit, expectation or fear that has kept life too small.",
      "Beginner's mind: The Fool asks for honesty, playfulness and a lighter grip on outcomes.",
      "A leap that needs care: The card favors movement, but the cliff in the image reminds you to notice risks before you step."
    ],
    reversed: [
      "Reckless movement: The Fool reversed warns of acting too quickly without enough information or respect for consequences.",
      "Fear of starting: It can also show a good opening being delayed because uncertainty feels too uncomfortable.",
      "Naivety: Someone may be overlooking obvious signs, trusting the wrong person or mistaking impulse for courage.",
      "Poor preparation: The situation may need a plan, a boundary or a clearer sense of direction before the leap.",
      "Wasted potential: The card can point to scattered choices that make a promising beginning harder to sustain."
    ]
  },
  "The Magician": {
    upright: [
      "Skill in action: The Magician shows ability, timing and the practical use of the tools already available.",
      "Focused will: This card favors clear intention followed by deliberate movement.",
      "Making something real: It points to the power to turn an idea into a visible result through craft, speech or effort.",
      "Resourcefulness: The table holds all four suits, so the card asks you to use mind, feeling, energy and material support together.",
      "Confident initiative: The Magician is not waiting for permission. It begins with what is at hand."
    ],
    reversed: [
      "Misused skill: The Magician reversed can point to manipulation, persuasive talk or talent used without integrity.",
      "Scattered effort: The tools are present, but attention is divided and nothing is being carried through.",
      "Blocked confidence: It may show doubt in abilities that are real but underused.",
      "Empty presentation: A polished image may be hiding weak substance or unfinished work.",
      "Poor alignment: Words, actions and intention may not be pointing in the same direction."
    ]
  },
  "The High Priestess": {
    upright: [
      "Inner knowing: The High Priestess points to quiet perception, intuition and information that is not yet fully spoken.",
      "Patient observation: This card favors watching, listening and letting facts reveal themselves in their own time.",
      "Hidden knowledge: It can show secrets, private motives or deeper layers beneath the obvious story.",
      "Trusting subtle signs: Dreams, body responses and repeated patterns may matter more than direct explanation.",
      "Wisdom through restraint: The High Priestess asks you to pause before forcing an answer."
    ],
    reversed: [
      "Ignoring intuition: The High Priestess reversed warns that a quiet truth is being dismissed or rationalized away.",
      "Secrets causing strain: Hidden information may be creating mistrust, confusion or distance.",
      "Surface knowledge: The situation may look clear from outside but lacks real depth or context.",
      "Emotional reactivity: A private feeling may be pushing decisions before it has been understood.",
      "Silence used poorly: Holding back may protect the wrong thing or prevent an honest conversation."
    ]
  },
  "The Empress": {
    upright: [
      "Growth and care: The Empress represents fertility in the broad sense, including creativity, comfort, healing and steady development.",
      "Nurturing presence: This card favors warmth, generosity and support that helps life flourish.",
      "Creative production: It points to making, shaping and bringing something into form through patience and attention.",
      "Embodied abundance: The Empress values the body, the home, pleasure, nature and material ease.",
      "Receiving as well as giving: It asks for care that includes your own needs."
    ],
    reversed: [
      "Overgiving: The Empress reversed warns of nurturing others while neglecting the self.",
      "Blocked growth: A project, relationship or healing process may be stalled because it is not being properly tended.",
      "Smothering care: Help may become control when another person is not allowed space to grow.",
      "Disconnection from the body: The card can show exhaustion, numbness or ignoring practical physical needs.",
      "Scarcity around comfort: Pleasure, support or rest may feel undeserved even when they are needed."
    ]
  },
  "The Emperor": {
    upright: [
      "Structure and order: The Emperor represents leadership, boundaries and systems that make stability possible.",
      "Responsible authority: This card favors decisions made with maturity, protection and long term consequences in mind.",
      "Practical control: It asks for clear rules, strong foundations and a steady hand.",
      "Protection through discipline: The Emperor creates safety by naming what is allowed and what is not.",
      "Building something durable: It points to plans, commitments and responsibilities that can stand under pressure."
    ],
    reversed: [
      "Control without wisdom: The Emperor reversed warns of rigidity, domination or authority used to silence others.",
      "Weak structure: A situation may lack clear rules, follow through or reliable leadership.",
      "Resistance to discipline: Necessary boundaries may be rejected because they feel uncomfortable.",
      "Fear behind control: Someone may be clinging to power because uncertainty feels threatening.",
      "Unbalanced authority: The card can show harshness on one side or passivity on the other."
    ]
  },
  "The Hierophant": {
    upright: [
      "Tradition and teaching: The Hierophant points to established knowledge, mentors, institutions and shared values.",
      "Learning through structure: This card favors study, guidance and tested practices rather than improvising alone.",
      "Commitment to principle: It can show vows, agreements or beliefs that give life a moral framework.",
      "Community standards: The Hierophant asks how belonging, duty and social expectations are shaping the situation.",
      "Respect for lineage: It supports wisdom passed down through teachers, family, faith or culture."
    ],
    reversed: [
      "Questioning the system: The Hierophant reversed asks whether a rule or tradition still deserves obedience.",
      "Empty conformity: It can show following expectations without understanding or belief.",
      "Dogma: A person or institution may be mistaking authority for truth.",
      "Private path: The card can point to learning outside conventional approval.",
      "Weak guidance: Advice may be too rigid, outdated or disconnected from the lived situation."
    ]
  },
  "The Lovers": {
    upright: [
      "Meaningful choice: The Lovers is not only romance. It is the need to choose in alignment with values.",
      "Mutual attraction: In relationships it points to chemistry, trust and a bond that asks for honest participation.",
      "Shared values: The card favors connection where both people can stand openly as themselves.",
      "Union with consequences: A choice may bring two paths, people or parts of life together.",
      "Integrity in desire: The Lovers asks whether the heart and the decision are moving in the same direction."
    ],
    reversed: [
      "Misalignment: The Lovers reversed shows desire pulling one way while values or actions pull another.",
      "Avoided choice: A decision may be delayed because every option has a cost.",
      "Relationship imbalance: Attraction may be present but trust, honesty or commitment may be uneven.",
      "Divided loyalty: The card can point to conflicting promises or unclear priorities.",
      "Choosing from fear: A choice may be shaped by pressure, guilt or the need to please."
    ]
  },
  "The Chariot": {
    upright: [
      "Directed will: The Chariot shows progress through discipline, focus and control of competing forces.",
      "Victory through effort: It favors determination rather than luck.",
      "Self-command: The card asks you to steer emotions, impulses and outside pressure toward one clear aim.",
      "Momentum: It can show travel, advancement or a situation moving quickly once direction is set.",
      "Confidence under pressure: The Chariot succeeds by staying centered when the road is demanding."
    ],
    reversed: [
      "Loss of direction: The Chariot reversed shows force without a clear course.",
      "Competing impulses: Different needs, people or goals may be pulling the situation apart.",
      "Control issues: Someone may be pushing too hard or trying to win at the cost of wisdom.",
      "Stalled progress: Movement may be blocked because focus, discipline or coordination is missing.",
      "Emotional driving: Anger, fear or pride may be steering instead of judgment."
    ]
  },
  "Strength": {
    upright: [
      "Gentle courage: Strength shows bravery expressed through patience, compassion and self-control.",
      "Mastery of instinct: The woman and lion point to working with strong feelings rather than fighting them blindly.",
      "Quiet endurance: This card favors steady resilience over dramatic force.",
      "Kind influence: It can show the ability to calm conflict through trust, warmth and confidence.",
      "Inner steadiness: Strength asks you to meet pressure without losing yourself."
    ],
    reversed: [
      "Self-doubt: Strength reversed can show courage weakened by insecurity or shame.",
      "Force replacing patience: Someone may be trying to control what needs understanding.",
      "Unchecked emotion: Anger, fear or desire may be leading behavior before it is examined.",
      "Exhaustion: The card can point to strain from holding everything together for too long.",
      "Avoiding vulnerability: Real strength may require admitting tenderness, need or fear."
    ]
  },
  "The Hermit": {
    upright: [
      "Solitude with purpose: The Hermit points to withdrawal for reflection, study and honest self-inquiry.",
      "Inner guidance: The lantern suggests wisdom found by slowing down and looking carefully.",
      "Prudence: This card favors caution, discernment and not moving with the crowd.",
      "A search for truth: It can show a period of questioning what matters and what no longer fits.",
      "Mentorship: The Hermit may represent a guide, teacher or elder whose insight is earned through experience."
    ],
    reversed: [
      "Isolation: The Hermit reversed warns of withdrawing so far that support and perspective are lost.",
      "Avoiding reflection: Busyness or distraction may be preventing a necessary inner reckoning.",
      "Unhelpful secrecy: A private struggle may need conversation rather than silence.",
      "Fear of being seen: The card can show hiding wisdom, pain or need from others.",
      "Loneliness mistaken for independence: Solitude may have stopped serving its purpose."
    ]
  },
  "Wheel of Fortune": {
    upright: [
      "A turning point: Wheel of Fortune marks change, cycles and movement beyond complete personal control.",
      "Timing: The card shows that conditions are shifting and the moment may matter as much as the plan.",
      "Change of circumstance: It can bring a rise, fall, surprise or reversal in the pattern.",
      "Learning from cycles: Repeated events may reveal how the same lesson is returning in a new form.",
      "Adaptability: The best response is awareness, flexibility and respect for what is changing."
    ],
    reversed: [
      "Resistance to change: Wheel of Fortune reversed warns of clinging to a cycle that is already turning.",
      "Repeated pattern: The same issue may return because its lesson has not been faced.",
      "Unsteady timing: Plans may be affected by delays, chance events or changing conditions.",
      "Feeling powerless: The card can show frustration when control is limited.",
      "A downturn that teaches: Reversal may reveal where flexibility and humility are needed."
    ]
  },
  "Justice": {
    upright: [
      "Truth and fairness: Justice points to facts, accountability and consequences that match actions.",
      "Clear judgment: This card favors evidence, honesty and careful weighing of both sides.",
      "Accountability: It asks what part each person has played and what must be corrected.",
      "Ethical decision: Justice supports choices made from principle rather than convenience.",
      "Cause and effect: The situation is likely showing the results of earlier behavior."
    ],
    reversed: [
      "Unfairness: Justice reversed can show bias, imbalance or a decision that does not honor the facts.",
      "Avoided responsibility: Someone may be refusing to own their part in the outcome.",
      "Distorted judgment: Emotion, loyalty or fear may be interfering with honesty.",
      "Legal or formal delays: The card can point to complications in agreements, rules or official processes.",
      "Harsh self-judgment: Accountability is needed, but punishment may be replacing understanding."
    ]
  },
  "The Hanged Man": {
    upright: [
      "Necessary pause: The Hanged Man shows suspension, waiting and the wisdom of not forcing movement.",
      "New perspective: The upside down figure asks you to see the matter from another angle.",
      "Letting go: This card favors release of control, especially when pressure has stopped helping.",
      "Sacrifice with purpose: Something may need to be surrendered so a deeper truth can emerge.",
      "Patience in uncertainty: The card asks for stillness while the meaning of the delay becomes clear."
    ],
    reversed: [
      "Stalling without insight: The Hanged Man reversed warns of waiting that has become avoidance.",
      "Resisting surrender: A needed release may be delayed because control feels safer.",
      "Martyrdom: Someone may be sacrificing too much and calling it wisdom.",
      "Forced pause: Circumstances may create delay before the lesson is willingly accepted.",
      "Old perspective: The card can show clinging to the same view even though it no longer works."
    ]
  },
  "Death": {
    upright: [
      "An ending that matters: Death marks closure, release and the end of a chapter that cannot stay as it was.",
      "Transformation: The card points to change that clears the old so something different can take root.",
      "Letting go: It asks you to stop carrying what has already served its purpose.",
      "Transition: Death often shows the difficult middle between what is gone and what is not yet built.",
      "Honest finality: The card is clearer when treated as a serious ending rather than a dramatic threat."
    ],
    reversed: [
      "Clinging to the old: Death reversed warns that an ending is being resisted even though the pattern is spent.",
      "Delayed transition: Change may be happening slowly because release is incomplete.",
      "Fear of loss: The card can show holding on because the unknown feels worse than the familiar.",
      "Inertia: Something may remain technically alive while no longer growing.",
      "Private transformation: The outer situation may look the same while an inner ending is already underway."
    ]
  },
  "Temperance": {
    upright: [
      "Balance through adjustment: Temperance points to moderation, patience and blending different needs into a workable whole.",
      "Healing rhythm: This card favors gradual improvement rather than extremes.",
      "Integration: It asks you to bring opposing parts into relationship instead of choosing only one side.",
      "Measured action: The best step is usually steady, proportionate and carefully timed.",
      "Cooperation: Temperance supports peace made through listening, compromise and practical calm."
    ],
    reversed: [
      "Imbalance: Temperance reversed warns of excess, impatience or a situation pulled too far to one side.",
      "Poor timing: The pace may be rushed, delayed or uneven.",
      "Clashing interests: Different needs may not be blending because no one is adjusting.",
      "Disconnection from moderation: All-or-nothing choices may be making the problem worse.",
      "Healing interrupted: The card can show recovery that needs steadier habits and more realistic expectations."
    ]
  },
  "The Devil": {
    upright: [
      "Binding attachment: The Devil points to patterns, desires or dependencies that limit freedom.",
      "Temptation and habit: It can show pleasure, power or comfort becoming difficult to question.",
      "Material fixation: The card asks where control, status, sex, money or fear has too much influence.",
      "Shadow made visible: The Devil brings honest attention to what is usually denied.",
      "Chains that can be examined: The figures are bound, but the card asks whether the bondage is as fixed as it seems."
    ],
    reversed: [
      "Recognizing the chain: The Devil reversed can show awareness of an unhealthy pattern beginning to break through.",
      "Release from compulsion: It supports detachment, recovery and choosing differently.",
      "Denial wearing thin: A truth about dependence, control or avoidance may be harder to ignore.",
      "Relapse risk: Freedom may be possible, but the old pattern still has pull.",
      "Shame losing power: The card asks for honest responsibility without turning the struggle into self-hatred."
    ]
  },
  "The Tower": {
    upright: [
      "Sudden disruption: The Tower marks a shock, revelation or collapse of something built on weak ground.",
      "Truth breaking through: What was hidden, unstable or false may become impossible to deny.",
      "Forced change: The card often appears when change happens because avoidance has run out of room.",
      "Loss of false security: It can be painful, but it clears structures that could not hold.",
      "Aftermath and honesty: The Tower asks what can be rebuilt only after the truth is faced."
    ],
    reversed: [
      "Delayed upheaval: The Tower reversed warns that disruption may be building beneath the surface.",
      "Avoiding the truth: Someone may sense a problem but keep protecting the unstable structure.",
      "A smaller collapse: The damage may be contained if the lesson is faced early.",
      "Fear of change: The card can show clinging to a false sense of safety.",
      "Rebuilding carefully: After a shock, the reversal asks for repair based on honesty rather than denial."
    ]
  },
  "The Star": {
    upright: [
      "Renewed hope: The Star points to healing, calm and the return of faith after difficulty.",
      "Clear inspiration: It favors sincerity, openness and a future that feels possible again.",
      "Restoration: The card can show recovery after loss, illness, stress or emotional strain.",
      "Guidance without pressure: The Star offers direction through quiet confidence rather than urgency.",
      "Vulnerability that heals: The uncovered figure suggests honesty, simplicity and trust in the process."
    ],
    reversed: [
      "Discouragement: The Star reversed can show hope feeling distant even when healing is still possible.",
      "Loss of faith: A person may struggle to believe that recovery, trust or renewal can return.",
      "Disconnected from support: Help may be available but not recognized or received.",
      "False optimism: The card can warn against pretending everything is healed before it is.",
      "Healing delayed: The process may need more rest, honesty and patience than expected."
    ]
  },
  "The Moon": {
    upright: [
      "Uncertainty: The Moon points to confusion, partial information and a path that is hard to read clearly.",
      "Illusion and fear: It asks what anxiety, projection or secrecy may be distorting the situation.",
      "Instinct and dream life: The card values intuition, but it also asks for careful testing of impressions.",
      "Hidden influence: Something beneath the surface may be shaping behavior more than facts alone.",
      "Moving through ambiguity: The Moon does not give instant clarity. It asks for patience until the fog thins."
    ],
    reversed: [
      "Confusion lifting: The Moon reversed can show fear or illusion beginning to lose its hold.",
      "Truth emerging slowly: More information may be coming out, but it may still need careful handling.",
      "Ignoring instinct: The reversal can warn against dismissing uneasy feelings that deserve attention.",
      "Anxiety exposed: A fear may be named clearly enough to work with.",
      "Misread signs: The card asks for grounding before treating intuition as certainty."
    ]
  },
  "The Sun": {
    upright: [
      "Clarity and vitality: The Sun points to openness, confidence and a situation becoming easier to see.",
      "Joy that is grounded: This card favors warmth, success and simple happiness that can be shared.",
      "Success and recognition: It often shows progress, visibility and a positive outcome earned through openness.",
      "Truth in daylight: The Sun asks for honesty without hidden games.",
      "Renewed life: The child and bright sky suggest health, growth and the ability to move freely."
    ],
    reversed: [
      "Muted joy: The Sun reversed can show happiness delayed, dimmed or harder to access.",
      "False brightness: Someone may be forcing positivity instead of naming a real concern.",
      "Ego in the way: Confidence may turn into pride, attention seeking or carelessness.",
      "Temporary delay: A good outcome may still be possible but needs patience or adjustment.",
      "Difficulty receiving good news: The card can show suspicion or sadness blocking simple relief."
    ]
  },
  "Judgement": {
    upright: [
      "Awakening: Judgement points to a reckoning, a call to rise and a new understanding of the past.",
      "Review and renewal: The card asks you to look honestly at what happened so the next step can be wiser.",
      "A decisive moment: It can show an important announcement, realization or choice that changes direction.",
      "Accountability without denial: Judgement favors truth that leads to renewal rather than punishment alone.",
      "Answering the call: The card asks whether you are ready to live from what you now know."
    ],
    reversed: [
      "Avoiding the reckoning: Judgement reversed warns that a necessary review is being delayed.",
      "Harsh self-judgment: The card can show shame blocking growth instead of helping repair.",
      "Unfinished business: Something from the past may need a clearer response before moving on.",
      "Ignoring the call: A person may know what needs to change but resist the responsibility.",
      "Clouded decision: The situation may require better honesty before a final choice is made."
    ]
  },
  "The World": {
    upright: [
      "Completion: The World marks fulfillment, integration and the closing of a major cycle.",
      "Wholeness: This card shows different parts of life coming together into a coherent result.",
      "Achievement: It can point to graduation, arrival, recognition or a goal finally reaching form.",
      "Expanded horizon: Travel, wider perspective or a larger stage may be involved.",
      "Readiness for the next cycle: The World celebrates completion while preparing the beginning that follows."
    ],
    reversed: [
      "Incomplete closure: The World reversed shows a cycle that is nearly finished but not fully integrated.",
      "Delayed fulfillment: A result may be close but still needs follow through.",
      "Loose ends: The card asks what remains unfinished before the next chapter can begin cleanly.",
      "Feeling outside the whole: Success may be present but not yet felt or owned.",
      "Repeating the ending: A pattern may keep circling because the lesson has not been absorbed."
    ]
  },
  "Ace of Wands": {
    upright: [
      "Creative spark: Ace of Wands shows a fresh surge of desire, courage or inspiration.",
      "A beginning with heat: This card favors action, invention and the first push toward something alive.",
      "Opportunity to initiate: It can point to a new project, attraction or personal calling that wants movement.",
      "Boldness: The Ace asks you to act before enthusiasm cools.",
      "Raw potential: The beginning is promising, but it still needs direction and care."
    ],
    reversed: [
      "Blocked motivation: Ace of Wands reversed shows inspiration present but hard to start.",
      "False start: A beginning may flare up quickly then fade without structure.",
      "Burnout: The card can warn that drive has been overused or scattered.",
      "Poor timing: The idea may need waiting, planning or a clearer reason to move.",
      "Lack of follow through: Desire alone is not enough to sustain the opportunity."
    ]
  },
  "Two of Wands": {
    upright: [
      "Planning ahead: Two of Wands shows vision, strategy and the first decision after inspiration.",
      "Standing at a threshold: The card asks whether you will stay with what is known or move toward a wider horizon.",
      "Personal power: It favors deliberate choice and confidence in long term direction.",
      "World in hand: The image points to ambition, travel, expansion or a larger field of possibility.",
      "Choosing the next move: The plan matters because the opportunity is still early."
    ],
    reversed: [
      "Hesitation: Two of Wands reversed shows a choice delayed by fear, comfort or uncertainty.",
      "Small thinking: The card can warn against shrinking the plan to avoid risk.",
      "Poor preparation: Ambition may be present but the route is not clear enough.",
      "Restlessness without action: Wanting more does not yet become a step.",
      "Fear of leaving the familiar: The known place may feel safe even when it limits growth."
    ]
  },
  "Three of Wands": {
    upright: [
      "Expansion: Three of Wands shows plans moving outward and early results beginning to appear.",
      "Foresight: This card favors watching the horizon, preparing for growth and thinking beyond the present moment.",
      "Enterprise: It can point to trade, travel, collaboration or a venture that reaches farther than before.",
      "Waiting for returns: The first work has been done and now timing matters.",
      "Confidence in direction: The card shows a person looking ahead because the path has begun to open."
    ],
    reversed: [
      "Delayed results: Three of Wands reversed shows expansion slowed by timing, logistics or weak follow through.",
      "Narrow horizon: The card can warn against expecting growth while refusing to look beyond the familiar.",
      "Poor coordination: Plans may suffer from missing support or unclear execution.",
      "Impatience: Waiting for results may lead to frustration before the process has matured.",
      "Opportunity missed through caution: The ship may pass if the next step is avoided too long."
    ]
  },
  "Four of Wands": {
    upright: [
      "Shared celebration: Four of Wands points to joy, welcome and a moment worth marking with others.",
      "Home and belonging: This card favors stability in family, community, partnership or place.",
      "A milestone: It can show marriage, gathering, completion of a stage or a return to safety.",
      "Supportive foundation: The wands create a threshold that feels protected and open.",
      "Ease after effort: The card invites rest and appreciation before the next task begins."
    ],
    reversed: [
      "Unsettled home: Four of Wands reversed can show instability in family, living situation or community.",
      "Delayed celebration: A milestone may be postponed, private or not fully enjoyed.",
      "Surface harmony: The outside may look happy while tension remains underneath.",
      "Lack of belonging: The card can point to feeling unwelcome or disconnected from a group.",
      "Foundation needing repair: Peace is possible, but it needs honest attention."
    ]
  },
  "Five of Wands": {
    upright: [
      "Active conflict: Five of Wands shows competition, tension and clashing efforts.",
      "Testing through friction: This card can be productive when conflict sharpens skill instead of becoming hostility.",
      "Different agendas: People may be moving at once without coordination.",
      "Creative struggle: The card can point to debate, challenge or practice that builds strength.",
      "Need for order: The situation may need rules, roles or a clearer goal."
    ],
    reversed: [
      "Conflict avoidance: Five of Wands reversed warns that tension may be hidden instead of resolved.",
      "Resolution possible: It can also show arguments cooling down and people ready to cooperate.",
      "Unfair competition: The struggle may become passive aggressive or poorly managed.",
      "Scattered energy: Effort is being wasted because no one is aligned.",
      "Choosing battles: The card asks which conflict is worth entering and which one only drains attention."
    ]
  },
  "Six of Wands": {
    upright: [
      "Recognition: Six of Wands shows success being seen, praised or publicly acknowledged.",
      "Victory after effort: This card favors confidence earned through challenge.",
      "Leadership by example: The rider is visible, so the card asks you to carry success with grace.",
      "Support from others: It can show a community, team or audience responding positively.",
      "Momentum from achievement: A win can open the next stage if pride stays balanced."
    ],
    reversed: [
      "Unseen effort: Six of Wands reversed can show work that succeeds privately but lacks recognition.",
      "Pride problem: The card warns of arrogance, image management or needing applause too much.",
      "Setback after victory: A public goal may be delayed, questioned or less secure than expected.",
      "Confidence shaken: The person may doubt a success that is still real.",
      "Measuring worth by approval: The card asks whether outside praise is driving the choices."
    ]
  },
  "Seven of Wands": {
    upright: [
      "Defending your ground: Seven of Wands shows pressure from challengers and the need to hold a position.",
      "Courage under challenge: This card favors resilience, alertness and standing up for what matters.",
      "Advantage with effort: The higher ground helps, but it still requires focus.",
      "Boundaries: The card asks what must be protected from intrusion, doubt or competition.",
      "A test of conviction: The situation reveals whether the position is worth defending."
    ],
    reversed: [
      "Exhausted defense: Seven of Wands reversed shows strain from always having to prove or protect something.",
      "Giving up ground: The card can warn of retreating too quickly under pressure.",
      "Defensiveness: Someone may be treating every question as an attack.",
      "Overwhelmed by challenge: The number of demands may exceed available strength.",
      "Choose the right stand: The reversal asks whether this fight still deserves your energy."
    ]
  },
  "Eight of Wands": {
    upright: [
      "Fast movement: Eight of Wands shows speed, momentum and events moving without much delay.",
      "Messages and travel: This card often points to communication, news, movement or plans taking flight.",
      "Clear direction: The wands move together, so the card favors alignment and direct progress.",
      "Acceleration: A slow situation may suddenly begin to advance.",
      "Act while the path is open: Timing matters because momentum may not last forever."
    ],
    reversed: [
      "Delays: Eight of Wands reversed shows messages, travel or plans slowed down.",
      "Rushed communication: Speed may cause mistakes, misunderstanding or careless words.",
      "Scattered movement: Too many things may be happening without coordination.",
      "Blocked momentum: A plan may need obstacles cleared before it can move.",
      "Impatience: The card warns against forcing speed when timing is not ready."
    ]
  },
  "Nine of Wands": {
    upright: [
      "Resilience: Nine of Wands shows endurance after repeated pressure or past wounds.",
      "Guarded strength: The figure is tired but still standing, so the card favors caution with courage.",
      "Boundaries earned by experience: It asks you to protect what matters without closing down completely.",
      "Last test: The situation may be near completion but still requires stamina.",
      "Preparedness: The card supports watchfulness, patience and refusing to quit too soon."
    ],
    reversed: [
      "Burnout: Nine of Wands reversed warns that endurance may be turning into exhaustion.",
      "Paranoia: Past wounds may make every new situation feel dangerous.",
      "Dropping the guard too soon: The card can show fatigue leading to poor boundaries.",
      "Defensive isolation: Protection may have become a wall against needed support.",
      "Questioning the fight: The reversal asks whether persistence is wise or simply habitual."
    ]
  },
  "Ten of Wands": {
    upright: [
      "Heavy responsibility: Ten of Wands shows burdens, obligations and the strain of carrying too much.",
      "Near completion: The figure is close to town, so the effort may be almost finished.",
      "Success with cost: A goal may be reached, but the load has become difficult to sustain.",
      "Overextension: The card asks what has been accepted that now needs to be shared or reduced.",
      "Duty and pressure: It favors honest limits before responsibility becomes resentment."
    ],
    reversed: [
      "Releasing the load: Ten of Wands reversed can show burdens being put down or delegated.",
      "Collapse from overwork: The card warns that the body or situation may force rest if limits are ignored.",
      "Unfair responsibility: Someone may be carrying work that belongs to others too.",
      "Avoiding duties: The reversal can also show dropping commitments without finishing what matters.",
      "Simplifying: The card asks what can be done with less weight and more clarity."
    ]
  },
  "Page of Wands": {
    upright: [
      "Fresh enthusiasm: Page of Wands shows curiosity, courage and the first exploration of a new idea.",
      "Creative message: This card can bring news, invitation or interest that sparks movement.",
      "Beginner confidence: The Page is still learning but willing to try.",
      "Exploration: It favors discovery, travel, experimentation and asking what else is possible.",
      "A spark to develop: The card asks for follow through so excitement becomes practice."
    ],
    reversed: [
      "Scattered excitement: Page of Wands reversed shows interest that jumps around without grounding.",
      "Immaturity: The card can warn of bold words without enough experience behind them.",
      "Delayed news: A message, plan or creative start may be slow to arrive.",
      "Fear of trying: Enthusiasm may be present but blocked by self-consciousness.",
      "No follow through: The idea needs discipline or it will remain only a spark."
    ]
  },
  "Knight of Wands": {
    upright: [
      "Bold action: Knight of Wands shows movement, passion and the drive to pursue a goal quickly.",
      "Adventure: This card favors travel, risk, attraction and energetic pursuit.",
      "Courage under motion: It asks you to use confidence without losing awareness.",
      "Restless ambition: The Knight wants progress and may resist staying still.",
      "Heat that needs direction: The card is strongest when passion has a real purpose."
    ],
    reversed: [
      "Impulsiveness: Knight of Wands reversed warns of rushing in without patience or follow through.",
      "Inconsistency: The card can show intense interest that disappears when effort is required.",
      "Reckless risk: Speed may create avoidable conflict, mistakes or regret.",
      "Frustrated movement: Plans may be blocked, delayed or scattered by impatience.",
      "Unreliable desire: The card asks whether excitement is being confused with commitment."
    ]
  },
  "Queen of Wands": {
    upright: [
      "Confident presence: Queen of Wands shows warmth, courage and a strong sense of self.",
      "Magnetism: This card favors charisma, creativity and the ability to encourage others.",
      "Independent fire: It supports leadership that remains generous without becoming dependent on approval.",
      "Trust in instinct: The Queen acts from clear inner confidence.",
      "Joyful determination: The card combines sociability with the will to keep going."
    ],
    reversed: [
      "Low confidence: Queen of Wands reversed can show self-doubt hidden beneath a strong exterior.",
      "Jealousy or comparison: The card warns against measuring worth through attention or rivalry.",
      "Demanding recognition: Confidence may slip into control, pride or resentment.",
      "Burned out charisma: Social energy may be depleted and need restoration.",
      "Blocked self-expression: The person may be afraid to take up space honestly."
    ]
  },
  "King of Wands": {
    upright: [
      "Visionary leadership: King of Wands shows bold direction, courage and the ability to inspire action.",
      "Entrepreneurial drive: This card favors initiative, expansion and a willingness to take responsibility for a big idea.",
      "Command of fire: The King channels passion into strategy rather than impulse.",
      "Confidence with purpose: It asks for leadership that moves people forward.",
      "Long range ambition: The card supports building from vision rather than reacting moment by moment."
    ],
    reversed: [
      "Domineering leadership: King of Wands reversed warns of impatience, control or a need to be obeyed.",
      "Poor follow through: A large vision may lack practical execution.",
      "Temper and pride: The card can show anger or ego driving decisions.",
      "Unrealistic ambition: Confidence may outrun facts, resources or timing.",
      "Leadership without listening: The reversal asks for humility before action."
    ]
  },
  "Ace of Cups": {
    upright: [
      "Emotional opening: Ace of Cups shows new feeling, compassion and the heart becoming available.",
      "Beginning of love or healing: This card can point to romance, forgiveness, friendship or renewed tenderness.",
      "Creative feeling: It favors art, prayer, empathy and expression that flows from sincerity.",
      "Receptivity: The cup is offered, so the card asks whether care can be received.",
      "Overflow: The Ace brings feeling that wants a healthy place to go."
    ],
    reversed: [
      "Blocked feeling: Ace of Cups reversed shows emotion held back, denied or difficult to express.",
      "Emotional overwhelm: The cup may feel too full to manage clearly.",
      "Withheld care: Love, apology or kindness may be present but not offered.",
      "Dryness after hurt: The card can point to numbness, disappointment or fear of opening again.",
      "Need for self-compassion: The reversal asks that tenderness begin inward before it is demanded elsewhere."
    ]
  },
  "Two of Cups": {
    upright: [
      "Mutual bond: Two of Cups shows partnership based on recognition, respect and emotional exchange.",
      "Reciprocity: This card favors equal effort and the willingness to meet another person honestly.",
      "Attraction with trust: It can point to romance, reconciliation, friendship or a meaningful agreement.",
      "Shared healing: The cups are exchanged, so both sides matter.",
      "Balanced connection: The card asks how care can stay mutual rather than one sided."
    ],
    reversed: [
      "Relationship imbalance: Two of Cups reversed shows unequal effort, mixed feelings or a bond out of rhythm.",
      "Separation or tension: The card can point to conflict, breakup, distance or trouble repairing trust.",
      "Poor communication: Feelings may be present but not exchanged clearly.",
      "Self-relationship strained: The reversal can show difficulty honoring your own needs in connection.",
      "Harmony needing repair: The card asks for honesty before closeness can return."
    ]
  },
  "Three of Cups": {
    upright: [
      "Friendship and community: Three of Cups shows shared joy, support and emotional belonging.",
      "Celebration: This card favors gatherings, reunions, teamwork and good news enjoyed together.",
      "Mutual encouragement: The figures raise cups as equals, showing care that circulates.",
      "Social healing: Connection may restore confidence after isolation or stress.",
      "Creative collaboration: The card supports people bringing warmth and talent into the same space."
    ],
    reversed: [
      "Social strain: Three of Cups reversed can show exclusion, gossip or tension in a group.",
      "Overindulgence: Celebration may become avoidance if limits are ignored.",
      "Feeling left out: The card can point to loneliness even around people.",
      "Unbalanced friendship: One person may give more support than they receive.",
      "Need for sincere connection: The reversal asks for fewer appearances and more honest belonging."
    ]
  },
  "Four of Cups": {
    upright: [
      "Emotional withdrawal: Four of Cups shows boredom, dissatisfaction or turning inward.",
      "Missed offer: A real opportunity may be present but hard to notice because attention is elsewhere.",
      "Reevaluation: The card asks what no longer satisfies and why.",
      "Protective pause: Disinterest may be a sign that rest or reflection is needed.",
      "Closed posture: The figure suggests that openness will not return through pressure alone."
    ],
    reversed: [
      "Reengagement: Four of Cups reversed can show interest returning after a period of withdrawal.",
      "Seeing the offer: The card may point to finally recognizing help, love or opportunity.",
      "Restlessness: Dissatisfaction may push movement before the real need is understood.",
      "Ending apathy: The reversal supports choosing participation instead of staying closed off.",
      "Emotional reset: The card asks what kind of contact now feels honest."
    ]
  },
  "Five of Cups": {
    upright: [
      "Grief and regret: Five of Cups shows sorrow over what has been lost or spilled.",
      "Focus on loss: The figure sees the fallen cups while the standing cups remain unnoticed.",
      "Disappointment: This card often appears when expectation and reality have painfully separated.",
      "Need to mourn honestly: The loss should not be minimized or rushed.",
      "A bridge still exists: The card asks when attention can slowly turn toward what remains."
    ],
    reversed: [
      "Recovery after loss: Five of Cups reversed shows grief beginning to soften into acceptance.",
      "Turning toward what remains: The standing cups may finally become visible.",
      "Release of regret: The card supports learning from the past without living inside it.",
      "Difficulty moving on: The reversal can also show sorrow that keeps circling.",
      "Repair where possible: It asks whether apology, forgiveness or closure can help."
    ]
  },
  "Six of Cups": {
    upright: [
      "Memory and tenderness: Six of Cups points to nostalgia, kindness and the emotional influence of the past.",
      "Innocent exchange: The card favors simple generosity without hidden agenda.",
      "Childhood themes: It can show old places, family patterns or early experiences shaping the present.",
      "Return or reunion: A person, feeling or memory may come back into view.",
      "Comfort from familiarity: The card asks what from the past still nourishes you."
    ],
    reversed: [
      "Stuck in the past: Six of Cups reversed warns that memory may be idealized or hard to leave.",
      "Outgrowing old patterns: The card can show readiness to relate to the past with more maturity.",
      "Childish behavior: Someone may be avoiding adult responsibility through innocence or nostalgia.",
      "Uneven memory: What feels sweet may also hide unresolved pain.",
      "Moving forward gently: The reversal asks how to honor the past without living there."
    ]
  },
  "Seven of Cups": {
    upright: [
      "Many options: Seven of Cups shows imagination, possibility and choices that may not all be real.",
      "Illusion: The card warns that fantasy, desire or fear can make some cups look better than they are.",
      "Need for discernment: Not every option has the same value, cost or truth.",
      "Overwhelm by choice: Too much possibility can make action harder.",
      "Vision before commitment: The card asks you to separate dream from workable decision."
    ],
    reversed: [
      "Clarity returning: Seven of Cups reversed shows options narrowing and reality becoming easier to see.",
      "Poor choices exposed: A fantasy may lose its shine when facts appear.",
      "Decision needed: The card asks for commitment rather than endless imagining.",
      "Escapism ending or worsening: The reversal can show waking up from illusion or hiding deeper inside it.",
      "Grounded desire: It favors choosing what can actually be lived."
    ]
  },
  "Eight of Cups": {
    upright: [
      "Leaving what is incomplete: Eight of Cups shows walking away from something that no longer answers the heart.",
      "Search for deeper meaning: The card favors emotional honesty over staying for appearances.",
      "Necessary withdrawal: A person may need distance to understand what is missing.",
      "Renunciation: The cups remain standing, so the leaving is not always due to failure.",
      "Courage to seek more: The card asks whether comfort is enough."
    ],
    reversed: [
      "Fear of leaving: Eight of Cups reversed shows difficulty walking away even when fulfillment is missing.",
      "Returning to old cups: The card can point to revisiting a situation to see if it still has meaning.",
      "Avoiding the search: Staying busy may hide a deeper dissatisfaction.",
      "Unclear departure: A decision to leave may be delayed by guilt, hope or uncertainty.",
      "Need for emotional truth: The reversal asks what is really keeping you there."
    ]
  },
  "Nine of Cups": {
    upright: [
      "Satisfaction: Nine of Cups shows contentment, pleasure and a wish or goal reaching a rewarding place.",
      "Personal fulfillment: The card favors enjoying what has been earned or received.",
      "Emotional comfort: It can point to ease, gratitude and a period of enough.",
      "Confidence in desire: The seated figure suggests pride in what has been gathered.",
      "Enjoyment with awareness: The card asks you to savor success without becoming complacent."
    ],
    reversed: [
      "Empty satisfaction: Nine of Cups reversed warns that getting what was wanted may not bring the expected feeling.",
      "Overindulgence: Pleasure may become avoidance, excess or self-focus.",
      "Dissatisfaction: The card can show comfort on the outside with longing underneath.",
      "Wish delayed: A desired result may be close but not fully realized.",
      "Need for gratitude and depth: The reversal asks what would make fulfillment honest rather than performative."
    ]
  },
  "Ten of Cups": {
    upright: [
      "Emotional harmony: Ten of Cups shows shared happiness, family peace and a sense of belonging.",
      "Fulfillment with others: The card favors relationships where joy is not kept private.",
      "Home and support: It can point to family, chosen family or a community that feels safe.",
      "Long term happiness: The rainbow suggests a vision of emotional security and hope.",
      "Shared values: The card asks what kind of life can hold love over time."
    ],
    reversed: [
      "Family or group tension: Ten of Cups reversed shows disharmony where peace is expected.",
      "Unrealistic ideal: A perfect image may hide ordinary human problems.",
      "Private unhappiness: The outside may look whole while someone feels disconnected.",
      "Misaligned values: Love may be present but the shared vision may not match.",
      "Repairing belonging: The card asks what honest conversation would make home feel real again."
    ]
  },
  "Page of Cups": {
    upright: [
      "Tender message: Page of Cups shows emotional news, apology, invitation or a gentle opening.",
      "Creative sensitivity: This card favors imagination, art and honest feeling in an early form.",
      "Beginner heart: The Page is sincere but still learning emotional maturity.",
      "Surprise from feeling: The fish in the cup suggests something unexpected rising from within.",
      "Openness: The card asks you to stay receptive without losing discernment."
    ],
    reversed: [
      "Emotional immaturity: Page of Cups reversed can show feelings expressed poorly or avoided altogether.",
      "Blocked creativity: A sensitive idea may be dismissed before it develops.",
      "Unrealistic affection: The card warns of fantasy, crushes or promises that lack grounding.",
      "Withheld apology or message: Something tender may need to be said but is being delayed.",
      "Overreaction: The reversal asks for care with vulnerable feelings."
    ]
  },
  "Knight of Cups": {
    upright: [
      "Romantic movement: Knight of Cups shows an offer, invitation or pursuit led by feeling.",
      "Idealism: This card favors beauty, charm, art and the desire to follow the heart.",
      "Emotional quest: The Knight moves with purpose, but the purpose is shaped by longing.",
      "Graceful approach: It supports kindness, diplomacy and sincerity in expression.",
      "An offer to examine: The card asks whether the promise has depth as well as beauty."
    ],
    reversed: [
      "Unreliable charm: Knight of Cups reversed warns of attractive words without steady action.",
      "Emotional inconsistency: Feelings may change quickly or be difficult to trust.",
      "Escapist romance: The card can show fantasy replacing real commitment.",
      "Manipulative sweetness: Kindness may be used to avoid honesty.",
      "Disappointment in an offer: The reversal asks for evidence beneath the gesture."
    ]
  },
  "Queen of Cups": {
    upright: [
      "Deep empathy: Queen of Cups shows emotional intelligence, compassion and intuitive care.",
      "Listening heart: This card favors understanding feelings without rushing to fix them.",
      "Creative and psychic sensitivity: It can point to art, dreams and subtle emotional perception.",
      "Nurture with presence: The Queen offers care that is calm, receptive and sincere.",
      "Emotional maturity: The card asks for feeling guided by wisdom."
    ],
    reversed: [
      "Poor boundaries: Queen of Cups reversed warns of absorbing too much from others.",
      "Emotional overwhelm: Compassion may become exhaustion if there is no self-protection.",
      "Self-neglect: The card can show caring for everyone except yourself.",
      "Unclear intuition: Feelings may be strong but mixed with fear or projection.",
      "Emotional dependence: The reversal asks for care that does not lose itself."
    ]
  },
  "King of Cups": {
    upright: [
      "Emotional steadiness: King of Cups shows maturity, calm and the ability to feel deeply without being ruled by feeling.",
      "Compassionate leadership: This card favors guidance that is fair, kind and controlled.",
      "Wise restraint: The King does not deny emotion. He contains it responsibly.",
      "Support in difficulty: It can point to a counselor, mediator or person who remains steady under pressure.",
      "Balanced heart and judgment: The card asks for kindness with clear limits."
    ],
    reversed: [
      "Emotional suppression: King of Cups reversed can show feelings hidden until they leak out indirectly.",
      "Manipulation through calm: The card warns of using composure to control or avoid accountability.",
      "Instability under pressure: A steady image may crack when emotions rise.",
      "Detachment: Someone may be too distant, unavailable or hard to read.",
      "Need for honest regulation: The reversal asks for feelings to be named instead of managed through avoidance."
    ]
  },
  "Ace of Swords": {
    upright: [
      "Mental clarity: Ace of Swords shows truth, insight and the first clean cut through confusion.",
      "New idea: This card favors a decision, plan or conversation based on clear thinking.",
      "Breakthrough: It can point to facts becoming visible or a solution appearing sharply.",
      "Honest communication: The sword asks for direct words and intellectual integrity.",
      "Power of truth: The Ace can help, but it also asks that truth be handled responsibly."
    ],
    reversed: [
      "Confusion: Ace of Swords reversed shows clouded judgment, mixed messages or unclear facts.",
      "Harsh words: Truth may be used carelessly or without compassion.",
      "Blocked decision: The mind may know something needs to be named but cannot yet say it.",
      "Miscommunication: The card can point to assumptions, crossed wires or poor timing.",
      "False certainty: The reversal warns against calling an opinion a fact too quickly."
    ]
  },
  "Two of Swords": {
    upright: [
      "Stalemate: Two of Swords shows a decision paused because both sides feel difficult.",
      "Guarded judgment: The blindfold suggests blocking emotion or information to hold stillness.",
      "Need for balance: This card favors calm consideration before choosing.",
      "Avoided conflict: Peace may be maintained by not speaking about the real issue.",
      "Choice under pressure: The card asks what truth must be admitted before the swords can lower."
    ],
    reversed: [
      "Decision forced: Two of Swords reversed shows pressure building until avoidance no longer works.",
      "Emotional overwhelm: The blindfold may slip and feelings may rush in.",
      "Information imbalance: A choice may be made with incomplete or conflicting facts.",
      "Avoidance ending: The card can show a stalemate breaking because silence becomes impossible.",
      "Poor compromise: The reversal asks whether peace is being bought at the cost of honesty."
    ]
  },
  "Three of Swords": {
    upright: [
      "Painful truth: Three of Swords shows heartbreak, grief and the sharp recognition of hurt.",
      "Separation or loss: The card often points to sorrow involving love, trust or communication.",
      "Words that wound: It can show the impact of honesty, betrayal or realization.",
      "Necessary grief: The pain is real and needs space rather than denial.",
      "Clarity through sorrow: The card asks what truth the heart can no longer avoid."
    ],
    reversed: [
      "Healing after pain: Three of Swords reversed shows grief beginning to release or soften.",
      "Lingering hurt: The card can also point to pain that is still present beneath the surface.",
      "Forgiveness or repair: An apology, conversation or acceptance may be part of recovery.",
      "Avoided sorrow: The reversal warns that pretending to be fine can prolong the wound.",
      "Learning from heartbreak: The card asks what the pain has made clear."
    ]
  },
  "Four of Swords": {
    upright: [
      "Rest and recovery: Four of Swords shows pause after stress, conflict or mental strain.",
      "Quiet restoration: This card favors sleep, retreat, prayer, meditation or simply doing less.",
      "Protected stillness: The figure rests in a place of shelter, away from immediate pressure.",
      "Mental reset: It asks for space before deciding, speaking or fighting again.",
      "Healing time: The card reminds you that recovery is active work."
    ],
    reversed: [
      "Restlessness: Four of Swords reversed shows difficulty slowing down even when rest is needed.",
      "Burnout risk: The card warns that pushing through may damage recovery.",
      "Returning too soon: A person may reenter conflict before strength has returned.",
      "Awakening from retreat: The reversal can also show readiness to move after a necessary pause.",
      "Interrupted healing: The card asks what conditions would make real rest possible."
    ]
  },
  "Five of Swords": {
    upright: [
      "Hollow victory: Five of Swords shows conflict won at the cost of trust, dignity or peace.",
      "Defeat and resentment: Someone may walk away feeling humiliated or unheard.",
      "Unfair tactics: The card can point to sharp words, power plays or a need to be right.",
      "Aftermath of conflict: The real issue is what remains once the fight is over.",
      "Questionable gain: The card asks whether winning is worth the damage."
    ],
    reversed: [
      "Repair after conflict: Five of Swords reversed can show regret, apology or a desire to stop fighting.",
      "Conflict avoidance: It may also warn that peace is being kept by burying the truth.",
      "Learning from defeat: The card asks what the argument revealed about motives and limits.",
      "Letting go of the need to win: The reversal supports stepping back from a damaging contest.",
      "Unresolved resentment: The fight may be over outwardly but still active underneath."
    ]
  },
  "Six of Swords": {
    upright: [
      "Transition: Six of Swords shows moving from difficulty toward a calmer place.",
      "Recovery with baggage: The swords travel in the boat, so the past is carried but no longer ruling the same way.",
      "Guided passage: Help, distance or a change of environment may support healing.",
      "Leaving conflict: The card favors practical movement away from what cannot be solved where it stands.",
      "Quiet progress: It is not dramatic victory. It is safer passage."
    ],
    reversed: [
      "Stuck in transition: Six of Swords reversed shows difficulty leaving a troubled place or mindset.",
      "Carrying too much: Old thoughts may make the new shore harder to reach.",
      "Resistance to change: The card can show staying with the familiar because movement feels uncertain.",
      "Delayed travel or movement: Plans may be slowed by emotional or practical obstacles.",
      "Unfinished recovery: The reversal asks what needs closure before the crossing can continue."
    ]
  },
  "Seven of Swords": {
    upright: [
      "Strategy and secrecy: Seven of Swords shows planning, self-protection or acting outside direct confrontation.",
      "Hidden motives: The card asks what is being taken, avoided or left unsaid.",
      "Tactical movement: It can be clever when used for privacy, timing or careful escape.",
      "Dishonesty risk: The same strategy can become theft, evasion or betrayal.",
      "Partial truth: The figure carries some swords and leaves others, suggesting incomplete disclosure."
    ],
    reversed: [
      "Exposure: Seven of Swords reversed can show secrecy coming to light.",
      "Confession or correction: The card may support admitting what was hidden and repairing trust.",
      "Poor strategy: A plan may fail because it was too clever, risky or dishonest.",
      "Self-deception: The person may be hiding the truth even from themselves.",
      "Changing tactics: The reversal asks whether directness would now work better than avoidance."
    ]
  },
  "Eight of Swords": {
    upright: [
      "Mental restriction: Eight of Swords shows feeling trapped by fear, belief or limited perspective.",
      "Powerlessness that may not be total: The bindings are real but not impossible to loosen.",
      "Overthinking: The card points to thoughts that circle until options feel smaller than they are.",
      "Fear of movement: Not acting may feel safer than testing what is possible.",
      "Need for perspective: The card asks which restriction is external and which one has become internal."
    ],
    reversed: [
      "Seeing options: Eight of Swords reversed shows the beginning of release from fear or limiting thought.",
      "Freeing yourself: The card supports naming the trap and taking one practical step out of it.",
      "Lingering anxiety: Freedom may be possible before it feels comfortable.",
      "Victim mindset challenged: The reversal asks where agency can be reclaimed.",
      "Breaking a mental loop: The card favors facts, support and movement over rumination."
    ]
  },
  "Nine of Swords": {
    upright: [
      "Anxiety and distress: Nine of Swords shows worry, guilt or fear that keeps the mind awake.",
      "Pain in private: The figure sits alone, suggesting suffering that may not be visible to others.",
      "Mental torment: The card often appears when thoughts amplify pain beyond the immediate facts.",
      "Conscience and regret: A real concern may need attention rather than endless replay.",
      "Need for support: The card asks what would happen if the worry were spoken aloud."
    ],
    reversed: [
      "Relief beginning: Nine of Swords reversed can show anxiety easing as truth, support or perspective returns.",
      "Worry worsening: It may also warn that fear is deepening because it remains unaddressed.",
      "Asking for help: The reversal favors not carrying distress alone.",
      "Guilt examined: The card asks whether remorse can lead to repair instead of punishment.",
      "Naming the fear: What is spoken clearly may become more workable."
    ]
  },
  "Ten of Swords": {
    upright: [
      "Painful ending: Ten of Swords shows finality, betrayal or a collapse that cannot be ignored.",
      "Lowest point: The card often marks a hard stop after mental strain, conflict or denial.",
      "Truth after defeat: Something is over, and pretending otherwise only extends the pain.",
      "A new dawn in the distance: The ending is severe, but the sky suggests it is not the whole future.",
      "Release from the story: The card asks what can finally be laid down."
    ],
    reversed: [
      "Recovery after ending: Ten of Swords reversed shows the first movement after a painful close.",
      "Refusing finality: The card can warn of clinging to a story that is already done.",
      "Surviving betrayal: It supports rebuilding after harm without denying what happened.",
      "Lingering pain: The wounds may not be fresh, but they still need care.",
      "Letting the worst be over: The reversal asks how to stop reopening the ending."
    ]
  },
  "Page of Swords": {
    upright: [
      "Curious mind: Page of Swords shows alertness, study and the hunger to understand.",
      "New communication: This card can point to messages, questions or a truth beginning to form.",
      "Watchfulness: The Page notices details and stays ready.",
      "Mental courage: It favors speaking, learning and testing ideas.",
      "Immature but useful honesty: The card asks for curiosity with care."
    ],
    reversed: [
      "Gossip or prying: Page of Swords reversed warns of information used carelessly.",
      "Defensive communication: Questions may become arguments too quickly.",
      "Immature words: The card can show bluntness without enough understanding.",
      "Confused message: News may be delayed, incomplete or poorly delivered.",
      "Restless mind: The reversal asks for focus before speaking or acting."
    ]
  },
  "Knight of Swords": {
    upright: [
      "Fast decisive action: Knight of Swords shows urgency, ambition and a strong push toward a goal.",
      "Mental drive: This card favors directness, courage and pursuit of truth.",
      "Sharp focus: The Knight moves quickly because the objective feels clear.",
      "Confrontation when needed: It can support speaking up or cutting through delay.",
      "Speed with consequences: The card asks whether the rush still leaves room for judgment."
    ],
    reversed: [
      "Reckless haste: Knight of Swords reversed warns of charging ahead without listening.",
      "Harsh communication: Words may be accurate but damaging in delivery.",
      "Tunnel vision: A single goal may block awareness of people, timing or context.",
      "Scattered aggression: Energy may burst out in arguments rather than useful action.",
      "Poor strategy: The reversal asks for thought before the next move."
    ]
  },
  "Queen of Swords": {
    upright: [
      "Clear boundaries: Queen of Swords shows honesty, discernment and the courage to name what is true.",
      "Mature judgment: This card favors reason shaped by experience.",
      "Direct communication: The Queen speaks plainly without needing to be cruel.",
      "Independence: It can point to self-respect, emotional clarity and wise distance.",
      "Truth with perspective: The card asks for fairness that still protects the self."
    ],
    reversed: [
      "Harshness: Queen of Swords reversed warns of truth delivered with bitterness or contempt.",
      "Defensive distance: The card can show protection becoming emotional coldness.",
      "Clouded judgment: Pain may be shaping interpretation more than facts.",
      "Withheld honesty: Someone may avoid direct speech while acting from resentment.",
      "Need for balanced clarity: The reversal asks for truth without cruelty and compassion without self-betrayal."
    ]
  },
  "King of Swords": {
    upright: [
      "Intellectual authority: King of Swords shows logic, law, expertise and disciplined judgment.",
      "Clear standards: This card favors decisions based on principle, evidence and experience.",
      "Command of language: It can point to contracts, strategy, counsel or serious communication.",
      "Fair leadership: The King asks for authority used with impartiality.",
      "Truth above preference: The card supports reason when emotion or bias would distort the issue."
    ],
    reversed: [
      "Misuse of authority: King of Swords reversed warns of intellect used to dominate, intimidate or excuse harm.",
      "Bias dressed as logic: The card can show an argument that sounds rational but serves a private agenda.",
      "Cold judgment: Fairness may be missing because feeling has been dismissed entirely.",
      "Manipulative speech: Words may be used to control the outcome rather than clarify truth.",
      "Rigid thinking: The reversal asks for humility, evidence and a broader view."
    ]
  },
  "Ace of Pentacles": {
    upright: [
      "Material opportunity: Ace of Pentacles shows a new opening in work, money, health, home or practical life.",
      "Seed of stability: The card is promising, but it must be planted and cared for.",
      "Tangible beginning: It favors offers, resources, plans and commitments that can become real.",
      "Long term potential: The garden path suggests growth through patience.",
      "Practical blessing: The Ace asks what concrete step will help the opportunity take root."
    ],
    reversed: [
      "Missed opportunity: Ace of Pentacles reversed warns that a practical opening may be ignored or mishandled.",
      "Delayed start: Money, work or material plans may need more time or preparation.",
      "Poor investment: The card can show resources placed somewhere unstable.",
      "Lack of grounding: An idea may sound promising but lacks practical support.",
      "Need to reassess value: The reversal asks whether the offer is truly worth the cost."
    ]
  },
  "Two of Pentacles": {
    upright: [
      "Balancing demands: Two of Pentacles shows juggling time, money, duties or priorities.",
      "Adaptability: This card favors flexibility while conditions keep changing.",
      "Managing flow: The figure moves with the pentacles, showing that balance is active rather than fixed.",
      "Practical choices: It asks which responsibilities can realistically be held at once.",
      "Changing rhythm: The card supports adjustment before pressure becomes overload."
    ],
    reversed: [
      "Overwhelm: Two of Pentacles reversed shows too many demands and not enough capacity.",
      "Dropped responsibility: Something important may be neglected because attention is divided.",
      "Poor prioritizing: The card warns against treating every task as equally urgent.",
      "Financial or schedule strain: Practical pressure may need a clearer system.",
      "Need to simplify: The reversal asks what can be paused, delegated or released."
    ]
  },
  "Three of Pentacles": {
    upright: [
      "Skilled collaboration: Three of Pentacles shows teamwork, craft and respect for different roles.",
      "Learning through feedback: This card favors improvement guided by people who know the work.",
      "Building well: The image points to planning, structure and care in execution.",
      "Recognition of skill: It can show early respect for competence and contribution.",
      "Shared standards: The card asks whether everyone is working toward the same quality."
    ],
    reversed: [
      "Poor teamwork: Three of Pentacles reversed warns of weak communication, unclear roles or lack of cooperation.",
      "Ignored feedback: Skill may stall because advice is rejected or not offered well.",
      "Low quality work: The card can show shortcuts, weak planning or unfinished craft.",
      "Unequal contribution: One person may be carrying too much of the build.",
      "Need for alignment: The reversal asks for clearer expectations before progress continues."
    ]
  },
  "Four of Pentacles": {
    upright: [
      "Holding on: Four of Pentacles shows protection of money, security, time or control.",
      "Need for stability: The card favors saving and caution when resources matter.",
      "Fear of loss: The tight grip may show anxiety beneath the desire for safety.",
      "Control of boundaries: It can be wise to protect what has been earned.",
      "Security versus openness: The card asks whether holding on is helping or hardening."
    ],
    reversed: [
      "Letting go: Four of Pentacles reversed can show release of control, spending, sharing or opening up.",
      "Scarcity fear: The card may warn that fear of loss is still driving choices.",
      "Financial looseness: Resources may be leaking because boundaries are weak.",
      "Hoarding exposed: Protection may have become isolation or greed.",
      "Rebalancing security: The reversal asks what can be held wisely and what should be released."
    ]
  },
  "Five of Pentacles": {
    upright: [
      "Hardship: Five of Pentacles shows lack, illness, exclusion or a period of material strain.",
      "Feeling outside support: The figures pass a lit window, suggesting help may exist but feel unreachable.",
      "Practical vulnerability: Money, health, shelter or belonging may need attention.",
      "Shared difficulty: The card often shows people suffering together rather than alone.",
      "Need to seek help: It asks where pride, shame or exhaustion is blocking support."
    ],
    reversed: [
      "Recovery from hardship: Five of Pentacles reversed shows help, stability or hope beginning to return.",
      "Finding support: The card favors accepting assistance and reconnecting with resources.",
      "Shame easing: A difficult period may become easier once it is not hidden.",
      "Slow improvement: Material or health recovery may be gradual but real.",
      "Leaving the cold: The reversal asks what practical step moves life toward shelter."
    ]
  },
  "Six of Pentacles": {
    upright: [
      "Giving and receiving: Six of Pentacles shows generosity, support and the balance of resources.",
      "Fair distribution: This card favors help offered with respect and clear intention.",
      "Power in exchange: The standing figure has more, so the card asks how power is being used.",
      "Practical kindness: It can point to charity, payment, loan, mentorship or shared opportunity.",
      "Reciprocity over control: The card asks whether giving preserves dignity on both sides."
    ],
    reversed: [
      "Unequal exchange: Six of Pentacles reversed warns of generosity with strings attached or help that creates dependence.",
      "Unfair distribution: Resources, attention or credit may not be shared justly.",
      "Difficulty receiving: Pride or distrust may block useful support.",
      "Debt and obligation: The card can point to imbalance in money, favors or power.",
      "Need for cleaner terms: The reversal asks for honesty about what is being given and expected."
    ]
  },
  "Seven of Pentacles": {
    upright: [
      "Patience with results: Seven of Pentacles shows assessment after long effort.",
      "Long term growth: This card favors investment, cultivation and waiting for work to mature.",
      "Taking stock: It asks whether the harvest matches the effort being given.",
      "Delayed reward: Progress may be real even if it is not finished.",
      "Strategic pause: The card supports reviewing before deciding whether to continue, change method or harvest."
    ],
    reversed: [
      "Impatience: Seven of Pentacles reversed warns of giving up before growth has had time.",
      "Wasted effort: The card can also show work invested in something that is not paying back.",
      "Poor assessment: A situation may need clearer measurement and less wishful thinking.",
      "Unrealistic expectations: Growth may be judged too quickly or too harshly.",
      "Need to redirect labor: The reversal asks whether the effort still deserves continuation."
    ]
  },
  "Eight of Pentacles": {
    upright: [
      "Dedicated practice: Eight of Pentacles shows skill built through repetition and attention to detail.",
      "Apprenticeship: This card favors study, training and steady improvement.",
      "Quality work: The figure keeps making pentacles, showing pride in craft.",
      "Discipline: It asks for patience with the process rather than shortcuts.",
      "Meaningful mastery: The card supports doing the work well because the work matters."
    ],
    reversed: [
      "Careless work: Eight of Pentacles reversed warns of poor quality, shortcuts or lack of attention.",
      "Boredom with routine: Practice may feel dull because the purpose has been forgotten.",
      "Perfectionism: The card can show never finishing because nothing feels good enough.",
      "Misused effort: Hard work may be going toward the wrong skill or goal.",
      "Need to refine method: The reversal asks how to work smarter without abandoning discipline."
    ]
  },
  "Nine of Pentacles": {
    upright: [
      "Earned independence: Nine of Pentacles shows comfort, refinement and stability created through effort.",
      "Self-sufficiency: This card favors autonomy, discipline and the pleasure of standing on your own ground.",
      "Material ease: It can point to financial comfort, a beautiful environment or enjoying what has been cultivated.",
      "Standards and self-respect: The figure suggests knowing the value of time, taste and labor.",
      "Quiet success: The card asks you to enjoy achievement without needing to prove it."
    ],
    reversed: [
      "Insecure independence: Nine of Pentacles reversed can show comfort that feels unstable or unearned.",
      "Overspending or overvaluing appearance: The card warns against luxury used to cover anxiety.",
      "Dependence: Practical freedom may be limited by money, approval or relationship dynamics.",
      "Isolated success: Achievement may feel lonely if it has cut off connection.",
      "Need for self-worth beyond status: The reversal asks what makes life rich besides display."
    ]
  },
  "Ten of Pentacles": {
    upright: [
      "Long term security: Ten of Pentacles shows legacy, family, property and material continuity.",
      "Generational stability: This card favors systems that support more than one person or moment.",
      "Shared wealth: It can point to inheritance, business, home, tradition or lasting resources.",
      "Established structure: The card asks how stability is maintained and passed on.",
      "Belonging through roots: It values what is built carefully enough to outlast the present."
    ],
    reversed: [
      "Family or legacy tension: Ten of Pentacles reversed shows conflict around money, inheritance, property or tradition.",
      "Unstable foundation: The card can warn that long term security is less solid than it appears.",
      "Material pressure: Wealth or status may be shaping choices too heavily.",
      "Breaking from tradition: The reversal can show leaving a family pattern or inherited expectation.",
      "Need to protect the future: It asks whether present decisions truly support lasting stability."
    ]
  },
  "Page of Pentacles": {
    upright: [
      "Practical beginning: Page of Pentacles shows study, planning and the first serious step toward a material goal.",
      "Student mindset: This card favors learning, practice and respect for fundamentals.",
      "New opportunity: It can point to work, money, health or education beginning in a grounded way.",
      "Focus on the tangible: The Page studies the pentacle, asking what can be built in real life.",
      "Slow promise: The card supports patience with early progress."
    ],
    reversed: [
      "Lack of focus: Page of Pentacles reversed shows distraction from study, work or practical growth.",
      "Procrastination: A real opportunity may be delayed by avoidance or immaturity.",
      "Missed lesson: The card warns against wanting results without learning the basics.",
      "Weak planning: An idea may not yet have the structure to become real.",
      "Need for discipline: The reversal asks for a smaller plan that can actually be followed."
    ]
  },
  "Knight of Pentacles": {
    upright: [
      "Steady progress: Knight of Pentacles shows reliability, routine and patient effort.",
      "Duty and follow through: This card favors consistency more than speed.",
      "Practical service: It can point to work done carefully, responsibly and without much drama.",
      "Long term commitment: The Knight succeeds by returning to the task again and again.",
      "Trust through action: The card asks what can be proven by dependability."
    ],
    reversed: [
      "Stagnation: Knight of Pentacles reversed warns that caution may have become immobility.",
      "Stubborn routine: The card can show doing the same thing because change feels uncomfortable.",
      "Slow delays: Progress may be blocked by perfectionism, fear or lack of flexibility.",
      "Neglected duty: The reversal can also show laziness or unfinished practical responsibilities.",
      "Need for useful movement: It asks where steady effort should continue and where the method must change."
    ]
  },
  "Queen of Pentacles": {
    upright: [
      "Practical nurture: Queen of Pentacles shows care expressed through food, home, resources and steady support.",
      "Grounded abundance: This card favors comfort, competence and generosity rooted in real capacity.",
      "Resource management: It can point to finances, health, caregiving or work handled with warmth and skill.",
      "Body and home wisdom: The Queen asks what makes daily life more sustainable.",
      "Care with boundaries: The card supports giving that also protects the giver."
    ],
    reversed: [
      "Overcaretaking: Queen of Pentacles reversed warns of giving so much that personal needs disappear.",
      "Practical insecurity: Money, home or health worries may be driving choices.",
      "Neglect of self or space: The card can show daily life becoming disordered because care is stretched thin.",
      "Control through usefulness: Help may become a way to manage others.",
      "Need to restore the body: The reversal asks for grounded support, rest and realistic limits."
    ]
  },
  "King of Pentacles": {
    upright: [
      "Material mastery: King of Pentacles shows stability, wealth, stewardship and proven practical success.",
      "Responsible leadership: This card favors mature management of money, work, land, business or resources.",
      "Security through experience: The King builds slowly and protects what is worth keeping.",
      "Generous authority: It points to success that can support others without losing structure.",
      "Long term value: The card asks what kind of prosperity is strong enough to last."
    ],
    reversed: [
      "Greed or control: King of Pentacles reversed warns of measuring worth only by money, status or ownership.",
      "Poor management: Resources may be mishandled through rigidity, indulgence or short term thinking.",
      "Material insecurity: Fear of loss may make someone controlling or closed.",
      "Stubborn leadership: The card can show success that refuses to adapt.",
      "Need for ethical stewardship: The reversal asks whether power is serving life or only protecting itself."
    ]
  }
};

const readingOverrides = {
  "The Fool": `Read The Fool as the card of an untested beginning. In a spread, check whether the person is being invited into honest movement or using novelty to avoid responsibility. The surrounding cards show whether the leap is supported by real opportunity, pressure or escape.

Upright, it favors openness, curiosity and a first step before every detail is known. Reversed, it warns of poor preparation, naivety or fear of beginning. Ask yourself: What is actually ready to start and what still needs a boundary or basic plan?`,
  "The Magician": `Read The Magician as applied ability. In a spread, look for what tools, knowledge, contacts or timing are already available and whether they are being used well. This card is strongest when intention is followed by visible action.

Upright, it points to skill, focus and the power to make something real. Reversed, it can show manipulation, scattered effort or a polished image with weak substance. Ask yourself: What can be done with the resources already on the table and where must intention become action?`,
  "The High Priestess": `Read The High Priestess as the need to listen before acting. In a spread, she points to hidden information, private motives, silence or a truth that is felt before it is proven. Do not rush her into a simple answer.

Upright, she favors patience, observation and trust in subtle knowledge. Reversed, she can show secrets, ignored intuition or surface understanding. Ask yourself: What is being sensed but not said and what facts still need time to reveal themselves?`,
  "The Empress": `Read The Empress as growth that depends on care. In a spread, she asks what is being nourished, who is doing the nurturing and whether the conditions are healthy enough for something to flourish.

Upright, she favors creativity, comfort, fertility, generosity and embodied wellbeing. Reversed, she can show overgiving, neglect, smothering care or blocked growth. Ask yourself: What needs more care and where has care become control or self-neglect?`,
  "The Emperor": `Read The Emperor as structure, authority and protection. In a spread, check the rules, boundaries and leadership around the situation. The card often asks whether life needs firmer order or less rigid control.

Upright, he supports discipline, stability and responsible decisions. Reversed, he can show domination, weak leadership, rebellion against necessary limits or fear hidden behind control. Ask yourself: What structure would make this safer and what control has gone too far?`,
  "The Hierophant": `Read The Hierophant as tradition, teaching and shared values. In a spread, look at institutions, mentors, customs, vows or the rules a person has inherited. The question is whether those rules still carry wisdom.

Upright, he supports study, guidance, commitment and belonging. Reversed, he can show empty conformity, dogma or the need to learn outside approval. Ask yourself: Which belief or rule is genuinely useful and which one is only being followed out of habit?`,
  "The Lovers": `Read The Lovers as a values-based choice, not only romance. In a spread, look at attraction, trust, agreement and the consequences of choosing one path over another. The card asks whether desire and integrity are aligned.

Upright, it favors honest union, mutual respect and a meaningful decision. Reversed, it can show misalignment, avoidance, mixed motives or divided loyalty. Ask yourself: What choice reflects who I truly am and what choice only avoids discomfort?`,
  "The Chariot": `Read The Chariot as willpower under direction. In a spread, check whether the person has a clear goal and enough self-control to steer conflicting pressures. Movement alone is not the message. Directed movement is.

Upright, it favors discipline, momentum, confidence and victory through effort. Reversed, it can show force without direction, emotional driving or a plan pulled apart by competing aims. Ask yourself: What needs to be steered rather than forced?`,
  "Strength": `Read Strength as courage that stays gentle. In a spread, look at how the person is handling fear, anger, desire or vulnerability. The card is less about overpowering something and more about meeting it without losing control.

Upright, it favors patience, compassion, endurance and inner steadiness. Reversed, it can show self-doubt, exhaustion, harsh control or emotion acting before wisdom. Ask yourself: What needs kindness and firmness at the same time?`,
  "The Hermit": `Read The Hermit as purposeful withdrawal. In a spread, he asks whether solitude, study or distance from noise would reveal the truth. This card often appears when outside opinions have become louder than inner judgment.

Upright, it favors reflection, caution, mentorship and an honest search for meaning. Reversed, it can show isolation, avoidance or hiding from support. Ask yourself: Is this solitude helping me see clearly or keeping me away from life?`,
  "Wheel of Fortune": `Read Wheel of Fortune as a turning cycle. In a spread, look for timing, repeated patterns and circumstances that are moving beyond one person's control. The card asks how to respond wisely when conditions shift.

Upright, it favors change, opportunity, momentum and acceptance of cycles. Reversed, it can show resistance, delays, repeated lessons or frustration with uncertainty. Ask yourself: What is changing now and what pattern am I being asked to recognize?`,
  "Justice": `Read Justice as facts, accountability and fair consequence. In a spread, stay close to evidence rather than wishful thinking. This card asks what is true, what is owed and what decision would be clean enough to stand behind.

Upright, it favors honesty, balance, contracts, ethics and right judgment. Reversed, it can show bias, unfairness, avoidance of responsibility or harsh judgment. Ask yourself: What part of this situation needs truth before it needs emotion?`,
  "The Hanged Man": `Read The Hanged Man as a pause with purpose. In a spread, ask what cannot be solved by pushing harder. The card often shows delay, surrender or a need to see the matter from a completely different angle.

Upright, it favors patience, perspective and meaningful release. Reversed, it can show stalling, martyrdom or resistance to letting go. Ask yourself: What would become clear if I stopped trying to force the outcome?`,
  "Death": `Read Death as a serious ending and transition. In a spread, identify what has completed its purpose and what must be released for the next stage to be honest. This card is about closure, not drama for its own sake.

Upright, it favors transformation, release and the end of an old form. Reversed, it can show clinging, delayed change or fear of loss. Ask yourself: What is already over in practice, even if I have not accepted it yet?`,
  "Temperance": `Read Temperance as adjustment, moderation and healing pace. In a spread, look at what needs blending rather than choosing one extreme. This card often asks for cooperation between needs that seem opposed.

Upright, it favors balance, patience, recovery and wise timing. Reversed, it can show excess, impatience, poor coordination or clashing priorities. Ask yourself: What needs to be brought back into proportion?`,
  "The Devil": `Read The Devil as attachment that limits freedom. In a spread, look honestly at dependence, temptation, shame, control, fear or desire that has gained too much power. The card does not condemn. It exposes.

Upright, it shows the binding pattern clearly. Reversed, it can show awareness, release or the difficult work of breaking a habit. Ask yourself: What has control over this situation and what would real freedom require?`,
  "The Tower": `Read The Tower as truth breaking through a weak structure. In a spread, look for what has become unstable, false or impossible to maintain. This card often shows disruption that arrives because warning signs were ignored.

Upright, it points to shock, revelation and collapse of false security. Reversed, it can show delayed upheaval, denial or careful rebuilding after damage. Ask yourself: What foundation is failing and what truth can no longer be protected?`,
  "The Star": `Read The Star as recovery after strain. In a spread, look for where hope, trust and calm can return without pretending the hardship never happened. This card is gentle, but it is not empty optimism.

Upright, it favors healing, renewal, faith and sincere guidance. Reversed, it can show discouragement, false hope or feeling cut off from support. Ask yourself: What would help healing become real rather than only wished for?`,
  "The Moon": `Read The Moon as uncertainty, fear and hidden influence. In a spread, do not treat every feeling as fact. The card asks you to notice dreams, instincts and anxiety while still checking what can be verified.

Upright, it shows fog, illusion, intuition and subconscious pressure. Reversed, it can show confusion lifting or fear being exposed. Ask yourself: What am I imagining, what am I sensing and what do I actually know?`,
  "The Sun": `Read The Sun as clarity and visible life. In a spread, look for what becomes easier when it is brought into the open. This card favors honesty, warmth and a result that can be shared without secrecy.

Upright, it supports joy, success, confidence and vitality. Reversed, it can show muted happiness, delayed success or forced positivity. Ask yourself: What truth brings relief when it is seen plainly?`,
  "Judgement": `Read Judgement as a call to review and respond. In a spread, it asks what the past is teaching and what decision must follow from that awareness. This card is about reckoning that leads to renewal.

Upright, it favors awakening, accountability and a clear next calling. Reversed, it can show shame, avoidance or unfinished business. Ask yourself: What do I know now that I can no longer pretend not to know?`,
  "The World": `Read The World as completion and integration. In a spread, look for what has come full circle and what needs to be acknowledged before the next cycle begins. This card is not only an ending. It is the meaning of the journey gathered together.

Upright, it favors fulfillment, mastery, travel, closure and arrival. Reversed, it can show loose ends, delay or a lesson not fully absorbed. Ask yourself: What is complete and what still needs to be integrated?`,
  "Ace of Wands": `Read Ace of Wands as the first spark of action. In a spread, look for desire, inspiration or a new opening that wants movement. The card is raw potential, so the question is how to give the spark a real direction.

Upright, it favors initiative, attraction, creativity and courage. Reversed, it can show blocked drive, burnout or a false start. Ask yourself: What wants to begin and what would help the spark become sustained action?`,
  "Two of Wands": `Read Two of Wands as planning from the threshold. In a spread, it shows a person who has outgrown the first spark and now must choose a direction. Look at ambition, distance, risk and long term strategy.

Upright, it favors vision, preparation and deliberate expansion. Reversed, it can show hesitation, small thinking or weak planning. Ask yourself: What future am I actually preparing for and what familiar place is keeping me small?`,
  "Three of Wands": `Read Three of Wands as expansion already set in motion. In a spread, look at what has been launched and what is now returning through timing, trade, travel or wider contact. This card is patient but active.

Upright, it favors foresight, enterprise and waiting for results. Reversed, it can show delays, narrow horizons or poor follow through. Ask yourself: What has already been sent out and what must be managed while I wait?`,
  "Four of Wands": `Read Four of Wands as a stable threshold worth celebrating. In a spread, check home, belonging, community, partnership and milestones. This card asks whether the foundation feels welcoming as well as secure.

Upright, it favors celebration, reunion, peace and shared joy. Reversed, it can show unstable home life, delayed celebration or surface harmony. Ask yourself: What foundation supports joy and what needs repair before peace feels real?`,
  "Five of Wands": `Read Five of Wands as active friction. In a spread, identify whether conflict is useful practice, ego competition or disorganized effort. This card does not always mean harm. It often means everyone is swinging before agreeing on the goal.

Upright, it shows tension, rivalry and creative struggle. Reversed, it can show conflict avoidance, resolution or passive aggression. Ask yourself: What is the real disagreement and what rule or purpose would make the struggle useful?`,
  "Six of Wands": `Read Six of Wands as visible success. In a spread, look at recognition, reputation and the relationship between confidence and public response. The card asks how victory is being carried.

Upright, it favors praise, achievement, leadership and morale. Reversed, it can show unseen effort, pride, shaky confidence or dependence on approval. Ask yourself: What deserves recognition and how can success stay humble enough to last?`,
  "Seven of Wands": `Read Seven of Wands as the need to defend a position. In a spread, look for challenge after progress, pressure from others or a boundary that must be held. The card asks whether the stance is worth the effort.

Upright, it favors courage, persistence and holding ground. Reversed, it can show exhaustion, defensiveness or giving up too soon. Ask yourself: What am I protecting and is this fight still aligned with what matters?`,
  "Eight of Wands": `Read Eight of Wands as speed and direct movement. In a spread, look for messages, travel, fast decisions or a situation that has started moving after delay. Timing matters because momentum can pass quickly.

Upright, it favors swift progress, aligned action and clear communication. Reversed, it can show delays, rushed words or scattered motion. Ask yourself: What needs a quick response and what needs slowing down before mistakes happen?`,
  "Nine of Wands": `Read Nine of Wands as endurance after strain. In a spread, look at the history behind the current defense. This card often appears when someone is tired, wary and still close to finishing.

Upright, it favors resilience, boundaries and preparedness. Reversed, it can show burnout, paranoia or a guard that has become too heavy. Ask yourself: What protection is still necessary and what fear is left over from the past?`,
  "Ten of Wands": `Read Ten of Wands as responsibility that has become too heavy. In a spread, look at obligations, pressure, duty and the cost of success. The figure is close to the destination, but the load is still a problem.

Upright, it shows burden, overwork and completion under strain. Reversed, it can show release, collapse or the need to delegate. Ask yourself: What am I carrying that belongs to someone else or no longer needs to be carried?`,
  "Page of Wands": `Read Page of Wands as beginner fire. In a spread, look for curiosity, a message, a creative idea or the courage to explore before mastery is present. This card is sincere, but still early.

Upright, it favors enthusiasm, discovery and first attempts. Reversed, it can show scattered interest, immaturity or fear of trying. Ask yourself: What am I excited to explore and what small practice would make it real?`,
  "Knight of Wands": `Read Knight of Wands as fast moving fire. In a spread, look at passion, action, travel, risk and urgency. The card can be exciting or unstable depending on whether the energy has direction.

Upright, it favors bold movement, confidence and pursuit. Reversed, it can show impulsiveness, inconsistency or reckless desire. Ask yourself: Is this movement guided by purpose or only by the need for intensity?`,
  "Queen of Wands": `Read Queen of Wands as confident presence. In a spread, look at charisma, independence, warmth and the ability to encourage life around her. The card asks whether self-expression is generous and grounded.

Upright, it favors courage, magnetism, creativity and self-trust. Reversed, it can show comparison, jealousy, low confidence or attention seeking. Ask yourself: Where can I take up space without needing to compete?`,
  "King of Wands": `Read King of Wands as vision turned into leadership. In a spread, look at strategy, ambition and the responsibility that comes with influence. This card asks whether fire is being directed wisely.

Upright, it favors bold leadership, enterprise and long range confidence. Reversed, it can show domination, impatience or big plans without listening. Ask yourself: What vision needs leadership and where must power become more accountable?`,
  "Ace of Cups": `Read Ace of Cups as an emotional opening. In a spread, look for the first sign of love, healing, apology, creativity or compassion. The card shows feeling becoming available, but it still needs a healthy container.

Upright, it favors tenderness, new affection and emotional renewal. Reversed, it can show blocked feeling, overwhelm or withheld care. Ask yourself: What feeling is trying to open and what would make it safe to receive or express?`,
  "Two of Cups": `Read Two of Cups as mutual exchange. In a spread, focus on reciprocity, trust and whether two people are meeting each other honestly. The card is strongest when both sides are emotionally present.

Upright, it favors partnership, attraction, repair and shared respect. Reversed, it can show imbalance, distance or trouble with communication. Ask yourself: Is this connection mutual and what would restore honest exchange?`,
  "Three of Cups": `Read Three of Cups as belonging through shared joy. In a spread, look at friendship, group support, celebration and the emotional effect of community. This card asks whether connection is sincere or only social performance.

Upright, it favors reunion, encouragement, celebration and collaboration. Reversed, it can show gossip, exclusion, overindulgence or social strain. Ask yourself: Which connections restore me and which ones leave me feeling outside the circle?`,
  "Four of Cups": `Read Four of Cups as emotional withdrawal and reevaluation. In a spread, look at boredom, apathy, disappointment or the inability to notice an offer. The card asks what has stopped feeling meaningful.

Upright, it favors reflection and a pause before accepting more of the same. Reversed, it can show renewed interest, restlessness or finally seeing an opportunity. Ask yourself: What am I rejecting and is it wisdom or numbness?`,
  "Five of Cups": `Read Five of Cups as grief focused on what has been lost. In a spread, do not rush the person past sorrow. First identify the spill, then look for what remains standing and what bridge is still available.

Upright, it shows regret, mourning and disappointment. Reversed, it can show acceptance, recovery or difficulty releasing sorrow. Ask yourself: What needs to be grieved honestly and what still remains worth turning toward?`,
  "Six of Cups": `Read Six of Cups as the past returning through memory, kindness or old patterns. In a spread, look at childhood, nostalgia, family history and the way old feelings shape present choices.

Upright, it favors tenderness, reunion and simple generosity. Reversed, it can show being stuck in the past or outgrowing an old story. Ask yourself: What from the past still nourishes me and what keeps me from growing up?`,
  "Seven of Cups": `Read Seven of Cups as choice under illusion. In a spread, look at options, fantasies, fears and desires that make everything look possible but not equally real. The card asks for discernment before commitment.

Upright, it shows imagination, temptation and overwhelm by choice. Reversed, it can show clarity, decision or fantasy being exposed. Ask yourself: Which option can actually be lived and which one only looks good from a distance?`,
  "Eight of Cups": `Read Eight of Cups as leaving in search of deeper meaning. In a spread, ask what no longer satisfies even if it once mattered. The card often shows a departure that is emotional before it is practical.

Upright, it favors walking away, seeking truth and choosing depth over comfort. Reversed, it can show fear of leaving, returning or avoiding the real dissatisfaction. Ask yourself: What am I outgrowing and what keeps me attached?`,
  "Nine of Cups": `Read Nine of Cups as personal satisfaction. In a spread, look at desire fulfilled, comfort, pride and whether the result feels as good as it looks. This card asks how enjoyment is being held.

Upright, it favors contentment, pleasure and gratitude. Reversed, it can show emptiness beneath success, overindulgence or a wish that does not satisfy. Ask yourself: What do I truly want and would receiving it actually fulfill me?`,
  "Ten of Cups": `Read Ten of Cups as shared emotional fulfillment. In a spread, look at family, chosen family, community and the life people are trying to build together. The card asks whether the shared vision is real.

Upright, it favors harmony, belonging and lasting happiness with others. Reversed, it can show family tension, private unhappiness or an ideal that hides problems. Ask yourself: What version of happiness are we building and is everyone included honestly?`,
  "Page of Cups": `Read Page of Cups as a tender emotional beginning. In a spread, look for a message, apology, crush, creative impulse or feeling that is sincere but still young. The card asks for openness with discernment.

Upright, it favors sensitivity, imagination and gentle expression. Reversed, it can show immaturity, blocked creativity or fantasy mistaken for depth. Ask yourself: What feeling wants to be expressed and how can it stay honest?`,
  "Knight of Cups": `Read Knight of Cups as feeling in motion. In a spread, look at invitations, romance, charm, art and the pursuit of an ideal. The important question is whether the offer has substance beneath its beauty.

Upright, it favors sincere approach, romance and graceful expression. Reversed, it can show unreliable charm, emotional inconsistency or escapist fantasy. Ask yourself: Is this offer backed by action or only by attractive words?`,
  "Queen of Cups": `Read Queen of Cups as deep emotional understanding. In a spread, look at empathy, intuition, care and the ability to listen without rushing to fix. The card asks whether compassion has healthy boundaries.

Upright, it favors tenderness, creativity and mature emotional presence. Reversed, it can show overwhelm, self-neglect, blurred boundaries or projection. Ask yourself: What am I feeling that belongs to me and what am I absorbing from others?`,
  "King of Cups": `Read King of Cups as emotional maturity under pressure. In a spread, look for calm leadership, self-regulation and compassionate judgment. The card asks whether feelings can be honored without letting them rule the outcome.

Upright, it favors steadiness, wisdom and support during difficulty. Reversed, it can show suppression, manipulation or emotional instability hidden behind calm. Ask yourself: What feeling needs to be named before it can be managed well?`,
  "Ace of Swords": `Read Ace of Swords as a clean mental breakthrough. In a spread, look for truth, decision, direct speech or an idea that cuts through confusion. The card asks for precision, not aggression.

Upright, it favors clarity, honesty and a new intellectual path. Reversed, it can show confusion, harsh words, false certainty or blocked communication. Ask yourself: What fact needs to be named and how can truth be spoken responsibly?`,
  "Two of Swords": `Read Two of Swords as a decision being held in suspension. In a spread, look at what information or feeling has been blocked to keep the peace. The card often shows avoidance dressed as balance.

Upright, it favors calm consideration but warns against staying frozen. Reversed, it can show pressure, forced choice or emotional overwhelm. Ask yourself: What truth would I have to admit before this decision becomes possible?`,
  "Three of Swords": `Read Three of Swords as pain caused by truth, loss or separation. In a spread, do not soften the wound until it has been named. The card asks what has pierced the heart and what reality must now be faced.

Upright, it shows heartbreak, grief and painful clarity. Reversed, it can show healing, lingering hurt or the need for honest repair. Ask yourself: What hurts because it is true and what would begin the healing?`,
  "Four of Swords": `Read Four of Swords as necessary rest. In a spread, look at what has exhausted the mind and what kind of pause would restore perspective. The card asks for recovery before action.

Upright, it favors stillness, retreat, sleep and mental repair. Reversed, it can show restlessness, burnout or returning too soon. Ask yourself: What would real rest look like and what decision can wait until I am clearer?`,
  "Five of Swords": `Read Five of Swords as conflict with a cost. In a spread, look beyond who won and ask what trust, dignity or relationship was damaged. The card warns against victory that leaves everyone poorer.

Upright, it shows harsh conflict, ego, resentment and hollow success. Reversed, it can show regret, repair or buried tension. Ask yourself: What am I trying to win and what might be lost if I keep fighting this way?`,
  "Six of Swords": `Read Six of Swords as movement away from difficulty. In a spread, look at transition, recovery, travel or a practical change of environment. The past is not gone, but it is being carried toward calmer water.

Upright, it favors passage, support and gradual healing. Reversed, it can show resistance, delays or being stuck between places. Ask yourself: What would help this crossing continue and what baggage still needs attention?`,
  "Seven of Swords": `Read Seven of Swords as strategy, secrecy and partial truth. In a spread, ask what is being avoided, taken or hidden. The card can be wise self-protection or dishonest evasion, depending on context.

Upright, it shows tactics, privacy, avoidance or deceit. Reversed, it can show exposure, confession or failed strategy. Ask yourself: What is not being said and is secrecy protecting safety or protecting avoidance?`,
  "Eight of Swords": `Read Eight of Swords as restriction shaped by fear and thought. In a spread, look at where the person feels trapped and whether all the limits are as fixed as they seem. The card asks for perspective before panic.

Upright, it shows mental bondage, fear and limited options. Reversed, it can show release, agency and the first step out. Ask yourself: Which restriction is real and which one has become a belief?`,
  "Nine of Swords": `Read Nine of Swords as anxiety that has become private torment. In a spread, look at guilt, worry, sleeplessness and the thoughts that return when there is no distraction. The card asks for support and honest naming.

Upright, it shows distress, remorse and fear. Reversed, it can show relief, asking for help or worry getting worse if hidden. Ask yourself: What fear needs daylight and what support would make it less lonely?`,
  "Ten of Swords": `Read Ten of Swords as a painful finality. In a spread, look for an ending that cannot be reasoned away. The card is severe, but it also shows that the worst point is not the whole future.

Upright, it shows collapse, betrayal, exhaustion or a hard stop. Reversed, it can show recovery, survival or refusal to accept an ending. Ask yourself: What story is finished and how can I stop reopening the wound?`,
  "Page of Swords": `Read Page of Swords as a searching mind. In a spread, look for questions, messages, observation and the early stage of speaking or learning the truth. The card asks for curiosity with care.

Upright, it favors study, alertness and honest inquiry. Reversed, it can show gossip, defensiveness, prying or confused communication. Ask yourself: What information do I need and how can I seek it without creating unnecessary harm?`,
  "Knight of Swords": `Read Knight of Swords as urgent mental action. In a spread, look at speed, confrontation, ambition and the push to cut through delay. The card asks whether the rush is intelligent or merely forceful.

Upright, it favors decisive movement and direct speech. Reversed, it can show recklessness, tunnel vision or words that wound. Ask yourself: What needs swift action and what must be checked before I charge ahead?`,
  "Queen of Swords": `Read Queen of Swords as clear judgment shaped by experience. In a spread, look at boundaries, direct speech and the ability to separate fact from sentiment. The card asks for honesty without cruelty.

Upright, it favors discernment, independence and clean communication. Reversed, it can show bitterness, coldness, withheld truth or pain shaping judgment. Ask yourself: What boundary or truth needs to be named plainly?`,
  "King of Swords": `Read King of Swords as disciplined authority of the mind. In a spread, look at law, strategy, expertise, contracts and decisions that must be guided by principle. The card asks for reason without manipulation.

Upright, it favors logic, fairness, leadership and clear standards. Reversed, it can show bias, harsh control or intellect used to dominate. Ask yourself: What decision would still make sense if ego were removed?`,
  "Ace of Pentacles": `Read Ace of Pentacles as a tangible opportunity. In a spread, look for work, money, health, home or a practical seed that could grow if properly tended. The card asks what can become real.

Upright, it favors new resources, stability and grounded beginnings. Reversed, it can show delay, missed opportunity or poor investment. Ask yourself: What practical step would help this opportunity take root?`,
  "Two of Pentacles": `Read Two of Pentacles as active balance. In a spread, look at schedules, money, duties and the pressure of keeping several things moving. The card asks whether flexibility is helping or becoming overload.

Upright, it favors adaptation, rhythm and practical juggling. Reversed, it can show overwhelm, dropped responsibilities or poor priorities. Ask yourself: What must be simplified so the important things stay manageable?`,
  "Three of Pentacles": `Read Three of Pentacles as shared work and skill. In a spread, look at teamwork, feedback, planning and whether people respect each other's roles. The card asks if the build has enough cooperation and craft.

Upright, it favors collaboration, learning and quality work. Reversed, it can show weak teamwork, ignored advice or poor standards. Ask yourself: Who needs to be involved and what standard are we building toward?`,
  "Four of Pentacles": `Read Four of Pentacles as holding on for security. In a spread, look at money, control, boundaries and fear of loss. The card can show wise protection or a grip so tight that life cannot move.

Upright, it favors saving, caution and guarding resources. Reversed, it can show release, hoarding, scarcity fear or weak boundaries. Ask yourself: What is worth protecting and what am I gripping because I am afraid?`,
  "Five of Pentacles": `Read Five of Pentacles as material or emotional hardship. In a spread, look at illness, money pressure, exclusion, shame and the support that may be nearby but hard to receive. The card asks where help is needed.

Upright, it shows struggle, lack and feeling left outside. Reversed, it can show recovery, assistance and shame beginning to ease. Ask yourself: What support is available and what keeps me from reaching for it?`,
  "Six of Pentacles": `Read Six of Pentacles as the balance of giving and receiving. In a spread, look at generosity, money, favors, power and whether help preserves dignity. The card asks who has resources and how they are being shared.

Upright, it favors fairness, charity and practical support. Reversed, it can show strings attached, imbalance or difficulty receiving. Ask yourself: Is this exchange fair and what is being expected in return?`,
  "Seven of Pentacles": `Read Seven of Pentacles as assessment after effort. In a spread, look at what has been invested and whether the result is worth more time. The card asks for patience, but not blind persistence.

Upright, it favors long term growth, review and steady cultivation. Reversed, it can show impatience, wasted effort or a need to redirect labor. Ask yourself: Is this growing slowly or am I feeding the wrong thing?`,
  "Eight of Pentacles": `Read Eight of Pentacles as craft built through repetition. In a spread, look at training, quality, routine and the willingness to keep improving. The card asks whether the work is being done carefully enough to matter.

Upright, it favors practice, study and mastery through discipline. Reversed, it can show shortcuts, boredom, perfectionism or effort aimed at the wrong skill. Ask yourself: What would better practice look like today?`,
  "Nine of Pentacles": `Read Nine of Pentacles as earned independence. In a spread, look at self-sufficiency, comfort, standards and the relationship between success and solitude. The card asks whether the person can enjoy what they have built.

Upright, it favors refinement, security and quiet achievement. Reversed, it can show dependence, overspending, insecurity or isolated success. Ask yourself: What have I earned and what kind of freedom does it actually give me?`,
  "Ten of Pentacles": `Read Ten of Pentacles as legacy and long term structure. In a spread, look at family, property, business, inheritance, tradition and what will outlast the present moment. The card asks whether stability supports real life.

Upright, it favors lasting security, roots and shared resources. Reversed, it can show family conflict, unstable foundations or pressure from tradition. Ask yourself: What am I building that should last and who is affected by it?`,
  "Page of Pentacles": `Read Page of Pentacles as a practical student. In a spread, look at study, first jobs, financial plans, health habits or a grounded opportunity that needs attention. The card asks for beginner discipline.

Upright, it favors learning, planning and the first serious step. Reversed, it can show procrastination, distraction or wanting results without practice. Ask yourself: What basic skill or habit needs my full attention now?`,
  "Knight of Pentacles": `Read Knight of Pentacles as steady work over time. In a spread, look at routine, duty, reliability and progress that comes from consistency rather than speed. The card asks whether the method is still useful.

Upright, it favors patience, follow through and dependable effort. Reversed, it can show stagnation, stubbornness, delay or neglected responsibility. Ask yourself: Where does consistency help and where has routine become a hiding place?`,
  "Queen of Pentacles": `Read Queen of Pentacles as practical care. In a spread, look at home, body, money, food, caregiving and the ability to make daily life sustainable. The card asks whether nurture includes the giver too.

Upright, it favors grounded support, resource management and warm competence. Reversed, it can show overcaretaking, self-neglect or insecurity around home and resources. Ask yourself: What care is needed and what boundary protects the caregiver?`,
  "King of Pentacles": `Read King of Pentacles as mature stewardship. In a spread, look at money, work, property, leadership and the responsible use of success. The card asks whether prosperity is stable, ethical and useful.

Upright, it favors security, management, generosity and proven results. Reversed, it can show greed, rigidity, poor management or status used as control. Ask yourself: What resources am I responsible for and how should they be used well?`
};

const sourceReaderData = {
  "The Fool": {
    "url": "https://www.learntarot.com/maj00.htm",
    "keywords": [
      "Beginning",
      "Spontaneity",
      "Faith",
      "Apparent Folly"
    ],
    "actions": [
      "beginning",
      "entering a new phase",
      "striking out on a new path",
      "expanding horizons",
      "starting something new",
      "beginning an adventure",
      "going on a journey",
      "heading into the unknown",
      "being spontaneous",
      "living in the moment"
    ],
    "edges": []
  },
  "The Magician": {
    "url": "https://www.learntarot.com/maj01.htm",
    "keywords": [
      "Action",
      "Conscious Awareness",
      "Concentration",
      "Power"
    ],
    "actions": [
      "taking action",
      "doing what needs to be done",
      "realizing your potential",
      "making what's possible real",
      "practicing what you preach",
      "carrying out plans",
      "producing magical results",
      "using your talents",
      "acting consciously",
      "knowing what you are doing and why"
    ],
    "edges": []
  },
  "The High Priestess": {
    "url": "https://www.learntarot.com/maj02.htm",
    "keywords": [
      "Nonaction",
      "Unconscious Awareness",
      "Potential",
      "Mystery"
    ],
    "actions": [
      "staying nonactive",
      "withdrawing from involvement",
      "allowing events to proceed without intervention",
      "being receptive to influence",
      "becoming calm",
      "being passive",
      "waiting patiently",
      "accessing the unconscious",
      "using your intuition",
      "seeking guidance from within"
    ],
    "edges": []
  },
  "The Empress": {
    "url": "https://www.learntarot.com/maj03.htm",
    "keywords": [
      "Mothering",
      "Abundance",
      "Senses",
      "Nature"
    ],
    "actions": [
      "mothering",
      "giving birth",
      "nourishing life",
      "nurturing and caring for others",
      "cherishing the world",
      "expressing tenderness",
      "working with children",
      "welcoming abundance",
      "enjoying extravagance",
      "receiving lavish reward"
    ],
    "edges": []
  },
  "The Emperor": {
    "url": "https://www.learntarot.com/maj04.htm",
    "keywords": [
      "Fathering",
      "Structure",
      "Authority",
      "Regulation"
    ],
    "actions": [
      "fathering",
      "establishing a family line",
      "setting direction and tone",
      "protecting and defending",
      "guiding growth",
      "bringing security and comfort",
      "offering explanations",
      "emphasizing structure",
      "creating order out of chaos",
      "categorizing"
    ],
    "edges": []
  },
  "The Hierophant": {
    "url": "https://www.learntarot.com/maj05.htm",
    "keywords": [
      "Education",
      "Belief Systems",
      "Conformity",
      "Group Identification"
    ],
    "actions": [
      "getting an education",
      "pursuing knowledge",
      "becoming informed",
      "increasing understanding",
      "studying and learning",
      "seeking a deeper meaning",
      "finding out more",
      "having a belief system",
      "sharing a cultural heritage",
      "learning a religious tradition"
    ],
    "edges": []
  },
  "The Lovers": {
    "url": "https://www.learntarot.com/maj06.htm",
    "keywords": [
      "Relationship",
      "Sexuality",
      "Personal Beliefs",
      "Values"
    ],
    "actions": [
      "relating to others",
      "establishing bonds",
      "feeling love",
      "forming a union or marriage",
      "acknowledging kinship",
      "sympathizing with another",
      "getting closer",
      "making a connection",
      "being intimate",
      "being sexual"
    ],
    "edges": []
  },
  "The Chariot": {
    "url": "https://www.learntarot.com/maj07.htm",
    "keywords": [
      "Victory",
      "Will",
      "Self-Assertion",
      "Hard Control"
    ],
    "actions": [
      "achieving victory",
      "reaching your goal",
      "winning",
      "being successful",
      "dominating",
      "coming out on top",
      "beating the competition",
      "using your will",
      "being determined to succeed",
      "focusing your intent"
    ],
    "edges": []
  },
  "Strength": {
    "url": "https://www.learntarot.com/maj08.htm",
    "keywords": [
      "Patience",
      "Compassion",
      "Soft Control"
    ],
    "actions": [
      "showing strength",
      "knowing you can endure",
      "having a gallant spirit",
      "feeling an unshakable resolve",
      "taking heart despite setbacks",
      "having stamina",
      "being a rock",
      "being patient",
      "dealing calmly with frustration",
      "accepting others"
    ],
    "edges": []
  },
  "The Hermit": {
    "url": "https://www.learntarot.com/maj09.htm",
    "keywords": [
      "Introspection",
      "Searching",
      "Guidance",
      "Solitude"
    ],
    "actions": [
      "being introspective",
      "thinking things over",
      "focusing inward",
      "concentrating less on the senses",
      "quieting yourself",
      "looking for answers within",
      "needing to understand",
      "searching",
      "seeking greater understanding",
      "looking for something"
    ],
    "edges": []
  },
  "Wheel of Fortune": {
    "url": "https://www.learntarot.com/maj10.htm",
    "keywords": [
      "Destiny",
      "Turning Point",
      "Movement",
      "Personal Vision"
    ],
    "actions": [
      "feeling a sense of destiny",
      "using what chance offers",
      "seeing life's threads weave together",
      "finding opportunity in an accident",
      "opening to luck",
      "sensing the action of fate",
      "witnessing miracles",
      "being at a turning point",
      "reversing",
      "moving in a different direction"
    ],
    "edges": []
  },
  "Justice": {
    "url": "https://www.learntarot.com/maj11.htm",
    "keywords": [
      "Responsibility",
      "Decision",
      "Cause And Effect"
    ],
    "actions": [
      "respecting justice",
      "insisting on fairness",
      "acting on ethical principles",
      "being involved in legal concerns",
      "committing to honesty",
      "seeking equality",
      "being impartial",
      "trying to do what is right",
      "assuming responsibility",
      "settling old accounts and debts"
    ],
    "edges": []
  },
  "The Hanged Man": {
    "url": "https://www.learntarot.com/maj12.htm",
    "keywords": [
      "Letting Go",
      "Reversal",
      "Suspension",
      "Sacrifice"
    ],
    "actions": [
      "letting go",
      "having an emotional release",
      "accepting what is",
      "surrendering to experience",
      "ending the struggle",
      "being vulnerable and open",
      "giving up control",
      "accepting God's will",
      "reversing",
      "turning the world around"
    ],
    "edges": []
  },
  "Death": {
    "url": "https://www.learntarot.com/maj13.htm",
    "keywords": [
      "Ending",
      "Transition",
      "Elimination",
      "Inexorable Forces"
    ],
    "actions": [
      "ending",
      "closing one door to open another",
      "bringing something to a close",
      "completing a chapter",
      "concluding unfinished business",
      "putting the past behind you",
      "having a parting of the ways",
      "going through transition",
      "changing status",
      "moving from the known to the unknown"
    ],
    "edges": []
  },
  "Temperance": {
    "url": "https://www.learntarot.com/maj14.htm",
    "keywords": [
      "Balance",
      "Health",
      "Combination"
    ],
    "actions": [
      "being temperate",
      "finding the middle ground",
      "showing moderation",
      "avoiding excess",
      "mitigating a harsh position",
      "looking for the golden mean",
      "reaching a compromise",
      "offsetting an extreme",
      "maintaining balance",
      "experiencing harmony"
    ],
    "edges": []
  },
  "The Devil": {
    "url": "https://www.learntarot.com/maj15.htm",
    "keywords": [
      "Bondage",
      "Materialism",
      "Ignorance",
      "Hopelessness"
    ],
    "actions": [
      "experiencing bondage",
      "accepting an unwanted situation",
      "being obsessed",
      "feeling tied down against your will",
      "losing independence",
      "allowing yourself to be controlled",
      "being addicted and enslaved",
      "submitting to another",
      "focusing on the material",
      "being caught up in appearances"
    ],
    "edges": []
  },
  "The Tower": {
    "url": "https://www.learntarot.com/maj16.htm",
    "keywords": [
      "Sudden Change",
      "Release",
      "Downfall",
      "Revelation"
    ],
    "actions": [
      "going through sudden change",
      "experiencing upheaval",
      "having plans disrupted",
      "being surprised",
      "undergoing a crisis",
      "having routines shaken up",
      "being in chaos",
      "releasing",
      "exploding",
      "having an emotional outburst"
    ],
    "edges": []
  },
  "The Star": {
    "url": "https://www.learntarot.com/maj17.htm",
    "keywords": [
      "Hope",
      "Inspiration",
      "Generosity",
      "Serenity"
    ],
    "actions": [
      "regaining hope",
      "having faith in the future",
      "thinking positively",
      "believing",
      "counting your blessings",
      "seeing the light at the end of the tunnel",
      "feeling great expectation",
      "looking forward to success",
      "being inspired",
      "regaining motivation"
    ],
    "edges": []
  },
  "The Moon": {
    "url": "https://www.learntarot.com/maj18.htm",
    "keywords": [
      "Fear",
      "Illusion",
      "Imagination",
      "Bewilderment"
    ],
    "actions": [
      "feeling fear",
      "releasing inner demons",
      "feeling a nameless apprehension",
      "suffering from phobias",
      "giving in to the shadow self",
      "lacking courage",
      "being overcome by anxieties",
      "believing illusions",
      "accepting a false picture",
      "deceiving yourself"
    ],
    "edges": []
  },
  "The Sun": {
    "url": "https://www.learntarot.com/maj19.htm",
    "keywords": [
      "Enlightenment",
      "Greatness",
      "Vitality",
      "Assurance"
    ],
    "actions": [
      "becoming enlightened",
      "understanding",
      "finding the sense behind the chaos",
      "attaining a new level of insight",
      "having an intellectual breakthrough",
      "getting to the heart of the matter",
      "realizing the truth",
      "experiencing greatness",
      "achieving prominence",
      "being singled out for notice"
    ],
    "edges": []
  },
  "Judgement": {
    "url": "https://www.learntarot.com/maj20.htm",
    "keywords": [
      "Judgment",
      "Rebirth",
      "Inner Calling",
      "Absolution"
    ],
    "actions": [
      "making a judgment",
      "having a day of reckoning",
      "separating the wheat from the chaff",
      "making an honest appraisal",
      "getting off the fence",
      "using critical faculties",
      "taking a stand",
      "making hard choices",
      "feeling reborn",
      "awakening to possibilities"
    ],
    "edges": []
  },
  "The World": {
    "url": "https://www.learntarot.com/maj21.htm",
    "keywords": [
      "Integration",
      "Accomplishment",
      "Involvement",
      "Fulfillment"
    ],
    "actions": [
      "integrating",
      "experiencing wholeness",
      "bringing parts together",
      "achieving dynamic balance",
      "combining",
      "creating synthesis",
      "joining together",
      "working in unison",
      "accomplishing",
      "realizing your goals"
    ],
    "edges": []
  },
  "Ace of Wands": {
    "url": "https://www.learntarot.com/wa.htm",
    "keywords": [
      "Creative Force",
      "Enthusiasm",
      "Confidence",
      "Courage"
    ],
    "actions": [
      "using creative force",
      "inventing a better way",
      "expanding your potential",
      "opening to greater possibilities",
      "conceiving a dream",
      "expressing yourself",
      "stimulating your imagination",
      "allowing a talent to unfold",
      "coming up with a solution",
      "showing enthusiasm"
    ],
    "edges": []
  },
  "Two of Wands": {
    "url": "https://www.learntarot.com/w2.htm",
    "keywords": [
      "Personal Power",
      "Boldness",
      "Originality"
    ],
    "actions": [
      "having personal power",
      "commanding attention and respect",
      "directing those around you",
      "holding the world in your hands",
      "wielding influence",
      "calling the shots",
      "having authority",
      "swaying others to your position",
      "being able to achieve your goals",
      "being bold"
    ],
    "edges": []
  },
  "Three of Wands": {
    "url": "https://www.learntarot.com/w3.htm",
    "keywords": [
      "Exploration",
      "Foresight",
      "Leadership"
    ],
    "actions": [
      "exploring the unknown",
      "seeking out uncharted areas",
      "going in quest of new adventure",
      "expanding horizons",
      "leaving the secure behind",
      "tackling something different",
      "having foresight",
      "being visionary",
      "looking for greater possibilities",
      "planning ahead"
    ],
    "edges": []
  },
  "Four of Wands": {
    "url": "https://www.learntarot.com/w4.htm",
    "keywords": [
      "Celebration",
      "Freedom",
      "Excitement"
    ],
    "actions": [
      "celebrating",
      "rejoicing over a happy event",
      "recognizing a success",
      "observing an anniversary, milestone or special time",
      "congratulating on a job well-done",
      "reflecting on accomplishments",
      "enjoying some well-deserved rewards",
      "taking part in a ceremony or rite",
      "seeking freedom",
      "getting out of an oppressive situation"
    ],
    "edges": []
  },
  "Five of Wands": {
    "url": "https://www.learntarot.com/w5.htm",
    "keywords": [
      "Disagreement",
      "Competition",
      "Hassles"
    ],
    "actions": [
      "disagreeing",
      "feeling everyone is at cross-purposes",
      "being torn by dissension",
      "quarreling, arguing and bickering",
      "becoming embroiled in a debate",
      "being at odds with others",
      "quibbling over details",
      "experiencing competition",
      "feeling the thrill of the contest",
      "going against an opponent"
    ],
    "edges": []
  },
  "Six of Wands": {
    "url": "https://www.learntarot.com/w6.htm",
    "keywords": [
      "Triumph",
      "Acclaim",
      "Pride"
    ],
    "actions": [
      "triumphing",
      "having your day in the sun",
      "being vindicated",
      "walking away with the prize",
      "prevailing against all comers",
      "coming out on top",
      "achieving success",
      "receiving acclaim",
      "being acknowledged",
      "getting a pat on the back"
    ],
    "edges": []
  },
  "Seven of Wands": {
    "url": "https://www.learntarot.com/w7.htm",
    "keywords": [
      "Aggression",
      "Defiance",
      "Conviction"
    ],
    "actions": [
      "being aggressive",
      "going after what you want",
      "asserting yourself",
      "taking the offensive",
      "firing the first shot",
      "fighting",
      "making your point forcefully",
      "seizing the advantage",
      "being defiant",
      "holding out against pressure"
    ],
    "edges": []
  },
  "Eight of Wands": {
    "url": "https://www.learntarot.com/w8.htm",
    "keywords": [
      "Quick Action",
      "Conclusion",
      "News"
    ],
    "actions": [
      "taking quick action",
      "making your move",
      "striking while the iron is hot",
      "declaring yourself openly",
      "putting plans into action",
      "rushing into a new area",
      "moving into high gear",
      "getting caught up in change",
      "coming to a conclusion",
      "culminating an effort"
    ],
    "edges": []
  },
  "Nine of Wands": {
    "url": "https://www.learntarot.com/w9.htm",
    "keywords": [
      "Defensiveness",
      "Perseverance",
      "Stamina"
    ],
    "actions": [
      "defending yourself",
      "assuming ill will",
      "expecting the worst",
      "taking extra precautions",
      "being paranoid",
      "feeling wary and guarded",
      "protecting others",
      "remembering past attacks",
      "persevering",
      "persisting despite all setbacks"
    ],
    "edges": []
  },
  "Ten of Wands": {
    "url": "https://www.learntarot.com/w10.htm",
    "keywords": [
      "Overextending",
      "Burdens",
      "Struggle"
    ],
    "actions": [
      "overextending",
      "trying to do too much",
      "refusing to say \"no\"",
      "taking all the blame",
      "assuming another's debt",
      "doing the lion's share",
      "having to work overtime",
      "shouldering all the work",
      "being saddled with an extra load",
      "feeling burdened"
    ],
    "edges": []
  },
  "Page of Wands": {
    "url": "https://www.learntarot.com/wpg.htm",
    "keywords": [
      "Be Creative",
      "Be Enthusiastic",
      "Be Confident",
      "Be Courageous"
    ],
    "actions": [
      "take a novel approach",
      "be original",
      "invent",
      "find a new area of self-expression",
      "use your art",
      "come up with a solution",
      "go in a new direction",
      "jump in wholeheartedly",
      "get excited",
      "show optimism"
    ],
    "edges": []
  },
  "Knight of Wands": {
    "url": "https://www.learntarot.com/wkn.htm",
    "keywords": [],
    "actions": [
      "is physically attractive",
      "disarms opposition with a smile",
      "is sexy and seductive",
      "generates glamour and excitement",
      "is honey-tongued",
      "totally lacks self-doubt",
      "has a can-do attitude",
      "is convinced of his or her talent",
      "is self-assured at all times",
      "doesn't sweat the small stuff"
    ],
    "edges": [
      "focuses on style and appearance",
      "can be thoughtless and insensitive",
      "pursues sexual conquests",
      "avoids deep or serious matters",
      "may say or do what's expedient",
      "overestimates abilities"
    ]
  },
  "Queen of Wands": {
    "url": "https://www.learntarot.com/wqn.htm",
    "keywords": [
      "Attractive",
      "Wholehearted",
      "Energetic",
      "Cheerful"
    ],
    "actions": [
      "is appealing and popular",
      "creates a powerful first impression",
      "makes friends easily",
      "has great sex appeal",
      "is warm and outgoing",
      "is loaded with enthusiasm",
      "tackles a task with total dedication",
      "gives the utmost in any situation",
      "is open and sincere",
      "doesn't hold anything back"
    ],
    "edges": []
  },
  "King of Wands": {
    "url": "https://www.learntarot.com/wkg.htm",
    "keywords": [
      "Creative",
      "Inspiring",
      "Forceful",
      "Charismatic"
    ],
    "actions": [
      "develops innovative projects and ideas",
      "opens up new areas of experience",
      "has natural artistic ability",
      "uses self-expression for useful purposes",
      "masterminds new strategies",
      "is original and inventive",
      "communicates enthusiasm",
      "creates an atmosphere of excitement",
      "sets an example others want to follow",
      "instills confidence"
    ],
    "edges": []
  },
  "Ace of Cups": {
    "url": "https://www.learntarot.com/ca.htm",
    "keywords": [
      "Emotional Force",
      "Intuition",
      "Intimacy",
      "Love"
    ],
    "actions": [
      "using emotional force",
      "getting in touch with your feelings",
      "letting your heart lead the way",
      "empathizing with others",
      "expressing deep feelings",
      "responding viscerally",
      "developing intuition",
      "trusting your inner voice",
      "responding to messages from within",
      "experiencing direct knowing"
    ],
    "edges": []
  },
  "Two of Cups": {
    "url": "https://www.learntarot.com/c2.htm",
    "keywords": [
      "Connection",
      "Truce",
      "Attraction"
    ],
    "actions": [
      "making a connection",
      "joining with another",
      "celebrating a marriage or union",
      "cementing a friendship",
      "establishing a partnership",
      "working together",
      "sharing",
      "helping and being helped",
      "seeing commonalities",
      "calling a truce"
    ],
    "edges": []
  },
  "Three of Cups": {
    "url": "https://www.learntarot.com/c3.htm",
    "keywords": [
      "Exuberance",
      "Friendship",
      "Community"
    ],
    "actions": [
      "feeling exuberant",
      "bursting with energy",
      "being in the flow",
      "celebrating",
      "overflowing with high spirits",
      "feeling on top of the world",
      "dancing and singing",
      "putting yourself out there",
      "enjoying friendship",
      "getting together with people you like"
    ],
    "edges": []
  },
  "Four of Cups": {
    "url": "https://www.learntarot.com/c4.htm",
    "keywords": [
      "Self-Absorption",
      "Apathy",
      "Going Within"
    ],
    "actions": [
      "being self-absorbed",
      "concentrating on your own feelings",
      "wanting for yourself",
      "being unaware of others",
      "giving out little",
      "withholding affection",
      "seeing only your point of view",
      "ignoring gifts and blessings",
      "feeling apathetic",
      "passively accepting"
    ],
    "edges": []
  },
  "Five of Cups": {
    "url": "https://www.learntarot.com/c5.htm",
    "keywords": [
      "Loss",
      "Bereavement",
      "Regret"
    ],
    "actions": [
      "suffering a loss",
      "letting go of a hope",
      "giving up the win",
      "experiencing a setback",
      "being defeated",
      "having a possession taken away",
      "saying good-bye",
      "feeling bereft",
      "breaking up a relationship",
      "feeling deprived of love"
    ],
    "edges": []
  },
  "Six of Cups": {
    "url": "https://www.learntarot.com/c6.htm",
    "keywords": [
      "Good Will",
      "Innocence",
      "Childhood"
    ],
    "actions": [
      "experiencing good will",
      "acting kindly or charitably",
      "doing a good turn for another",
      "sharing what you have",
      "having a noble impulse",
      "receiving a gift",
      "feeling blessed",
      "being well-intentioned",
      "enjoying innocence",
      "feeling simple contentment"
    ],
    "edges": []
  },
  "Seven of Cups": {
    "url": "https://www.learntarot.com/c7.htm",
    "keywords": [
      "Wishful Thinking",
      "Options",
      "Dissipation"
    ],
    "actions": [
      "indulging in wishful thinking",
      "creating fantasies",
      "dreaming",
      "getting caught up in illusions",
      "letting your imagination run wild",
      "kidding yourself about the facts",
      "building castles in the air",
      "waiting for your ship to come in",
      "lacking focus and commitment",
      "avoiding putting ideas to the test"
    ],
    "edges": []
  },
  "Eight of Cups": {
    "url": "https://www.learntarot.com/c8.htm",
    "keywords": [
      "Deeper Meaning",
      "Moving On",
      "Weariness"
    ],
    "actions": [
      "seeking deeper meaning",
      "focusing on personal truth",
      "leaving the rat race",
      "looking for answers",
      "concentrating on what is important",
      "starting on a journey of discovery",
      "finding out the facts",
      "devoting more time to the spiritual",
      "moving on",
      "realizing the current cycle is over"
    ],
    "edges": []
  },
  "Nine of Cups": {
    "url": "https://www.learntarot.com/c9.htm",
    "keywords": [
      "Wish Fulfillment",
      "Satisfaction",
      "Sensual Pleasure"
    ],
    "actions": [
      "having your wish fulfilled",
      "achieving what you desire",
      "obtaining your goal",
      "getting what you think you want",
      "having your dream come true",
      "feeling satisfied",
      "indulging in a little smugness",
      "enjoying the situation just as it is",
      "feeling pleased as punch",
      "getting the results you hoped for"
    ],
    "edges": []
  },
  "Ten of Cups": {
    "url": "https://www.learntarot.com/c10.htm",
    "keywords": [
      "Joy",
      "Peace",
      "Family"
    ],
    "actions": [
      "feeling joy",
      "embracing happiness",
      "having a sense of well-being",
      "radiating love",
      "delighting in good fortune",
      "counting your blessings",
      "expressing delight",
      "enjoying peace",
      "experiencing serenity",
      "doing away with hostilities"
    ],
    "edges": []
  },
  "Page of Cups": {
    "url": "https://www.learntarot.com/cpg.htm",
    "keywords": [
      "Be Emotional",
      "Be Intuitive",
      "Be Intimate",
      "Be Loving"
    ],
    "actions": [
      "be moved or touched",
      "let your feelings show",
      "respond to beauty",
      "be sentimental or romantic",
      "shed your detachment",
      "let your heart lead the way",
      "receive guidance from within",
      "act on a hunch",
      "remember your dreams",
      "have a psychic experience"
    ],
    "edges": []
  },
  "Knight of Cups": {
    "url": "https://www.learntarot.com/ckn.htm",
    "keywords": [],
    "actions": [
      "idealizes love",
      "emphasizes feelings",
      "concentrates on the poetry of life",
      "remembers special occasions",
      "expresses sentiments beautifully",
      "can tap the fertile unconscious",
      "looks beyond the obvious",
      "never takes the mundane path",
      "spins marvelous tales",
      "is visionary"
    ],
    "edges": [
      "lacks self-restraint",
      "is often jealous",
      "tends to be gushy and melodramatic",
      "blows hot and cold in affections",
      "brings flowers, but forgets to put gas in the car",
      "indulges in idle daydreams"
    ]
  },
  "Queen of Cups": {
    "url": "https://www.learntarot.com/cqn.htm",
    "keywords": [
      "Loving",
      "Tenderhearted",
      "Intuitive",
      "Psychic"
    ],
    "actions": [
      "turns away wrath with caring",
      "is unconditionally accepting",
      "is sensitive to the feelings of others",
      "dispels anger and hate",
      "has infinite patience",
      "is easily moved by another's pain",
      "reacts with sensitivity and compassion",
      "is kind and gentle with all creatures",
      "can never turn away someone in need",
      "feels what others are feeling"
    ],
    "edges": []
  },
  "King of Cups": {
    "url": "https://www.learntarot.com/ckg.htm",
    "keywords": [
      "Wise",
      "Calm",
      "Diplomatic",
      "Caring"
    ],
    "actions": [
      "gives good advice",
      "has a deep grasp of human nature",
      "teaches through loving attention",
      "knows what others need for growth",
      "sees right to the heart of the matter",
      "understands many levels of experience",
      "maintains his or her composure",
      "has a quieting influence on others",
      "is emotionally stable and secure",
      "keeps his or her head in a crisis"
    ],
    "edges": []
  },
  "Ace of Swords": {
    "url": "https://www.learntarot.com/sa.htm",
    "keywords": [
      "Mental Force",
      "Truth",
      "Justice",
      "Fortitude"
    ],
    "actions": [
      "using mental force",
      "being objective",
      "thinking your way through",
      "finding out the facts",
      "analyzing the situation",
      "using your intellect",
      "applying logic and reason",
      "having fortitude",
      "overcoming adversity",
      "facing problems"
    ],
    "edges": []
  },
  "Two of Swords": {
    "url": "https://www.learntarot.com/s2.htm",
    "keywords": [
      "Blocked Emotions",
      "Avoidance",
      "Stalemate"
    ],
    "actions": [
      "blocking emotions",
      "denying true feelings",
      "stifling a natural response",
      "keeping another at arm's length",
      "hiding distress",
      "turning a deaf ear",
      "being defensive",
      "maintaining your cool",
      "avoiding the truth",
      "refusing to look at facts"
    ],
    "edges": []
  },
  "Three of Swords": {
    "url": "https://www.learntarot.com/s3.htm",
    "keywords": [
      "Heartbreak",
      "Loneliness",
      "Betrayal"
    ],
    "actions": [
      "feeling heartbreak",
      "causing heartbreak",
      "suffering emotional pain",
      "hurting inside",
      "being disappointed",
      "getting some unsettling news",
      "having your feelings hurt",
      "hurting someone's feelings",
      "receiving little solace",
      "feeling lonely"
    ],
    "edges": []
  },
  "Four of Swords": {
    "url": "https://www.learntarot.com/s4.htm",
    "keywords": [
      "Rest",
      "Contemplation",
      "Quiet Preparation"
    ],
    "actions": [
      "resting",
      "taking a break",
      "giving your body time to heal",
      "avoiding overexertion",
      "finding peace and quiet",
      "relaxing body and soul",
      "taking life easy",
      "contemplating",
      "gaining a better perspective",
      "listening for your Inner Voice"
    ],
    "edges": []
  },
  "Five of Swords": {
    "url": "https://www.learntarot.com/s5.htm",
    "keywords": [
      "Self-Interest",
      "Discord",
      "Open Dishonor"
    ],
    "actions": [
      "acting in your own self-interest",
      "setting aside the concerns of others",
      "looking out for number one",
      "thinking of your own needs",
      "knowing you must concentrate on yourself",
      "encountering selfishness",
      "indulging in power plays",
      "gloating",
      "going for the win-lose result",
      "experiencing discord"
    ],
    "edges": []
  },
  "Six of Swords": {
    "url": "https://www.learntarot.com/s6.htm",
    "keywords": [
      "The Blues",
      "Recovery",
      "Travel"
    ],
    "actions": [
      "feeling the blues",
      "experiencing a low-level sadness",
      "just keeping your head above water",
      "working to get through the day",
      "feeling somewhat depressed",
      "avoiding the lows, but also the highs",
      "feeling listless",
      "functioning, but not much more",
      "recovering",
      "dealing with the effects of trauma"
    ],
    "edges": []
  },
  "Seven of Swords": {
    "url": "https://www.learntarot.com/s7.htm",
    "keywords": [
      "Running Away",
      "Lone Wolf Style",
      "Hidden Dishonor"
    ],
    "actions": [
      "running away",
      "shirking responsibility",
      "sneaking off",
      "leaving",
      "avoiding obligations",
      "being afraid to face the music",
      "taking the easy way out",
      "hiding from the truth",
      "procrastinating",
      "being a lone wolf"
    ],
    "edges": []
  },
  "Eight of Swords": {
    "url": "https://www.learntarot.com/s8.htm",
    "keywords": [
      "Restriction",
      "Confusion",
      "Powerlessness"
    ],
    "actions": [
      "feeling restricted",
      "being fenced in by obstacles",
      "staying in a limited situation",
      "feeling trapped by circumstances",
      "experiencing few options",
      "being blind to freedom",
      "feeling persecuted",
      "feeling confused",
      "being unsure which way to turn",
      "feeling at a loss"
    ],
    "edges": []
  },
  "Nine of Swords": {
    "url": "https://www.learntarot.com/s9.htm",
    "keywords": [
      "Worry",
      "Guilt",
      "Anguish"
    ],
    "actions": [
      "worrying",
      "doubting all will go well",
      "brooding",
      "making yourself sick over your troubles",
      "going over and over an issue",
      "feeling anxious and tense",
      "getting worked up",
      "feeling guilty",
      "regretting some offense",
      "refusing to forgive yourself"
    ],
    "edges": []
  },
  "Ten of Swords": {
    "url": "https://www.learntarot.com/s10.htm",
    "keywords": [
      "Bottoming Out",
      "Victim Mentality",
      "Martyrdom"
    ],
    "actions": [
      "bottoming out",
      "having nowhere to go but up",
      "knowing it's darkest before the dawn",
      "being at the lowest point",
      "feeling things can't get worse",
      "preparing for an upturn",
      "reaching the pits",
      "feeling like a victim",
      "bemoaning your fate",
      "feeling powerless"
    ],
    "edges": []
  },
  "Page of Swords": {
    "url": "https://www.learntarot.com/spg.htm",
    "keywords": [
      "Use Your Mind",
      "Be Truthful",
      "Be Just",
      "Have Fortitude"
    ],
    "actions": [
      "analyze the problem",
      "use logic and reason",
      "reexamine beliefs",
      "develop an idea or plan",
      "study or research the facts",
      "learn or teach",
      "think everything through",
      "act honestly",
      "face the facts",
      "stop deceiving"
    ],
    "edges": []
  },
  "Knight of Swords": {
    "url": "https://www.learntarot.com/skn.htm",
    "keywords": [],
    "actions": [
      "is frank and outspoken",
      "gets straight to the point",
      "does not mince words",
      "lets others know where they stand",
      "gives an honest answer",
      "speaks with assurance",
      "commands attention",
      "acts with total certainty",
      "gives orders naturally",
      "has great influence"
    ],
    "edges": [
      "is tactless and rude",
      "may have a brusque manner",
      "does not spare the feelings of others",
      "can't hold his or her tongue",
      "shows little discretion",
      "tends to be domineering"
    ]
  },
  "Queen of Swords": {
    "url": "https://www.learntarot.com/sqn.htm",
    "keywords": [
      "Honest",
      "Astute",
      "Forthright",
      "Witty"
    ],
    "actions": [
      "faces the truth, even if unpleasant",
      "is up front with everyone",
      "likes everything on the table",
      "plays by the rules",
      "avoids lies and deception",
      "sizes up a situation quickly",
      "understands hidden motives and desires",
      "is difficult to fool, trick or con",
      "figures out the unspoken rules and agendas",
      "is quick on the uptake"
    ],
    "edges": []
  },
  "King of Swords": {
    "url": "https://www.learntarot.com/skg.htm",
    "keywords": [
      "Intellectual",
      "Analytical",
      "Articulate",
      "Just"
    ],
    "actions": [
      "is comfortable in the world of the mind",
      "uses thought creatively",
      "grasps information quickly and completely",
      "inspires and challenges through ideas",
      "ably carries out research",
      "is knowledgeable",
      "cuts through confusion and mental fog",
      "applies reason and logic",
      "is talented with games and other mental challenges",
      "easily breaks up complicated subjects"
    ],
    "edges": []
  },
  "Ace of Pentacles": {
    "url": "https://www.learntarot.com/pa.htm",
    "keywords": [
      "Material Force",
      "Prosperity",
      "Practicality",
      "Trust"
    ],
    "actions": [
      "using material force",
      "focusing on concrete results",
      "having a real-world impact",
      "working with the physical",
      "achieving tangible results",
      "improving the body/health",
      "becoming involved with nature",
      "prospering",
      "having the means to reach a goal",
      "enjoying abundance"
    ],
    "edges": []
  },
  "Two of Pentacles": {
    "url": "https://www.learntarot.com/p2.htm",
    "keywords": [
      "Juggling",
      "Flexibility",
      "Fun"
    ],
    "actions": [
      "juggling",
      "keeping everything in balance",
      "coping with demands",
      "getting people to work together",
      "making sure all areas are covered",
      "having a lot of irons in the fire",
      "moving forward smoothly",
      "emphasizing all aspects equally",
      "being flexible",
      "adapting quickly"
    ],
    "edges": []
  },
  "Three of Pentacles": {
    "url": "https://www.learntarot.com/p3.htm",
    "keywords": [
      "Teamwork",
      "Planning",
      "Competence"
    ],
    "actions": [
      "working as a team",
      "coordinating with others",
      "finding all the needed elements",
      "functioning as a unit",
      "getting the job done together",
      "contributing to the group",
      "cooperating",
      "combining efforts",
      "planning",
      "organizing resources"
    ],
    "edges": []
  },
  "Four of Pentacles": {
    "url": "https://www.learntarot.com/p4.htm",
    "keywords": [
      "Possessiveness",
      "Control",
      "Blocked Change"
    ],
    "actions": [
      "wanting to possess",
      "keeping what you have",
      "getting your share",
      "acquiring material goods",
      "hanging on to someone",
      "being greedy",
      "penny-pinching",
      "declaring ownership",
      "saving",
      "maintaining control"
    ],
    "edges": []
  },
  "Five of Pentacles": {
    "url": "https://www.learntarot.com/p5.htm",
    "keywords": [
      "Hard Times",
      "Ill Health",
      "Rejection"
    ],
    "actions": [
      "experiencing hard times",
      "running into material troubles",
      "losing a job or income",
      "feeling insecure",
      "going through a period of hardship",
      "lacking what you need",
      "struggling to make ends meet",
      "suffering ill health",
      "feeling run down and tired",
      "refusing to take care of yourself"
    ],
    "edges": []
  },
  "Six of Pentacles": {
    "url": "https://www.learntarot.com/p6.htm",
    "keywords": [
      "Resources",
      "Knowledge",
      "Power"
    ],
    "actions": [
      "having/not having resources",
      "giving/receiving",
      "taking care of/being taken care of",
      "sponsoring/being sponsored",
      "supporting/being supported",
      "offering/receiving a gift or reward",
      "acquiring/not acquiring what you need",
      "having/not having knowledge",
      "teaching/learning",
      "imparting/receiving information"
    ],
    "edges": []
  },
  "Seven of Pentacles": {
    "url": "https://www.learntarot.com/p7.htm",
    "keywords": [
      "Assessment",
      "Reward",
      "Direction Change"
    ],
    "actions": [
      "assessing",
      "evaluating the status",
      "reflecting on progress to date",
      "reviewing what's been done",
      "pausing to check results",
      "making sure you're on course",
      "finding out where you stand",
      "taking stock",
      "reaping a reward",
      "finally seeing some results"
    ],
    "edges": []
  },
  "Eight of Pentacles": {
    "url": "https://www.learntarot.com/p8.htm",
    "keywords": [
      "Diligence",
      "Knowledge",
      "Detail"
    ],
    "actions": [
      "showing diligence",
      "making an effort",
      "working hard",
      "applying yourself totally",
      "being absorbed in a project",
      "dedicating yourself to a task",
      "plugging away",
      "producing steady results",
      "increasing knowledge",
      "taking a course"
    ],
    "edges": []
  },
  "Nine of Pentacles": {
    "url": "https://www.learntarot.com/p9.htm",
    "keywords": [
      "Discipline",
      "Self-Reliance",
      "Refinement"
    ],
    "actions": [
      "being disciplined",
      "exercising self-control",
      "showing restraint",
      "reining in impulses",
      "sacrificing to reach a goal",
      "sticking to a program",
      "taking a step-by-step approach",
      "relying on yourself",
      "handling the situation alone",
      "acting on your own"
    ],
    "edges": []
  },
  "Ten of Pentacles": {
    "url": "https://www.learntarot.com/p10.htm",
    "keywords": [
      "Affluence",
      "Permanence",
      "Convention"
    ],
    "actions": [
      "enjoying affluence",
      "having material abundance",
      "being free from money problems",
      "enjoying business success",
      "feeling financially secure",
      "seeing your ventures flourish",
      "having a run of good fortune",
      "seeking permanence",
      "looking for a solution that will last",
      "creating a lasting foundation"
    ],
    "edges": []
  },
  "Page of Pentacles": {
    "url": "https://www.learntarot.com/ppg.htm",
    "keywords": [
      "Have An Effect",
      "Be Practical",
      "Be Prosperous",
      "Be Trusting/Trustworthy"
    ],
    "actions": [
      "make your plans real",
      "mold the physical world",
      "use your body",
      "experience nature",
      "achieve tangible results",
      "act on your dreams",
      "get events in motion",
      "take a realistic approach",
      "apply the tools at hand",
      "find a solution that works"
    ],
    "edges": []
  },
  "Knight of Pentacles": {
    "url": "https://www.learntarot.com/pkn.htm",
    "keywords": [],
    "actions": [
      "is dogged in pursuit of a goal",
      "will not quit",
      "stands firm against opposition",
      "stays fixed to a chosen course",
      "keeps true to personal convictions",
      "checks and double-checks",
      "examines all angles beforehand",
      "proceeds slowly and carefully",
      "prefers the safe, known path",
      "is prudent and careful"
    ],
    "edges": [
      "is hardheaded and obstinate",
      "digs into a position",
      "must have his or her own way",
      "refuses to listen to reason",
      "resists compromise",
      "is too conservative"
    ]
  },
  "Queen of Pentacles": {
    "url": "https://www.learntarot.com/pqn.htm",
    "keywords": [
      "Nurturing",
      "Bighearted",
      "Down-To-Earth",
      "Resourceful"
    ],
    "actions": [
      "gives love and support",
      "creates a warm, secure environment",
      "makes people feel better",
      "responds to the natural world",
      "has a green thumb",
      "has a way with children and animals",
      "will do any service for others",
      "is a soft touch",
      "always has an open door and welcoming smile",
      "gives freely and abundantly"
    ],
    "edges": []
  },
  "King of Pentacles": {
    "url": "https://www.learntarot.com/pkg.htm",
    "keywords": [
      "Enterprising",
      "Adept",
      "Reliable",
      "Supporting"
    ],
    "actions": [
      "makes any venture successful",
      "finds opportunity everywhere",
      "attracts wealth",
      "takes an idea and makes it work",
      "is a natural manager and businessperson",
      "has the Midas touch",
      "is informed about practical matters",
      "has a wide range of natural abilities",
      "has quick reflexes",
      "is skillful with his or her hands"
    ],
    "edges": []
  }
};

const formatSeries = (items = []) => {
  const cleanItems = items.filter(Boolean);
  if (cleanItems.length <= 1) return cleanItems[0] || "";
  if (cleanItems.length === 2) return cleanItems[0] + " and " + cleanItems[1];
  return cleanItems.slice(0, -1).join(", ") + " and " + cleanItems[cleanItems.length - 1];
};

const firstParagraph = (value = "") =>
  String(value)
    .split(/\r?\n\s*\r?\n/)[0]
    ?.trim() || "";

const readingParagraphs = (value = "") =>
  String(value)
    .split(/\r?\n\s*\r?\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

const removeAskYourself = (value = "") =>
  String(value)
    .replace(/\s*Ask yourself:.*$/is, "")
    .trim();

const readerFriendlyText = (value = "") =>
  String(value)
    .replace(/\bIn a spread,\s*/g, "When this card appears, ")
    .replace(/\bIn a spread\b/g, "When this card appears")
    .replace(/\bIn the spread,\s*/g, "In the reading, ")
    .trim();

const readingGuideParagraphs = (card) => {
  const paragraphs = readingParagraphs(readingOverrides[card.name] || card.reading);
  const intro = readerFriendlyText(paragraphs[0] || firstParagraph(card.description));
  const uprightReversed = readerFriendlyText(removeAskYourself(paragraphs.slice(1).join("\n\n")));

  return [intro, uprightReversed].filter(Boolean);
};

const contextReadingParagraph = (card) => {
  const grounded = groundedSections[card.name];
  if (!grounded) return "";

  return [
    "Apply the card to the actual question instead of reading it as general atmosphere.",
    clean(grounded.loveMeaning),
    clean(grounded.careerMeaning),
    clean(grounded.spiritualMeaning),
  ]
    .filter(Boolean)
    .join(" ");
};

const courtTraitSeries = (items = []) => {
  const actions = items.filter(Boolean);
  if (!actions.length) return "";

  const first = actions[0];
  const needsPerson = /^(is|gets|does|lets|gives|speaks|commands|acts|has|may|can|tends|knows|reasons|analyzes|concentrates|makes|turns|reacts|responds|feels|sees|enjoys|believes|lacks|expects)\b/i.test(first);
  return needsPerson ? "someone who " + formatSeries(actions) : "the need to " + formatSeries(actions);
};

const practiceReadingParagraph = (card, source, isCourtCard) => {
  const primaryActions = source.actions.slice(0, 3);
  const secondaryActions = source.actions.slice(5, 8);
  const courtEdges = source.edges.slice(0, 3);

  if (isCourtCard) {
    const edgeSentence = courtEdges.length
      ? " If the card feels difficult, watch for the same energy turning into " + courtTraitSeries(courtEdges) + "."
      : "";

    return (
      "For this court card, decide first whether it describes you, another person, a role or the way the situation is being handled. Practical clues include " +
      courtTraitSeries(primaryActions) +
      "." +
      edgeSentence
    );
  }

  return (
    "Look at the cards around it before giving advice. Practical clues include " +
    formatSeries(primaryActions) +
    ". If those signs are not visible, check whether the nearby cards point to " +
    formatSeries(secondaryActions) +
    " instead."
  );
};

const buildSourceReading = (card) => {
  const source = sourceReaderData[card.name];
  if (!source) return readingOverrides[card.name] || card.reading;

  const isCourtCard = /^(Page|Knight|Queen|King) of /.test(card.name);
  const context = contextReadingParagraph(card);
  const practice = practiceReadingParagraph(card, source, isCourtCard);

  const paragraphs = [
    ...readingGuideParagraphs(card),
    context,
    practice,
  ].filter(Boolean);

  return paragraphs.join("\n\n");
};

const rankDetails = {
  Ace: {
    symbolLabel: "Single {symbol}",
    symbolMeaning: "The single {symbol} marks the first living seed of {field}.",
    movement: "a clean beginning",
  },
  Two: {
    symbolLabel: "Two {plural}",
    symbolMeaning: "The pair of {plural} shows relationship, choice and the need to hold two truths at once.",
    movement: "a moment of choice",
  },
  Three: {
    symbolLabel: "Three {plural}",
    symbolMeaning: "The three {plural} bring early growth and the first visible shape of effort.",
    movement: "growth taking shape",
  },
  Four: {
    symbolLabel: "Four {plural}",
    symbolMeaning: "The four {plural} create a square of stability, pause and structure.",
    movement: "a need for steadiness",
  },
  Five: {
    symbolLabel: "Five {plural}",
    symbolMeaning: "The five {plural} disturb the pattern and reveal tension that wants attention.",
    movement: "friction that cannot be ignored",
  },
  Six: {
    symbolLabel: "Six {plural}",
    symbolMeaning: "The six {plural} restore balance by showing what can be shared, repaired or carried forward.",
    movement: "balance after pressure",
  },
  Seven: {
    symbolLabel: "Seven {plural}",
    symbolMeaning: "The seven {plural} ask for discernment, courage and a sharper relationship with truth.",
    movement: "a test of discernment",
  },
  Eight: {
    symbolLabel: "Eight {plural}",
    symbolMeaning: "The eight {plural} gather momentum and show where repeated action becomes a path.",
    movement: "momentum through repetition",
  },
  Nine: {
    symbolLabel: "Nine {plural}",
    symbolMeaning: "The nine {plural} concentrate the suit into a personal reckoning, reward or realization.",
    movement: "a personal turning point",
  },
  Ten: {
    symbolLabel: "Ten {plural}",
    symbolMeaning: "The ten {plural} show completion, consequence and the full weight of the suit.",
    movement: "the end of a cycle",
  },
  Page: {
    symbolLabel: "Page with {symbol}",
    symbolMeaning: "The Page studies the {symbol} with beginner eyes, showing curiosity and first practice.",
    movement: "a learning stage",
  },
  Knight: {
    symbolLabel: "Knight with {symbol}",
    symbolMeaning: "The Knight carries the {symbol} into motion and shows how the suit acts under pressure.",
    movement: "movement with a distinct motive",
  },
  Queen: {
    symbolLabel: "Queen with {symbol}",
    symbolMeaning: "The Queen holds the {symbol} from within, showing mature care and receptive mastery.",
    movement: "inner mastery",
  },
  King: {
    symbolLabel: "King with {symbol}",
    symbolMeaning: "The King directs the {symbol} outward, showing authority, responsibility and practiced skill.",
    movement: "outer mastery",
  },
};

const ranks = Object.keys(rankDetails);

const clean = (value = "") =>
  String(value)
    .replace(/\s+/g, " ")
    .trim();

const lowerFirst = (value = "") => {
  const text = clean(value);
  return text ? text.charAt(0).toLowerCase() + text.slice(1) : text;
};

const splitMeaning = (item = "") => {
  const text = clean(item);
  const colonIndex = text.indexOf(":");
  if (colonIndex > -1) {
    return {
      label: clean(text.slice(0, colonIndex)),
      text: clean(text.slice(colonIndex + 1)),
    };
  }

  const dashMatch = text.match(/^(.+?)\s[-–—]\s(.+)$/);
  if (dashMatch) {
    return { label: clean(dashMatch[1]), text: clean(dashMatch[2]) };
  }

  return { label: "Meaning", text };
};

const getMeaning = (items = [], index = 0) =>
  splitMeaning(items[index] || items[0] || "");

const getRank = (name = "") => ranks.find((rank) => name.startsWith(`${rank} of `)) || "";

const applyTokens = (text, card, detail, rankDetail) =>
  text
    .replace(/\{card\}/g, card.name)
    .replace(/\{symbol\}/g, detail.symbol)
    .replace(/\{plural\}/g, detail.plural)
    .replace(/\{field\}/g, detail.field)
    .replace(/\{movement\}/g, rankDetail?.movement || "the card's movement");

const buildSymbols = (card) => {
  if (majorSymbols[card.name]) return majorSymbols[card.name];
  if (minorSymbols[card.name]) return minorSymbols[card.name];

  const detail = suitDetails[card.suit] || suitDetails["Major Arcana"];
  const rank = getRank(card.name);
  const rankDetail = rankDetails[rank] || rankDetails.Ace;

  return [
    {
      label: applyTokens(rankDetail.symbolLabel, card, detail, rankDetail),
      meaning: applyTokens(rankDetail.symbolMeaning, card, detail, rankDetail),
    },
    {
      label: `${detail.element.charAt(0).toUpperCase()}${detail.element.slice(1)} element`,
      meaning: `${card.name} belongs to ${card.suit}, so its imagery is read through ${detail.field}.`,
    },
    {
      label: rankDetail.movement.charAt(0).toUpperCase() + rankDetail.movement.slice(1),
      meaning: `The card's number or court role points to ${rankDetail.movement} inside ${detail.field}.`,
    },
  ];
};

const createEnhancement = (card) => {
  const detail = suitDetails[card.suit] || suitDetails["Major Arcana"];
  const grounded = groundedSections[card.name] || {};
  const upright = getMeaning(card.upright, 0);
  const secondUpright = getMeaning(card.upright, 1);
  const reversed = getMeaning(card.reversed, 0);
  const symbols = buildSymbols(card);
  const descriptionLead =
    clean(card.description).match(/^.*?[.!?](?:\s|$)/)?.[0]?.trim() ||
    `${card.name} carries the theme of ${lowerFirst(upright.label)}.`;
  const mirrorSlug = card.mirroring?.[0];
  const mirrorName = mirrorSlug && cardData[mirrorSlug]?.name;

  const finalLoveMeaning = grounded.loveMeaning;
  const finalCareerMeaning = grounded.careerMeaning;
  const finalSpiritualMeaning = grounded.spiritualMeaning;
  const finalShadowMessage = grounded.shadowMessage;
  const finalJournalPrompts = grounded.journalPrompts || [];

  const uniqueIntro = `${descriptionLead} When this card appears, ${card.name} becomes a practical lens for ${detail.field}, especially when ${lowerFirst(reversed.label)} is quietly shaping the situation.`;

  return {
    seoTitle: `${card.name} | Upright, Reversed, Love and Career | TarotFate`,
    metaDescription: `${card.name} in tarot: ${lowerFirst(upright.label)}, ${lowerFirst(secondUpright.label)} and ${lowerFirst(reversed.label)} in upright, reversed, love, career and spiritual readings.`,
    h1: card.name,
    imageAlt: `${card.name} tarot card artwork for ${card.suit}, showing ${symbols
      .slice(0, 2)
      .map((symbol) => lowerFirst(symbol.label))
      .join(" and ")}`,
    uniqueIntro,
    symbols,
    loveMeaning: finalLoveMeaning,
    careerMeaning: finalCareerMeaning,
    spiritualMeaning: finalSpiritualMeaning,
    shadowMessage: finalShadowMessage,
    journalPrompts: finalJournalPrompts,
    commonQuestions: [
      {
        question: `What does ${card.name} usually point to in a reading?`,
        answer: `${descriptionLead} The most useful reading of this card stays close to its established themes, visible artwork and the specific question being asked.`,
      },
      ...(finalShadowMessage
        ? [
            {
              question: `What should I be careful of with ${card.name}?`,
              answer: finalShadowMessage,
            },
          ]
        : []),
      ...(mirrorName
        ? [
            {
              question: `Which card pairs well with ${card.name}?`,
              answer: `${mirrorName} pairs well with ${card.name} because it can echo, challenge or clarify the same theme from another angle.`,
            },
          ]
        : []),
    ],
    seoFaqs: [
      ...(finalLoveMeaning
        ? [
            {
              question: `What does ${card.name} mean in love?`,
              answer: finalLoveMeaning,
            },
          ]
        : []),
      ...(finalCareerMeaning
        ? [
            {
              question: `What does ${card.name} mean for career?`,
              answer: finalCareerMeaning,
            },
          ]
        : []),
      ...(finalSpiritualMeaning
        ? [
            {
              question: `What is the spiritual message of ${card.name}?`,
              answer: finalSpiritualMeaning,
            },
          ]
        : []),
    ],
    relatedCards: card.mirroring || [],
    suitPage: suitPagePaths[card.suit],
  };
};

const enhancedCardData = Object.fromEntries(
  Object.entries(cardData).map(([slug, card]) => {
    const meaningOverride = meaningOverrides[card.name] || {};
    const readingOverride = buildSourceReading(card);
    const cardWithMeaning = {
      ...card,
      upright: meaningOverride.upright || card.upright,
      reversed: meaningOverride.reversed || card.reversed,
      reading: readingOverride || card.reading,
    };
    const enhancement = createEnhancement(cardWithMeaning);
    return [
      slug,
      {
        ...enhancement,
        ...cardWithMeaning,
        symbols: cardWithMeaning.symbols || enhancement.symbols,
        journalPrompts: cardWithMeaning.journalPrompts || enhancement.journalPrompts,
        commonQuestions: cardWithMeaning.commonQuestions || enhancement.commonQuestions,
        seoFaqs: cardWithMeaning.seoFaqs || enhancement.seoFaqs,
        relatedCards: cardWithMeaning.relatedCards || enhancement.relatedCards,
        suitPage: cardWithMeaning.suitPage || enhancement.suitPage,
      },
    ];
  })
);

export default enhancedCardData;

